import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { View, Text, StyleSheet, Dimensions, TextInput, Platform, ScrollView, TouchableOpacity, Alert, Linking, Image, LayoutAnimation, Vibration, ActivityIndicator, Share , useWindowDimensions, BackHandler } from 'react-native';
import { useAppStore } from '../../store/useAppStore';
import { useThemeColors, Colors, SIZES } from '../../constants/theme';
import Button3D from '../../components/Button3D';
import { RideOfferCard } from '../../components/RideOfferCard';
import { ActiveRideCard } from '../../components/ActiveRideCard';
import EspacioPatrocinado from '../../components/EspacioPatrocinado';
import { PenaltyGate } from '../../components/PenaltyGate';
import RouteDeviationModal from '../../components/RouteDeviationModal';
import MotivoDeEsperaModal from '../../components/MotivoDeEsperaModal';
import CustomModal from '../../components/CustomModal';
import PaymentModal from '../../components/PaymentModal';
import { Ionicons } from '@expo/vector-icons';
import { useRouter, useFocusEffect, useLocalSearchParams } from 'expo-router';
import { createAudioPlayer } from 'expo-audio';
import * as Haptics from 'expo-haptics';
import * as KeepAwake from 'expo-keep-awake';
import NetInfo from '@react-native-community/netinfo';

import { KYC_WALL_ENFORCED, VERTICAL_LOGISTICA_VISIBLE } from '../../constants/featureFlags';
import { esInvitado, bloquearSiInvitado } from '../../services/modoInvitado';
import { locationService } from '../../services/locationService';
import { rideSessionStore } from '../../services/rideSessionStore';
import { ratingDisplay } from '../../services/ratingUtils';
import { firestoreService } from '../../services/firestoreService';
import { zonaDe, suggestFare } from '../../services/geoUtils';
import { biometricService } from '../../services/biometricService';
import { notificationService } from '../../services/notificationService';
import { callOnRideCompleted, callGetDriverPublicInfo, callSubmitRideRating, callStartRideWithPin, callGetRoute, callCompletarEnvioConPin, callConfirmarEntregaDelEnvio, callConfirmarCobroDelViaje, callReverseGeocode } from '../../services/cloudFunctions';
import { observarSesion } from '../../services/firebaseConfig';
// Usamos el observer nativo — el del SDK web no se dispara en RN+Hermes
// (mismo bug que tenía el login antes de migrar).
import { onAuthStateChanged } from '@react-native-firebase/auth';
import { DriverRatingModal } from '../../components/DriverRatingModal';
import NavigationLauncher from '../../components/NavigationLauncher';
import StreetViewButton from '../../components/StreetViewButton';
import PlacesAutocomplete from '../../components/PlacesAutocomplete';
import BackgroundLocationDisclosure from '../../components/BackgroundLocationDisclosure';
import CintilloDemostracion from '../../components/CintilloDemostracion';
import { useMargenesSeguros } from '../../components/PantallaSegura';
import { encuadrarMapa, nuevoEstadoEncuadre, marcarGestoDelUsuario, limpiarGesto } from '../../components/Map/encuadre';
import { UBICACION_SEGUNDO_PLANO } from '../../constants/featureFlags';
import { vibrarExito } from '../../utils/vibrar';
import { mensajeDeError } from '../../utils/mensajeDeError';
import { placaValida } from '../../utils/placa';
import { pesos } from '../../utils/dinero';
import { direccionCorta } from '../../utils/direccion';
import * as Clipboard from 'expo-clipboard';
import { callSubmitPassengerRating } from '../../services/cloudFunctions';
// NOTA: @gorhom/bottom-sheet (Uber-style con gestos) NO funciona en web —
// requiere RCTAnimation module nativo. Lo activaremos en map.tsx cuando
// hagamos `eas build` para iOS/Android. Por ahora, el sheet usa View+ScrollView
// que se ve igual visualmente sin los gestos de drag.

const { width, height } = Dimensions.get('window');

const INITIAL_VEHICLES: any[] = [];

/**
 * Haversine simple — devuelve km entre dos coords. Sirve para mostrar al
 * chofer cuán lejos está la recogida y al pasajero cuán lejos viene el
 * chofer.
 */
function haversineKm(a: { lat: number; lng: number } | null | undefined, b: any): number | null {
  if (!a) return null;
  const bLat = b?.origin_lat ?? b?.originLat ?? b?.lat;
  const bLng = b?.origin_lng ?? b?.originLng ?? b?.lng;
  if (typeof bLat !== 'number' || typeof bLng !== 'number') return null;
  const R = 6371; // km
  const dLat = ((bLat - a.lat) * Math.PI) / 180;
  const dLng = ((bLng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (bLat * Math.PI) / 180;
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  return R * c;
}

/**
 * Emoji del vehículo. Un motorista al que el pasajero le ve un carro dibujado
 * mira a la calle buscando algo que no va a llegar; y si además la app le
 * escribió "CAR", lo que le enseñó fue un dato falso.
 */
function emojiDeVehiculo(tipo: string | null | undefined): string {
  const t = String(tipo ?? '').toUpperCase();
  if (t === 'MOTORCYCLE') return '🏍️';
  if (t === 'MINIBUS') return '🚐';
  return '🚗';
}

/**
 * El vehiculo pedido, escrito para una persona.
 *
 * La tarjeta de solicitud imprimia el valor crudo de la base: al chofer le
 * salia «MINIBUS • 2 pas.» y «MOTORCYCLE • 1 pas.». Son los nombres internos,
 * en ingles, que NO se tocan (los compara `dispatch.ts` y los cobra
 * `loyalty.ts`); lo que cambia es como se leen.
 */
const nombreDeVehiculo = (tipo: unknown): string => {
  const t = String(tipo ?? '').trim().toUpperCase();
  if (t === 'MOTORCYCLE') return 'Motor';
  if (t === 'MINIBUS') return 'Minibús';
  if (t === 'CAR') return 'Carro';
  // Un tipo que no conocemos se ensena tal cual: es mejor que inventarse uno.
  return t || 'Carro';
};


/** Texto "X.X km" o "—" si no se puede computar. */
function distanceLabel(loc: any, ride: any): string {
  const km = haversineKm(loc, ride);
  if (km == null) return '—';
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(1)} km`;
}

/** ETA aproximada asumiendo ~30 km/h promedio urbano Santo Domingo. */
function etaLabel(loc: any, ride: any): string {
  const km = haversineKm(loc, ride);
  if (km == null) return '—';
  const minutes = Math.max(1, Math.round((km / 30) * 60));
  return `${minutes} min`;
}

/**
 * LO QUE EL CHOFER NO PODÍA SABER.
 *
 * La tarjeta le decía a cuántos km está la RECOGIDA, pero no cuántos km mide
 * el viaje en sí: veía la dirección del destino y adivinaba. Así no hay forma
 * de decidir si un viaje conviene, porque lo que se gasta en gasolina es el
 * total —ir a buscarlo MÁS llevarlo— y el precio solo cubre la segunda parte
 * (suggestFare en geoUtils.ts cuenta únicamente origen→destino del pasajero).
 *
 * El 1.3 es el mismo factor que ya usa la pantalla del pasajero para pasar de
 * línea recta a calles reales: no se inventa un número nuevo.
 */
const FACTOR_CALLES = 1.3;

function kmDelViaje(ride: any): number | null {
  const oLat = Number(ride?.origin_lat ?? ride?.originLat);
  const oLng = Number(ride?.origin_lng ?? ride?.originLng);
  const dLat = Number(ride?.destination_lat ?? ride?.destinationLat);
  const dLng = Number(ride?.destination_lng ?? ride?.destinationLng);
  if (![oLat, oLng, dLat, dLng].every((x) => Number.isFinite(x))) return null;
  const recto = haversineKm({ lat: oLat, lng: oLng }, { lat: dLat, lng: dLng });
  return recto == null ? null : recto * FACTOR_CALLES;
}

/** "unos 6.1 km de viaje" — el largo del viaje, que antes no se veía. */
function largoDelViajeLabel(ride: any): string | null {
  const km = kmDelViaje(ride);
  if (km == null) return null;
  return km < 1 ? `${Math.round(km * 1000)} m de viaje` : `${km.toFixed(1)} km de viaje`;
}

/**
 * La cuenta hecha, para que no tenga que hacerla de cabeza con el semáforo en
 * verde: "Recogida 4.2 + viaje 6.1 = 10.3 km · RD$29 por km".
 * Todo es aritmética local: ni una llamada de red.
 */
function economiaLabel(loc: any, ride: any): string | null {
  const viaje = kmDelViaje(ride);
  const precio = Number(ride?.offerPrice ?? 0);
  if (viaje == null || !(precio > 0)) return null;
  const recogida = haversineKm(loc, ride);
  const total = (recogida ?? 0) + viaje;
  if (!(total > 0.1)) return null;
  const porKm = Math.round(precio / total);
  if (recogida == null) return `${viaje.toFixed(1)} km · RD$${porKm} por km`;
  return `Recogida ${recogida.toFixed(1)} + viaje ${viaje.toFixed(1)} = ${total.toFixed(1)} km · RD$${porKm} por km`;
}

/**
 * ¿Estos dos valores son el MISMO dato?
 *
 * Ante la duda dice que NO: equivocarse por ese lado solo repite el trabajo de
 * hoy, mientras que equivocarse por el otro dejaría la pantalla mostrando algo
 * viejo, que sí es un fallo visible.
 */
function valorIgual(a: any, b: any): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  // Un Timestamp de Firestore llega como objeto NUEVO en cada snapshot aunque
  // marque el mismo instante: comparado por referencia nunca coincidiría.
  if (typeof a.toMillis === 'function' && typeof b.toMillis === 'function') {
    try { return a.toMillis() === b.toMillis(); } catch { return false; }
  }
  if (typeof a === 'object' && typeof b === 'object') {
    try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; }
  }
  return false;
}

/**
 * ¿El documento del viaje trae algún cambio real?
 *
 * POR QUÉ EXISTE: el listener del viaje dispara en CADA snapshot del
 * documento, incluidos los que no cambian nada (la doble emisión del
 * `serverTimestamp`, por ejemplo). Al escribir siempre un objeto nuevo, ese
 * objeto viajaba por `pickupCoordMemo` y `puntosParaEncuadrar` hasta las props
 * del mapa y rompía su `React.memo`: Android volvía a medir, maquetar y
 * RASTERIZAR hasta 50 marcadores en el hilo principal, varias veces por
 * segundo. Eso es el congelamiento del mapa.
 */
function mismoViaje(a: any, b: any): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const claves = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of claves) {
    if (!valorIgual(a[k], b[k])) return false;
  }
  return true;
}

/**
 * TODA ESPERA DE RED, CON RELOJ Y SALIDA.
 *
 * Una escritura de Firestore sin cobertura NO falla: se queda encolada en el
 * dispositivo y su promesa no se resuelve hasta que alcanza el servidor —
 * puede ser dentro de una hora. Un `await` pelado sobre eso deja al chofer
 * mirando un botón que no contesta y sin manera de saber si quedó conectado.
 *
 * `conPlazo` convierte ese silencio en un fallo con nombre a los `ms` dados.
 * OJO: no cancela la escritura, solo deja de esperarla. Por eso quien la use
 * tiene que dejar el estado del servidor coherente por su cuenta (aquí:
 * mandando el apagado detrás, que Firestore entrega en orden).
 */
function conPlazo<T>(promesa: Promise<T>, ms: number, queEra: string): Promise<T> {
  return new Promise<T>((resolver, rechazar) => {
    const reloj = setTimeout(() => rechazar(new Error(`tiempo agotado: ${queEra}`)), ms);
    promesa.then(
      (v) => { clearTimeout(reloj); resolver(v); },
      (e) => { clearTimeout(reloj); rechazar(e); }
    );
  });
}

/**
 * ¿Este viaje es el de mentira?
 *
 * El id lo delata, y es la comprobación MÁS SEGURA que hay: un viaje real
 * lleva un id de Firestore y jamás empieza por `qa_mock_ride_`. Mirar la
 * bandera del modo en vez del id sería peor — se puede apagar con el viaje
 * falso todavía en pantalla, y entonces habría que decidir qué hacer con él;
 * el id no cambia nunca.
 *
 * Vive FUERA del componente a propósito: hace falta desde el efecto del PIN,
 * que se declara mucho antes que el resto del modo demostración.
 */
const esViajeFalso = (id: unknown) => String(id ?? '').startsWith('qa_mock_ride_');

export default function MapScreen() {
  const router = useRouter();
  const role = useAppStore((state) => state.userRole);
  // EL RADIO DE TRABAJO DEL CHOFER. Lo elige él en Actividad. NO esconde
  // ningún viaje: decide por cuáles SUENA el teléfono y cuáles se rotulan
  // «Lejos de ti». Ver la nota larga donde se silencia el aviso.
  const radioZonaKm = useAppStore((state) => state.radioZonaKm);
  const setSolicitudesVisibles = useAppStore((state) => state.setSolicitudesVisibles);
  // Inset REAL de la barra del sistema. Con `edgeToEdgeEnabled=true` el panel
  // inferior y la botonera del chofer se dibujaban DEBAJO de los tres botones
  // de Android: los controles existían pero el toque se lo llevaba el sistema.
  const { abajo: margenAbajo } = useMargenesSeguros();
  // `alto` reactivo. El `Dimensions.get('window')` de nivel de módulo es una
  // foto tomada una sola vez que además, bajo edge-to-edge, incluye la franja
  // de las barras — por eso los topes de altura salían mal calculados.
  const { height: alto } = useWindowDimensions();
  /**
   * Alto REAL de la hoja inferior que esté montada, medido en pantalla.
   *
   * Hace falta para dos cosas: decirle al mapa cuánto espacio tiene tapado
   * (`mapPadding`) y colocar el botón de "mi ubicación" por encima. Se mide en
   * vez de estimarse porque la altura cambia con el contenido, el idioma y el
   * tamaño de letra del teléfono.
   */
  const [altoHoja, setAltoHoja] = useState(0);
  /**
   * Contador de "recentrar ahora". El mapRef vive dentro del contenedor del
   * mapa, así que la forma limpia de pedirle un encuadre desde fuera es
   * incrementar este número: el hijo reacciona a su cambio.
   */
  const [reencuadrarAhora, setReencuadrarAhora] = useState(0);
  const medirHoja = useCallback((e: any) => {
    const h = e?.nativeEvent?.layout?.height ?? 0;
    // Histéresis de 4 px: sin ella, cada render devolvía una altura con
    // decimales distintos y el padre se re-renderizaba en bucle.
    setAltoHoja((prev) => (Math.abs(h - prev) > 4 ? h : prev));
  }, []);

  // ── EL VEHÍCULO REAL DEL CHOFER ────────────────────────────────────────
  //
  // `selectedVehicleType` es el selector del PASAJERO ("¿en qué quieres que te
  // recojan?"): arranca en 'CAR' escrito a mano y TODOS sus setters viven
  // dentro del bloque `role === 'PASSENGER'`. El chofer nunca ve ese selector.
  //
  // Aun así, su valor viajaba en tres escrituras del CHOFER (setDriverOnline y
  // las dos ofertas). Resultado: un minibús se publicaba como 'CAR'. Y eso no
  // es solo cosmético —
  //   · `dispatch.ts` filtra por `drivers.vehicleType`, así que el minibús NO
  //     recibía las solicitudes de minibús;
  //   · `loyalty.ts` cobra MINIBUS 1500 vs CAR 1300 → RD$200/mes sin cobrar.
  //
  // El dato bueno ya existe: lo escribe `setDriverRole` en
  // `users/{uid}.vehicleType` cuando el chofer elige su vehículo en el KYC.
  // Aquí solo se lee.
  const [vehiculoRealChofer, setVehiculoRealChofer] = useState<string | null>(null);
  /**
   * El mismo dato en un ref, para poder leerlo DENTRO del listener de
   * solicitudes sin meterlo en las dependencias de ese efecto.
   *
   * Si se metiera como dependencia, el listener de Firestore se desmontaría y
   * se volvería a montar en cuanto llegue el perfil del chofer — justo al
   * arrancar, que es cuando más importa no perder una solicitud. Es el mismo
   * patrón que ya usan `incomingPassengerRequestRef` y compañía en este
   * archivo.
   */
  const vehiculoRealChoferRef = useRef<string | null>(null);
  useEffect(() => { vehiculoRealChoferRef.current = vehiculoRealChofer; }, [vehiculoRealChofer]);
  /**
   * CONTRATO (d) — AQUÍ YA NO SE LEE LA NOTA DEL CHOFER.
   *
   * Este estado guardaba `driverRatingAvg` / `driverRatingCount` del propio
   * chofer para mandarlos dentro de su oferta. El dato era real, pero el
   * ESCRITOR era el interesado: un cliente parcheado ponía la nota que
   * quisiera en la tarjeta que el pasajero mira antes de montarse. Ahora la
   * reputación de la oferta la estampa `sellarReputacionDeLaOferta`
   * (functions/src/rides.ts) leyendo /users/{driverId} con Admin SDK, y las
   * reglas rechazan la oferta que traiga `rating`, `ratingCount`,
   * `isFaceIdVerified` o `isDriverVerified`. Por eso el estado se fue: leerlo
   * aquí ya no sirve para nada y volver a mandarlo rompería toda oferta.
   */
  useEffect(() => {
    const uid = useAppStore.getState().userFirebaseUid;
    const esChofer = role === 'DRIVER' || role === 'DELIVERY_DRIVER' || role === 'SCHOOL_DRIVER';
    if (!uid || !esChofer) return;
    let vivo = true;
    (async () => {
      try {
        const perfil = await firestoreService.getUserProfile(uid);
        const v = String(perfil?.vehicleType ?? '').trim();
        if (vivo && v) setVehiculoRealChofer(v);
      } catch (e: any) {
        // Sin el perfil se cae al selector, que es el comportamiento viejo:
        // peor dato, pero nunca deja al chofer sin poder conectarse.
        console.warn('[map] no se pudo leer el vehículo del chofer:', e?.message);
      }
    })();
    return () => { vivo = false; };
  }, [role]);


  // Selector, no `esInvitado()`: esto se usa en render y tiene que redibujarse
  // solo cuando el invitado crea su cuenta.
  const guestPreview = useAppStore((state) => state.guestPreview);
  const uidParaInvitado = useAppStore((state) => state.userFirebaseUid);
  const invitado = guestPreview && !uidParaInvitado;
  const userFirebaseUid = useAppStore((state) => state.userFirebaseUid);
  const isVerified = useAppStore((state) => state.isVerified);
  const colors = useThemeColors();
  // Param que llega cuando el user tapea la notificación persistente estando
  // la app killed → _layout hace router.replace('/(tabs)/map?resumeRideId=...')
  // y aquí lo levantamos para restaurar el ride correcto.
  const { resumeRideId } = useLocalSearchParams<{ resumeRideId?: string }>();
  
  // Passenger state
  const [destination, setDestination] = useState('');
  const [passengers, setPassengers] = useState('1');
  const [selectedVehicleType, setSelectedVehicleType] = useState<'CAR' | 'MOTORCYCLE' | 'MINIBUS'>('CAR');

  /**
   * ══ ¿EL ENVÍO VA POR LOGÍSTICA? ══════════════════════════════════
   *
   * Es una bandera APARTE y no un valor más de `selectedVehicleType`, y la
   * diferencia importa: ese estado alimenta `vehiculoParaPublicar`,
   * `suggestFare` y el `vehicleType` que se guarda en el viaje, y de ahi lo
   * lee `dispatch.ts`, que filtra los choferes contra un conjunto cerrado de
   * tres valores. Meter 'LOGISTICA' ahi seria cruzar la frontera que el
   * fundador declaró intocable — y en el mejor caso el filtro no casaría con
   * nadie, en el peor saldría a TODOS los choferes online.
   *
   * Así `selectedVehicleType` sigue siendo siempre un vehículo de viaje
   * válido, y logística es otro camino: no crea un viaje, crea un trabajo que
   * los transportistas cotizan.
   */
  const [envioPorLogistica, setEnvioPorLogistica] = useState(false);

  /**
   * ══ ¿EL VEHÍCULO DEL ENVÍO LO ELIGIÓ LA PERSONA? ═══════════════════════
   *
   * Parece redundante y no lo es. En las versiones de la app que ya están
   * instaladas, el selector de vehículo NO SE VE en modo paquete, pero el campo
   * `vehicleType` viaja igual con el valor por defecto: 'CAR'. O sea que un
   * 'CAR' en un envío puede significar dos cosas opuestas — «quiero un carro» o
   * «nadie me preguntó»— y la lista del chofer tiene que tratarlas distinto:
   *
   *   · elegido  → se respeta, que es lo que pidió el fundador.
   *   · heredado → no se filtra, o al motorista dejarían de salirle TODOS los
   *     envíos de las apps no actualizadas — y esa lista era su ÚNICO canal,
   *     porque el reparto automático del servidor ya filtraba por vehículo.
   *
   * Las apps viejas no mandan esta marca, así que su ausencia ES la señal. Se
   * puede quitar cuando la base esté actualizada.
   */
  const [vehiculoElegidoPorMi, setVehiculoElegidoPorMi] = useState(false);

  /**
   * El vehículo que el CHOFER debe publicar. NUNCA el selector del pasajero.
   * Va justo aquí porque depende de `selectedVehicleType` como respaldo, y en
   * JS una `const` no se puede usar antes de su declaración.
   */
  const vehiculoParaPublicar = vehiculoRealChofer || selectedVehicleType;
  const [offerPrice, setOfferPrice] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [incomingDriverOffers, setIncomingDriverOffers] = useState(false);
  const [receivedOffers, setReceivedOffers] = useState<any[]>([]);
  const [isActiveTrip, setIsActiveTrip] = useState(false);
  // NOTA: el efecto de notificación persistente Uber-style vive MÁS ABAJO
  // (después de declarar `currency` y `currentRideId`). Si se coloca aquí
  // arriba, su array de dependencias lee `currency`/`currentRideId` en su
  // TDZ (aún sin inicializar) → ReferenceError en cada render. Ver el bloque
  // marcado "NOTIFICACIÓN PERSISTENTE" tras la declaración de `currency`.

  const [paymentMethod, setPaymentMethod] = useState<'CASH' | 'CARD' | 'TRANSFER'>('CASH');
  const [preferences, setPreferences] = useState({ quiet: false, ac: true });
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);

  /**
   * EL COBRO AL BAJARSE DEL CARRO.
   *
   * Un viaje por transferencia NO se paga durante el trayecto: se paga al
   * llegar. Y justo ahí el código hacía lo contrario de lo que hace falta —
   * cerraba el modal de pago, BORRABA las cuentas del chofer y saltaba a las
   * estrellas—, así que el pasajero se quedaba sin saber a dónde transferir.
   * Reportado desde la calle: viaje del 29-sep, RD$150 por transferencia.
   *
   * Esto captura lo necesario ANTES de limpiar y lo enseña como cierre.
   */
  /**
   * EL CHOFER CONFIRMA QUE LE PAGARON.
   *
   * No existia. El chofer cerraba el viaje y lo unico que le salia era
   * calificar al pasajero: la app nunca le preguntaba si habia cobrado, y el
   * documento del viaje no tenia donde anotarlo. Para COMIDA si existia ese
   * aviso (delivery-active.tsx); los viajes se quedaron sin el.
   *
   * Importa porque el dinero del viaje va DIRECTO del pasajero al chofer: si
   * nadie anota que se pago, no hay forma de resolver un "yo si te pague"
   * contra un "a mi no me llego".
   */
  const [cobroPendiente, setCobroPendiente] = useState<{
    rideId: string;
    monto: number;
    metodo: string;
    pasajero: any;
  } | null>(null);
  const [confirmandoCobro, setConfirmandoCobro] = useState(false);

  /**
   * MIS PROPIAS cuentas de cobro (no las del chofer que mira un pasajero).
   * Nada en toda la app miraba esto: un chofer sin cuenta registrada aceptó un
   * viaje marcado como transferencia, y el pasajero se quedó sin saber a dónde
   * pagarle. Nadie avisó a ninguno de los dos.
   */
  const [misCuentasDeCobro, setMisCuentasDeCobro] = useState<any[] | null>(null);

  const [cierreDePago, setCierreDePago] = useState<{
    metodo: string;
    monto: number;
    cuentas: any[];
    chofer: string;
  } | null>(null);

  // Espejo de las cuentas: el escuchador del viaje vive en una clausura y
  // leería el valor de cuando se montó, no el de ahora.
  const driverBankAccountsRef = useRef<any[]>([]);
  const [driverBankAccounts, setDriverBankAccounts] = useState<any[]>([]);
  useEffect(() => { driverBankAccountsRef.current = driverBankAccounts; }, [driverBankAccounts]);

  // `null` mientras no se sabe: así el aviso no parpadea diciendo «no tienes
  // cuenta» antes de haber mirado.
  useEffect(() => {
    const soyChofer = role === 'DRIVER' || role === 'DELIVERY_DRIVER' || role === 'SCHOOL_DRIVER';
    if (!soyChofer || !userFirebaseUid) { setMisCuentasDeCobro(null); return; }
    const unsub = firestoreService.listenToMyBankAccounts(userFirebaseUid, (lista) => {
      setMisCuentasDeCobro(Array.isArray(lista) ? lista : []);
    });
    return () => { if (typeof unsub === 'function') unsub(); };
  }, [role, userFirebaseUid]);
  
  // FIX HALLAZGO 2: Fidelidad real desde Firestore (NO hardcoded).
  // Antes useState(9) regalaba viaje gratis a TODOS los usuarios.
  // Ahora arrancamos en 0 y cargamos el contador real del perfil al montar
  // (loyalty.passengerTripsCompleted lo escribe la Cloud Function
  // `onRideCompleted` después de cada viaje cobrado, idempotente).
  // El primer viaje del usuario sigue siendo gratis (count = 0) por la lógica
  // de Cloud Function — no por hardcode en el cliente.
  const [tripsCompleted, setTripsCompleted] = useState(0);
  // FIX AUDIT — Antes `isFreeTrip = tripsCompleted >= 9`, que IGNORABA el
  // primer viaje gratis y los cupones de fidelidad reales. El servidor sí
  // regala el primer viaje (loyalty.isFirstRideFreeUsed) y cada 10
  // (freeTripsAvailable), pero el pasajero nunca lo veía. Ahora leemos esas
  // señales reales del perfil. Por defecto asumimos "NO gratis" para nunca
  // prometer gratis y luego cobrar; al cargar el perfil se corrige.
  const [firstRideUsed, setFirstRideUsed] = useState(true);
  const [freeTripsAvailable, setFreeTripsAvailable] = useState(0);
  // V11/V24/V27 — H04: la cuenta sin teléfono, correo verificado ni cédula NO
  // tiene su primer viaje gratis hasta verificarse. El servidor lo marca en
  // `loyalty.primerViajeGratisRequiereIdentidad` y retira la cobertura del
  // viaje; sin leer la marca, esta pantalla lo seguía prometiendo en cada
  // solicitud. Con un viaje de fidelidad ganado sigue cubierto (LOYALTY), igual
  // que en el servidor.
  const [primerGratisEnEspera, setPrimerGratisEnEspera] = useState(false);
  const freeTripReason: 'FIRST' | 'LOYALTY' | null =
    (!firstRideUsed && !primerGratisEnEspera) ? 'FIRST' : (freeTripsAvailable > 0 ? 'LOYALTY' : null);
  const isFreeTrip = freeTripReason !== null;
  // Se sube para volver a leer el perfil: las marcas de gratis las escribe el
  // servidor DESPUÉS de crear o cerrar un viaje, y leerlas solo al montar dejaba
  // la pantalla prometiendo con datos viejos toda la sesión.
  const [recargaPerfilGratis, setRecargaPerfilGratis] = useState(0);
  const recargarGratisDelPerfil = () => setRecargaPerfilGratis((n) => n + 1);
  // La cobertura REAL del viaje del pasajero, según su documento (no según lo
  // que esta pantalla prometió al pedirlo).
  const coberturaDelViajeRef = useRef<{ rideId: string | null; cubierto: boolean }>({ rideId: null, cubierto: false });

  useEffect(() => {
    let alive = true;
    const uid = useAppStore.getState().userFirebaseUid;
    if (!uid) return;
    (async () => {
      try {
        const profile = await firestoreService.getUserProfile(uid);
        if (!alive || !profile) return;
        const count = Number(
          profile.loyalty?.passengerTripsCompleted
          ?? profile.tripsCompleted
          ?? 0
        );
        setTripsCompleted(Number.isFinite(count) ? count : 0);
        // Señales reales de gratis desde el perfil (loyalty server-side).
        setFirstRideUsed(profile.loyalty?.isFirstRideFreeUsed === true);
        setPrimerGratisEnEspera(profile.loyalty?.primerViajeGratisRequiereIdentidad === true);
        const freeCount = Number(profile.loyalty?.freeTripsAvailable ?? 0);
        setFreeTripsAvailable(Number.isFinite(freeCount) ? freeCount : 0);
      } catch {
        // Sin perfil legible (usuario nuevo / sin permisos) → 0 trips.
      }
    })();
    return () => { alive = false; };
  }, [userFirebaseUid, recargaPerfilGratis]);
  const [customModalConfig, setCustomModalConfig] = useState<{visible: boolean, title: string, message: string, icon?: any, color?: string}>({visible: false, title: '', message: ''});
  
  // Counter Offer Modal
  const [showCounterOfferModal, setShowCounterOfferModal] = useState(false);
  const [newCounterPrice, setNewCounterPrice] = useState('');
  
  // Reparto y Ubicación Verificada
  const [serviceType, setServiceType] = useState<'RIDE' | 'DELIVERY'>('RIDE');
  const [packageDescription, setPackageDescription] = useState('');
  // Lo que el pasajero quiere decirle al chofer antes de que acepte.
  // Nace de ver que en inDrive la gente escribe ahi que prefiere un carro
  // comodo. Aqui NO filtra choferes a proposito: filtrar con la flota que
  // hay hoy dejaria la solicitud sin que nadie la vea. Es una preferencia,
  // y decide el chofer.
  const [notaDelPasajero, setNotaDelPasajero] = useState('');
  // `showSuggestions` vivía aquí y NADIE lo leía desde que `PlacesAutocomplete`
  // sustituyó a la lista fija de lugares: un estado muerto que hacía creer que
  // esta pantalla todavía dibuja un desplegable propio de sugerencias.
  // ── POR QUÉ YA NO EXISTE `selectedDropoffGPS` ────────────────────────────
  //
  // Era un booleano paralelo a `destinationLat/Lng` que decía "el destino ya
  // tiene coordenadas". Los dos podían DISCREPAR, y discrepaban: tocar la
  // pestaña "Viaje" o "Enviar Paquete" lo apagaba SIN borrar las coordenadas.
  // Como la exigencia del sector colgaba de ese booleano, el campo del sector
  // desaparecía de la pantalla y la validación se saltaba: el viaje salía con
  // coordenadas reales y `destinationSector: null`, y el chofer volvía a
  // aceptar a ciegas — justo lo que el sector vino a evitar.
  //
  // La cura no es sincronizar los dos: es que solo haya UNO, derivado del dato
  // mismo y no de un booleano que alguien tenga que acordarse de apagar:
  // `hayCoordenadaDestino` para lo que de verdad necesita el punto en el mapa, y
  // `hayDestinoEscrito` para el sector, que hace más falta cuanto MENOS
  // coordenadas hay.
  // H4 FIX — Coords del destino elegido en el autocompletado. Antes solo
  // guardábamos `destination` (string) y el chofer navegaba a las coords
  // hardcoded de fallback (18.4731, -69.9329 = Santo Domingo). Ahora
  // propagamos lat/lng al ride para que NavigationLauncher del chofer
  // apunte al lugar real.
  /**
   * Sector / barrio / residencial del destino. OBLIGATORIO.
   *
   * En Santo Domingo una misma calle existe en varios sectores y a distancias
   * muy distintas. Si el pasajero solo escribe la calle, el chofer acepta a
   * ciegas y descubre tarde que el viaje es el doble de largo — de ahí salen
   * las discusiones y las cancelaciones. Es la queja número uno de los choferes
   * contra Uber e InDriver en el país.
   */
  const [sectorDestino, setSectorDestino] = useState('');
  const [destinationLat, setDestinationLat] = useState<number | null>(null);
  const [destinationLng, setDestinationLng] = useState<number | null>(null);
  /**
   * ¿El destino tiene punto en el mapa?
   *
   * La ÚNICA fuente de verdad. Sustituye al booleano `selectedDropoffGPS`, que
   * podía quedar apagado con las coordenadas puestas y por ahí se colaba un
   * viaje sin sector. Derivado de las coordenadas, no puede discrepar de ellas.
   */
  const hayCoordenadaDestino =
    typeof destinationLat === 'number' && typeof destinationLng === 'number';
  /**
   * ¿HAY DESTINO ESCRITO? De aquí cuelga el sector, y no de las coordenadas.
   *
   * Se cerró el hueco de las pestañas, pero quedaba otra puerta: el campo solo
   * se dibujaba con COORDENADAS de destino y la validación colgaba del mismo
   * sitio. La caja del destino admite texto libre —se puede escribir la calle a
   * mano sin elegir sugerencia—, y en ese caso no aparecía el campo NI se
   * exigía nada: el viaje salía con destino escrito y `destinationSector: null`,
   * el chofer aceptando a ciegas, que es justo lo que el sector vino a evitar.
   *
   * Y ahí hace MÁS falta, no menos: sin punto en el mapa, el sector es lo único
   * que sitúa el viaje.
   */
  const hayDestinoEscrito = destination.trim().length > 0;

  // ── PEDIR EL VIAJE PARA OTRA PERSONA ──────────────────────────────────────
  //
  // Hasta ahora había UN solo campo y lo único que editaba era el destino. La
  // dirección de recogida solo aparecía dentro del TEXTO DE RELLENO ("Desde
  // Calle 18W, Cancino a…"), que en React Native se borra al escribir la
  // primera letra: la caja anunciaba el origen mientras estaba vacía y después
  // se quedaba solo con el destino. Por eso confundía.
  //
  // Ahora la recogida es un campo propio, relleno con la ubicación de quien
  // pide y editable. Si la cambia, el viaje sale desde ESE punto: es lo que
  // permite pedirle el carro a la mamá. El backend ya lo soportaba entero (el
  // despacho lee el origen del DOCUMENTO del viaje, no el GPS del teléfono).
  const [origenTexto, setOrigenTexto] = useState('');
  const [origenLat, setOrigenLat] = useState<number | null>(null);
  const [origenLng, setOrigenLng] = useState<number | null>(null);
  /**
   * `true` en cuanto el texto de la recogida deja de ser la dirección donde
   * está el teléfono. Mientras sea `false`, el campo se sigue rellenando solo
   * con la geocodificación inversa.
   */
  const [origenManual, setOrigenManual] = useState(false);
  /**
   * VERSIÓN DEL FORMULARIO — H06/H35 (auditoría 16-sep-2026).
   *
   * Los dos buscadores de dirección (recogida y destino) solo copian el valor
   * del padre MIENTRAS NADIE HA ELEGIDO nada (ver `PlacesAutocomplete`). Tras
   * elegir una sugerencia se quedan con su propio texto. Si el viaje se cierra
   * con el formulario A LA VISTA —cancelar mientras se busca chofer—, vaciar
   * el estado no bastaba: la caja seguía enseñando la dirección de la mamá
   * mientras el viaje ya iba a salir del GPS del teléfono, o un destino escrito
   * que al pedir respondía "Destino requerido".
   *
   * `cerrarViajeDelPasajero` sube este número y los dos buscadores lo llevan
   * como `key`: se vuelven a montar limpios. Solo cambia al CERRAR un viaje,
   * nunca mientras alguien escribe, así que no pierde el foco del teclado ni
   * rehace la sesión de Google a media búsqueda (el motivo por el que ese
   * componente evita `key` para sincronizarse).
   */
  const [versionFormulario, setVersionFormulario] = useState(0);
  /**
   * CANCELAR VIAJE EN VUELO — H05/H07 (auditoría 16-sep-2026).
   *
   * La cancelación ahora espera la confirmación del servidor, y eso tarda. Sin
   * cerrojo, un segundo toque lanzaba otra cancelación; y sin decirlo, el
   * botón parecía muerto. El ref corta el doble toque (el estado de React
   * llega tarde para eso); el estado le pone "Cancelando…" al botón.
   */
  const cancelandoViajeRef = useRef(false);
  const [cancelandoViaje, setCancelandoViaje] = useState(false);
  /**
   * CANDADOS CONTRA EL DOBLE TOQUE — H12/H40/H42/H44 (auditoría 16-sep-2026).
   *
   * Mismo patrón que `cancelandoViajeRef`: el ref corta el segundo toque en el
   * MISMO instante (el estado de React llega un render tarde para eso) y el
   * estado solo le dice al botón que ya se está trabajando. Todos se sueltan en
   * `finally`, o sea en el éxito Y en el error: un candado que se queda puesto
   * tras un fallo sería un botón muerto.
   *
   *  · solicitandoViaje (H12): "Solicitar Viaje" espera al GPS y a dos escrituras.
   *    Dos toques en ese rato creaban DOS viajes PENDING —todos los choferes
   *    veían la solicitud duplicada— y las ofertas de los dos se mezclaban en
   *    una sola lista.
   *  · enviandoOferta (H40): "Aceptar Viaje" y "Enviar" contraoferta hacen
   *    `.add()`; cada toque era un documento de oferta más y el pasajero veía al
   *    mismo chofer dos veces en la subasta.
   *  · marcandoLlegada (H42): las reglas dejan ARRIVED→ARRIVED (a propósito,
   *    para reintentos), así que el segundo toque reescribía `arrivedAt`, la
   *    base del contador de espera.
   *  · enviandoPin (H44): el botón "Iniciar viaje" no miraba si ya había un
   *    envío en vuelo. El servidor rechazaba el segundo (el viaje ya estaba
   *    IN_PROGRESS) y al chofer le salía "No pudimos iniciar el viaje" con el
   *    viaje YA arrancado.
   */
  const solicitandoViajeRef = useRef(false);
  const [solicitandoViaje, setSolicitandoViaje] = useState(false);
  const enviandoOfertaRef = useRef(false);
  const [enviandoOferta, setEnviandoOferta] = useState(false);
  /**
   * H40 — Envuelve "Aceptar Viaje" y "Enviar" contraoferta del chofer. Va como
   * envoltorio para no tocar ni una línea de lo que cada botón hace por dentro
   * (biometría, datos de la oferta, avisos): solo decide si ese cuerpo corre.
   * Los dos comparten candado a propósito: son la misma oferta del mismo chofer.
   */
  const conCandadoDeOferta = useCallback(async (accion: () => Promise<unknown>) => {
    if (enviandoOfertaRef.current) return;
    enviandoOfertaRef.current = true;
    setEnviandoOferta(true);
    // Sin señal la oferta queda en la cola de Firestore y la promesa no contesta
    // hasta que vuelve la conexión. El candado NO se suelta por tiempo: cada
    // toque crea una oferta nueva (`add`) y el pasajero vería al mismo chofer
    // dos veces. Lo que sí se hace es decirle al chofer qué está pasando, y
    // solo si de verdad no hay red (la huella puede tardar lo que tarde).
    const avisoSinSenal = setTimeout(() => {
      if (!enviandoOfertaRef.current) return;
      NetInfo.fetch()
        .then((red) => {
          if (!enviandoOfertaRef.current) return;
          if (red.isConnected === false || red.isInternetReachable === false) {
            showAlert(
              'Sin señal',
              'Tu oferta quedó guardada y sale sola en cuanto vuelva la conexión. No hace falta tocar otra vez.',
              'cloud-offline',
              '#FF9500'
            );
          }
        })
        .catch(() => {});
    }, 15000);
    try {
      await accion();
    } finally {
      clearTimeout(avisoSinSenal);
      enviandoOfertaRef.current = false;
      setEnviandoOferta(false);
    }
  }, []);
  const marcandoLlegadaRef = useRef(false);
  const [marcandoLlegada, setMarcandoLlegada] = useState(false);
  const telefonoSincronizadoRef = useRef<string | null>(null);
  const enviandoPinRef = useRef(false);
  /**
   * CUÁNDO SE ACEPTÓ EL VIAJE DEL PASAJERO — H24 (auditoría 16-sep-2026).
   *
   * El diálogo de "Cancelar Viaje" tiene que decir la verdad según el momento:
   * los 2 primeros minutos tras aceptar se cancela sin multa. La hora sale de
   * `rides/{id}.acceptedAt`, la copia que escribe el servidor en la MISMA
   * transacción que el sello con el que decide la multa (`sellos_de_viaje`,
   * functions/src/rides.ts). Se guarda con el id del viaje para que la hora de
   * un viaje anterior no se lea nunca como la del actual. Es un ref y no estado:
   * solo se consulta al tocar el botón, no pinta nada.
   */
  const aceptacionDelViajeRef = useRef<{ rideId: string; ms: number } | null>(null);
  const recordarAceptacionDelViaje = useCallback((rideId: string, sello: any) => {
    const ms =
      typeof sello?.toMillis === 'function' ? sello.toMillis()
        : typeof sello === 'number' ? sello
        : null;
    // Sin dato no se borra lo que ya había de ESTE viaje: un snapshot que llega
    // sin el campo no puede hacer que el diálogo olvide la hora.
    if (!rideId || typeof ms !== 'number' || !Number.isFinite(ms) || ms <= 0) return;
    aceptacionDelViajeRef.current = { rideId, ms };
  }, []);
  /**
   * H36 (auditoría 16-sep-2026) — ¿Este teléfono, en modo chofer, escribió en
   * `offerPrice` el precio de una solicitud AJENA? Ver `mostrarSolicitud`.
   */
  const precioAjenoEnFormularioRef = useRef(false);
  /**
   * El origen ELEGIDO A MANO, y solo si tiene coordenadas.
   *
   * Sin lat/lng el despacho hace early-return y el viaje queda invisible para
   * todos los choferes (`functions/src/dispatch.ts`), así que un origen escrito
   * a mano sin punto en el mapa NO vale: aquí devuelve null y `handleRequestRide`
   * lo bloquea con un mensaje. Nunca se rellena con el GPS por debajo — eso
   * mandaría el carro a la casa de quien pide, no a la de su mamá.
   */
  const origenElegido = useMemo(() => {
    if (!origenManual) return null;
    if (typeof origenLat !== 'number' || typeof origenLng !== 'number') return null;
    return { lat: origenLat, lng: origenLng, texto: origenTexto.trim() };
  }, [origenManual, origenLat, origenLng, origenTexto]);
  /**
   * LA RECOGIDA DEL VIAJE VIVO, LEÍDA DEL DOCUMENTO.
   *
   * `origenElegido` es estado de PANTALLA y NADIE lo rehidrata: al reabrir la
   * app con el viaje en curso valía null, el pin de recogida se caía sobre el
   * GPS del teléfono y —si el carro se pidió para su mamá— el mapa y la recta
   * volvían a mentir. De paso desaparecían el aviso de que el chofer va a otra
   * dirección y el botón de mandarle el PIN por WhatsApp, justo cuando hacen
   * falta.
   *
   * El dato bueno ya está en el viaje (`origin_lat`/`origin_lng`, inmutables
   * por regla) y la restauración YA lo lee; lo único que faltaba era usarlo.
   */
  const [recogidaDelViaje, setRecogidaDelViaje] = useState<{ lat: number; lng: number; texto: string } | null>(null);
  /**
   * Saca la recogida de un documento de viaje. Devuelve null si el viaje no
   * trae coordenadas: sin dato es sin dato, NO se rellena con el GPS —ese
   * relleno es exactamente el fallo que esto viene a cerrar.
   */
  const recogidaDeDocumento = useCallback((viaje: any) => {
    const la = viaje?.origin_lat ?? viaje?.originLat;
    const lo = viaje?.origin_lng ?? viaje?.originLng;
    if (typeof la !== 'number' || typeof lo !== 'number') return null;
    return { lat: la, lng: lo, texto: String(viaje?.originAddress ?? '').trim() };
  }, []);
  /**
   * EL DESTINO DEL VIAJE, SACADO DEL DOCUMENTO.
   *
   * Existe por la misma razón que `recogidaDelViaje`: `destinationLat/Lng` es
   * estado de PANTALLA y nadie lo rehidrata, así que al reabrir la app durante
   * un viaje valía null. El pasajero necesita ver la línea hacia SU DESTINO en
   * cuanto se monta, y ese dato solo puede venir del viaje, que sí sobrevive al
   * reinicio.
   *
   * Sin coordenadas devuelve null y no se dibuja nada: en más de la mitad de
   * los viajes reales el destino se escribió a mano y no tiene punto en el mapa.
   * Eso se DICE (ver `extremosDeLaLinea`), no se inventa.
   */
  const [destinoDelViaje, setDestinoDelViaje] = useState<{ lat: number; lng: number } | null>(null);
  const destinoDeDocumento = useCallback((viaje: any) => {
    const la = viaje?.destinationLat ?? viaje?.destination_lat;
    const lo = viaje?.destinationLng ?? viaje?.destination_lng;
    if (typeof la !== 'number' || typeof lo !== 'number') return null;
    return { lat: la, lng: lo };
  }, []);
  const [selectedDriver, setSelectedDriver] = useState<any>(null);
  const [currentRideId, setCurrentRideId] = useState<string | null>(null);
  // V11 — Releer las marcas de gratis del perfil al crear un viaje y al
  // cerrarlo (completado, cancelado y expirado ponen `currentRideId` en null).
  // El montaje ya lo lee el efecto de arriba, por eso se salta la primera vez.
  const primeraLecturaGratisRef = useRef(true);
  useEffect(() => {
    if (primeraLecturaGratisRef.current) { primeraLecturaGratisRef.current = false; return; }
    // En modo chofer `currentRideId` cambia con cada solicitud que se muestra, y
    // el gratis es cosa del pasajero: no se lee el perfil por cada una.
    if (useAppStore.getState().userRole !== 'PASSENGER') return;
    recargarGratisDelPerfil();
  }, [currentRideId]);
  // AVISO DESTACADO de ubicación en segundo plano (política Google Play).
  // Se muestra ANTES del diálogo de permiso del sistema. El ref evita el bucle
  // (tras aceptar, re-ejecutamos handleToggleOnline saltando el chequeo).
  const [bgDisclosureVisible, setBgDisclosureVisible] = useState(false);
  const bgDisclosureAcceptedRef = useRef(false);
  const [ratingModalVisible, setRatingModalVisible] = useState(false);
  // FIX AUDIT (rating no-op) — `currentRideId` se pone en null ANTES de abrir
  // el modal de rating, así que el onSubmit lo leía null y NUNCA enviaba la
  // calificación. Capturamos el rideId del viaje completado aquí.
  const [ratingRideId, setRatingRideId] = useState<string | null>(null);

  // ══ EL CHOFER TAMBIÉN CALIFICA ═══════════════════════════════
  // El diálogo de completar viaje promete desde hace meses «se calificará al
  // pasajero» y no ocurría nunca: no existía ni la pantalla ni la función del
  // servidor. El fundador lo reportó tras el primer viaje real. Ahora existen
  // las dos (`submitPassengerRating` en functions/src/ratings.ts).
  // Se guarda el viaje aparte de `ratingRideId` porque los dos lados califican
  // por separado y en momentos distintos.
  const [califPasajeroVisible, setCalifPasajeroVisible] = useState(false);
  const [califPasajeroViaje, setCalifPasajeroViaje] = useState<{
    rideId: string;
    nombre: string;
    foto: string;
  } | null>(null);

  // FLUJO UBER-STYLE — Estado explícito del ride para el chofer entre
  // "envié oferta" y "pasajero aceptó". Antes el chofer entraba a Viaje
  // en Curso inmediatamente al aceptar → si otro chofer ganaba, UI fantasma.
  const [pendingOfferRideId, setPendingOfferRideId] = useState<string | null>(null);
  // Estado del ride en curso: ACCEPTED (chofer en camino) → ARRIVED (llegó) →
  // IN_PROGRESS (viajando). Controla qué botón de acción se muestra al chofer
  // en la ActiveRideCard.
  const [rideStatus, setRideStatus] = useState<'ACCEPTED' | 'ARRIVED' | 'IN_PROGRESS'>('ACCEPTED');
  /** Cuándo marcó el chofer su llegada. Alimenta el contador de espera del
   *  lado del PASAJERO: el del chofer lo saca de su propio documento. */
  const [llegadaDelChoferMs, setLlegadaDelChoferMs] = useState<number | null>(null);
  // PIN DE VIAJE (Uber-style) — El pasajero VE este código y se lo dice al
  // chofer; el chofer lo ingresa para iniciar el viaje. Se captura del ride
  // doc en los listeners de estado del pasajero. Para el chofer NO se necesita
  // en UI (startRide valida contra el ridePin real del server).
  const [activeRidePin, setActiveRidePin] = useState<string | null>(null);
  // H63 — el código con el que se cierra un ENVÍO de paquete. Al remitente le
  // sirve para dárselo a quien recibe; al chofer, para teclearlo al entregar.
  const [pinDeEntrega, setPinDeEntrega] = useState<string | null>(null);
  // Sale del DOCUMENTO del viaje, no del selector de la pantalla: el selector
  // es una preferencia que se queda puesta y podría mentir sobre el viaje que
  // está vivo. Y hace falta aparte del código porque un envío pedido ANTES de
  // que existiera el código de entrega no trae ninguno: sin esto, ese envío se
  // quedaría sin forma de cerrarse (las reglas ya no dejan al cliente hacerlo).
  const [envioDePaqueteVivo, setEnvioDePaqueteVivo] = useState(false);
  const [modalPinEntrega, setModalPinEntrega] = useState(false);
  const [entradaPinEntrega, setEntradaPinEntrega] = useState('');
  const [cerrandoEnvio, setCerrandoEnvio] = useState(false);
  const [confirmandoEntrega, setConfirmandoEntrega] = useState(false);

  // PIN DE RECOGIDA — Ya NO viaja en el doc del viaje (el chofer podía leerlo
  // y escribirlo él mismo). Vive en /rides/{id}/private/security y SOLO el
  // pasajero puede leerlo; se lo dicta al chofer al abordar. El servidor lo
  // valida en la Cloud Function startRideWithPin.
  useEffect(() => {
    if (!currentRideId || role !== 'PASSENGER') { setActiveRidePin(null); return; }
    // ⚠️ EL VIAJE DE DEMOSTRACIÓN TIENE SU PROPIO PIN, SEMBRADO AQUÍ.
    //
    // El PIN vive en `/rides/{id}/private/security`, y su regla hace
    // `get(/rides/{rideId}).data.passengerId` — sobre un viaje que no existe
    // eso sale denegado, el callback devuelve null y la caja del PIN no se
    // pinta NUNCA (ActiveRideCard exige `!!ridePin`).
    //
    // Y eso dejaba la demostración coja justo en lo que más hay que enseñar:
    // el PIN es lo que distingue a Go Safely de pedir un carro por WhatsApp.
    // Peor todavía, al tocar «Confirmo que es la persona de la foto» la
    // tarjeta anunciaba «tu viaje inicia ahora automáticamente» y
    // `handlePassengerConfirmStart` no hacía nada, porque entraba por
    // `if (activeRidePin)` con el PIN en null. La app decía haber hecho algo
    // que no hizo.
    //
    // Sembrarlo es seguro: `handlePassengerConfirmStart` solo lo ENSEÑA en un
    // aviso, no llama a ningún servidor.
    if (esViajeFalso(currentRideId)) {
      setActiveRidePin('1234');
      setPinDeEntrega(null);
      return;
    }
    const unsub = firestoreService.listenToRidePin(currentRideId, (pin, entrega) => {
      setActiveRidePin(pin);
      setPinDeEntrega(entrega ?? null);
    });
    return () => { try { (unsub as any)?.(); } catch {} };
  }, [currentRideId, role]);
  // Modal de entrada de PIN para el CHOFER (arrancar viaje). Android no
  // soporta Alert.prompt, así que usamos un modal propio con TextInput.
  const [pinModalVisible, setPinModalVisible] = useState(false);
  const [pinEntry, setPinEntry] = useState('');
  const [pinSubmitting, setPinSubmitting] = useState(false);
  // FIX BUG #5 — Contador real de notificaciones no-leídas para el badge de la
  // campana (antes el punto rojo estaba SIEMPRE encendido, hardcodeado).
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  useEffect(() => {
    if (!userFirebaseUid) { setUnreadNotifs(0); return; }
    const unsub = firestoreService.listenToUnreadNotificationCount(userFirebaseUid, setUnreadNotifs);
    return () => { try { (unsub as any)?.(); } catch {} };
  }, [userFirebaseUid]);

  // ANTI-FANTASMA — Fuente de verdad LOCAL de "en qué viaje estoy". Estos
  // refs se hidratan desde AsyncStorage (rideSessionStore) al montar y se
  // actualizan de forma síncrona cuando entramos/salimos de un viaje. El
  // listener global del chofer los consulta para distinguir "mi oferta fue
  // aceptada" de "ride fantasma con mi driverId de una prueba anterior".
  const activeRideIdRef = useRef<string | null>(null);
  const pendingOfferRideIdRef = useRef<string | null>(null);
  const sessionHydratedRef = useRef(false);
  // State paralelo al ref para forzar que el listener global del chofer se
  // re-evalúe cuando la sesión termine de hidratar (evita perder una
  // aceptación legítima si el chofer se conectó antes de leer AsyncStorage).
  const [sessionHydrated, setSessionHydrated] = useState(false);
  // ANTI-FLICKER — Mientras decidimos si hay un viaje que restaurar, ocultamos
  // el formulario de solicitud para que el pasajero no vea "Solicitar Viaje"
  // parpadear antes de que aparezca su viaje en curso. Se apaga apenas la
  // hidratación decide (rápido) o por un timeout de seguridad.
  const [resumeChecking, setResumeChecking] = useState(true);
  useEffect(() => { pendingOfferRideIdRef.current = pendingOfferRideId; }, [pendingOfferRideId]);
  useEffect(() => { activeRideIdRef.current = currentRideId; }, [currentRideId]);
  // Sin viaje no hay recogida que enseñar. Se limpia AQUÍ y no en cada rama de
  // COMPLETED/CANCELLED/EXPIRED —hay cinco— porque olvidar una sola dejaría el
  // pin de un viaje viejo clavado en el mapa del siguiente.
  useEffect(() => {
    if (!currentRideId) {
      setRecogidaDelViaje(null);
      // El destino se limpia con la recogida y por lo mismo: un destino viejo
      // clavado en el mapa dibujaría la línea del viaje anterior.
      setDestinoDelViaje(null);
    }
  }, [currentRideId]);

  // Refs para liberar listeners de Firestore al cambiar/terminar el ride.
  // Sin esto, cada nueva solicitud apilaba un onSnapshot que nunca se cerraba
  // (fuga de memoria + cuota de Firestore desperdiciada).
  const offersUnsubRef = useRef<null | (() => void)>(null);
  const rideStatusUnsubRef = useRef<null | (() => void)>(null);
  const driverProfileUnsubRef = useRef<null | (() => void)>(null);
  // Listener global del chofer para detectar cuando un pasajero acepta su
  // oferta. Se enciende al ponerse online y se apaga al desconectar.
  const driverAcceptedRideUnsubRef = useRef<null | (() => void)>(null);

  const cleanupRideListeners = useCallback(() => {
    // OJO: driverAcceptedRideUnsubRef NO se toca aquí a propósito.
    // Ese es el listener GLOBAL del chofer (escucha "un pasajero aceptó mi
    // oferta") y su ciclo de vida lo controla EXCLUSIVAMENTE el useEffect
    // keyed en [role, isOnline, sessionHydrated] (más abajo). Si lo
    // matáramos aquí, cada vez que un viaje termina/cancela (cleanupRideListeners
    // corre en el callback de COMPLETED/CANCELLED) el chofer quedaría sordo a
    // nuevos viajes hasta togglear online/offline — DoS de ingresos. Solo
    // limpiamos los listeners POR-VIAJE (ofertas, estado, perfil).
    offersUnsubRef.current?.();
    rideStatusUnsubRef.current?.();
    driverProfileUnsubRef.current?.();
    offersUnsubRef.current = null;
    rideStatusUnsubRef.current = null;
    driverProfileUnsubRef.current = null;
  }, []);

  useEffect(() => {
    return () => cleanupRideListeners();
  }, [cleanupRideListeners]);

  /**
   * COMPLETA LOS DATOS DEL CHOFER CON LO QUE EL SERVIDOR YA ESTABA MANDANDO.
   *
   * `getDriverPublicInfo` devuelve nombre, foto, nota, sello de verificación,
   * tipo, marca, modelo, color y placa. El cliente se quedaba SOLO con
   * `bankAccounts` y tiraba el resto: la tarjeta del viaje salía con huecos
   * aunque el dato hubiera llegado al teléfono y estuviera pagado.
   *
   * COMPLETA, NO PISA: si el viaje ya trae el dato, ese manda —es el que el
   * chofer congeló al ofertar—. Solo se rellenan los huecos, y el hueco que
   * siga vacío se queda vacío: aquí no se inventa ni una estrella.
   *
   * LA PLACA NO SALE DE `/drivers`: esa colección la lee cualquier usuario con
   * sesión y en bloque sería un padrón de matrículas con posición en vivo. Le
   * llega al pasajero por el documento del viaje o por esta llamada, que el
   * servidor acota al pasajero de ese viaje.
   *
   * Se pide UNA vez por (viaje, chofer). El listener del viaje dispara en cada
   * snapshot del documento y antes llamaba a la función en todos ellos.
   */
  const infoChoferPedidaRef = useRef<string | null>(null);
  const completarDatosDelChofer = useCallback((driverId: string, rideId: string | null) => {
    if (!driverId) return;
    const clave = `${rideId ?? ''}:${driverId}`;
    if (infoChoferPedidaRef.current === clave) return;
    infoChoferPedidaRef.current = clave;
    callGetDriverPublicInfo({ driverId, rideId: rideId ?? undefined })
      .then((resp) => {
        const info: any = resp?.data;
        if (!info) return;
        if (Array.isArray(info.bankAccounts)) setDriverBankAccounts(info.bankAccounts);
        setSelectedDriver((prev: any) => {
          // Si el viaje ya se cerró mientras la función contestaba, no se
          // fabrica una tarjeta de chofer de la nada: sería el "Conductor
          // Cercano" de un viaje que ya no existe.
          if (!prev && rideId && activeRideIdRef.current !== rideId) return prev;
          const base: any = prev ?? { driverId };
          // Un hueco es null, undefined o texto vacío. Un `false` o un 0 SON
          // datos y no se tocan.
          const rellenar = (actual: any, nuevo: any) =>
            (actual === null || actual === undefined || actual === '') &&
            nuevo !== null && nuevo !== undefined && nuevo !== ''
              ? nuevo
              : actual;
          const siguiente = {
            ...base,
            driverId: base.driverId ?? driverId,
            driverName: rellenar(base.driverName, info.userName),
            userName: rellenar(base.userName, info.userName),
            driverPhoto: rellenar(base.driverPhoto, info.photoURL),
            rating: rellenar(base.rating, info.rating),
            ratingCount: rellenar(base.ratingCount, info.ratingCount),
            vehicleType: rellenar(base.vehicleType, info.vehicleType),
            vehicleBrand: rellenar(base.vehicleBrand, info.vehicleBrand),
            vehicleModel: rellenar(base.vehicleModel, info.vehicleModel),
            vehicleColor: rellenar(base.vehicleColor, info.vehicleColor),
            licensePlate: rellenar(base.licensePlate, info.licensePlate),
            // El servidor ya los devolvia y aqui se tiraban. La foto del carro
            // NO viene en esta respuesta (la lista blanca de `drivers.ts` no la
            // incluye): por este camino no se pinta, que es mejor que pintar la
            // de otro carro.
            nivel: rellenar(base.nivel, info.nivel),
            viajesCompletados: rellenar(base.viajesCompletados, info.viajesCompletados),
            // El escudo: booleano de verdad o null. Nunca un `true` de relleno.
            isFaceIdVerified:
              typeof base.isFaceIdVerified === 'boolean'
                ? base.isFaceIdVerified
                : typeof info.isDriverVerified === 'boolean'
                  ? info.isDriverVerified
                  : null,
          };
          // Si no cambió nada se devuelve EL MISMO objeto: una referencia nueva
          // viaja hasta las props del mapa y le rompe el `React.memo`.
          return mismoViaje(prev, siguiente) ? prev : siguiente;
        });
      })
      .catch((e: any) => {
        // Se libera la marca: un bache de red no puede dejar la tarjeta con
        // huecos para el resto del viaje.
        infoChoferPedidaRef.current = null;
        console.warn('[map] getDriverPublicInfo falló:', e?.message ?? e);
      });
  }, []);

  /**
   * V13 — EL CHOFER ACEPTADO YA IBA EN OTRO VIAJE.
   *
   * Si la pasajera acepta la oferta de un chofer que ya está llevando a otra
   * persona, el servidor (congelarDatosDelViaje, functions/src/rides.ts) devuelve
   * la solicitud a PENDING con `reabiertoMotivo: 'CHOFER_OCUPADO'`, borra esa
   * oferta y manda un push. Sin esta rama la pantalla se quedaba con la tarjeta de
   * "tu chofer va en camino" de alguien que no iba a llegar. Se vuelve a las
   * ofertas que quedan (o al radar si no queda ninguna); el formulario y la
   * solicitud siguen como estaban.
   *
   * Una sola vez por reapertura: se recuerda el sello del servidor, así los
   * snapshots siguientes del mismo PENDING no repiten el aviso.
   */
  const reaperturaManejadaRef = useRef<string | null>(null);
  const soltarChoferQueYaIbaEnOtroViaje = (data: any, rideId: string): boolean => {
    if (String(data?.status ?? '') !== 'PENDING') return false;
    if (String(data?.reabiertoMotivo ?? '') !== 'CHOFER_OCUPADO') return false;
    if (data?.passengerId !== useAppStore.getState().userFirebaseUid) return false;
    const sello = data?.reabiertoPorServidorEn?.toMillis?.() ?? 0;
    const clave = `${rideId}:${sello}`;
    if (reaperturaManejadaRef.current === clave) return true;
    reaperturaManejadaRef.current = clave;
    setIsActiveTrip(false);
    setSelectedDriver(null);
    setRideStatus('ACCEPTED');
    estadoAnteriorDelViaje.current = null;
    setLlegadaDelChoferMs(null);
    setDriverBankAccounts([]);
    infoChoferPedidaRef.current = null;
    // Con ofertas vivas se vuelve a la lista; sin ninguna, al radar. El listener
    // de ofertas ya recibió (o recibirá) el borrado de la oferta de ese chofer.
    setReceivedOffers((ofertas) => {
      if (ofertas.length > 0) {
        setIsSearching(false);
        setIncomingDriverOffers(true);
      } else {
        setIsSearching(true);
      }
      return ofertas;
    });
    showAlert(
      'Ese chofer ya tomó otro viaje',
      'Tu solicitud sigue activa. Elige otra de las ofertas o espera nuevas.',
      'car',
      '#FF9500'
    );
    return true;
  };

  // ────────────────────────────────────────────────────────────────────────
  // ESCUDO DE CARGA — RACE CONDITION FIX
  // ----------------------------------------------------------------------
  // ANTES: el MapView se renderizaba y los listeners de Firestore arrancaban
  // ANTES de que (a) Firebase Auth confirmara la sesión y (b) el GPS diera
  // las coords iniciales. Resultado: mapa congelado en negro + queries a
  // Firestore con uid null que las rules rechazaban.
  // AHORA: bloqueamos el render del mapa y de los listeners hasta que ambos
  // estén confirmados. Mientras tanto mostramos un ActivityIndicator limpio.
  const [isFirebaseReady, setIsFirebaseReady] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<{lat: number, lng: number} | null>(null);
  /**
   * ERROR DEL GPS EN METROS, o null si todavia no se sabe.
   *
   * El GPS arranca en dos fases a proposito: un fix inmediato de cache o
   * «Balanced» (2-5 s, cientos de metros) y despues un afinado de alta
   * precision. Con el fix grueso, preguntarle a Google «que direccion es
   * esta» no puede devolver una calle —a 300 m caben varias— y contesta con
   * la ciudad y el codigo postal: «Santo Domingo Este 11901».
   *
   * Eso no es un fallo, pero tiene una consecuencia cara: quien pide el viaje
   * en esos primeros segundos manda al chofer a un punto que puede estar a
   * cientos de metros. El chofer llega donde no hay nadie, el pasajero cree
   * que la app lo mando mal, y se pierden los dos.
   *
   * Lo reporto el fundador el 17-oct-2026 estando parado en la San Vicente.
   */
  const [precisionMetros, setPrecisionMetros] = useState<number | null>(null);
  /** El umbral es el MISMO que ya usa el arranque para decidir si afina. */
  const PRECISION_MINIMA_M = 100;
  /** Solo cuenta cuando la recogida la pone el GPS: si la eligio de la lista,
   *  las coordenadas son de Google y el GPS da igual. */
  const recogidaImprecisa =
    !origenManual && (precisionMetros === null || precisionMetros > PRECISION_MINIMA_M);
  /**
   * LO QUE COSTARIA ESTE VIAJE, calculado con el destino puesto.
   *
   * Sale de `suggestFare` (services/geoUtils.ts): tarifa base + costo por km +
   * minimo garantizado, por tipo de vehiculo. Esa funcion existia desde hace
   * meses y NO LA LLAMABA NADIE — el pasajero tenia que inventarse el numero y
   * el viaje cubierto por la app pagaba 250 fijo.
   *
   * La distancia es en linea recta por 1.3: las calles no van en linea recta y
   * ese factor es el que usa la industria para estimar sin gastar una llamada a
   * Directions en cada tecla. Es una SUGERENCIA (el pasajero la puede cambiar),
   * salvo en el viaje cubierto, donde es lo que la app le paga al chofer.
   */
  const FACTOR_CALLE = 1.3;
  /**
   * Lo maximo que la app paga por un viaje cubierto. Es el MISMO numero que
   * `MAX_FREE_RIDE_CREDIT_DOP` en functions/src/loyalty.ts, y tiene que seguir
   * siendolo: si aqui se prometiera mas, el pasajero pediria un viaje "gratis"
   * de RD$1.200 y el chofer cobraria 800 — la discusion en la calle la tendrian
   * ellos dos, por un numero que decidimos nosotros.
   */
  const TOPE_VIAJE_CUBIERTO_DOP = 800;
  const kmEstimadosDelViaje = (() => {
    const oLat = origenManual && origenLat !== null ? origenLat : currentLocation?.lat;
    const oLng = origenManual && origenLng !== null ? origenLng : currentLocation?.lng;
    if (oLat == null || oLng == null || destinationLat == null || destinationLng == null) return null;
    // `haversineKm` es el de este archivo (linea 60): devuelve null si alguna
    // coordenada falta, en vez de un NaN que se pasearia por la tarifa.
    const km = haversineKm({ lat: oLat, lng: oLng }, { lat: destinationLat, lng: destinationLng });
    if (km === null || !Number.isFinite(km) || km <= 0) return null;
    return km * FACTOR_CALLE;
  })();
  const tarifaSugerida =
    kmEstimadosDelViaje === null ? null : suggestFare(kmEstimadosDelViaje, selectedVehicleType);
  const [mapRegion, setMapRegion] = useState({
    latitude: 18.4861, // Santo Domingo default fallback
    longitude: -69.9312,
    latitudeDelta: 0.04,
    longitudeDelta: 0.04
  });

  // 1) AUTH GUARD — onAuthStateChanged se dispara al menos UNA vez siempre
  //    (con user o con null). Confirmamos que Firebase terminó de verificar
  //    la sesión antes de permitir cualquier query a Firestore.
  useEffect(() => {
    // `observarSesion` en vez del observador nativo directo: en web el SDK
    // nativo no tiene app y esto tumbaba la pantalla del mapa al montar.
    const unsubscribe = observarSesion(() => {
      setIsFirebaseReady(true);
    });
    return () => unsubscribe();
  }, []);

  // ── RELOJ DEL ESCUDO DE CARGA ───────────────────────────────────────────
  //
  // El comentario de arriba prometía "como máximo 10 s en el peor caso" y esa
  // promesa era FALSA: los dos candados esperan un aviso que puede no llegar
  // nunca, y ninguno de los dos tenía reloj.
  //
  //  · `isFirebaseReady` solo se enciende DENTRO del callback de sesión. Si el
  //    callback no llega —sin red al arrancar, el SDK nativo sin inicializar—
  //    nadie lo enciende y nadie avisa.
  //  · La ubicación tiene tope de 20 s, pero ese tope cubre el FIX del GPS, no
  //    el DIÁLOGO DE PERMISO: `getCurrentLocation` espera a
  //    `requestForegroundPermissionsAsync`, y en una instalación nueva ese
  //    diálogo queda encolado detrás del de notificaciones. Puede tardar lo que
  //    tarde la persona en contestarlo, o no contestarse nunca.
  //
  // En el teléfono del fundador los dos permisos llevan meses concedidos y este
  // camino no se recorre; en uno nuevo sí, y el resultado es "Buscando tu
  // ubicación…" para siempre.
  //
  // Aquí no se acelera nada: se le pone RELOJ a las dos esperas y una SALIDA.
  // Vencido el plazo la pantalla AVANZA — el mapa se abre donde se pueda y dice
  // lo que pasa. Un mapa sin mi punto sirve; una pantalla que no avanza, no.
  const [vencioEsperaSesion, setVencioEsperaSesion] = useState(false);
  const [vencioEsperaUbicacion, setVencioEsperaUbicacion] = useState(false);
  useEffect(() => {
    // 8 s para la sesión: cuando funciona contesta en menos de uno.
    const relojSesion = setTimeout(() => setVencioEsperaSesion(true), 8000);
    // 28 s para la ubicación: por encima de los 20 s del propio GPS, para no
    // cortar un fix que ya venía en camino.
    const relojUbicacion = setTimeout(() => setVencioEsperaUbicacion(true), 28000);
    return () => { clearTimeout(relojSesion); clearTimeout(relojUbicacion); };
  }, []);

  // FOCO DE LA PESTAÑA + REDIRECT POR ROL.
  // react-native-maps en Android usa SurfaceView nativo que "sangra" sobre
  // otras pantallas al cambiar de tab si seguimos renderizando el MapView.
  // En vez de desmontarlo (lento), usamos `display: 'none'` cuando perdemos
  // el foco — el SurfaceView se libera y el GL context queda cacheado para
  // un re-foco rápido.
  // Además, si el rol cambió a uno que NO debe ver el mapa (FOOD_DELIVERY,
  // RENT_CAR_OWNER, etc.), redirigimos al landing correcto del rol — esto
  // resuelve el bug "Modo Conductor paseándose" cuando el usuario usaba
  // el QA Mode para cambiar de rol y volvía al map por accidente.
  const [isTabFocused, setIsTabFocused] = useState(true);
  useFocusEffect(
    useCallback(() => {
      setIsTabFocused(true);
      // Redirect defensivo si llegamos aquí con un rol que no corresponde
      const r = useAppStore.getState().userRole;
      if (r === 'FOOD_DELIVERY') { router.replace('/(tabs)/food'); return; }
      if (r === 'RENT_CAR_OWNER') { router.replace('/(tabs)/dashboard'); return; }
      if (r === 'CAR_RENTER') { router.replace('/(tabs)/marketplace'); return; }
      if (r === 'SCHOOL_DIRECTOR') { router.replace('/school-director-dashboard'); return; }
      return () => setIsTabFocused(false);
    }, [router])
  );

  // PLACEHOLDER DINÁMICO: muestra la dirección actual del usuario (calle + sector)
  // como sugerencia inicial en el input de búsqueda. UX Uber-style.
  const [currentAddressLabel, setCurrentAddressLabel] = useState<string>('');
  useEffect(() => {
    let alive = true;
    if (!currentLocation) return;
    // FIX PERF (#8) — Debounce del reverse-geocode. Durante el tracking en vivo
    // el GPS emite lat/lng muy seguido; sin debounce se disparaba una llamada
    // a reverseGeocodeAsync por cada tick (la API está rate-limited por el SO y
    // lanza/lentifica bajo ráfaga). Esperamos a que la ubicación se estabilice
    // ~1.2s antes de resolver la dirección; cada tick nuevo reinicia el timer.
    const timer = setTimeout(() => {
      (async () => {
        try {
          // El geocodificador del SISTEMA OPERATIVO no sirve en RD: devuelve
          // `street` vacío y como localidad el MUNICIPIO, así que estando en La
          // Caleta decía «Boca Chica». `reverseGeocode` (ya desplegada, con
          // caché y límite de uso) pregunta a Google en español y devuelve la
          // dirección completa. Si falla, se cae al del SO como antes.
          const g = await callReverseGeocode({
            lat: currentLocation.lat,
            lng: currentLocation.lng,
          });
          if (!alive) return;
          if (g.data?.formattedAddress) {
            // Quita el Plus Code del principio y el país del final. La regla
            // vivía suelta aquí y solo limpiaba ESTA recogida, la automática;
            // ahora está en `direccionCorta` y la comparten los cuatro sitios
            // que pintan una dirección de Google.
            // `tieneCalle === false` significa que Google NO tiene calle para
            // este punto y lo unico especifico es el Plus Code: ahi se conserva.
            // `undefined` (respuestas cacheadas de antes de este cambio) se
            // trata como antes, limpiando.
            setCurrentAddressLabel(
              direccionCorta(g.data.formattedAddress, {
                conservarPlusCode: g.data.tieneCalle === false,
              })
            );
            return;
          }
          throw new Error('sin resultado');
        } catch {
        try {
          const Location = await import('expo-location');
          const results = await Location.reverseGeocodeAsync({
            latitude: currentLocation.lat,
            longitude: currentLocation.lng,
          });
          const r = results?.[0];
          if (!alive || !r) return;
          const parts = [r.street, r.district || r.subregion || r.city].filter(Boolean);
          if (parts.length > 0) {
            setCurrentAddressLabel(parts.join(', '));
          }
        } catch {
          // Si falla reverse geocoding, mantenemos el placeholder genérico
        }
        }
      })();
    }, 1200);
    return () => { alive = false; clearTimeout(timer); };
  }, [currentLocation?.lat, currentLocation?.lng]);

  /**
   * EL CAMPO DE RECOGIDA SE RELLENA SOLO — HASTA QUE LO TOQUEN.
   *
   * La dirección de quien pide no está disponible al abrir la pantalla: la
   * resuelve la geocodificación inversa de arriba ~1,2 s después. Si no se
   * copiara aquí, el campo de recogida saldría VACÍO y habría que escribir la
   * propia casa cada vez.
   *
   * En cuanto la persona la cambia (`origenManual`), esto deja de escribir:
   * pisarle el texto mientras pide el carro para su mamá sería el peor fallo
   * posible de esta pantalla.
   */
  useEffect(() => {
    if (origenManual) return;
    if (!currentAddressLabel) return;
    setOrigenTexto(currentAddressLabel);
  }, [currentAddressLabel, origenManual]);
  /**
   * La misma dirección, legible desde los listeners del viaje. Esos listeners
   * se crean al pedir el viaje y guardan los valores de AQUEL render: leer
   * `currentAddressLabel` desde `cerrarViajeDelPasajero` daría la dirección de
   * donde estaba la persona cuando pidió, no la de ahora (H35).
   */
  const direccionActualRef = useRef('');
  useEffect(() => { direccionActualRef.current = currentAddressLabel; }, [currentAddressLabel]);

  // CENTRAR EN MI UBICACIÓN: fuerza fix HIGH ignorando cache. El usuario
  // pulsó el botón explícitamente, queremos su ubicación REAL, no la cacheada
  // (que podría tener varios minutos / km de desfase si cambió de barrio).
  const centerOnMyLocation = useCallback(async () => {
    try {
      const loc = await locationService.getCurrentLocation(true); // fresh
      if (loc) {
        const coords = { lat: loc.coords.latitude, lng: loc.coords.longitude };
        setCurrentLocation(coords);
        setPrecisionMetros(
          typeof loc.coords.accuracy === 'number' ? loc.coords.accuracy : null
        );
        setMapRegion({
          latitude: coords.lat,
          longitude: coords.lng,
          latitudeDelta: 0.01,
          longitudeDelta: 0.01,
        });
        Haptics.selectionAsync().catch(() => {});
        // Petición EXPLÍCITA del usuario: recupera el encuadre automático
        // aunque acabe de arrastrar el mapa. Es el botón de "vuelve a
        // enseñarme lo importante".
        setReencuadrarAhora((n) => n + 1);
      }
    } catch {
      // silent fail
    }
  }, []);

  // GPS AUTO — arranca con un fix rápido (cache + Balanced) para que la UI
  // avance, y en paralelo abre un watcher HIGH durante 30s para refinar la
  // ubicación cuando el GPS afine. El user ve el marcador moverse del centro
  // de SD a su barrio real conforme llega el fix preciso.
  //
  // El background tracking del DRIVER NO se enciende aquí: arranca solo cuando
  // el chofer toca "Conectarme" (handleToggleOnline).
  useEffect(() => {
    let alive = true;
    let unsubscribeWatch: (() => void) | null = null;
    (async () => {
      // Fase 1 — fix inmediato (cache o Balanced en 2-5s).
      const loc = await locationService.getCurrentLocation();
      if (!alive) return;
      const coords = { lat: loc.coords.latitude, lng: loc.coords.longitude };
      setCurrentLocation(coords);
      setPrecisionMetros(
        typeof loc.coords.accuracy === 'number' ? loc.coords.accuracy : null
      );
      setMapRegion(prev => ({
        ...prev,
        latitude: coords.lat,
        longitude: coords.lng,
        latitudeDelta: 0.01,
        longitudeDelta: 0.01
      }));

      // Fase 2 — refinar en background. Si la accuracy del primer fix es
      // pobre (>100m o null=fallback SD) suscribimos un watcher HIGH que
      // entrega updates conforme el GPS converge. Auto-stop a los 30s.
      const initialAccuracy = loc.coords.accuracy;
      const needsRefine = initialAccuracy == null || initialAccuracy > 100;
      if (needsRefine) {
        unsubscribeWatch = await locationService.watchInitialLocation((preciseLoc) => {
          if (!alive) return;
          const c = { lat: preciseLoc.coords.latitude, lng: preciseLoc.coords.longitude };
          setCurrentLocation(c);
          setPrecisionMetros(
            typeof preciseLoc.coords.accuracy === 'number' ? preciseLoc.coords.accuracy : null
          );
          setMapRegion(prev => ({
            ...prev,
            latitude: c.lat,
            longitude: c.lng,
          }));
        });
      }
    })();
    return () => {
      alive = false;
      unsubscribeWatch?.();
    };
  }, []);

  // Promociones Dinámicas (Carrusel)
  const [activePromoIndex, setActivePromoIndex] = useState(0);

  // Moneda Dinámica (Se detectará por GPS/Perfil en el futuro)
  const [currency, setCurrency] = useState('DOP');

  // ── NOTIFICACIÓN PERSISTENTE (Uber-style en la barra del sistema) ──────────
  // Se posta cuando el user entra a "Buscando" o "Viaje en curso" y se limpia
  // al cancelar/terminar. Si el user minimiza la app durante esos estados, la
  // notificación queda anclada y al tocarla regresa al app.
  // Debe declararse AQUÍ (no al tope del componente) porque su array de
  // dependencias lee `currency` y `currentRideId`; colocarlo antes de esas
  // declaraciones dispara un ReferenceError por TDZ en cada render.
  useEffect(() => {
    // FIX DEEP-LINK RESTORE — Pasamos `currentRideId` a la notificación
    // persistente para que el tap (después de force-close) restaure el ride
    // exacto en la UI, no la pantalla genérica de solicitud.
    if (isActiveTrip) {
      notificationService.postPersistentRideStatus('IN_TRIP', 'Toca para volver al mapa.', currentRideId);
      return;
    }
    if (isSearching) {
      const fareLabel = offerPrice ? `Ofreciendo ${pesos(offerPrice)}` : 'Toca para ver ofertas.';
      notificationService.postPersistentRideStatus('SEARCHING', fareLabel, currentRideId);
      return;
    }
    if (incomingDriverOffers) {
      notificationService.postPersistentRideStatus('DRIVER_ASSIGNED', 'Tienes ofertas de choferes esperando.', currentRideId);
      return;
    }
    // Ningún estado activo → limpiar.
    notificationService.clearPersistentRideStatus();
  }, [isActiveTrip, isSearching, incomingDriverOffers, offerPrice, currency, currentRideId]);

  // ── CINTILLO DEL PASAJERO ────────────────────────────────────────────────
  // Antes era texto fijo para TODO el mundo: prometía "tu primer viaje es
  // GRATIS" aunque la persona ya lo hubiera usado, enseñaba un contador "(0/10)"
  // que no contaba nada, y ofrecía un código de descuento (GOSAFE50) que NO
  // EXISTE en ninguna parte del código: quien lo escribiera no obtenía nada.
  // Ahora solo se anuncia lo que de verdad tiene esa persona en ese momento.
  const PASSENGER_PROMOS = [
    ...(isFreeTrip
      ? [{
          title: freeTripReason === 'FIRST' ? '¡Tu primer viaje es GRATIS!' : '¡Tienes un viaje GRATIS!',
          text: freeTripReason === 'FIRST'
            ? 'La app cubre la tarifa'
            : 'Ganado por tus viajes acumulados',
          icon: 'gift', color: '#E65100',
        }]
      : []),
    // ── ESTE CINTILLO LO VE TODO PASAJERO, SIN NINGUNA CONDICIÓN ─────────
    // Decía "Viaja Seguro · Conductores 100% verificados": una afirmación
    // sobre TODOS los choferes de la plataforma, y encima con un "100%" que
    // nadie puede sostener. Lo que sí es verdad, y está escrito igual en los
    // Términos (punto 4) y en la Política de Privacidad (punto 2.c), es que
    // los papeles los revisa UNA PERSONA a mano —no hay biometría ni
    // reconocimiento facial— y que sin esa revisión aprobada el chofer no
    // puede ponerse en línea ni ofertar (`isVerifiedDriver()` en
    // firestore.rules corta /drivers con isOnline:true y /rides/{id}/offers).
    // Eso es lo que dice ahora, ni una palabra más.
    { title: 'Antes de montarte:', text: 'una persona revisa la cédula y la licencia de cada chofer.', icon: 'shield-checkmark', color: '#1565C0' },
  ];

  // Los textos DEBEN coincidir con functions/src/incentives.ts. Antes el banner
  // prometía bono "con 20 viajes este mes" cuando la meta real son 250: un
  // chofer que hace 25 viajes y no ve nada siente que la app le mintió, y con
  // razón. Montos y metas reales, sin adornos:
  //   3 referidos que PAGUEN → RD$800 (moto/bici) o RD$1,000 (carro/minibús)
  //   250 viajes en el mes    → RD$500
  //   1,500 viajes de por vida → RD$2,000 (una sola vez)
  const DRIVER_PROMOS = [
    { title: 'Invita y gana:', text: 'Por cada 3 choferes que invites y paguen: hasta RD$ 1,000.', icon: 'cash', color: '#2E7D32' },
    { title: 'Meta del mes:', text: '250 viajes en el mes = RD$ 500 de bono.', icon: 'flag', color: '#D84315' },
    { title: 'Bono de veterano:', text: 'Al llegar a 1,500 viajes: RD$ 2,000, una sola vez.', icon: 'trophy', color: '#F9A825' },
    { title: 'Tú decides tu bono:', text: 'Lo abonamos a tu membresía o te lo transferimos.', icon: 'wallet', color: '#1565C0' },
  ];

  React.useEffect(() => {
    // Sin foco no hay nada que animar. `LayoutAnimation.configureNext` programa
    // una transición en el hilo de UI NATIVO; hacerlo cada 5 segundos con el
    // mapa oculto (el usuario está en Comida, Actividad o Perfil) es trabajo
    // del hilo principal a cambio de nada, y en un teléfono modesto se nota.
    if (!isTabFocused) return;
    const promos = role === 'PASSENGER' ? PASSENGER_PROMOS : DRIVER_PROMOS;
    const interval = setInterval(() => {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
      setActivePromoIndex((prev) => (prev + 1) % promos.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [role, isTabFocused]);

  // ── SE FUERON `handleDestinationSelect` Y `handleDestinationChange` ───────
  // Eran CÓDIGO MUERTO: nadie los llamaba desde que `PlacesAutocomplete`
  // sustituyó a la lista fija de 7 lugares (ver el comentario del campo de
  // destino). Lo único que hacían era encender y apagar `selectedDropoffGPS`,
  // el booleano paralelo que ya no existe porque podía discrepar de las
  // coordenadas del destino y por ahí salían viajes sin sector.
  //
  // Y con ellos se fue el array `SUGGESTIONS` de siete lugares fijos, que
  // tampoco tenía un solo lector. Un array de direcciones a la vista, en una
  // pantalla de viajes, se lee como si la app todavía las ofreciera: quien lo
  // encuentre va a perder un rato averiguando por qué no salen nunca.

  const handlePassengersChange = (text: string) => {
    setPassengers(text);
    const num = parseInt(text, 10);
    if (!isNaN(num)) {
      if (num > 4) {
        setSelectedVehicleType('MINIBUS');
      } else if (num === 1) {
        // Keep current or allow switching manually
      } else if (num > 1 && num <= 4) {
        setSelectedVehicleType('CAR');
      }
    }
  };

  /**
   * REPORTAR UNA NOTA OFENSIVA.
   *
   * La nota viaja con la solicitud y la lee el chofer ANTES de aceptar, o sea
   * antes de que exista ningun chat. El bloquear/reportar del chat
   * (AccionesDeChat) no le sirve ahi: todavia no hay conversacion que abrir.
   * Escribe en `reportes_usuarios`, la MISMA coleccion que el reporte del chat,
   * para que al admin le llegue por un solo sitio.
   */
  const reportarNota = async (ride: any) => {
    const quiere = await preguntarSiNo(
      'Reportar esta nota',
      'Le avisamos al equipo de Go Safely para que la revise. Puedes seguir aceptando el viaje o dejarlo pasar: reportar no lo cancela.',
      'Cancelar',
      'Reportar'
    );
    if (!quiere) return;
    try {
      await firestoreService.reportarUsuario({
        yo: useAppStore.getState().userFirebaseUid ?? '',
        otroUid: String(ride?.passengerId ?? ''),
        otroNombre: String(ride?.passengerName ?? ''),
        // Contexto propio: esto NO viene de un chat (todavia no hay), y el
        // aviso al admin cambia el texto segun esto.
        contexto: 'NOTA_SOLICITUD',
        referenciaId: String(ride?.id ?? ''),
        motivo: 'Nota ofensiva en la solicitud: ' + String(ride?.notaDelPasajero ?? ''),
      });
      showAlert('Reporte enviado', 'Gracias. Una persona del equipo la va a revisar.', 'shield-checkmark', '#00C853');
    } catch (e: any) {
      showAlert('No se pudo reportar', mensajeDeError(e, 'Revisa tu conexion e intentalo de nuevo.'), 'alert-circle', '#FF3B30');
    }
  };

  const showAlert = (title: string, message: string, icon: any = 'information-circle', color: string = colors.primary) => {
    setCustomModalConfig({ visible: true, title, message, icon, color });
  };

  // ══════════════════════════════════════════════════════════════════════
  // EL CHOFER SE ENTERA DE LA RESPUESTA DENTRO DE LA APP
  // ══════════════════════════════════════════════════════════════════════
  //
  // ── LO QUE PASABA ────────────────────────────────────────────────────
  //
  // El chofer proponía el desvío, el pasajero contestaba, y el veredicto
  // viajaba SOLO por push (`notifyRouteDeviation`, functions/src/dispatch.ts).
  // Un push no se ve si la app está abierta en primer plano, si los avisos
  // están apagados, o con el ahorro de batería puesto. Y el chofer —que va
  // manejando, con la app abierta en el mapa— es justo el caso en el que menos
  // llega.
  //
  // Así que proponía el desvío y se quedaba esperando una respuesta que su
  // pantalla no le iba a dar nunca. Si el pasajero decía que NO, el mensaje que
  // el fundador pidió expresamente —«el pasajero no está de acuerdo, por favor
  // permanece en la misma ruta»— estaba escrito en el servidor desde hacía
  // tiempo y no lo leía nadie.
  //
  // Ahora sale del mismo listener del viaje que ya está abierto, que es el
  // camino que no depende de ningún permiso. El push se queda igual, para
  // cuando tenga la app cerrada.
  //
  // La clave de «ya lo enseñé» lleva la hora de la propuesta: si en el mismo
  // viaje hubiera un segundo desvío más adelante, su veredicto vuelve a entrar.
  const desvioYaAnunciado = useRef<string>('');

  const anunciarVeredictoDelDesvio = (data: any) => {
    const dev = data?.routeDeviation;
    const estado = dev?.status;
    if (estado !== 'APPROVED' && estado !== 'REJECTED') return;
    const yo = useAppStore.getState().userFirebaseUid;
    if (!yo || data?.driverId !== yo) return; // esto es para el CHOFER
    const clave = `${dev?.requestedAt ?? ''}|${estado}`;
    if (desvioYaAnunciado.current === clave) return;
    desvioYaAnunciado.current = clave;
    if (estado === 'APPROVED') {
      showAlert(
        'Desvío aprobado',
        'El pasajero aceptó el cambio de ruta. Puedes continuar.',
        'navigate',
        colors.success
      );
    } else {
      showAlert(
        'El pasajero no está de acuerdo',
        'Por favor permanece en la misma ruta.',
        'warning',
        colors.error
      );
    }
  };

  /**
   * "CANCELAR VIAJE" DICE LO QUE CUESTA ANTES DE COSTARLO — H24 (auditoría 16-sep-2026).
   *
   * Cancelar un viaje ya aceptado era UN toque, sin confirmación y sin aviso: la
   * multa se enteraba por push DESPUÉS de apuntada. Y con la ventana de gracia
   * que ahora aplica el servidor (`onRideCancelledPenalty`, functions/src/
   * penalties.ts), el mismo toque puede salir gratis o costar según el momento,
   * así que el diálogo tiene que decir cuál de los dos es.
   *
   * La regla que se describe es la del servidor, no otra:
   *  · Chofer: siempre cuenta (la gracia es solo del pasajero).
   *  · Pasajero con el chofer ya llegado o el viaje en marcha: cuenta.
   *  · Pasajero con el viaje ACEPTADO: no cuenta dentro de los 2 minutos desde
   *    la hora de aceptación que selló el servidor.
   *
   * La hora del teléfono no es la del servidor, y la persona tarda en leer. Por
   * eso a la cuenta atrás se le quitan 20 s de margen: si el texto se equivoca,
   * que sea avisando de una multa que al final no llega, nunca prometiendo una
   * gracia que ya pasó. Sin `acceptedAt` en el documento el servidor tampoco
   * tiene el sello (se escriben en la misma transacción) y esa cancelación
   * CONTARÍA: el texto no promete nada.
   */
  const GRACIA_CANCELAR_MS = 2 * 60 * 1000;
  const MARGEN_GRACIA_MS = 20 * 1000;
  const avisoAntesDeCancelar = (
    soyChofer: boolean,
    rideId: string,
    estado: 'ACCEPTED' | 'ARRIVED' | 'IN_PROGRESS'
  ): { titulo: string; mensaje: string } => {
    const titulo = '¿Cancelar este viaje?';
    const consecuencia =
      'se suma una cancelación a tu contador y se te apunta una multa del 20% de la tarifa. ' +
      'Al llegar a 5 cancelaciones tu cuenta queda en pausa hasta que pagues las multas.';
    if (soyChofer) {
      return { titulo, mensaje: `El pasajero cuenta con este viaje. Si cancelas, ${consecuencia}` };
    }
    if (estado === 'ARRIVED') {
      return { titulo, mensaje: `Tu chofer ya llegó a buscarte. Si cancelas ahora, ${consecuencia}` };
    }
    if (estado === 'IN_PROGRESS') {
      return { titulo, mensaje: `Tu viaje ya empezó. Si cancelas ahora, ${consecuencia}` };
    }
    const aceptacion = aceptacionDelViajeRef.current;
    // Una hora de aceptación "en el futuro" (más de 5 s) quiere decir que el
    // reloj de este teléfono va atrasado: cualquier cuenta atrás con él saldría
    // larga de más y prometería una gracia que ya no existe. Se trata como si no
    // hubiera hora.
    const relojAtrasado = !!aceptacion && aceptacion.ms - Date.now() > 5000;
    if (!aceptacion || aceptacion.rideId !== rideId || relojAtrasado) {
      return {
        titulo,
        mensaje:
          'Solo se cancela sin multa en los 2 primeros minutos después de que tu chofer acepta, y todavía no tenemos confirmada esa hora. ' +
          'Si cancelas ahora, puede que se sume una cancelación a tu contador y se te apunte una multa del 20% de la tarifa. ' +
          'Al llegar a 5 cancelaciones tu cuenta queda en pausa hasta que pagues las multas.',
      };
    }
    const transcurrido = Math.max(0, Date.now() - aceptacion.ms);
    // Redondeado hacia abajo a 5 s: "unos 35 segundos", no "37".
    const quedanS = Math.floor((GRACIA_CANCELAR_MS - MARGEN_GRACIA_MS - transcurrido) / 5000) * 5;
    if (quedanS >= 5) {
      const min = Math.floor(quedanS / 60);
      const seg = quedanS % 60;
      const cuanto =
        min > 0
          ? `${min} minuto${seg > 0 ? ` y ${seg} segundos` : ''}`
          : `${seg} segundos`;
      const verbo = min === 1 && seg === 0 ? 'queda' : 'quedan';
      return {
        titulo,
        mensaje:
          `Tu chofer aceptó hace poco: te ${verbo} ${cuanto} para cancelar sin multa y sin que cuente para el bloqueo ` +
          `(hasta 2 veces al día; a partir de la tercera cuenta como cualquier cancelación). ` +
          `Pasado ese tiempo, ${consecuencia}`,
      };
    }
    return {
      titulo,
      mensaje:
        `Se acabó el tiempo para cancelar sin multa (son 2 minutos desde que tu chofer acepta). Si cancelas ahora, ${consecuencia}`,
    };
  };

  /**
   * Pregunta Sí/No y espera la respuesta. `Alert.alert` no pinta nada en web
   * (lección ya pagada en esta app): allí se usa el diálogo del navegador, igual
   * que en `modoInvitado.ts` y `profile.tsx`. Tocar fuera o Atrás cuenta como No.
   */
  const confirmarCancelacion = (titulo: string, mensaje: string): Promise<boolean> =>
    new Promise((resolver) => {
      if (Platform.OS === 'web') {
        resolver(typeof window !== 'undefined' && window.confirm(`${titulo}\n\n${mensaje}`));
        return;
      }
      Alert.alert(
        titulo,
        mensaje,
        [
          { text: 'No, seguir con el viaje', style: 'cancel', onPress: () => resolver(false) },
          { text: 'Sí, cancelar', style: 'destructive', onPress: () => resolver(true) },
        ],
        { cancelable: true, onDismiss: () => resolver(false) }
      );
    });

  /**
   * Lo mismo que `confirmarCancelacion` pero con los botones que le pases.
   *
   * Ese otro trae clavados «No, seguir con el viaje» y «Sí, cancelar», que
   * no sirven para una pregunta que no es cancelar nada. Se saca aparte en vez
   * de añadirle parámetros al de cancelar — tiene 36 llamadas en esta
   * pantalla y ninguna necesita cambiar.
   *
   * La razón de fondo es la misma, y es la que importa: en web `Alert.alert`
   * NO PINTA NADA. Un aviso que explica por qué no se puede pedir el viaje y
   * que en web no se ve deja a la persona con un error mudo, que es
   * exactamente lo que ya costó un rechazo de Google Play.
   */
  const preguntarSiNo = (
    titulo: string,
    mensaje: string,
    textoNo: string,
    textoSi: string
  ): Promise<boolean> =>
    new Promise((resolver) => {
      if (Platform.OS === 'web') {
        resolver(typeof window !== 'undefined' && window.confirm(`${titulo}\n\n${mensaje}`));
        return;
      }
      Alert.alert(
        titulo,
        mensaje,
        [
          { text: textoNo, style: 'cancel', onPress: () => resolver(false) },
          { text: textoSi, onPress: () => resolver(true) },
        ],
        { cancelable: true, onDismiss: () => resolver(false) }
      );
    });

  /**
   * EL VIAJE CADUCÓ SIN QUE NADIE LO TOMARA.
   *
   * ── POR QUÉ ESTO NO EXISTÍA Y POR QUÉ IMPORTA TANTO ──────────────────────
   * El cron del servidor marca `EXPIRED` toda solicitud que lleva más de 10
   * minutos en PENDING, y a propósito no manda ningún aviso. Del lado del
   * pasajero el listener solo tenía ramas para ACCEPTED/ARRIVED/IN_PROGRESS,
   * COMPLETED y CANCELLED: `EXPIRED` no lo leía NADIE. Nunca se llamaba a
   * `setIsSearching(false)`, así que el radar "Buscando conductores cercanos…"
   * giraba para siempre, sin mensaje y sin salida.
   *
   * Quien pruebe la app en una zona o a una hora sin choferes —un revisor de
   * Google Play, por ejemplo— reproduce el "se congeló" en exactamente diez
   * minutos, y desde fuera es indistinguible de un cuelgue.
   *
   * Esta función es la salida: apaga el radar, suelta los listeners del viaje,
   * limpia la sesión y dice en cristiano qué pasó y qué puede hacer.
   */
  const manejarViajeExpirado = useCallback(() => {
    setIsSearching(false);
    setIsActiveTrip(false);
    setIncomingDriverOffers(false);
    setReceivedOffers([]);
    setSelectedDriver(null);
    setCurrentRideId(null);
    setActiveRidePin(null);
    rideSessionStore.clearActiveRide();
    cleanupRideListeners();
    showAlert(
      'No encontramos chofer',
      'Ningún chofer tomó tu solicitud. Puedes volver a pedirlo, subir un poco la tarifa o intentar en unos minutos.',
      'time-outline',
      '#FF9500'
    );
    // `showAlert` no va en las dependencias: solo llama a un setter de estado y
    // se redefine en cada render — incluirlo rearmaría esta función sin motivo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanupRideListeners]);

  // FIX HALLAZGO 4: SOS lee coordenadas GPS REALES en el momento del evento.
  // Antes mandaba 18.4861,-69.9312 (Santo Domingo) hardcoded, lo cual
  // enviaría coordenadas equivocadas si el usuario está en Santiago, Punta
  // Cana, USA, etc. Con vidas de por medio, es inaceptable.
  // `locationService.getCurrentLocation()` nunca lanza ni retorna null
  // (tiene timeout 10s + fallback), así que el SOS jamás queda bloqueado.
  const ejecutarSOS = async () => {
    // M5 FIX — Antes usábamos `currentLocation` o `getCurrentLocation()`
    // sin distinguir entre fix REAL y fallback SD (18.4861,-69.9312). Si el
    // usuario estaba en Punta Cana con GPS timeout, el SOS transmitía las
    // coords de Santo Domingo → cops llegaban al lugar equivocado.
    //
    // Ahora forzamos un fix HIGH (fresh=true) y avisamos si el GPS no
    // pudo resolverse — dejamos al user decidir si quiere llamar sin GPS
    // preciso o esperar. `coords.accuracy === null` marca el fallback SD.
    let lat: number | null = null;
    let lng: number | null = null;
    let isReal = false;
    const loc = await locationService.getCurrentLocation(true); // fresh
    if (loc.coords.accuracy !== null) {
      lat = loc.coords.latitude;
      lng = loc.coords.longitude;
      isReal = true;
    } else if (currentLocation) {
      // Ubicación cacheada de una sesión previa — mejor que nada, pero
      // marcamos que puede no ser exacta.
      lat = currentLocation.lat;
      lng = currentLocation.lng;
      isReal = false;
    }

    if (lat === null || lng === null) {
      // ⚠️ ESTE TEXTO PROMETIA LO QUE HARIAN LOS OPERADORES DEL 911.
      // Decia «los operadores rastrearán tu llamada por señal celular». Puede
      // que lo hagan y puede que no: eso depende de la central de emergencias
      // y del operador telefónico, no de Go Safely. En una emergencia, decirle
      // a alguien que lo van a localizar cuando no lo sabemos puede hacer que
      // no diga dónde está. Ahora solo se dice lo que SÍ está en nuestra mano.
      // ⚠️ ESTO ERA `Alert.alert` Y EN LA WEB NO MARCABA NADA. En
      // react-native-web `Alert` es `class Alert { static alert() {} }`: ni
      // pinta ni ejecuta el `onPress`. O sea que en el navegador, y en la rama
      // del PEOR escenario —la de quien se quedó sin GPS—, pulsar SOS no hacía
      // absolutamente nada. `preguntarSiNo` (arriba) es el patrón que esta casa
      // ya usa en otros 12 sitios y sí funciona en los dos lados.
      if (
        await preguntarSiNo(
          '🚨 SIN SEÑAL GPS',
          'No pudimos leer tu ubicación. Puedes llamar al 911 igual: la llamada sale de tu teléfono como cualquier otra.\n\nDile al operador dónde estás — la app no puede decírselo por ti.',
          'Cancelar',
          'Llamar 911'
        )
      ) {
        Linking.openURL('tel:911').catch(() => {});
      }
      return;
    }

    const coordsLabel = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    const gpsWarning = isReal
      ? ''
      // Igual que arriba: lo que haga la central de emergencias no lo
      // sabemos, así que no se promete. Lo que sí sabemos es que el punto que
      // guardamos no es de fiar, y eso es lo que hay que decir.
      : '\n\n⚠️ Esta es tu última ubicación conocida, no la de ahora mismo: el GPS no respondió. Puede estar lejos de donde estás.';

    // ⚠️ ESTO ERA `Alert.alert` Y EN LA WEB SE PERDÍA ENTERO: el `onPress` de
    // «Llamar al 911» —donde cuelgan la llamada Y el `registrarSOS` que es lo
    // ÚNICO que avisa al equipo— no se ejecutaba nunca en el navegador. Se
    // pulsaba el botón de pánico, se pedía el GPS, y después silencio: ni
    // llamada, ni documento en `sos_alerts`, ni aviso a nadie.
    const quiereLlamar = await preguntarSiNo(
      '🚨 EMERGENCIA - LLAMAR AL 911',
      // ⚠️⚠️ ESTE TEXTO DECIA «Y TRANSMITIR TU UBICACION SATELITAL EXACTA».
      //
      // No se transmite nada al 911. `registrarSOS` escribe un documento en
      // `sos_alerts` (firestoreService.ts) — o sea, el aviso llega AL EQUIPO
      // DE GO SAFELY, que es otra cosa y es útil, pero no es lo que la frase
      // daba a entender. Quien lee «transmitir tu ubicación al 911» entiende
      // que los bomberos ya saben dónde está, y puede colgar antes de decirlo.
      //
      // Unas líneas más abajo hay un comentario de cuando se arregló la mitad
      // de esta mentira: el mensaje DESPUES de llamar decía que se había
      // «transmitido tu señal GPS junto a tu perfil biométrico». Se corrigió
      // ese y se quedó este, que es el que la persona lee ANTES de decidir.
      //
      // Lo que dice ahora es exactamente lo que pasa: se abre el marcador, la
      // persona habla, y nosotros nos enteramos y guardamos dónde estaba.
      `Vamos a abrir el marcador de tu teléfono con el 911. Habla tú con el operador y dile dónde estás: la app no puede hablar por ti ni enviarle tus datos.\n\nAl mismo tiempo guardamos tu ubicación y avisamos al equipo de Go Safely.\n\n📍 Coordenadas: ${coordsLabel}${gpsWarning}`,
      'Cancelar',
      'Llamar al 911'
    );
    if (quiereLlamar) {
            // El 911 se marca de verdad — eso siempre funcionó.
            Linking.openURL('tel:911').catch((err) => {
              console.error('Call error:', err);
              showAlert('No se pudo llamar', 'Marca el 911 directamente desde tu teléfono.', 'call', '#FF3B30');
            });

            // Lo que NO era verdad: la app decía que había "transmitido tu
            // señal GPS junto a tu perfil biométrico" al 911. Nunca envió nada
            // a ningún lado. Prometerle eso a alguien en una emergencia es lo
            // más grave que puede hacer esta app.
            // Ahora sí queda registro: se guarda la alerta con la ubicación,
            // y Go Safely recibe aviso al instante para poder actuar.
            firestoreService
              .registrarSOS({
                uid: useAppStore.getState().userFirebaseUid ?? null,
                nombre: useAppStore.getState().userName ?? null,
                telefono: useAppStore.getState().userPhone ?? null,
                // ── LAS MISMAS COORDENADAS QUE SE LE ENSEÑARON ──────────
                // Antes se guardaba `currentLocation`, el último valor en
                // memoria, mientras que en pantalla se le mostraban las de un
                // fix FRESCO pedido al pulsar el SOS. Podían ser distintas: la
                // persona leía unas coordenadas y al equipo le llegaban otras.
                // En una emergencia esa diferencia se mide en cuadras.
                lat,
                lng,
                // Y se guarda si la lectura era real o aproximada: quien reciba
                // esta alerta tiene que saber cuánto puede fiarse del punto.
                ubicacionReal: isReal,
                rideId: currentRideId ?? null,
                rol: role ?? null,
              })
              .catch((e) => console.warn('[SOS] no se pudo registrar la alerta:', e?.message));

            showAlert(
              'Llamando al 911',
              `Guardamos tu ubicación (${coordsLabel}) y avisamos al equipo de Go Safely para darte seguimiento.\n\nHabla directamente con el 911: la app no puede hablar por ti.`,
              'shield-checkmark',
              '#FF3B30'
            );
    }
  };

  /**
   * EL BOTÓN SOS TIENE QUE DAR SEÑAL DE VIDA.
   *
   * `ejecutarSOS` empieza pidiendo un fix GPS de PRECISIÓN ALTA, y
   * `locationService.getCurrentLocation(true)` corre contra un reloj de 20
   * segundos antes de rendirse al respaldo. Durante esos segundos el botón no
   * se deshabilitaba, no giraba y no cambiaba de aspecto: quien lo pulsa en una
   * emergencia concluye que no funcionó y lo vuelve a pulsar.
   *
   * Y cada pulsación abría OTRA petición de GPS en paralelo y, al resolverse,
   * OTRO diálogo de confirmación encima del anterior — con el riesgo de
   * registrar la alerta dos veces y de que la persona confirme sobre el diálogo
   * equivocado.
   *
   * LA LÓGICA DEL SOS NO SE TOCA. Lo único que cambia es que se VE:
   *  • el candado es un REF, no el estado, porque dos toques seguidos ocurren
   *    dentro del mismo fotograma, antes de que React repinte nada;
   *  • el estado es lo que repinta el botón (girando y atenuado);
   *  • el `finally` garantiza que el candado se suelta aunque algo reviente:
   *    un SOS que se queda trabado para siempre sería peor que el problema.
   *
   * El candado se suelta en cuanto aparece el diálogo de confirmación, no
   * cuando termina la llamada: la espera ciega es el GPS, y el diálogo ya es
   * modal.
   */
  const [sosBuscandoUbicacion, setSosBuscandoUbicacion] = useState(false);
  const sosEnCursoRef = useRef(false);
  const handleSOS = async () => {
    if (sosEnCursoRef.current) return;
    sosEnCursoRef.current = true;
    setSosBuscandoUbicacion(true);
    try {
      await ejecutarSOS();
    } finally {
      sosEnCursoRef.current = false;
      setSosBuscandoUbicacion(false);
    }
  };

  // Driver state
  const [isOnline, setIsOnline] = useState(false);
  /**
   * EL INTERRUPTOR NO PUEDE MENTIR NI IR POR DELANTE.
   *
   * `cambiandoConexion` es el rato —normalmente un segundo, con mala señal
   * hasta el plazo— en que ya se pulsó pero todavía no se sabe qué pasó. Sin
   * él, el botón se quedaba mudo con la misma etiqueta y el chofer volvía a
   * pulsarlo.
   *
   * `cambioConexionRef` es el cerrojo: dos toques seguidos lanzaban dos
   * cambios que se pisan (encender y apagar entrelazados), que es justo como
   * se llega al estado invertido —pantalla apagada emitiendo posición— que
   * este arreglo viene a cerrar. El ref, y no el state, porque tiene que
   * valer YA en el mismo toque, sin esperar a un re-render.
   */
  const [cambiandoConexion, setCambiandoConexion] = useState(false);
  const cambioConexionRef = useRef(false);
  // V12 — sube cuando el resume vuelve a encender GPS y presencia de un chofer
  // con viaje en curso; la limpieza de arranque offline se retira al verlo.
  const reanudandoViajeChoferRef = useRef(false);
  const [incomingPassengerRequest, setIncomingPassengerRequest] = useState(false);
  // Stores the ride the driver accepted so the ActiveRideCard shows real passenger info.
  const [driverCurrentRide, setDriverCurrentRide] = useState<any>(null);

  /**
   * Escribe el viaje del chofer SOLO si de verdad cambió algo.
   *
   * `fusionar` replica el `{...prev, ...data}` de antes para los listeners que
   * traen el documento parcial; la diferencia es que si el resultado es
   * idéntico al que ya había se devuelve EL MISMO objeto, y React no vuelve a
   * renderizar. Ver `mismoViaje` arriba para el porqué.
   */
  const aplicarViajeDelChofer = useCallback((entrante: any, fusionar = false) => {
    setDriverCurrentRide((prev: any) => {
      const siguiente = fusionar ? { ...(prev || {}), ...entrante } : entrante;
      return mismoViaje(prev, siguiente) ? prev : siguiente;
    });
  }, []);

  /**
   * Copia SIEMPRE fresca del viaje del chofer para quien necesite leerlo sin
   * poder tenerlo en sus dependencias (ver el geofence más abajo).
   */
  const driverCurrentRideRef = useRef<any>(null);
  useEffect(() => { driverCurrentRideRef.current = driverCurrentRide; }, [driverCurrentRide]);

  // Ride IDs this driver dismissed this session — filtered out of Firestore snapshots.
  const rejectedRideIdsRef = useRef<Set<string>>(new Set());
  // Solicitudes cuyo cierre ya se le dijo al chofer (lo rechazaron, caducó o la
  // tomó otro). El feed dispara en cada snapshot; sin esto, el aviso saldría una
  // y otra vez.
  const avisoDeOfertaCerradaRef = useRef<Set<string>>(new Set());
  /**
   * DE QUÉ VIAJE es la contraoferta que el chofer está escribiendo.
   *
   * Se fija al ABRIR el modal y no se vuelve a mirar `currentRideId`, que es
   * una casilla compartida: mientras el chofer piensa el monto, el feed puede
   * apuntarla a OTRA solicitud (si la suya se canceló y entró otra) o dejarla
   * en null (si no entró ninguna). Entonces pasaba una de dos: la contraoferta
   * se escribía en el viaje de otra persona, o no se escribía en ninguno y la
   * app igual decía «Contraoferta Enviada» — el chofer esperando una respuesta
   * que no podía llegar. Con el id congelado aquí, la oferta va donde tiene que
   * ir o no va, y se dice.
   */
  const contraofertaRideIdRef = useRef<string | null>(null);
  /**
   * El feed es un ESCUCHADOR: solo repinta cuando Firestore manda algo nuevo.
   * Si un viaje se esconde por la ventana del chofer más cercano y no vuelve a
   * pasar nada, se quedaría escondido mucho más de los 25 segundos. Este
   * temporizador lo vuelve a sacar a la hora exacta.
   */
  const temporizadorVentanaRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * LO QUE CUESTA DE VERDAD ESE VIAJE, congelado junto con el id.
   * Los montos sugeridos eran porcentajes del precio del PASAJERO: si el
   * pasajero ofrecía poco, las sugerencias eran poco. Ninguna miraba los
   * kilómetros. Con esto se puede ofrecer además el precio que sale de la tabla
   * de tarifas contando los DOS tramos, el de ir a buscarlo y el del viaje —
   * que es lo que el chofer gasta.
   * (Y los multiplicadores ya solo suben: ver el comentario del modal.)
   */
  const contraofertaEconomiaRef = useRef<{ kmTotal: number; vehiculo: string } | null>(null);
  /**
   * CHOFERES QUE LA PASAJERA YA DESCARTÓ EN ESTE VIAJE.
   *
   * El rechazo se escribe en el viaje (`rechazados`), pero el listener de
   * ofertas puede entregar un snapshot ANTES de que esa escritura vuelva: sin
   * esta copia local, la oferta que acaba de rechazar reaparecía en pantalla.
   * Se llena por los dos lados —al rechazar y con lo que trae el documento del
   * viaje— y se vacía al empezar o cerrar un viaje.
   */
  const choferesRechazadosRef = useRef<Set<string>>(new Set());
  /**
   * Y LAS OFERTAS DESCARTADAS, POR SU PROPIO ID.
   *
   * Descartar por chofer no basta: una oferta sin `driverId` no se puede
   * apuntar en el Set de arriba —no hay a quién apuntar— y `ofertasVisibles`
   * la deja pasar a propósito (ocultarla sería perder un chofer real por un
   * campo que falta). Resultado: el rechazo la quitaba de la pantalla y el
   * siguiente snapshot la devolvía, porque `rejectDriverOffer` NO borra el
   * documento de la oferta, solo escribe en `rechazados` del viaje.
   */
  const ofertasRechazadasRef = useRef<Set<string>>(new Set());
  /**
   * Las ofertas que la pasajera SÍ debe ver: las de choferes que no ha
   * descartado. Una oferta sin `driverId` se deja pasar — es un dato roto, pero
   * ocultarla sería perder un chofer real por un campo que falta.
   */
  const ofertasVisibles = useCallback((ofertas: any[]) => {
    if (choferesRechazadosRef.current.size === 0 && ofertasRechazadasRef.current.size === 0) {
      return ofertas;
    }
    return ofertas.filter(
      (o: any) =>
        !ofertasRechazadasRef.current.has(String(o?.id ?? '')) &&
        (!o?.driverId || !choferesRechazadosRef.current.has(String(o.driverId)))
    );
  }, []);
  /**
   * Los rechazos que YA están escritos en el viaje, traídos a la copia local.
   * Hace falta para el arranque en frío —la app se cerró y se volvió a abrir con
   * la solicitud viva— y para quien rechazó desde otro teléfono: sin esto, las
   * ofertas que ya descartó volverían a su pantalla como si fueran nuevas.
   */
  const recordarRechazosDelViaje = useCallback((data: any) => {
    const lista = Array.isArray(data?.rechazados) ? data.rechazados : [];
    let nuevos = false;
    lista.forEach((uid: any) => {
      const id = String(uid ?? '').trim();
      if (id && !choferesRechazadosRef.current.has(id)) {
        choferesRechazadosRef.current.add(id);
        nuevos = true;
      }
    });
    if (!nuevos) return;
    setReceivedOffers((previas) => {
      const quedan = previas.filter(
        (o: any) => !o?.driverId || !choferesRechazadosRef.current.has(String(o.driverId))
      );
      if (quedan.length === 0 && !isActiveTripRef.current) {
        setIncomingDriverOffers(false);
        // Al radar, no a una hoja vacía. Con viaje vivo no se toca: ver la
        // guarda del tope de 6 minutos en `rechazarOferta`.
        if (activeRideIdRef.current) setIsSearching(true);
      }
      return quedan;
    });
  }, []);
  // Ride que el chofer está VIENDO ahora en "Nueva Solicitud". Se usa en el
  // listener de pending rides para detectar cuándo esa solicitud dejó de estar
  // disponible (pasajero canceló o la tomó otro chofer) y limpiar la pantalla.
  // Es un ref (no el state currentRideId) para no depender de un closure viejo
  // dentro del callback del onSnapshot.
  const shownRequestRideIdRef = useRef<string | null>(null);

  // FIX BACKGROUND PERSISTENCE — Antes el chofer tocaba "Conectarme",
  // setDriverOnline + startTracking arrancaban OK, foreground service Android
  // se mantenía vivo en background. PERO al minimizar la app más de unos
  // segundos, Android puede pausar el JS engine; al reabrir, React reset
  // todo el estado local → isOnline volvía a false, los listeners se
  // cancelaban y el chofer parecía offline aunque el GPS siguiera enviando
  // ubicaciones. Resultado: el chofer minimizaba 30 s y "perdía" su sesión
  // de online.
  //
  // Ahora al montar el componente, consultamos locationService.getCurrentMode
  // (que lee la task GPS persistida en TaskManager + AsyncStorage). Si hay
  // tracking activo, restauramos isOnline=true automáticamente y los
  // listeners de pending rides se vuelven a enganchar.
  useEffect(() => {
    if (Platform.OS === 'web') return;
    let cancelled = false;
    (async () => {
      try {
        // ARRANQUE SIEMPRE OFFLINE — El chofer NO se auto-conecta al abrir la
        // app. Antes restaurábamos isOnline=true si quedaba un GPS task
        // registrado o el flag `driverOnline` persistido; pero ambos SOBREVIVEN
        // al cierre/logout, así que al re-entrar (o tras loguearse) el chofer
        // aparecía "conectado" sin haberlo decidido. Regla del fundador: nunca
        // conectar automáticamente — el chofer siempre pulsa "Conectarse".
        //
        // Además, limpiamos cualquier tracking GPS HUÉRFANO de una sesión
        // anterior (app cerrada estando online): si no, el foreground service
        // seguiría enviando ubicación en segundo plano sin que el chofer lo
        // haya decidido. Así el arranque queda en offline REAL.
        // ⚠️ LA DECISIÓN DEL CHOFER MANDA SOBRE ESTA LIMPIEZA.
        // Esta limpieza tiene `await`s, así que puede terminar DESPUÉS de que
        // el chofer haya pulsado "Conectarse". Si siguiera adelante le
        // apagaría por la espalda el GPS y la presencia que acaba de encender
        // — y lo dejaría "En línea" sin nada detrás, que es exactamente el
        // estado invertido que este archivo cierra. Por eso se comprueba antes
        // de cada paso que muta algo: si hay un cambio de conexión en vuelo,
        // esta limpieza se retira.
        if (cambioConexionRef.current) return;

        // ── V12: UN CHOFER CON VIAJE EN CURSO NO SE APAGA AL ARRANCAR ──────
        // Si Android mató la app a mitad del viaje (Waze delante), el resume de
        // abajo le vuelve a encender GPS y presencia. Esta limpieza es asíncrona
        // y podía terminar DESPUÉS y apagarlo todo: el pasajero veía el carro
        // congelado el resto del viaje. Con un viaje de chofer guardado, o con la
        // reanudación ya en marcha, decide el resume.
        const sesionGuardada = await rideSessionStore.read().catch(() => null);
        if (sesionGuardada?.activeRideId && sesionGuardada.activeRideRole === 'DRIVER') return;
        if (cambioConexionRef.current || reanudandoViajeChoferRef.current) return;

        const mode = await locationService.getCurrentMode();
        if (cambioConexionRef.current || reanudandoViajeChoferRef.current) return;
        if (mode) {
          await locationService.stopTracking().catch(() => {});
          const uid = useAppStore.getState().userFirebaseUid;
          if (uid) {
            firestoreService
              .setDriverOnline(uid, { isOnline: false })
              .catch((e) => console.warn('[map] limpiar presencia al montar falló:', e?.message));
          }
        }
        // Borrar la intención persistida para que el próximo arranque tampoco
        // auto-conecte.
        if (!cancelled && !cambioConexionRef.current && !reanudandoViajeChoferRef.current) {
          await rideSessionStore.setDriverOnline(false).catch(() => {});
          if (!reanudandoViajeChoferRef.current) setIsOnline(false);
        }
      } catch (e) {
        console.warn('[map] arranque offline del chofer falló:', (e as any)?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ────────────────────────────────────────────────────────────────────────
  // HIDRATACIÓN DE SESIÓN — SIEMPRE corre al montar, sin depender del uid.
  // ----------------------------------------------------------------------
  // Debe correr aunque el uid aún no haya llegado, porque desbloquea el
  // listener global del chofer (que consulta pendingOfferRideIdRef). Si esto
  // dependiera del uid y el uid llegara tarde, el chofer nunca recibiría
  // rides. Es AsyncStorage local — no necesita sesión Firebase.
  useEffect(() => {
    let cancelled = false;
    // Safety: pase lo que pase, no dejar el shield encendido más de 4s.
    const safety = setTimeout(() => { if (!cancelled) setResumeChecking(false); }, 4000);
    (async () => {
      const session = await rideSessionStore.read();
      if (cancelled) return;
      pendingOfferRideIdRef.current = session.pendingOfferRideId;
      if (session.pendingOfferRideId) setPendingOfferRideId(session.pendingOfferRideId);
      sessionHydratedRef.current = true;
      setSessionHydrated(true);
      // Si NO hay viaje que restaurar (ni sesión local ni deep-link),
      // apagamos el shield de inmediato → el formulario aparece sin demora.
      if (!session.activeRideId && !resumeRideId) {
        setResumeChecking(false);
      }
    })();
    return () => { cancelled = true; clearTimeout(safety); };
  }, []);

  // ────────────────────────────────────────────────────────────────────────
  // BOOTSTRAP RESUME — Restaurar el ride activo al cold-start / deep-link
  // ----------------------------------------------------------------------
  // Uber e InDrive consultan al backend "¿tengo algún ride abierto?" en cada
  // cold-start. Si sí, muestran el estado real del ride. Antes esta app no
  // lo hacía — al reabrir la app durante un viaje activo, el usuario
  // aterrizaba en "Solicitar Viaje" y perdía el contexto.
  //
  // ANTI-FANTASMA — Solo restauramos el ride que el DEVICE conoce
  // (session.activeRideId) o el que llega por deep-link (resumeRideId). Un
  // ride viejo en Firestore que el device no guardó NUNCA se restaura.
  useEffect(() => {
    let cancelled = false;
    const uid = useAppStore.getState().userFirebaseUid;
    if (!uid || !role) return;
    (async () => {
      try {
        const session = await rideSessionStore.read();

        // ¿Qué ride restauramos? SOLO uno de estos, en orden de prioridad:
        //   1) `resumeRideId` del deep-link (tap de notificación).
        //   2) `session.activeRideId` — el viaje que YO guardé al entrar.
        // Si NINGUNO existe, NO restauramos nada. Esto mata el viaje
        // fantasma de raíz: un ride viejo en Firestore que el device no
        // conoce jamás se muestra.
        // ── H45 (auditoría 16-sep-2026): EL DEEP-LINK NO BORRA EL VIAJE BUENO ──
        // Antes se leía UN solo candidato (el deep-link si lo había) y, si ese
        // estaba terminado, se borraba el puntero guardado… aunque el puntero
        // fuera OTRO viaje, vivo. Con el puntero borrado, el anti-fantasma de
        // arriba garantiza que ese viaje no se restaure nunca más.
        //
        // Ahora se prueban los dos en el mismo orden de prioridad, y el puntero
        // solo se borra cuando el viaje terminado ES el guardado.
        const candidatos: string[] = [];
        if (resumeRideId) candidatos.push(String(resumeRideId));
        if (session.activeRideId && !candidatos.includes(session.activeRideId)) {
          candidatos.push(session.activeRideId);
        }
        if (candidatos.length === 0) return;

        const activeStatuses = new Set(['PENDING', 'ACCEPTED', 'ARRIVED', 'IN_PROGRESS']);
        let activeRide: any = null;
        for (const candidato of candidatos) {
          const esElGuardado = candidato === session.activeRideId;
          // Leer el ESTADO real de ESE ride específico.
          // FIX causa raíz #2 — Distinguimos "no existe/terminal" (limpiar es
          // seguro) de "la lectura FALLÓ" (NO limpiar: conservar el puntero y
          // reintentar en el próximo arranque / al volver a foreground). Antes un
          // error transitorio borraba el viaje activo y forzaba a buscarlo de nuevo.
          let leido: any = null;
          try {
            leido = await firestoreService.getRideById(candidato);
          } catch (readErr: any) {
            console.warn('[resume] getRideById falló, conservo el puntero del viaje:', readErr?.message);
            return; // NO borramos la sesión ante un fallo de lectura
          }
          if (!leido || !activeStatuses.has(String(leido.status ?? ''))) {
            // Confirmado: el ride ya terminó o no existe → limpiar la sesión
            // local, pero SOLO si es el que esta sesión guardó (H45).
            if (esElGuardado) await rideSessionStore.clearActiveRide();
            continue;
          }
          // FIX BUG #3 (causa raíz #2) — Chequeo de frescura que faltaba en el
          // resume por ID: si el ride está ABANDONADO (>4h sin actividad), lo
          // auto-expiramos y limpiamos el puntero ANTES de montarlo, para no
          // re-montar en cada arranque un viaje colgado (el patrón que producía
          // el crash-loop de horas).
          if (firestoreService.isRideStale(leido)) {
            await firestoreService.forceExpireOrphanRide(leido.id).catch(() => {});
            if (esElGuardado) await rideSessionStore.clearActiveRide();
            continue;
          }
          // Un candidato vivo en el que no soy la parte de este modo (el aviso
          // viejo de otro papel) no tapa al siguiente: se prueba el guardado.
          // La comprobación de abajo (H29) sigue siendo la que decide.
          if (
            (role === 'DRIVER' && leido.driverId !== uid) ||
            (role === 'PASSENGER' && leido.passengerId !== uid)
          ) {
            if (!esElGuardado) continue;
          }
          activeRide = leido;
          break;
        }
        if (!activeRide) return;
        if (cancelled) return;

        // ── H29 (auditoría 16-sep-2026): SE RESTAURA SEGÚN QUIÉN ERES EN EL VIAJE ──
        // La rama de abajo se elegía por el MODO del teléfono. Quien pidió un
        // viaje como pasajero, pasó el teléfono a Modo Conductor y reabrió la
        // app, veía la tarjeta DEL CHOFER sobre su propio viaje ("He llegado",
        // "Iniciar viaje") en un viaje cuyo chofer es otra persona: cada toque
        // lo denegaban las reglas. Es el mismo cruce de papeles que ya se cerró
        // en ActiveRideCard, pero antes, al restaurar.
        //
        // El documento dice quién es quién. Si el modo no coincide, no se monta
        // nada y el puntero SE CONSERVA: este efecto vuelve a correr al cambiar
        // `role`, así que al volver al modo correcto el viaje aparece solo.
        const soyChoferDelViaje = !!activeRide.driverId && activeRide.driverId === uid;
        const soyPasajeroDelViaje = !!activeRide.passengerId && activeRide.passengerId === uid;
        if ((role === 'DRIVER' && !soyChoferDelViaje) || (role === 'PASSENGER' && !soyPasajeroDelViaje)) {
          console.warn('[resume] el viaje guardado no es de este modo, no lo monto:', activeRide.id, role);
          return;
        }

        // Restaurar estado local + listeners.
        cleanupRideListeners();
        setCurrentRideId(activeRide.id);
        activeRideIdRef.current = activeRide.id;
        setEnvioDePaqueteVivo(String((activeRide as any)?.serviceType ?? '') === 'DELIVERY');

        // ── RESTAURACIÓN DEL CHOFER ──
        if (role === 'DRIVER') {
          setDriverCurrentRide(activeRide);
          setIsActiveTrip(true);
          setPendingOfferRideId(null);
          await rideSessionStore.setPendingOffer(null);
          setRideStatus(
            activeRide.status === 'IN_PROGRESS' ? 'IN_PROGRESS'
              : activeRide.status === 'ARRIVED' ? 'ARRIVED'
              : 'ACCEPTED'
          );
          // ── V12: GPS Y PRESENCIA DE VUELTA ────────────────────────────────
          // Tras un arranque en frío a mitad del viaje la tarjeta volvía, pero
          // el GPS no (y "Conectarse" está oculto con viaje activo): el pasajero
          // veía el carro congelado, el aviso de llegada no saltaba y el
          // listener del chofer, que exige estar en línea, quedaba apagado. Se
          // reenciende como al conectarse, y solo se da por conectado si el
          // GPS quedó vivo de verdad.
          reanudandoViajeChoferRef.current = true;
          (async () => {
            let rastreoOk = Platform.OS === 'web';
            if (Platform.OS !== 'web') {
              try {
                await locationService.startTracking(uid, 'ACTIVE_RIDE');
                rastreoOk = locationService.modoDeRastreoActual() !== null;
              } catch (e: any) {
                console.warn('[resume] no se pudo reencender el GPS del chofer:', e?.message);
              }
            }
            if (!rastreoOk) {
              showAlert(
                'Tu ubicación está apagada',
                'Tienes un viaje en curso y el pasajero no puede ver tu carro. Revisa que la app tenga permiso de ubicación y que el GPS esté encendido, y vuelve a abrir la app.',
                'alert-circle',
                '#FF3B30'
              );
              return;
            }
            try {
              await firestoreService.setDriverOnline(uid, {
                isOnline: true,
                userName: useAppStore.getState().userName || undefined,
              });
            } catch (e: any) {
              console.warn('[resume] no se pudo restaurar la presencia del chofer:', e?.message);
            }
            rideSessionStore.setDriverOnline(true).catch(() => {});
            setIsOnline(true);
          })();
          // Escuchar cambios de estado de ESTE ride para el chofer.
          rideStatusUnsubRef.current = firestoreService.listenToRideStatus(activeRide.id, (data) => {

        // EL DOCUMENTO MANDA SOBRE LA PANTALLA.
        // `paymentMethod` era un useState local cuyo ÚNICO escritor era el
        // modal de selección: nada lo releía del viaje. Con solo reabrir la
        // app, o que esta pantalla se volviera a montar, el pasajero que había
        // elegido Transferencia volvía a «Efectivo» y perdía la única puerta a
        // las cuentas del chofer. Es el mismo criterio que ya se usa con el
        // destino: lo que vale es lo que dice el documento.
        if (data?.paymentMethod) {
          const metodoDelViaje = String(data.paymentMethod).toUpperCase();
          if (metodoDelViaje === 'CASH' || metodoDelViaje === 'CARD' || metodoDelViaje === 'TRANSFER') {
            setPaymentMethod((prev) => (prev === metodoDelViaje ? prev : (metodoDelViaje as any)));
          }
        }
            if (!data) return;
            if (data.status === 'COMPLETED' || data.status === 'CANCELLED') {
              // Y al chofer tampoco se le preguntaba por el cobro si la app se
              // habia reiniciado. Va ANTES de limpiar `data`, que es de donde
              // salen el monto y el metodo. Un viaje CANCELLED no se cobra.
              if (data.status === 'COMPLETED') {
                abrirCobroDelChoferSiHaceFalta(data, String(data.id || activeRide.id));
              }
              setIsActiveTrip(false);
              setDriverCurrentRide(null);
              setCurrentRideId(null);
              setRideStatus('ACCEPTED');
              rideSessionStore.clearActiveRide();
              cleanupRideListeners();
              // H28 (auditoría 16-sep-2026) — viaje cerrado: el GPS vuelve al
              // modo de espera. Si el chofer no está conectado no hay rastreo y
              // esto no hace nada (`setTrackingMode` sale sin vigilante vivo).
              locationService.setTrackingMode('IDLE').catch(() => {});
            } else {
              // Antes esto creaba un objeto NUEVO en cada snapshot aunque los
              // datos fueran idénticos, y con él se caía el React.memo del mapa.
              aplicarViajeDelChofer(data, true);
              setRideStatus(
                data.status === 'IN_PROGRESS' ? 'IN_PROGRESS'
                  : data.status === 'ARRIVED' ? 'ARRIVED'
                  : 'ACCEPTED'
              );
            }
          });
          return;
        }

        // ── RESTAURACIÓN DEL PASAJERO ──
        if (role === 'PASSENGER') {
          // ── LA RECOGIDA, DEL DOCUMENTO Y NO DEL GPS ────────────────────
          // Este bloque ya leía el viaje entero; lo único que faltaba era
          // quedarse con su origen. Sin esto, al reabrir la app el pin de
          // recogida saltaba al teléfono de quien pidió el viaje, que no es
          // donde el chofer va a recoger cuando el carro se pidió para otra
          // persona. Si el viaje no trae coordenadas, queda null y manda el GPS.
          setRecogidaDelViaje(recogidaDeDocumento(activeRide));
          // El destino, del mismo documento y por la misma razón: al reabrir la
          // app en pleno viaje, la línea tiene que seguir apuntando a donde va
          // esta persona, no al punto donde ya se montó.
          setDestinoDelViaje(destinoDeDocumento(activeRide));
          // Restaurar UI según el status.
          if (activeRide.status === 'PENDING') {
            setIsSearching(true);
          } else if (
            activeRide.status === 'ACCEPTED' ||
            activeRide.status === 'ARRIVED' ||
            activeRide.status === 'IN_PROGRESS'
          ) {
            setIsActiveTrip(true);
            setRideStatus(
              activeRide.status === 'IN_PROGRESS' ? 'IN_PROGRESS'
                : activeRide.status === 'ARRIVED' ? 'ARRIVED'
                : 'ACCEPTED'
            );
            // H24 — al reabrir la app también se sabe cuándo se aceptó.
            recordarAceptacionDelViaje(String(activeRide.id), activeRide.acceptedAt);
            // Reconstruir selectedDriver desde el ride para que la card
            // muestre nombre/foto/vehículo del chofer aceptado.
            if (activeRide.driverId) {
              setSelectedDriver({
                driverId: activeRide.driverId,
                // Sin nombre en el viaje se deja NULL, no el literal 'Conductor':
                // así el hueco se puede rellenar con `getDriverPublicInfo` (un
                // texto de relleno no es un hueco y bloquearía el relleno). La
                // tarjeta ya pinta "Conductor" cuando no hay nombre.
                driverName: activeRide.driverName ?? null,
                userName: activeRide.driverName ?? null,
                driverPhoto: activeRide.driverPhoto ?? null,
                // Sin nota real, null. Un '5.0' escrito a mano es una estrella
                // falsa; `ratingDisplay` traduce el hueco a "Nuevo", que es cierto.
                rating: activeRide.driverRating ?? null,
                // ── LO QUE SE APAGABA AL REABRIR ────────────────────────────
                // `acceptRideOffer` congela en el viaje la verificación REAL del
                // chofer y el tipo de su vehículo. Sin leerlos aquí, al reabrir
                // la app el escudo se apagaba aunque el chofer SÍ esté verificado
                // y el marcador volvía al carrito genérico aunque viniera un
                // motor. Booleano de verdad o null: nunca un `true` de relleno.
                isFaceIdVerified:
                  typeof activeRide.isDriverVerified === 'boolean'
                    ? activeRide.isDriverVerified
                    : null,
                vehicleType: activeRide.driverVehicleType ?? activeRide.vehicleType ?? null,
                vehicleInfo: activeRide.vehicleInfo,
                vehicleBrand: activeRide.vehicleBrand ?? null,
                vehicleModel: activeRide.vehicleModel ?? null,
                vehicleColor: activeRide.vehicleColor ?? null,
                licensePlate: activeRide.licensePlate ?? null,
                // ── AQUI NO SE LEEN NI EL RANGO NI LA FOTO DEL CARRO ───────
                // Se intento el 17-oct-2026 y son campos que NADIE escribe en
                // el documento del viaje: `nivel` y `vehiclePhotoUrl` se
                // estampan en la OFERTA (functions/src/rides.ts:1453 y 1456),
                // y `congelarDatosDelViaje` no escribe ninguno de los dos en
                // `rides/{id}`. Leerlos aqui daba null siempre.
                // El rango sí vuelve al reabrir la app, por el otro camino:
                // `completarDatosDelChofer` lo rellena con lo que devuelve
                // `getDriverPublicInfo`. La foto del carro no vuelve — esa
                // lista blanca (functions/src/drivers.ts) no la incluye.
              });
              // Y lo que el viaje NO traiga, se pide: nombre, foto, nota, marca,
              // modelo, color y placa. Antes esta llamada no existía en el
              // resume, así que reabrir la app era la forma segura de perder los
              // datos del chofer hasta el final del viaje.
              completarDatosDelChofer(String(activeRide.driverId), String(activeRide.id));
            }
          }

          // Re-abrir listeners de ofertas + status.
          // El estado VIVO del viaje, no el del momento de montar: un viaje
          // restaurado en ACCEPTED que el servidor reabre (V13) tiene que volver a
          // enseñar las ofertas que le quedan.
          let estadoVivoDelViaje = String(activeRide.status ?? '');
          // ANTES de montar el listener: los rechazos ya escritos en el viaje
          // que se está restaurando. Si se dejara para el primer snapshot del
          // documento, la oferta que ya descartó parpadearía en pantalla.
          recordarRechazosDelViaje(activeRide);
          offersUnsubRef.current = firestoreService.listenToRideOffers(activeRide.id, (offers) => {
            const utiles = ofertasVisibles(offers);
            setReceivedOffers(utiles);
            if (utiles.length > 0 && estadoVivoDelViaje === 'PENDING') {
              setIsSearching(false);
              setIncomingDriverOffers(true);
            } else if (estadoVivoDelViaje === 'PENDING' && !isActiveTripRef.current) {
              // Sin ofertas que enseñar la solicitud SIGUE VIVA: el radar tiene
              // que estar girando, que es lo que devuelve el botón de cancelar.
              // ⚠️ `isActiveTripRef` no es de adorno: `isSearching` en true con
              // un viaje vivo ARMA el tope de seguridad de 6 minutos, y ese tope
              // CANCELA la solicitud — cancelaría el viaje en marcha.
              setIncomingDriverOffers(false);
              setIsSearching(true);
            }
          });
          rideStatusUnsubRef.current = firestoreService.listenToRideStatus(activeRide.id, (data) => {

        // EL DOCUMENTO MANDA SOBRE LA PANTALLA.
        // `paymentMethod` era un useState local cuyo ÚNICO escritor era el
        // modal de selección: nada lo releía del viaje. Con solo reabrir la
        // app, o que esta pantalla se volviera a montar, el pasajero que había
        // elegido Transferencia volvía a «Efectivo» y perdía la única puerta a
        // las cuentas del chofer. Es el mismo criterio que ya se usa con el
        // destino: lo que vale es lo que dice el documento.
        if (data?.paymentMethod) {
          const metodoDelViaje = String(data.paymentMethod).toUpperCase();
          if (metodoDelViaje === 'CASH' || metodoDelViaje === 'CARD' || metodoDelViaje === 'TRANSFER') {
            setPaymentMethod((prev) => (prev === metodoDelViaje ? prev : (metodoDelViaje as any)));
          }
        }
            if (!data) return;
            recordarRechazosDelViaje(data);
            estadoVivoDelViaje = String(data.status ?? '');
            if (soltarChoferQueYaIbaEnOtroViaje(data, String(activeRide.id))) return;
            if (data.status === 'ACCEPTED' || data.status === 'ARRIVED' || data.status === 'IN_PROGRESS') {
              setIsActiveTrip(true);
              setRideStatus(
                data.status === 'IN_PROGRESS' ? 'IN_PROGRESS'
                  : data.status === 'ARRIVED' ? 'ARRIVED'
                  : 'ACCEPTED'
              );
              // Si se reabrió la app con la solicitud todavía PENDIENTE y el
              // chofer acepta después, este camino no llenaba NUNCA los datos
              // del chofer: la tarjeta se quedaba sin nombre, sin foto y sin
              // carro hasta el final del viaje.
              if (data.driverId) {
                completarDatosDelChofer(String(data.driverId), String(activeRide.id));
              }
              // H24 — ver `aceptacionDelViajeRef`.
              recordarAceptacionDelViaje(String(activeRide.id), (data as any)?.acceptedAt);
              // El destino del documento manda sobre el de pantalla. Se guarda
              // con `?? prev` para que un snapshot sin el campo no borre el que
              // ya teníamos: perderlo dejaría al pasajero sin línea a mitad del
              // viaje sin que hubiera cambiado nada.
              setDestinoDelViaje((prev) => destinoDeDocumento(data) ?? prev);
            } else if (data.status === 'COMPLETED') {
              setIsActiveTrip(false);
              setRatingRideId(String(data.id || activeRide.id)); // capturar ANTES del null
              setCurrentRideId(null);
              setActiveRidePin(null);
              // ESTE CAMINO TAMBIEN. Es el de reanudar: el que corre cuando
              // Android mata la app y el usuario vuelve a abrirla, o sea el mas
              // comun en un telefono de gama baja. Iba directo a las estrellas,
              // asi que el pasajero seguia sin ver a donde pagar.
              if (!abrirCierreDePagoSiHaceFalta(data)) setRatingModalVisible(true);
              rideSessionStore.clearActiveRide();
              cleanupRideListeners();
            } else if (data.status === 'CANCELLED') {
              setIsActiveTrip(false);
              setCurrentRideId(null);
              setActiveRidePin(null);
              rideSessionStore.clearActiveRide();
              cleanupRideListeners();
              // H06 (auditoría 16-sep-2026) — el cierre COMPLETO, no medio. Sin
              // esto quedaban el chofer que canceló (salía como "Conductor
              // Cercano" con su botón de pedir), las ofertas muertas y el
              // formulario lleno del viaje anterior. Y aquí no había NINGÚN
              // aviso: el viaje desaparecía sin decir por qué.
              cerrarViajeDelPasajero();
              {
                const aviso = avisoDeCancelacion(data);
                if (aviso) showAlert(aviso.titulo, aviso.cuerpo, 'close-circle', colors.error);
              }
            } else if (data.status === 'EXPIRED') {
              // La rama que faltaba. Sin ella, reabrir la app con una solicitud
              // ya caducada dejaba el radar girando otra vez desde cero.
              manejarViajeExpirado();
              return;
            }
            // DESVÍO DE RUTA — detección para el pasajero (chofer propone).
            // Antes vivía en un useEffect separado que abría un segundo
            // onSnapshot al MISMO doc de ride (doble suscripción). Consolidado
            // aquí para no duplicar listeners.
            // GUARD "solo el pasajero": el modal de aprobar desvío es del
            // PASSENGER. Solo lo mostramos si YO soy el passengerId de este ride
            // (el chofer, que es quien PROPONE el desvío, no debe verlo).
            const dev = (data as any)?.routeDeviation;
            const iAmPassenger = (data as any)?.passengerId === useAppStore.getState().userFirebaseUid;
            if (dev?.status === 'PENDING' && iAmPassenger) {
              setDeviationModal((m) =>
                m.visible && m.role === 'PASSENGER' ? m : { visible: true, role: 'PASSENGER', reason: dev.reason }
              );
            } else {
              setDeviationModal((m) => (m.role === 'PASSENGER' ? { visible: false, role: null } : m));
            }
            anunciarVeredictoDelDesvio(data);
          });
        }
      } catch (e: any) {
        console.warn('[map] bootstrap resume ride falló:', e?.message);
      } finally {
        // Resume resuelto (con o sin viaje) → apagar el anti-flicker shield.
        if (!cancelled) setResumeChecking(false);
      }
    })();
    return () => { cancelled = true; };
    // Depende de role + uid: re-corre cuando el uid llega (auth async) para
    // que un cold-start con sesión restaurada tarde igual restaure el ride.
    // Un re-run de más es inofensivo: el bootstrap verifica que el ride siga
    // activo antes de restaurar y limpia la sesión si ya terminó.
  }, [role, userFirebaseUid, cleanupRideListeners]);

  // FIX KEEP AWAKE — Antes el celular se bloqueaba solo después de 30-60s
  // de inactividad. Críticamente fatal para Uber-style apps:
  //   - Chofer online esperando ofertas: si la pantalla se apaga, no ve
  //     la oferta entrante hasta desbloquear el celular → pierde el viaje.
  //   - Pasajero buscando viaje / esperando que su chofer llegue: misma
  //     historia, no ve actualizaciones en vivo.
  //   - Durante un ride activo: pierde la navegación en vivo y la ubicación
  //     del chofer/pasajero.
  // Mientras alguno de estos estados sea true, mantenemos la pantalla
  // encendida con expo-keep-awake. Cuando el flujo termina, liberamos el
  // wakelock para que el celular vuelva a su comportamiento normal y no
  // queme batería innecesariamente.
  /**
   * TOPE DE BÚSQUEDA DEL LADO DEL CLIENTE.
   *
   * La rama `EXPIRED` de arriba depende de que el cron del servidor corra y de
   * que el snapshot llegue. Si el cron se atrasa, si el teléfono perdió la
   * conexión un rato o si el listener se cayó, nadie apaga el radar y volvemos
   * al mismo síntoma. Este temporizador es la red de seguridad: a los 6
   * minutos —antes de los 10 del servidor— cerramos nosotros.
   *
   * Se cancela de verdad la solicitud en Firestore: dejarla PENDING permitiría
   * que un chofer la aceptara minutos después, con la pantalla ya cerrada, y
   * saliera a buscar a alguien que ya se fue.
   */
  // A los 20 segundos el mensaje del radar deja de ser «buscando» y pasa a
  // decir la verdad: que todavía no hay nadie cerca. Ver la nota del texto.
  const [buscandoHaceRato, setBuscandoHaceRato] = useState(false);
  useEffect(() => {
    if (!isSearching) {
      setBuscandoHaceRato(false);
      return;
    }
    const reloj = setTimeout(() => setBuscandoHaceRato(true), 20000);
    return () => clearTimeout(reloj);
  }, [isSearching]);

  const TOPE_BUSQUEDA_MS = 6 * 60 * 1000;
  useEffect(() => {
    if (role !== 'PASSENGER' || !isSearching) return;
    const temporizador = setTimeout(() => {
      const idCaducado = currentRideId;
      // Primero se suelta la pantalla (y con ella los listeners del viaje):
      // así la cancelación que viene detrás no le saca al pasajero un segundo
      // aviso de "Viaje Cancelado" encima del que acaba de leer.
      manejarViajeExpirado();
      if (idCaducado) {
        firestoreService.cancelRideAsPassenger(idCaducado).catch((e: any) =>
          console.warn('[busqueda] no se pudo cancelar la solicitud caducada:', e?.message)
        );
      }
    }, TOPE_BUSQUEDA_MS);
    return () => clearTimeout(temporizador);
    // `TOPE_BUSQUEDA_MS` es una constante literal, no hace falta en deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, isSearching, currentRideId, manejarViajeExpirado]);

  // ══════════════════════════════════════════════════════════════════════
  // EL VIAJE DE DEMOSTRACIÓN AVANZA SOLO — Y SE APAGA AL TERMINAR
  // ══════════════════════════════════════════════════════════════════════
  //
  // ── EL PROBLEMA QUE RESUELVE ────────────────────────────────────────────
  //
  // El viaje simulado llegaba hasta «¡Viaje Confirmado!» y AHÍ SE QUEDABA
  // PARA SIEMPRE. En un viaje real, quien mueve los estados —el chofer llegó,
  // el viaje empezó, el viaje terminó— es el listener del documento de
  // Firestore. Pero el viaje simulado no tiene documento: su id es
  // `qa_mock_ride_…` y no existe en ninguna colección, así que ningún listener
  // dispara nunca.
  //
  // Para el revisor eso es casi peor que no tener demostración: ve un chofer
  // «en camino» que no llega jamás, y lo que concluye es que la app se cuelga
  // a mitad del viaje. Aquí se simula lo que haría el listener.
  //
  // ── POR QUÉ SE APAGA SOLO AL FINAL ──────────────────────────────────────
  //
  // Es la defensa que de verdad importa. Lo peor que puede pasar con este modo
  // es que alguien se lo deje puesto, pida un viaje creyéndolo real y se quede
  // esperando en una acera a un chofer que no existe. Apagándolo al terminar,
  // lo normal es que nadie salga de aquí con el modo encendido. Las otras dos
  // defensas son el cintillo siempre visible y el candado de no poder
  // encenderlo con un viaje real en curso.
  //
  // ── NO TOCA NADA DE LO REAL ─────────────────────────────────────────────
  //
  // La condición exige las tres cosas a la vez: modo encendido, viaje activo, y
  // que el id EMPIECE POR `qa_mock_ride_`. Un viaje de verdad no cumple la
  // tercera ni por accidente, así que este efecto no puede tocarlo.
  /** El reloj de los 3,5 s que pinta la oferta de demostración. Ver su nota. */
  const relojDeLaOfertaRef = useRef<any>(null);
  const pararLaOfertaDeDemostracion = useCallback(() => {
    if (relojDeLaOfertaRef.current) {
      clearTimeout(relojDeLaOfertaRef.current);
      relojDeLaOfertaRef.current = null;
    }
  }, []);

  const enModoDemostracion = useAppStore((s) => s.qaMockRide);

  /** Recoge el viaje de demostración sin tocar el servidor. */
  const cerrarViajeDeDemostracion = useCallback(() => {
    setIsActiveTrip(false);
    setIncomingDriverOffers(false);
    setReceivedOffers([]);
    setSelectedDriver(null);
    setCurrentRideId(null);
    setRideStatus('ACCEPTED');
    setIsSearching(false);
  }, []);

  useEffect(() => {
    // El id es la prueba de que esto es de mentira. Un viaje de verdad no
    // empieza por `qa_mock_ride_` ni por accidente, así que este efecto no
    // puede tocarlo pase lo que pase.
    const esViajeDeDemostracion = String(currentRideId ?? '').startsWith('qa_mock_ride_');
    if (!esViajeDeDemostracion) return;

    // ── SE APAGÓ EL MODO CON EL VIAJE FALSO EN PANTALLA ──────────────────
    // Pasa al tocar «Salir» en el cintillo. Sin esto, el viaje de mentira se
    // quedaba puesto y los relojes seguían corriendo por detrás: alguien que
    // acaba de decir «sácame de aquí» seguiría viendo a Carlos R. en camino.
    if (!enModoDemostracion) {
      cerrarViajeDeDemostracion();
      return;
    }

    if (!isActiveTrip) return;

    // Los tiempos son cortos a propósito: medio minuto de principio a fin. Un
    // revisor no va a esperar más, y quien mira la demostración tampoco.
    const relojes = [
      setTimeout(() => setRideStatus('ARRIVED'), 7000),
      setTimeout(() => setRideStatus('IN_PROGRESS'), 14000),
      setTimeout(() => {
        // Cierre a mano: no se llama a nada del servidor. El camino real de
        // COMPLETED abre la calificación y el cobro, y las dos cosas
        // escribirían contra un viaje que no existe. Aquí no hay a quién
        // calificar ni qué cobrar.
        cerrarViajeDeDemostracion();
        // ⚠️ AQUÍ SE APAGA SOLO. Es la defensa que de verdad importa.
        useAppStore.getState().setQaMockRide(false);
        showAlert(
          'Así funciona un viaje',
          'Eso es todo: pides, eliges chofer, lo ves llegar y el viaje termina.\n\nEra una demostración, así que no vino nadie ni se cobró nada. Ya volviste a los viajes reales.',
          'checkmark-circle',
          '#00C853'
        );
      }, 26000),
    ];
    return () => relojes.forEach(clearTimeout);
    // `showAlert` y los setters de estado son estables.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enModoDemostracion, isActiveTrip, currentRideId, cerrarViajeDeDemostracion]);

  const needsScreenAwake = isOnline || isSearching || !!currentRideId || incomingDriverOffers;
  useEffect(() => {
    if (!needsScreenAwake) return;
    const TAG = 'gosafely-map-active';
    KeepAwake.activateKeepAwakeAsync(TAG).catch(() => {});
    return () => {
      // ⚠️ EL COMENTARIO QUE HABÍA AQUÍ ERA FALSO, Y POR ESO ESTO FALLABA.
      //
      // Decía: «deactivateKeepAwake es SÍNCRONO (no devuelve Promise)», y por
      // eso lo envolvía en un `try/catch` a secas. Pero la firma de la
      // librería es `deactivateKeepAwake(tag?: string): Promise<void>`
      // (node_modules/expo-keep-awake/build/index.d.ts:49). SÍ devuelve
      // promesa — y un `try/catch` síncrono NO atrapa el rechazo de una
      // promesa. Se escapaba entero.
      //
      // Lo que pasaba de verdad, medido el 3-oct-2026 soltando un viaje:
      //   Uncaught (in promise) CodedError: The wake lock with tag
      //   gosafely-map-active has not activated yet
      // Ocurre siempre que se suelta el candado ANTES de que el navegador
      // haya terminado de concedérselo — o sea, cada vez que un viaje empieza
      // y termina rápido. En desarrollo levanta el panel rojo a pantalla
      // completa; en producción es un rechazo sin dueño, que es peor porque no
      // se ve.
      //
      // Ahora se cubren las tres formas de fallar: que reviente al llamarla,
      // que devuelva una promesa que se rompe, y que una versión futura no
      // devuelva nada.
      try {
        const resultado: any = KeepAwake.deactivateKeepAwake(TAG);
        if (resultado && typeof resultado.catch === 'function') {
          resultado.catch(() => { /* no llegó a estar activo: no hay nada que soltar */ });
        }
      } catch { /* ídem */ }
    };
  }, [needsScreenAwake]);

  // ↑ La función handleNavigation antigua fue reemplazada por el componente
  //   <NavigationLauncher> + <StreetViewButton> abajo. Esos manejan Google
  //   Maps, Waze, Apple Maps, sistema default, Street View y persisten la
  //   preferencia del usuario.



  // Mock handlers
  // H12 (auditoría 16-sep-2026) — El candado va en este envoltorio y no dentro
  // de `solicitarViaje`, que tiene una docena de salidas tempranas: así se
  // suelta en TODAS (validación, GPS sin respuesta, error, éxito) sin tener
  // que acordarse de cada `return`. El nombre `handleRequestRide` se conserva
  // porque lo llaman el botón y la tarjeta del chofer cercano.
  const handleRequestRide = async () => {
    // El cinturón de logística. El botón ya manda a otra pantalla, pero un
    // botón que cambia de destino según una bandera es fácil de olvidar el día
    // que alguien añada otro camino hasta aquí — y crear un viaje con
    // logística elegida sería mandarle una mudanza a un motorista.
    if (envioPorLogistica) {
      router.push('/logistica-pedir' as any);
      return;
    }
    if (solicitandoViajeRef.current) return;
    solicitandoViajeRef.current = true;
    setSolicitandoViaje(true);
    try {
      await solicitarViaje();
    } finally {
      solicitandoViajeRef.current = false;
      setSolicitandoViaje(false);
    }
  };

  const solicitarViaje = async () => {
    // Plan invitado: solo explora, no puede pedir viaje real.
    // Antes esto miraba `role === 'GUEST'`. Ya no: el invitado conserva su rol
    // REAL (PASSENGER, DRIVER…) y la condición de "sin cuenta" vive en su
    // propia bandera. Con el chequeo viejo, un invitado que entró explorando
    // el perfil de pasajero pasaba de largo y la solicitud moría más adelante
    // con un permission-denied mudo.
    // ══════════════════════════════════════════════════════════════════
    // MODO DEMOSTRACIÓN — LAS DOS PUERTAS QUE SE SALTA, Y POR QUÉ SOLO ESAS
    // ══════════════════════════════════════════════════════════════════
    //
    // El viaje simulado vivía más abajo, DESPUÉS de este candado y del teléfono
    // obligatorio. O sea que quien más lo necesita no llegaba nunca a él: el
    // revisor de Apple, que entra sin cuenta y sin número dominicano, tocaba
    // «Solicitar Viaje» y lo único que veía era la alerta de «necesitas una
    // cuenta». La función principal de la app quedaba sin demostrar, que es el
    // rechazo 2.1 de libro.
    //
    // Estas dos puertas existen por razones REALES y no se tocan en el flujo
    // normal: sin cuenta no hay a quién cobrarle ni a quién reclamarle, y sin
    // teléfono el chofer no tiene cómo llamar al pasajero cuando llega.
    //
    // Pero en la demostración NO VIENE NADIE: no se escribe un solo documento
    // en Firestore, no se avisa a ningún chofer, no hay dinero y no hay a quién
    // llamar. Las dos razones desaparecen, y con ellas las dos puertas.
    //
    // ⚠️ SE SALTA ESTAS DOS Y NINGUNA MÁS. Las de abajo —tarifa, destino,
    // origen— SÍ se recorren igual que en un viaje real, a propósito: lo que
    // se quiere demostrar es el flujo DE VERDAD, con una sola cosa de mentira,
    // que es el chofer. Una demostración que se salte la mitad de la app no
    // demuestra la app.
    const enDemostracion = useAppStore.getState().qaMockRide === true;

    if (!enDemostracion && bloquearSiInvitado('solicitar un viaje')) return;
    // TELÉFONO OBLIGATORIO — Sin número el chofer no puede contactar al
    // pasajero. Cubre al usuario de Google que llegó al mapa sin teléfono.
    // En la demostración no hay chofer que llame: ver la nota de arriba.
    if (!enDemostracion) {
      const phoneDigits = String(useAppStore.getState().userPhone ?? '').replace(/[^0-9]/g, '');
      if (phoneDigits.length < 8) {
        Alert.alert(
          'Agrega tu teléfono',
          'Para solicitar un viaje necesitas un número de contacto para que el chofer pueda comunicarse contigo.',
          [
            { text: 'Ahora no', style: 'cancel' },
            { text: 'Agregar teléfono', onPress: () => router.push('/add-phone') },
          ]
        );
        return;
      }
      // ══════════════════════════════════════════════════════════════════
      // LOS 5 VIAJES DE CORTESÍA — EL AVISO VA ANTES DEL BOTÓN
      // ══════════════════════════════════════════════════════════════════
      //
      // Decisión del fundador (3-oct-2026): «lo dejaremos con 5 viajes antes de
      // usar la verificación». El candado de verdad está en las reglas de
      // Firestore (`puedePedirViajeSinVerificar`); esto es para que la persona
      // se entere POR QUÉ y tenga el botón para arreglarlo.
      //
      // Sin esto, el sexto viaje devolvería un `permission-denied` mudo: la
      // pantalla diría «no pudimos crear tu viaje, revisa tu conexión» y la
      // persona cambiaría de wifi diez veces. Ese silencio es exactamente el
      // rechazo vc16 que ya costó una ronda con Google Play.
      //
      // El campo lo escribe SOLO el servidor (`processRideCompletion`), así que
      // leerlo aquí no es confiar en el teléfono: es enseñar lo que el servidor
      // ya decidió. Y se lee del store, que el listener del perfil mantiene al
      // día — no hace falta una consulta más justo cuando la persona tiene
      // prisa.
      const estado = useAppStore.getState();
      if (estado.requiereVerificacion === true) {
        const tope = estado.viajesSinVerificarTope || 5;
        const quiere = await preguntarSiNo(
          // ⚠️ ESTO NO SON VIAJES GRATIS, Y EL TEXTO NO PUEDE INSINUARLO.
          //
          // Decía «Ya usaste tus viajes de cortesía» y «los N viajes que
          // damos». Corrección del fundador (4-oct-2026): «no estamos dando 5
          // viajes gratis, sino la facilidad de que el usuario pueda probar la
          // app de esa manera; el primero gratis de bienvenida como ya está, y
          // ya lo otro son de pago».
          //
          // Son dos cosas distintas y el código ya las trata por separado: el
          // viaje gratis de bienvenida vive en `loyalty.isFirstRideFreeUsed` y
          // este tope cuenta viajes PAGADOS. Lo que estaba mal era el nombre.
          // Y no es un detalle de estilo: quien lee «cortesía» espera no pagar,
          // y después se siente engañado al ver el cobro.
          'Llegó el momento de verificarte',
          `Ya probaste la app: hiciste ${tope} viajes sin verificar tu identidad.\n\n` +
            'Para seguir viajando, verifica tu identidad con tu cédula. Es una sola vez, ' +
            'la revisa una persona de nuestro equipo, y es lo que nos permite responder ' +
            'por ti ante el chofer que te lleva.',
          'Ahora no',
          'Verificar mi cédula'
        );
        if (quiere) router.push('/kyc-welcome');
        return;
      }

      // H14 — el teléfono ya no viaja en la solicitud: el servidor lo copia de
      // `users/{uid}.phone` cuando un chofer acepta. Hay caminos que dejan el
      // número solo en este teléfono (login por teléfono, perfil viejo), y sin
      // esta copia el chofer se quedaría sin a quién llamar. Una vez por sesión.
      const uidTelefono = useAppStore.getState().userFirebaseUid;
      if (uidTelefono && telefonoSincronizadoRef.current !== phoneDigits) {
        telefonoSincronizadoRef.current = phoneDigits;
        firestoreService
          .updateUserProfile(uidTelefono, { phone: String(useAppStore.getState().userPhone).trim() })
          .catch(() => {
            telefonoSincronizadoRef.current = null;
          });
      }
    }
    if (!isFreeTrip && (!offerPrice || offerPrice.trim() === '')) {
      showAlert('Tarifa Requerida', 'Por favor, ingresa tu tarifa ofertada (Ej: 200) antes de solicitar un chofer.', 'cash', '#00C853');
      return;
    }
    // FIX CERT — Validar que la tarifa sea un número POSITIVO real. Antes un
    // texto no numérico daba parseInt→NaN y se guardaba offerPrice: NaN en el
    // ride, rompiendo el cálculo de tarifa/crédito.
    if (!isFreeTrip) {
      const fare = parseInt(offerPrice, 10);
      if (!Number.isFinite(fare) || fare <= 0) {
        showAlert('Tarifa Inválida', 'Ingresa un monto numérico válido para tu tarifa (Ej: 200).', 'cash', '#00C853');
        return;
      }
      // Mismo techo que las reglas de /rides (H46): sin este aviso, una tarifa
      // mayor saldría como un error de conexión que no lo es.
      if (fare > 100000) {
        showAlert('Tarifa Inválida', 'La tarifa no puede pasar de RD$ 100,000. Revisa el monto.', 'cash', '#00C853');
        return;
      }
    }
    if (!destination || destination.trim().length === 0) {
      showAlert('Destino requerido', 'Selecciona primero una dirección de destino del autocompletado.', 'location', '#FF9500');
      return;
    }
    // ── LA RECOGIDA CAMBIADA A MANO TIENE QUE TENER PUNTO EN EL MAPA ───────
    //
    // Si la persona escribió otra dirección de recogida pero no eligió ninguna
    // sugerencia, no hay lat/lng. `dispatchRide` hace early-return sin
    // coordenadas de origen: el viaje se crea y NINGÚN chofer lo ve nunca.
    // Se para aquí, con palabras, en vez de dejarlo morir en silencio.
    if (origenManual && !origenElegido) {
      showAlert(
        'Falta el punto de recogida',
        'Elige la dirección de recogida de la lista de sugerencias. Sin un punto exacto en el mapa, ningún chofer puede encontrarla.',
        'location-outline',
        '#FF9500'
      );
      return;
    }
    // ── NO SE MANDA UN CHOFER A UN PUNTO QUE NO SABEMOS ───────────────────
    //
    // Con el GPS todavia afinando, la recogida puede estar a cientos de metros:
    // el chofer llega donde no hay nadie, el pasajero cree que la app lo mando
    // mal, y se pierden los dos. Pero NO se bloquea: dentro de un edificio el
    // GPS puede no afinar nunca, y dejar a alguien sin poder pedir un viaje es
    // perder el cliente seguro en vez de a medias. Se pregunta UNA vez y
    // decide la persona, que es quien sabe si esta en la acera o en un sotano.
    if (recogidaImprecisa) {
      const seguir = await preguntarSiNo(
        'Todavía estamos afinando tu ubicación',
        (typeof precisionMetros === 'number'
          ? `Tu posición puede estar a unos ${Math.round(precisionMetros)} metros de donde estás de verdad. `
          : 'Todavía no tenemos tu posición exacta. ') +
          'Si pides ahora, el chofer podría llegar a otra esquina.\n\n' +
          'Espera unos segundos a que el mapa afine, o escribe tu dirección de recogida.',
        'Esperar',
        'Pedir aquí igual'
      );
      if (!seguir) return;
    }

    // EL SECTOR ES OBLIGATORIO. No es burocracia: sin él el chofer acepta a
    // ciegas y descubre a mitad de camino que el viaje es el doble de largo.
    //
    // Cuelga de que EXISTA la coordenada del destino, no de un booleano
    // paralelo: ese booleano se apagaba al cambiar de pestaña sin borrar las
    // coordenadas, y por ese hueco salían viajes con `destinationSector: null`.
    if (hayDestinoEscrito && sectorDestino.trim().length < 3) {
      showAlert(
        'Falta el sector',
        'Escribe el sector, barrio o residencial de tu destino. El chofer necesita saber la zona: una misma calle puede estar en varios sectores.',
        'map-outline',
        '#FF9500'
      );
      return;
    }

    // Nuevo ride del pasajero — resetear el rideStatus a ACCEPTED (default)
    // para no arrastrar el estado de un viaje anterior.
    setRideStatus('ACCEPTED');

    // MODO MOCK — SOLO si el usuario lo activó EXPLÍCITAMENTE en Perfil → QA.
    // Antes lo detectábamos por ausencia de userFirebaseUid, pero eso bloqueaba
    // las pruebas reales entre dispositivos: si por alguna razón el uid llegaba
    // null, todos los users caían al mock y nunca se conectaban entre sí.
    // Ahora el mock es OPT-IN puro: el user con sesión real SIEMPRE va al
    // flujo Firestore para que sus probadores en otros móviles se conecten.
    // ⚠️ ESTO YA NO ESTÁ DETRÁS DE `__DEV__`, Y ES EL CAMBIO QUE IMPORTA.
    // Antes el bloque entero vivía dentro de `if (__DEV__)`, así que NO EXISTÍA
    // en el binario de la tienda: el modo demostración era inalcanzable justo
    // donde hacía falta. La bandera `qaMockRide` sigue siendo opt-in puro —
    // nadie cae aquí sin encenderla a mano en su perfil—, y mientras esté
    // apagada este bloque se salta igual que antes.
    {
      const isQaMockMode = enDemostracion;
      if (isQaMockMode) {
        cleanupRideListeners();
        setCurrentRideId('qa_mock_ride_' + Date.now());
        setIsSearching(true);
        // ⚠️ EL RELOJ SE GUARDA, Y ANTES NO.
        // Era un `setTimeout` suelto. Si alguien tocaba «Cancelar búsqueda»
        // dentro de estos 3,5 segundos, la pantalla se cerraba y el reloj
        // disparaba IGUAL, pintando la oferta de Carlos R. sobre un viaje ya
        // cerrado (`currentRideId` en null). Al aceptarla se montaba un viaje
        // con id nulo, y entonces el efecto de la progresión no lo reconocía
        // como de demostración: ni llegaba el chofer, ni arrancaba, ni se
        // cerraba a los 26 s, ni se apagaba el modo. «El chofer está en
        // camino» PARA SIEMPRE — justo la pantalla colgada que esta
        // demostración existe para evitar.
        relojDeLaOfertaRef.current = setTimeout(() => {
          setReceivedOffers([
            {
              id: 'qa_mock_offer_1',
              driverId: 'qa_mock_driver_carlos',
              driverName: 'Carlos R. (Demo)',
              userName: 'Carlos R. (Demo)',
              // `parseInt('')` es NaN, y una tarifa NaN pinta «RD$ NaN» en la
              // tarjeta de la oferta. En un viaje real no pasa (la validación de
              // tarifa corre antes), pero más vale que la demostración no pueda
              // enseñar eso nunca.
              offerAmount: isFreeTrip ? 0 : (parseInt(offerPrice, 10) || 250),
              rating: 4.9,
              etaMinutes: 3,
              vehicleType: selectedVehicleType,
              licensePlate: 'A0000XX',
            },
          ]);
          setIsSearching(false);
          setIncomingDriverOffers(true);
          vibrarExito();
          relojDeLaOfertaRef.current = null;
        }, 3500);
        return;
      }
    }

    /* === LÓGICA DE PRODUCCIÓN (Firestore Motor de Emparejamiento) === */
    try {
      // FIX AUDIT #3: NO usar fallback a 'passenger_123' — antes todas las
      // solicitudes sin sesión caían bajo el mismo ID falso, contaminando
      // matching, loyalty y rate-limits. Si no hay UID, exigir login.
      const passengerId = useAppStore.getState().userFirebaseUid;
      if (!passengerId) {
        showAlert(
          'Sesión requerida',
          'Inicia sesión para solicitar un viaje.',
          'log-in-outline',
          colors.primary
        );
        setIsSearching(false);
        return;
      }

      // Liberar listeners de cualquier ride anterior antes de abrir nuevos.
      cleanupRideListeners();

      // FIX COORDENADAS DE RECOGIDA — Antes el ride se creaba SIN
      // origin_lat/origin_lng. Resultado en captura del founder: el chofer
      // veía solo el destino y la "Recogida" mostraba un nombre genérico,
      // sin punto preciso en el mapa → el chofer no sabía dónde recoger.
      //
      // Además dispatchRide en Cloud Functions hace early-return cuando
      // faltan coords, así que el matching automático tampoco corría.
      //
      // Ahora pasamos:
      //   - origin_lat/origin_lng: las coords reales del pasajero (currentLocation).
      //   - originAddress: nombre legible si lo tenemos (fallback "Mi ubicación").
      //   - Coords de destino: si el autocomplete las trajo, las propagamos.
      //
      // ── EL ORIGEN NO PUEDE SER INVENTADO ──────────────────────────────
      //
      // Antes, si el GPS no respondía o el usuario había negado el permiso,
      // esto usaba el relleno del servicio de ubicación: el CENTRO DE SANTO
      // DOMINGO. El viaje se creaba desde ahí como si fuera donde está la
      // persona, y el chofer se desplazaba —gastando su gasolina, cobrando en
      // efectivo— a una esquina donde no había nadie. El pasajero, además,
      // veía en pantalla una dirección que no era la suya.
      //
      // `obtenerOrigenDeViaje` no devuelve coordenadas inventadas: o son
      // reales, o dice por qué no las tiene. Un viaje sin origen de verdad no
      // se crea: se le explica a la persona qué hacer.
      //
      // ── SALVO QUE LA RECOGIDA LA HAYA ELEGIDO LA PERSONA ────────────────
      // Si cambió el campo de recogida y eligió una sugerencia con punto en el
      // mapa, ESE es el origen del viaje y el GPS del teléfono no pinta nada:
      // el carro va a buscar a su mamá, no a ella. Ya se comprobó arriba que
      // un origen manual sin coordenadas no llega hasta aquí.
      let originLat: number;
      let originLng: number;
      if (origenElegido) {
        originLat = origenElegido.lat;
        originLng = origenElegido.lng;
      } else {
        const origen = await locationService.obtenerOrigenDeViaje(true);
        if (!origen.ok) {
          setIsSearching(false);
          showAlert('No pudimos ubicarte', origen.mensaje, 'location-outline', '#FF9500');
          return;
        }
        originLat = origen.latitud;
        originLng = origen.longitud;
        if (origen.bajaPrecision) {
          // Es una posición REAL, solo que poco precisa. No se bloquea el viaje
          // —dejar a alguien sin transporte por eso sería peor—, pero se avisa
          // para que confirme el punto con el chofer por el chat.
          console.warn('[requestRide] origen con baja precisión:', origen.precisionMetros, 'm');
        }
      }

      // FIX PROFESIONALISMO — Antes el ride solo guardaba passengerId
      // (un uid string). Al chofer le aparecía la card con "Pasajero"
      // genérico y avatar vacío porque desconocía nombre/foto. Ahora
      // propagamos el nombre real (cédula OCR si KYC OK) y la selfie
      // biométrica como photo. El chofer ve quién pidió el viaje.
      const me = useAppStore.getState();
      const myName = String(me.userName ?? '').trim() || 'Pasajero';
      const myPhoto = String(me.userPhoto ?? '').trim() || null;
      // (El teléfono ya no se lee aquí: ver la nota de H14 en el payload.)

      const rideRef = await firestoreService.requestRide(passengerId, {
        destination,
        origin_lat: originLat,
        origin_lng: originLng,
        originLat,
        originLng,
        // H4 FIX — Coords del destino elegido en el autocompletado. Sin
        // esto, NavigationLauncher del chofer caía a coords hardcoded de
        // Santo Domingo → chofer navegaba al lugar equivocado.
        destinationLat: destinationLat,
        destinationLng: destinationLng,
        // FIX ORIGEN REAL — Antes hardcoded "Mi ubicación actual", el chofer
        // no sabía dónde recoger. Ahora va EL TEXTO QUE LA PERSONA ESTÁ
        // VIENDO en el campo de recogida: si lo cambió, el chofer tiene que
        // leer esa dirección y no la del teléfono de quien pidió el viaje.
        // Si el campo aún está vacío (la geocodificación inversa tarda ~1,2 s)
        // cae al rótulo de siempre.
        originAddress:
          origenElegido?.texto || origenTexto.trim() || currentAddressLabel || 'Mi ubicación actual',
        // Sector/barrio del destino. Va al documento del viaje para que el
        // chofer lo vea ANTES de aceptar, junto a la calle.
        destinationSector: sectorDestino.trim() || null,
        // ── EL VIAJE QUE PAGA LA APP VALE LO QUE VALE (27-sep-2026) ───────
        // Aqui habia un `250` fijo. Daba igual que el pasajero fuera a la
        // esquina o a Boca Chica: al chofer se le abonaban 250 y punto, que es
        // justo lo que el fundador llamo abusivo para el conductor. El servidor
        // NUNCA tuvo ese limite —`MAX_FREE_RIDE_CREDIT_DOP` son RD$800 por
        // viaje y RD$3.000 al mes (functions/src/loyalty.ts)—: era el telefono
        // el que mandaba 250 y ese es el numero que se abona.
        // Ahora se calcula con la tarifa real del recorrido y se acota al mismo
        // tope que aplica el servidor, para no prometerle al chofer mas de lo
        // que se le va a abonar. Sin destino con coordenadas todavia, cae al
        // minimo de siempre: nunca se crea un viaje sin precio.
        offerPrice: isFreeTrip
          ? Math.min(tarifaSugerida ?? 250, TOPE_VIAJE_CUBIERTO_DOP)
          : parseInt(offerPrice, 10),
        // ── EL CHOFER TIENE QUE SABER QUE ESTE VIAJE LO PAGA LA APP ────────
        // Al pasajero se le anuncia "Tarifa Cubierta por la App" y se le
        // esconde el campo del precio, pero el viaje se creaba en EFECTIVO por
        // RD$250 sin ninguna marca. El chofer llegaba al destino y le pedía el
        // dinero a alguien convencido de que no pagaba: una discusión en la
        // calle, entre dos personas que hicieron exactamente lo que la app les
        // dijo. La marca del servidor (`appliedFirstRideFree`) se escribe DESPUÉS
        // de completar el viaje, o sea, demasiado tarde para evitarlo.
        cubiertoPorLaApp: isFreeTrip,
        motivoCobertura: freeTripReason,   // 'FIRST' | 'LOYALTY' | null
        serviceType,
        // ── QUÉ SE ENVÍA ────────────────────────────────────────────────────
        // El campo existía en la pantalla del pasajero desde siempre y NUNCA
        // salía del teléfono: no iba en este payload. El chofer aceptaba un
        // envío sin saber si eran unas llaves o una caja, y llegaba a la
        // recogida a preguntarlo. Arreglar solo la pantalla del chofer habría
        // hecho creer que estaba resuelto y habría seguido saliendo vacío.
        packageDescription:
          serviceType === 'DELIVERY' ? (packageDescription.trim() || null) : null,
        notaDelPasajero:
          serviceType === 'DELIVERY' ? null : (notaDelPasajero.trim() || null),
        passengers,
        vehicleType: selectedVehicleType,
        // La marca de que el vehículo lo eligió la persona (ver
        // `vehiculoElegidoPorMi`). En un VIAJE siempre es elección: el selector
        // se ha visto desde el primer día.
        vehiculoElegido: serviceType === 'RIDE' ? true : vehiculoElegidoPorMi,
        currency,
        // FIX AUDIT F1 (CRÍTICO) — Persistir el método de pago elegido. Sin
        // esto el ride se creaba SIN paymentMethod, así que el gate de cobro
        // (loyalty.ts onRideCompleted: `ride.paymentMethod === 'CARD'`) NUNCA
        // se cumplía → la tarjeta jamás se cobraba y el payout al chofer nunca
        // corría. Todo viaje se trataba como efectivo.
        paymentMethod,
        passengerName: myName,
        passengerPhoto: myPhoto,
        // ── H14 (auditoría 16-sep-2026): EL TELÉFONO YA NO VA EN LA SOLICITUD ──
        // Aquí iba `passengerPhone: myPhone`. Un viaje PENDING lo lee todo chofer
        // verificado del país (la regla no puede filtrar por zona), así que cada
        // solicitud repartía nombre + TELÉFONO + punto exacto de recogida a
        // choferes que nunca iban a llevar a esa persona. El chofer solo llama
        // DESPUÉS de aceptar.
        //
        // Ahora lo estampa el servidor (`congelarDatosDelViaje`,
        // functions/src/rides.ts) leyendo `users/{passengerId}.phone` cuando el
        // viaje pasa a ACCEPTED, y la tarjeta del chofer lo lee del documento
        // (`driverCurrentRide.passengerPhone`). El teléfono obligatorio de más
        // arriba sigue en pie: es ese mismo número el que el servidor copia.
        //
        // ⚠️ NO LO VUELVAS A PONER. Las reglas todavía lo admiten en el create
        // (las APK viejas lo mandan y bloquearlo les tumbaría la solicitud
        // entera), pero el servidor lo BORRA de todo viaje PENDING en cuanto
        // llega, así que ponerlo aquí solo lo expone unos segundos para nada.
        // ── EL SELLO DEL PASAJERO YA NO SALE DE ESTE TELÉFONO ───────────────
        // Aquí iba `passengerVerified: me.isVerified === true`. El escudo verde
        // que el chofer mira JUSTO ANTES de montar a un desconocido en su carro
        // lo estampaba el teléfono del propio desconocido: el que se beneficia
        // del sello era quien lo ponía, y un cliente parcheado escribía `true`
        // sin que nadie comprobara nada. Que el valor viniera del perfil no
        // arreglaba eso; solo lo hacía cierto mientras el cliente fuera el
        // nuestro.
        //
        // Ahora lo escribe el servidor (`congelarDatosDelViaje`,
        // functions/src/rides.ts) leyendo con Admin SDK
        // `users/{passengerId}.isVerified`, que solo escribe la aprobación del
        // KYC (kyc.ts). Y las reglas lo bloquean en el CREATE del viaje: si
        // este campo siguiera viajando en el payload, Firestore denegaría la
        // solicitud entera y NADIE PODRÍA PEDIR UN VIAJE.
        //
        // El disparador es `onDocumentWritten`, así que el sello aparece
        // también en los viajes PENDIENTES —que es donde el chofer lo mira— y
        // no solo al aceptar. Mientras no llega, el viaje NO trae el campo y la
        // tarjeta no pinta escudo: sin dato es sin dato, nunca un `true` de
        // relleno.
      });
      setCurrentRideId(rideRef.id);
      activeRideIdRef.current = rideRef.id;
      // Los choferes que descartó en el viaje ANTERIOR tienen derecho a ofertar
      // en este: la lista de rechazados es por viaje, no por persona.
      choferesRechazadosRef.current.clear();
    ofertasRechazadasRef.current.clear();
      // V11 — lo que se pidió; el documento del viaje lo corrige abajo si el
      // servidor retira la cobertura.
      coberturaDelViajeRef.current = { rideId: String(rideRef.id), cubierto: isFreeTrip };
      // La recogida REAL de este viaje, la misma que se acaba de escribir en el
      // documento. Guardarla aquí (y no depender de `origenElegido`, que es
      // estado de pantalla) hace que el pin diga lo mismo antes y después de
      // cerrar la app.
      setRecogidaDelViaje({
        lat: originLat,
        lng: originLng,
        texto: origenElegido?.texto || origenTexto.trim() || currentAddressLabel || '',
      });
      // El destino de ESTE viaje, congelado igual que la recogida: el campo de
      // pantalla se puede volver a escribir mientras el viaje corre, y la línea
      // no puede cambiar de rumbo porque alguien toque el buscador.
      setDestinoDelViaje(
        typeof destinationLat === 'number' && typeof destinationLng === 'number'
          ? { lat: destinationLat, lng: destinationLng }
          : null
      );
      // ANTI-FANTASMA / RESUME — Guardar como MI viaje activo. Si cierro la
      // app buscando/en viaje, al reabrir restauro exactamente este ride.
      rideSessionStore.setActiveRide(rideRef.id, 'PASSENGER');
      setIsSearching(true);

      // H12 — Si algo dejó un listener vivo durante los `await` de arriba, se
      // cierra ANTES de pisar su ref: pisarlo sin cerrarlo lo dejaba escribiendo
      // ofertas de otro viaje en esta pantalla para siempre. En el camino normal
      // `cleanupRideListeners()` ya los dejó en null y esto no hace nada.
      offersUnsubRef.current?.();
      rideStatusUnsubRef.current?.();

      // 2. Escuchar contraofertas en tiempo real
      offersUnsubRef.current = firestoreService.listenToRideOffers(rideRef.id, (offers) => {
        const utiles = ofertasVisibles(offers);
        setReceivedOffers(utiles);
        if (utiles.length > 0) {
          setIsSearching(false);
          setIncomingDriverOffers(true);
        } else if (!isActiveTripRef.current) {
          // Todas rechazadas (o ninguna todavía): vuelve el radar, y con él el
          // botón de cancelar la búsqueda y el tope de seguridad de 6 minutos.
          // La guarda del viaje activo es la misma de arriba: con el viaje ya
          // aceptado, encender el radar lo cancelaría a los 6 minutos.
          setIncomingDriverOffers(false);
          setIsSearching(true);
        }
      });

      // 3. Escuchar estado del viaje + switchear modo de tracking del driver
      // para optimizar batería (IDLE en espera, ACTIVE_RIDE durante el viaje).
      rideStatusUnsubRef.current = firestoreService.listenToRideStatus(rideRef.id, (data) => {

        // EL DOCUMENTO MANDA SOBRE LA PANTALLA.
        // `paymentMethod` era un useState local cuyo ÚNICO escritor era el
        // modal de selección: nada lo releía del viaje. Con solo reabrir la
        // app, o que esta pantalla se volviera a montar, el pasajero que había
        // elegido Transferencia volvía a «Efectivo» y perdía la única puerta a
        // las cuentas del chofer. Es el mismo criterio que ya se usa con el
        // destino: lo que vale es lo que dice el documento.
        if (data?.paymentMethod) {
          const metodoDelViaje = String(data.paymentMethod).toUpperCase();
          if (metodoDelViaje === 'CASH' || metodoDelViaje === 'CARD' || metodoDelViaje === 'TRANSFER') {
            setPaymentMethod((prev) => (prev === metodoDelViaje ? prev : (metodoDelViaje as any)));
          }
        }
        if (!data) return;
        recordarRechazosDelViaje(data);
        if (soltarChoferQueYaIbaEnOtroViaje(data, String(rideRef.id))) return;
        // ── V11 (H04): EL VIAJE PERDIÓ LA COBERTURA MIENTRAS BUSCA CHOFER ──
        // El servidor retira `cubiertoPorLaApp` a la cuenta sin identidad (o cuya
        // identidad ya gastó el viaje gratis). Solo avisaba un push, que no llega
        // sin permiso de notificaciones: la pasajera seguía creyendo que no
        // pagaba. Se le dice aquí, mientras todavía puede cancelar sin costo.
        const cobertura = coberturaDelViajeRef.current;
        if (cobertura.rideId === String(rideRef.id) && typeof data.cubiertoPorLaApp === 'boolean') {
          const perdioCobertura = cobertura.cubierto && data.cubiertoPorLaApp === false;
          coberturaDelViajeRef.current = { rideId: cobertura.rideId, cubierto: data.cubiertoPorLaApp };
          if (perdioCobertura) {
            recargarGratisDelPerfil();
            if (data.status === 'PENDING' || data.status === 'SEARCHING') {
              const sinIdentidad = String((data as any)?.coberturaDenegadaPor ?? '') === 'SIN_IDENTIDAD_VERIFICADA';
              showAlert(
                'Este viaje no es gratis',
                (sinIdentidad
                  ? 'Tu primer viaje gratis se activa cuando tu cuenta tenga teléfono verificado, correo verificado o cédula. '
                  : 'La app ya no cubre este viaje. ') +
                  'Lo pagas tú al chofer, al precio de la oferta que aceptes. Si no quieres pagarlo, cancélalo ahora sin costo: todavía no tiene chofer.',
                'alert-circle',
                '#FF9500'
              );
            }
          }
        }
        // FIX FLUJO UBER-STYLE — El pasajero entra a "Viaje en Curso" apenas
        // acepta al chofer (status=ACCEPTED), no espera hasta IN_PROGRESS.
        // Esto le permite ver al chofer aproximándose en tiempo real y
        // llamar/mensajear si necesita. Uber real hace lo mismo.
        if (data.status === 'ACCEPTED' || data.status === 'ARRIVED' || data.status === 'IN_PROGRESS') {
          setIsActiveTrip(true);
          // Sincronizar UI del pasajero con el estado del ride.
          const s: 'ACCEPTED' | 'ARRIVED' | 'IN_PROGRESS' =
            data.status === 'IN_PROGRESS' ? 'IN_PROGRESS'
              : data.status === 'ARRIVED' ? 'ARRIVED'
              : 'ACCEPTED';
          setRideStatus(s);
          setEnvioDePaqueteVivo(String((data as any)?.serviceType ?? '') === 'DELIVERY');
          // El destino que escribió el servidor manda sobre el de pantalla, y
          // `?? prev` evita que un snapshot sin el campo lo borre a mitad del
          // viaje (perderlo dejaría al pasajero sin línea sin motivo).
          setDestinoDelViaje((prev) => destinoDeDocumento(data) ?? prev);

          // Sello de la llegada, para el contador de espera. Se guarda tal cual
          // llegue (Timestamp o número): el servidor lo escribe como Timestamp,
          // pero un documento viejo puede traerlo en milisegundos.
          const llegada = (data as any)?.arrivedAt;
          const llegadaMs =
            typeof llegada?.toMillis === 'function' ? llegada.toMillis()
              : typeof llegada === 'number' ? llegada
              : null;
          setLlegadaDelChoferMs(llegadaMs);
          // H24 — la hora de aceptación, para que "Cancelar Viaje" diga si aún
          // está dentro de los 2 minutos sin multa. Ver `aceptacionDelViajeRef`.
          recordarAceptacionDelViaje(String(data.id || rideRef.id), (data as any)?.acceptedAt);

          // ── AVISO SONORO DE LOS DOS MOMENTOS QUE IMPORTAN ────────────────
          //
          // Antes, cuando el chofer llegaba o arrancaba el viaje, esto solo
          // cambiaba un texto en pantalla. Si el pasajero no estaba mirando el
          // teléfono, no se enteraba: "lo muestra escrito pero no suena".
          //
          // Se compara contra el estado ANTERIOR guardado en un ref, porque el
          // listener dispara en cada snapshot del documento (incluidos los que
          // no cambian el estado) y si no, sonaría en bucle.
          if (estadoAnteriorDelViaje.current !== s) {
            const eraOtroEstado = estadoAnteriorDelViaje.current !== null;
            estadoAnteriorDelViaje.current = s;
            const soyElPasajero =
              data.passengerId && data.passengerId === useAppStore.getState().userFirebaseUid;
            if (eraOtroEstado && soyElPasajero && (s === 'ARRIVED' || s === 'IN_PROGRESS')) {
              playNotificationSound();
            }
          }

          // Si yo soy el driver de este ride Y el pasajero acaba de arrancar
          // el viaje formalmente, subo a alta precisión GPS.
          if (data.status === 'IN_PROGRESS' && data.driverId && data.driverId === useAppStore.getState().userFirebaseUid) {
            locationService.setTrackingMode('ACTIVE_RIDE').catch(() => {});
          }
          // ANTI-STALKING — Si YO soy el chofer de este viaje, autorizo a MI
          // pasajero (y solo a él) a ver mi ubicación en vivo. Antes cualquier
          // cuenta podía volcar /driverLocations y seguir a toda la flota.
          if (data.driverId && data.driverId === useAppStore.getState().userFirebaseUid && data.passengerId) {
            firestoreService.setDriverActivePassenger(data.driverId, data.passengerId);
          }
        } else if (data.status === 'COMPLETED') {
          setIsActiveTrip(false);
          // ANTI-STALKING — el viaje terminó: nadie más puede ver mi ubicación.
          if (data.driverId && data.driverId === useAppStore.getState().userFirebaseUid) {
            firestoreService.setDriverActivePassenger(data.driverId, null);
          }
          setRatingRideId(String(data.id || rideRef.id)); // capturar ANTES del null
          setCurrentRideId(null);
          setPaymentModalVisible(false);

          // El cobro va ANTES que las estrellas. Aqui se hacia
          // `setDriverBankAccounts([])` y se saltaba directo a calificar: la app
          // borraba la cuenta del chofer en el segundo exacto en que habia que
          // pagarle. Ahora se captura todo antes de limpiar.
          if (!abrirCierreDePagoSiHaceFalta(data)) setRatingModalVisible(true);
          setDriverBankAccounts([]);
          rideSessionStore.clearActiveRide(); // viaje terminó → limpiar sesión
          cleanupRideListeners();
          // Driver vuelve a IDLE para ahorrar batería esperando próximo viaje.
          if (data.driverId && data.driverId === useAppStore.getState().userFirebaseUid) {
            locationService.setTrackingMode('IDLE').catch(() => {});
          }
          // Pasajero: procesar loyalty SERVER-SIDE (1er viaje gratis + cada 10).
          // La función es idempotente — si se llama 2 veces para el mismo ride,
          // la segunda no duplica. Notifica el resultado al usuario.
          if (data.passengerId && data.passengerId === useAppStore.getState().userFirebaseUid) {
            // M2 FIX — Usar el rideId del snapshot (data.id o rideRef.id)
            // en vez de la variable de closure. Antes `currentRideId!` era
            // stale porque `setCurrentRideId(null)` se llamaba unas líneas
            // arriba y `null!` llegaba al backend.
            const rideIdForLoyalty = String(data.id || rideRef.id);
            // Aseguramos el procesamiento server-side (idempotente). Las
            // notificaciones de loyalty (1er viaje, viaje #10, "te falta 1",
            // viaje de lealtad usado) las manda AHORA el servidor por push
            // — llegan aunque la app esté cerrada y no se duplican. Aquí ya no
            // disparamos notificaciones locales.
            callOnRideCompleted({ rideId: rideIdForLoyalty })
              // V11 — el cierre es quien gasta o libera el viaje gratis: se relee
              // el perfil cuando ya contestó, para no anunciar con la marca vieja.
              .then(() => recargarGratisDelPerfil())
              .catch((e) => console.warn('onRideCompleted falló:', e));
          }
        } else if (data.status === 'CANCELLED') {
          setIsActiveTrip(false);
          // ANTI-STALKING — viaje cancelado: revocar el permiso de ubicación.
          if (data.driverId && data.driverId === useAppStore.getState().userFirebaseUid) {
            firestoreService.setDriverActivePassenger(data.driverId, null);
          }
          setCurrentRideId(null);
          setPaymentModalVisible(false);
          setDriverBankAccounts([]);
          rideSessionStore.clearActiveRide(); // viaje cancelado → limpiar sesión
          // H06 (auditoría 16-sep-2026) — el aviso va por `showAlert`, como el
          // resto de esta pantalla: `Alert.alert` no pinta nada en web, y en el
          // teléfono salía DOBLE (este y el de "Cancelar Viaje"). Los dos
          // escriben ahora la misma ventana, así que se ve una sola.
          {
            const aviso = avisoDeCancelacion(data);
            if (aviso) showAlert(aviso.titulo, aviso.cuerpo, 'close-circle', colors.error);
          }
          cleanupRideListeners();
          // H06 — y el cierre COMPLETO del viaje. Esta rama limpiaba la sesión
          // pero no la pantalla: cuando el CHOFER cancelaba, al pasajero le
          // quedaba ese mismo chofer como "Conductor Cercano" con el botón de
          // pedir viaje, y al cerrarlo, el formulario lleno del viaje anterior.
          // Es el "parece que lo estoy pidiendo de nuevo" que el fundador
          // reportó al completar, vivo en toda cancelación.
          // Solo cuando hubo chofer: si el pasajero canceló la BÚSQUEDA, su
          // formulario (destino, precio, paquete) se le queda como estaba para
          // volver a pedir sin escribirlo todo otra vez.
          // 27-sep-2026 (auditoría): esto era `if (data.driverId)`, o sea que
          // solo se limpiaba la pantalla cuando había chofer. Pero el SERVIDOR
          // cancela solicitudes SIN chofer en varios casos reales —viaje
          // duplicado, exceso de solicitudes, cancelación forzada del admin— y
          // en todos esos la pasajera se quedaba con el radar girando y las
          // ofertas en pantalla de un viaje que ya no existe. Peor aún: podía
          // tocar «Aceptar» en una de esas ofertas (ver la guarda al principio
          // de `handleAcceptOffer`). Se cierra siempre.
          cerrarViajeDelPasajero();
          if (data.driverId && data.driverId === useAppStore.getState().userFirebaseUid) {
            locationService.setTrackingMode('IDLE').catch(() => {});
          }
        } else if (data.status === 'EXPIRED') {
          // ── LA RAMA QUE NO EXISTÍA ─────────────────────────────────────
          // El cron marca EXPIRED a los 10 minutos sin chofer. Aquí no había
          // caso para ese estado, así que el radar seguía girando para
          // siempre: el "congelamiento" que reprodujo Google Play.
          manejarViajeExpirado();
          return;
        }

        // Un viaje cancelado ya cerró la pantalla arriba: pedir los datos del
        // chofer aquí volvería a pintarlo.
        if (data.driverId && data.status !== 'CANCELLED') {
          // Las reglas Firestore ya no permiten leer /users/{driverId} directo.
          // En su lugar pedimos la info pública vía Cloud Function callable.
          // La función valida que el caller es el passenger del ride y solo
          // devuelve bankAccounts si el ride está ACCEPTED / IN_PROGRESS.
          //
          // Aquí se leía SOLO `bankAccounts` y se tiraba todo lo demás (nombre,
          // foto, nota, marca, modelo, color y placa), que es justo lo que la
          // tarjeta del viaje enseñaba vacío. Ahora la respuesta se aprovecha
          // entera, y la llamada se hace UNA vez por viaje en vez de en cada
          // snapshot del documento.
          //
          // El id del viaje sale del snapshot, no de `currentRideId`: esa
          // variable es del closure y ya se ha puesto a null unas ramas más
          // arriba, así que la función se quedaba sin saber de qué viaje le
          // hablaban y no podía devolver las cuentas de banco.
          completarDatosDelChofer(
            String(data.driverId),
            String(data.id ?? rideRef.id ?? '') || null
          );
        }

        // DESVÍO DE RUTA — detección para el pasajero (chofer propone).
        // Antes vivía en un useEffect separado que abría un segundo
        // onSnapshot al MISMO doc de ride (doble suscripción). Consolidado
        // aquí para no duplicar listeners.
        // GUARD "solo el pasajero" (igual que en el resume): el modal es del
        // PASSENGER; solo lo mostramos si YO soy el passengerId del ride.
        const dev = (data as any)?.routeDeviation;
        const iAmPassenger = (data as any)?.passengerId === useAppStore.getState().userFirebaseUid;
        if (dev?.status === 'PENDING' && iAmPassenger) {
          setDeviationModal((m) =>
            m.visible && m.role === 'PASSENGER' ? m : { visible: true, role: 'PASSENGER', reason: dev.reason }
          );
        } else {
          setDeviationModal((m) => (m.role === 'PASSENGER' ? { visible: false, role: null } : m));
        }
        anunciarVeredictoDelDesvio(data);
      });

    } catch (error: any) {
      console.error('[handleRequestRide] error:', error);
      setIsSearching(false);
      // ── ESTO NO ES UN FALLO DE RED, ES UNA DECISIÓN NUESTRA ────────────
      // `firestoreService.requestRide` lanza `ride/active-exists` con un mensaje
      // ya escrito para una persona ("Ya tienes un viaje en curso…"). Caía en el
      // genérico "Revisa tu conexión", que manda a la pasajera a mirar el wifi
      // por algo que no tiene nada que ver con el wifi.
      if (error?.code === 'ride/active-exists') {
        showAlert(
          'Ya tienes un viaje en curso',
          String(error?.message ?? '').trim() ||
            'Termina o cancela tu viaje actual antes de pedir otro.',
          'car',
          '#FF9500'
        );
        return;
      }
      const code = String(error?.code ?? error?.message ?? '');
      const hasFirebaseUid = !!useAppStore.getState().userFirebaseUid;
      const msg =
        !hasFirebaseUid
          ? 'No tienes sesión iniciada. Crea cuenta o inicia sesión con email/teléfono para enviar tu solicitud.'
          // Antes decía: "El servidor rechazó la solicitud... las reglas de
          // seguridad bloquearon esta acción. Avísame si persiste." — el
          // programador hablándole en primera persona a la pasajera, con
          // vocabulario que no significa nada para ella. Y al final pegaba
          // "Detalle técnico: FirebaseError...". El detalle va al log.
          : code.includes('permission-denied')
            ? 'Tu cuenta todavía no está verificada para pedir viajes. Completa la verificación de identidad desde tu perfil y vuelve a intentar.'
            : code.includes('unauthenticated')
              ? 'Tu sesión se cerró. Vuelve a entrar para continuar.'
              : 'No pudimos enviar tu solicitud. Revisa tu conexión e intenta de nuevo.';
      console.warn('[map] requestRide falló —', code, error?.message);
      showAlert('No pudimos solicitar el viaje', msg, 'alert-circle', '#FF3B30');
    }
  };

  const handleAcceptOffer = async (offer: any) => {
    // ── SIN VIAJE DETRÁS NO SE ACEPTA NADA ────────────────────────────────
    // Si la solicitud murió mientras las ofertas seguían en pantalla (la
    // canceló el servidor, caducó), `currentRideId` es null: el camino de
    // abajo se saltaba la escritura y aun así pintaba «¡Viaje Confirmado!» y
    // la tarjeta de viaje en curso. La pasajera esperaba en la acera a un
    // chofer al que nadie avisó. El modo de prueba (qaMockRide) sigue igual:
    // ese sí monta un viaje sin documento, a propósito, y solo en desarrollo.
    // ⚠️ LA EXCEPCIÓN POR LA BANDERA SE QUITÓ, Y ERA UN AGUJERO.
    // Decía `!currentRideId && qaMockRide !== true`: con el modo encendido se
    // podía aceptar una oferta SIN NINGÚN viaje detrás, y eso montaba un viaje
    // con `currentRideId` en null que ya no avanzaba ni se cerraba solo.
    // La excepción nunca hizo falta: el viaje de demostración SÍ tiene id
    // (`qa_mock_ride_…`), así que exigir que exista no le quita nada y cierra
    // el caso de la oferta fantasma.
    if (!currentRideId) {
      showAlert(
        'Esa solicitud ya no está activa',
        'Se canceló o caducó mientras elegías. Pídelo de nuevo: tu destino sigue puesto.',
        'alert-circle',
        '#FF9500'
      );
      setReceivedOffers([]);
      setIncomingDriverOffers(false);
      setIsSearching(false);
      return;
    }
    /* === LÓGICA DE PRODUCCIÓN (Firestore) === */
    // AUDIT FIX RIDE — Antes seteábamos el state local ANTES/después sin
    // importar si Firestore rechazaba la escritura. Si el server rechazaba
    // (permission-denied, KYC), el pasajero veía "¡Viaje Confirmado!" pero
    // el chofer nunca recibía la aceptación (status quedaba PENDING). Ahora:
    //   1) Escribimos a Firestore.
    //   2) Solo si SUCCESS, mostramos "Viaje Confirmado" y cambiamos state.
    //   3) Si falla, mostramos error real y NO cambiamos state — el pasajero
    //      puede reintentar con otra oferta.
    const isQaMockMode = useAppStore.getState().qaMockRide === true;
    if (currentRideId && !isQaMockMode) {
      try {
        await firestoreService.acceptRideOffer(currentRideId, offer.id, offer.driverId, offer);
      } catch (e: any) {
        console.warn('[handleAcceptOffer] Firestore rechazó:', e?.code, e?.message);
        const msg = String(e?.message ?? '');
        // FIX A-3 — La aceptación es transaccional: si el viaje ya no está
        // PENDING (lo cancelaste, expiró, o ya aceptaste otra oferta) el
        // servidor lo rechaza. Damos un mensaje claro en vez de "error de red".
        const rideTaken = msg.includes('RIDE_NOT_PENDING') || msg.includes('RIDE_ALREADY_ASSIGNED') || msg.includes('RIDE_NOT_FOUND');

        // ── EL RECHAZO SE DETECTA POR EL CÓDIGO, NO POR EL TEXTO ────────────
        // Se miraba solo `e.message`, y el mensaje real de Firestore es
        // "Missing or insufficient permissions" (web) o "PERMISSION_DENIED:
        // …" (nativo): la cadena 'permission-denied' vive en `e.code`, no en el
        // texto. O sea que el caso de abajo casi nunca entraba y el rechazo del
        // servidor terminaba saliendo como "Revisa tu conexión" — mandando a
        // mirar el wifi a alguien con el wifi perfecto.
        const pista = `${String(e?.code ?? '')} ${msg}`.toLowerCase();
        const denegado =
          pista.includes('permission-denied') ||
          pista.includes('permission_denied') ||
          pista.includes('insufficient permissions');

        // ── NO SE INFIERE LA CAUSA DESDE EL CÓDIGO DE ERROR ─────────────────
        // Aquí un rechazo se traducía a "Tu cuenta todavía no está verificada.
        // Completa la verificación desde tu perfil". Una pasajera con el KYC
        // aprobado iba a su perfil, veía su escudo verde, volvía, tocaba
        // Aceptar y leía exactamente lo mismo: la app la culpaba de algo que ya
        // había hecho y la dejaba sin salida.
        //
        // Y es que la causa ni siquiera era esa: para aceptar una oferta las
        // reglas NO piden que la pasajera esté verificada (firestore.rules,
        // `allow update` de /rides) — piden que el viaje sea suyo, que la
        // transición de estado sea legal y que no viaje ningún campo del
        // servidor. Un `permission-denied` aquí es un problema NUESTRO, no de
        // su identidad.
        //
        // Si su perfil ya está verificado se lo decimos con todas las letras,
        // porque es justo lo que le ahorra el viaje en balde a su perfil.
        const denegadoTexto = isVerified
          ? 'Tu perfil ya está verificado, así que esto no es cosa tuya: el servidor rechazó la aceptación. Prueba con otra oferta o vuelve a entrar en un minuto.'
          : 'El servidor rechazó la aceptación de esta oferta. No es algo que puedas arreglar desde aquí: prueba con otra oferta o vuelve a entrar en un minuto.';

        showAlert(
          rideTaken ? 'Esta oferta ya no está disponible' : 'No pudimos aceptar la oferta',
          rideTaken
            ? 'El viaje cambió de estado (quizás ya aceptaste otra oferta o se canceló). Elige otra oferta disponible.'
            : denegado
              ? denegadoTexto
              : 'No pudimos conectarte con el chofer. Revisa tu conexión y toca "Aceptar" otra vez.',
          'alert-circle',
          '#FF3B30'
        );
        return;
      }
    }

    // V11 — "Viaje Gratis Cubierto" solo si el DOCUMENTO del viaje lo dice. Antes
    // salía por `isFreeTrip` (lo que esta pantalla creía al pedirlo) aunque el
    // servidor ya hubiera retirado la cobertura y el chofer lo viera de pago.
    // Se lee el viaje recién aceptado sin frenar la confirmación; si la lectura
    // falla no se promete nada.
    if (currentRideId && !isQaMockMode) {
      const rideIdAceptado = String(currentRideId);
      firestoreService.getRideById(rideIdAceptado)
        .then((viaje: any) => {
          if (viaje?.cubiertoPorLaApp !== true) return;
          notificationService.sendPushNotification(
            '🎁 Viaje Gratis Cubierto',
            `La app pagará los ${pesos(Math.min(tarifaSugerida ?? 250, TOPE_VIAJE_CUBIERTO_DOP))} de este viaje. Se descontarán de la cuota mensual del chofer seleccionado.`,
            { type: 'DRIVER_DISCOUNT' }
          );
          setTripsCompleted(0);
        })
        .catch((e: any) => console.warn('[handleAcceptOffer] no se pudo leer la cobertura del viaje:', e?.message));
    }

    setIncomingDriverOffers(false);
    // La cara solo si el servidor ya selló la oferta con la selfie aprobada: si
    // la pasajera acepta antes del sello, la oferta todavía trae la que mandó el
    // teléfono del chofer. El viaje recibe después la sellada (congelarDatosDelViaje).
    setSelectedDriver({ ...offer, driverPhoto: offer.reputacionSource === 'SERVER_TRIGGER' ? (offer.driverPhoto ?? null) : null });
    setIsActiveTrip(true);
    setRideStatus('ACCEPTED');
    showAlert('¡Viaje Confirmado!', `${offer.userName} está en camino para recogerte.`, 'car', colors.primary);
  };

  /**
   * Aviso sonoro + vibración.
   *
   * ⚠️ EL SONIDO AHORA VIAJA DENTRO DEL APK.
   * Antes se DESCARGABA de `assets.mixkit.co` en el momento de reproducirlo.
   * Eso significa que un chofer sin datos —o con mala cobertura, que es cuando
   * más falta hace— no oía absolutamente nada. Y ataba la app a un CDN ajeno
   * que puede caerse o cambiar.
   *
   * El archivo se genera con `node scripts/generar-sonido-aviso.js`.
   *
   * La vibración va SIEMPRE, aunque el audio falle: si el teléfono está en
   * silencio (muy común manejando), la vibración es el único aviso que queda.
   *
   * ⚠️ UN SOLO REPRODUCTOR PARA TODA LA PANTALLA. NO CREAR UNO POR AVISO.
   *
   * Antes cada aviso hacía `createAudioPlayer(...)` y la liberación
   * (`subscription.remove()` + `player.release()`) vivía DENTRO del callback
   * de estado, condicionada a que el sonido terminara limpio. En la calle eso
   * casi nunca pasa: el teléfono en silencio, la app minimizada, el foco de
   * audio perdido por una llamada o dos avisos solapados dejan el reproductor
   * NATIVO vivo para siempre. Nada lo liberaba al desmontar la pantalla.
   *
   * Ese era el único recurso de toda la app que crecía sin tope con el uso
   * normal, y como vive fuera del heap de JavaScript no lanzaba ninguna
   * excepción: la app se quedaba sin memoria nativa y Android la mataba sin
   * dejar un solo stack trace. Es la causa del "se cierra sola".
   *
   * Con un único objeto reutilizado, la cuenta de reproductores nativos es
   * como mucho UNO por jornada, pase lo que pase con el sonido.
   */
  const avisoPlayerRef = useRef<any>(null);

  /** Devuelve el reproductor único, creándolo la primera vez. */
  const obtenerAvisoPlayer = () => {
    if (avisoPlayerRef.current) return avisoPlayerRef.current;
    avisoPlayerRef.current = createAudioPlayer(require('../../../assets/sounds/aviso.wav'));
    return avisoPlayerRef.current;
  };

  /** Suelta el reproductor nativo. Idempotente: se puede llamar dos veces. */
  const liberarAvisoPlayer = () => {
    const p = avisoPlayerRef.current;
    avisoPlayerRef.current = null;
    if (!p) return;
    // `release()` (SharedObject) es lo que suelta la memoria nativa; si esta
    // versión de expo-audio no lo expone, `remove()` hace lo mismo.
    try { if (typeof p.release === 'function') p.release(); else p.remove?.(); } catch {}
  };

  const playNotificationSound = async () => {
    // La vibración va SIEMPRE y va PRIMERO: con el teléfono en silencio —lo
    // normal manejando— es el único aviso que le queda al chofer, así que no
    // puede depender de que el audio arranque.
    vibrarExito();
    try {
      const player = obtenerAvisoPlayer();
      try {
        // Solo se rebobina si el aviso anterior dejó el cabezal avanzado: en
        // el primer aviso el archivo aún no está cargado y pedirle un `seekTo`
        // a un reproductor recién creado es buscarse un fallo para nada.
        // `seekTo` es asíncrono; se corre contra un tope de 300 ms para no
        // quedarse esperando a un buffer lento y perder el aviso.
        if (Number(player.currentTime) > 0) {
          await Promise.race([
            player.seekTo(0).catch(() => {}),
            new Promise((resolver) => setTimeout(resolver, 300)),
          ]);
        }
        player.play();
      } catch {
        // El objeto nativo pudo quedar inservible (sesión de audio perdida,
        // proceso reanudado). Se descarta y se crea otro — sigue habiendo como
        // máximo UNO vivo, que es todo el objetivo de este arreglo.
        liberarAvisoPlayer();
        obtenerAvisoPlayer().play();
      }
    } catch (error) {
      // Que no suene nunca puede tumbar el flujo del viaje.
      console.warn('[map] no se pudo reproducir el aviso:', error);
    }
  };

  // Liberación INCONDICIONAL al desmontar la pantalla. Este es el cierre que
  // no existía: sin él, el reproductor sobrevivía a la pantalla que lo creó.
  useEffect(() => () => liberarAvisoPlayer(), []);

  // --- DRIVER: ESCUCHAR PEDIDOS DE COMIDA REALES ---
  const [driverDeliveries, setDriverDeliveries] = useState<any[]>([]);
  const [driverPendingRides, setDriverPendingRides] = useState<any[]>([]);

  // Celda geográfica del chofer (~22 km). Cambia solo cuando cruza de zona,
  // no en cada actualización del GPS: es la señal para volver a consultar los
  // viajes disponibles de su nueva área.
  const zonaChofer = currentLocation ? zonaDe(currentLocation.lat, currentLocation.lng) : null;

  /**
   * Copias frescas de lo que este mismo efecto ESCRIBE.
   *
   * Tenerlas en sus dependencias lo convertía en un bucle: el callback llamaba
   * a `setIncomingPassengerRequest` / `setDriverDeliveries`, eso cambiaba la
   * dependencia, el efecto se limpiaba, cerraba el `onSnapshot` y abría uno
   * nuevo. Cada solicitud entrante costaba una suscripción de Firestore
   * completa, con su primer snapshot de todos los viajes de la zona.
   */
  const incomingPassengerRequestRef = useRef(incomingPassengerRequest);
  useEffect(() => { incomingPassengerRequestRef.current = incomingPassengerRequest; }, [incomingPassengerRequest]);
  const conteoEntregasRef = useRef(0);
  // V56 — id del viaje de mi oferta cuyo estado se está leyendo (una lectura a la vez).
  const comprobandoOfertaPendienteRef = useRef<string | null>(null);

  // H36 (auditoría 16-sep-2026) — Al salir del modo chofer se borra la tarifa
  // de la última solicitud ajena que `mostrarSolicitud` dejó en `offerPrice`.
  // Sin esto, el formulario del pasajero aparecía con el precio que ofreció un
  // desconocido. Solo se borra si la escribió el modo chofer: la tarifa que el
  // pasajero tecleó él mismo no se toca.
  useEffect(() => {
    if (role === 'DRIVER') return;
    if (!precioAjenoEnFormularioRef.current) return;
    precioAjenoEnFormularioRef.current = false;
    setOfferPrice('');
  }, [role]);

  useEffect(() => {
    if (!isOnline) {
      setDriverDeliveries([]);
      setDriverPendingRides([]);
      conteoEntregasRef.current = 0;
      return;
    }

    // SEPARACIÓN DE FLUJOS:
    //   - DRIVER (taxi / motor taxi)      → solo escucha rides de pasajero
    //   - DELIVERY_DRIVER (repartidor)    → solo escucha food orders READY
    //   - SCHOOL_DRIVER                   → tiene su propio flujo en school-route.tsx
    //
    // Sin esta separación, un chofer de taxi recibía pings de comida (los aceptaba
    // por error y dejaba al pasajero sin viaje) y un repartidor se pisaba con
    // viajes que no le competen.

    if (role === 'DRIVER') {
      // BÚSQUEDA POR ZONA — solo los viajes del área del chofer, ordenados por
      // cercanía. Si el GPS aún no respondió, se pasa sin origen y se mantiene
      // el comportamiento anterior: es preferible mostrarle todo un momento que
      // dejarlo sin viajes esperando al GPS.
      const origen = currentLocation
        ? { lat: currentLocation.lat, lng: currentLocation.lng }
        : undefined;
      const mostrarSolicitud = (req: any) => {
        // ── NO PISAR EL VIAJE ACTIVO ────────────────────────────────────────
        // Esta función apunta `currentRideId` a una solicitud PENDIENTE. Si el
        // chofer YA va en un viaje, eso deja el estado partido: la validación
        // mira `driverCurrentRide` (el viaje real) y la escritura va a
        // `currentRideId` (una solicitud ajena). De ahí salían dos daños:
        //   · el falso "No pudimos avisar tu llegada" — se escribía ARRIVED
        //     sobre un viaje de otro y las reglas lo denegaban;
        //   · botones que dejaban de responder EN SILENCIO, porque
        //     `activeRideIdRef` (sincronizado con currentRideId) ya no
        //     reconocía el viaje propio.
        // Se lee el REF y no la variable de la clausura: el ref siempre está
        // igual de fresco o más.
        if (isActiveTripRef.current) return;
        shownRequestRideIdRef.current = req.id;
        setCurrentRideId(req.id);
        // ── H36 (auditoría 16-sep-2026): EL FORMULARIO DEL PASAJERO NO ES DEL CHOFER ──
        // Aquí también iba `setDestination(req.destination)`: el destino de un
        // desconocido escrito en el campo "¿A dónde vas?" de ESTE teléfono. En
        // modo chofer nadie lo lee (la tarjeta pinta `ride.destination`), y al
        // pasar el teléfono a modo pasajero el formulario salía pre-llenado con
        // la dirección de otra persona. Se quita.
        //
        // `setOfferPrice` SE QUEDA: los montos sugeridos del modal de
        // contraoferta del chofer salen de `offerPrice` (base × 0.85/0.95/1.1) y
        // sin él desaparecerían. Lo que se hace es marcarlo, y al volver a modo
        // pasajero se borra (ver el efecto de H36 más abajo).
        setOfferPrice(req.offerPrice?.toString());
        precioAjenoEnFormularioRef.current = true;
      };

      const unsubscribeRides = firestoreService.listenToPendingRides((rides) => {
        // ── EL PASAJERO ME DESCARTÓ ──────────────────────────────────────────
        // `rechazados` es la lista de choferes que el pasajero rechazó, y la
        // escribe su app al tocar "Rechazar". Antes de la 1.0.13 ese rechazo
        // vivía SOLO en la memoria de su teléfono: esta pantalla se quedaba en
        // "Oferta enviada — esperando respuesta del pasajero" hasta que el cron
        // expiraba la solicitud DIEZ MINUTOS después (viaje LvrCVZjnl… del
        // 19-sep: oferta a las 22:18:28, pantalla pegada hasta las 22:29:01).
        // Se lee del mismo documento que este feed ya trae: ni un listener más.
        const miUid = useAppStore.getState().userFirebaseUid;
        if (miUid) {
          rides.forEach((r: any) => {
            const lista = Array.isArray(r?.rechazados) ? r.rechazados : [];
            if (!lista.includes(miUid)) return;
            // Fuera del feed: una solicitud de la que ya me descartaron no se me
            // vuelve a ofrecer (mismo mecanismo que cuando el rechazo es mío).
            rejectedRideIdsRef.current.add(r.id);
            if (pendingOfferRideIdRef.current === r.id) {
              pendingOfferRideIdRef.current = null;
              setPendingOfferRideId(null);
              rideSessionStore.setPendingOffer(null);
              if (!avisoDeOfertaCerradaRef.current.has(r.id)) {
                avisoDeOfertaCerradaRef.current.add(r.id);
                showAlert(
                  'El pasajero eligió otra opción',
                  'No te aceptaron esta oferta. Sigues en línea: te avisamos en cuanto entre otra solicitud.',
                  'close-circle',
                  '#FF9500'
                );
              }
            }
            if (shownRequestRideIdRef.current === r.id) {
              shownRequestRideIdRef.current = null;
              setIncomingPassengerRequest(false);
              if (!isActiveTripRef.current) setCurrentRideId(null);
            }
          });
        }
        // ── LA VENTANA DEL MÁS CERCANO ───────────────────────────────────
        // El servidor YA calcula quién es el chofer más cercano y le manda el
        // aviso: `dispatchRide` filtra a 8 km, ordena por distancia y escribe
        // `dispatchedDriverId` (functions/src/dispatch.ts). Pero ese campo no lo
        // leía NADIE — lo comprobamos buscándolo en todo el código y en las
        // reglas. O sea que ser el más cercano no servía de nada: el viaje le
        // aparecía igual a todo el mundo y ganaba el que ofertara más rápido.
        //
        // Ahora tiene 25 segundos de ventaja. No es un candado —es el cliente,
        // y las reglas siguen dejando ofertar a cualquiera— pero cambia el
        // reparto en la práctica y no hace falta tocar las reglas, que es donde
        // se rompen cosas. Pasada la ventana, el viaje se abre a todos: nadie
        // se queda esperando a un chofer que no contestó.
        const VENTANA_DEL_CERCANO_MS = 25 * 1000;
        const ahoraMs = Date.now();
        const reservadoParaOtro = (r: any): boolean => {
          const elegido = String(r?.dispatchedDriverId ?? '');
          if (!elegido || !miUid || elegido === miUid) return false;
          const desde =
            r?.dispatchedAt?.toMillis?.() ??
            r?.dispatchedAt?.toDate?.()?.getTime?.() ??
            0;
          // Sin marca de tiempo no se esconde nada: es peor perder un viaje que
          // enseñarlo un segundo antes de tiempo.
          if (!desde) return false;
          return ahoraMs - desde < VENTANA_DEL_CERCANO_MS;
        };

        // ══════════════════════════════════════════════════════════════════
        // A UN MOTOR NO SE LE ENSEÑA UNA SOLICITUD DE MINIBÚS
        // ══════════════════════════════════════════════════════════════════
        //
        // El reparto automático del servidor SÍ respeta el vehículo:
        // `dispatch.ts` hace `where('vehicleType','==',requestedVehicle)` y le
        // manda el aviso al más cercano que cuadra. Pero esta lista —la que el
        // chofer navega— se consulta por `status == 'PENDING'` y `zone in […]`
        // y NADA MÁS: ni la consulta, ni `cleanAndEmit`, ni esta pantalla
        // miraban el vehículo.
        //
        // Resultado, que es justo lo que preguntó el fundador: un motorista
        // veía la solicitud de quien pidió un minibús y podía ofertar. Y si el
        // pasajero aceptaba, `acceptRideOffer` sobrescribe el `vehicleType` del
        // viaje con el del chofer: pidió minibús y acabó montándose en un
        // motor.
        //
        // ── POR QUÉ FALLA ABIERTO ──────────────────────────────────────────
        //
        // Si no se sabe qué conduce este chofer (el perfil todavía no llegó, o
        // falló la lectura), NO se filtra nada: se le enseña todo, que es el
        // comportamiento de siempre. Dejar a un chofer con la pantalla vacía
        // por un dato que no cargó es peor que enseñarle una solicitud de más
        // — él ve el tipo escrito en la tarjeta y decide.
        //
        // ══ LOS ENVÍOS SÍ SE FILTRAN, Y ANTES NO ═════════════════════════
        //
        // Aquí había una línea que dejaba pasar TODO envío sin mirar el
        // vehículo, con el razonamiento «un paquete lo lleva cualquiera». Era
        // razonable cuando el pasajero NO podía elegir — el selector estaba
        // escondido en modo paquete y el campo viajaba con 'CAR' por defecto.
        //
        // Ahora el pasajero elige, así que esa línea se convirtió en ignorarle
        // la elección: un minibusero veía un paquete pedido EN MOTOR y podía
        // ofertar, y si la pasajera aceptaba se lo llevaba un minibús. Además
        // esta lista contradecía al servidor, que en `dispatch.ts` SÍ filtra
        // por `vehicleType` sin mirar si es viaje o envío: el reparto automático
        // y la lista manual del chofer decían cosas distintas del mismo viaje.
        //
        // El fundador lo pidió con estas palabras: «el conductor ve exactamente
        // lo que el usuario está solicitando».
        const miVehiculo = String(vehiculoRealChoferRef.current ?? '').trim().toUpperCase();
        const esParaMiVehiculo = (r: any): boolean => {
          if (!miVehiculo) return true;
          const pedido = String(r?.vehicleType ?? '').trim().toUpperCase();
          // Sin tipo pedido no se filtra: es un viaje viejo de antes de que el
          // campo existiera, y esconderlo sería perderlo.
          if (!pedido) return true;
          // UN ENVÍO DE UNA APP VIEJA TAMPOCO SE FILTRA. Ahí el selector no se
          // veía y el campo viajaba con 'CAR' por defecto: filtrar por un valor
          // que nadie eligió dejaría al motorista sin ver NINGÚN envío de la
          // base todavía no actualizada — y esta lista es su único canal,
          // porque el reparto automático ya filtra. La marca la mandan solo las
          // versiones nuevas, así que su ausencia es la señal.
          const esEnvio = String(r?.serviceType ?? '').toUpperCase() === 'DELIVERY';
          if (esEnvio && r?.vehiculoElegido !== true) return true;
          return pedido === miVehiculo;
        };

        const visible = rides.filter(
          (r: any) =>
            !rejectedRideIdsRef.current.has(r.id) &&
            !reservadoParaOtro(r) &&
            esParaMiVehiculo(r)
        );
        setDriverPendingRides(visible);
        // Para que Actividad pueda decir cuántas hay sin abrir otro oyente.
        setSolicitudesVisibles(visible.length);

        // Y se programa el momento exacto en que la ventana caduca, para que el
        // viaje aparezca solo aunque Firestore no vuelva a mandar nada.
        if (temporizadorVentanaRef.current) {
          clearTimeout(temporizadorVentanaRef.current);
          temporizadorVentanaRef.current = null;
        }
        const escondidos = rides.filter(reservadoParaOtro);
        if (escondidos.length > 0) {
          const falta = Math.max(
            500,
            Math.min(
              ...escondidos.map((r: any) => {
                const desde =
                  r?.dispatchedAt?.toMillis?.() ?? r?.dispatchedAt?.toDate?.()?.getTime?.() ?? 0;
                return VENTANA_DEL_CERCANO_MS - (ahoraMs - desde);
              })
            )
          );
          temporizadorVentanaRef.current = setTimeout(() => {
            // El mismo filtro de vehículo que arriba: sin él, al caducar la
            // ventana del más cercano reaparecían las solicitudes que no son
            // para este vehículo — y el arreglo se deshacía solo a los 25
            // segundos, que es el peor tipo de arreglo.
            setDriverPendingRides(
              rides.filter(
                (r: any) => !rejectedRideIdsRef.current.has(r.id) && esParaMiVehiculo(r)
              )
            );
          }, falta + 250);
        }

        // Con un viaje ya ACEPTADO por este chofer, no tocamos la tarjeta de
        // solicitudes (la maneja el flujo de viaje activo).
        //
        // Se lee el REF, no `isActiveTrip`: este callback se creó cuando se
        // montó la suscripción y CONGELÓ el valor de entonces. En la prueba
        // real, entre que el pasajero acepta y este efecto se vuelve a montar,
        // la clausura seguía creyendo que no había viaje activo y colaba una
        // solicitud ajena en `currentRideId`. Esa era la ventana exacta del
        // falso error de llegada.
        if (isActiveTripRef.current) return;

        // ── V56: LA PÍLDORA "OFERTA ENVIADA" NO SOBREVIVE A SU VIAJE ─────────
        // Solo la limpiaba el listener de "mi viaje aceptado", que no dispara
        // si el pasajero elige a OTRO chofer o la solicitud expira: la píldora
        // se quedaba tapando "En línea". Cuando el viaje de mi oferta sale de
        // este feed se confirma leyéndolo (el feed va por zona, así que salir
        // de él no prueba nada): si ya no está PENDING y no es mío, se limpia.
        // Si lo aceptaron conmigo no se toca: ese listener monta el viaje.
        const ofertaId = pendingOfferRideIdRef.current;
        if (ofertaId && !visible.some((r: any) => r.id === ofertaId) && comprobandoOfertaPendienteRef.current !== ofertaId) {
          comprobandoOfertaPendienteRef.current = ofertaId;
          const soltarOferta = () => {
            if (pendingOfferRideIdRef.current !== ofertaId || isActiveTripRef.current) return;
            pendingOfferRideIdRef.current = null;
            setPendingOfferRideId(null);
            rideSessionStore.setPendingOffer(null);
            // La píldora desaparecía SIN DECIR NADA: el chofer había esperado
            // diez minutos mirando "esperando respuesta del pasajero" y de
            // repente volvía a "Buscando viajes" sin saber si su oferta se
            // perdió, si la app se rompió o si el pasajero se fue. Aquí se dice.
            if (!avisoDeOfertaCerradaRef.current.has(ofertaId)) {
              avisoDeOfertaCerradaRef.current.add(ofertaId);
              showAlert(
                'Esa solicitud ya no está',
                'Caducó o la tomó otro chofer. Sigues en línea: te avisamos con la próxima.',
                'time-outline',
                '#FF9500'
              );
            }
            // La tarjeta de esa misma solicitud quedaba retenida por la guarda
            // de oferta aceptada de abajo; se cierra igual que una solicitud
            // que desaparece.
            if (incomingPassengerRequestRef.current && shownRequestRideIdRef.current === ofertaId) {
              shownRequestRideIdRef.current = null;
              setIncomingPassengerRequest(false);
              if (!isActiveTripRef.current) setCurrentRideId(null);
            }
          };
          firestoreService.getRideById(ofertaId)
            .then((viaje: any) => {
              const estado = String(viaje?.status ?? '');
              // ── ¿ME RECHAZARON? ────────────────────────────────────────
              // El escaneo de `rechazados` de arriba solo ve los viajes que
              // VIENEN en el feed, y el feed recorta por antigüedad (10 min),
              // por radio y a 10 tarjetas. Un viaje al que ofertó hace rato
              // deja de venir aunque siga PENDING: el pasajero lo rechaza, la
              // marca se escribe, y aquí se salía por el `return` de PENDING
              // sin mirarla — píldora encendida hasta que el cron expirara la
              // solicitud. Ya tenemos el documento leído delante; se mira.
              const rechazadosDelViaje = Array.isArray((viaje as any)?.rechazados)
                ? (viaje as any).rechazados
                : [];
              if (rechazadosDelViaje.includes(useAppStore.getState().userFirebaseUid)) {
                rejectedRideIdsRef.current.add(ofertaId);
                soltarOferta();
                return;
              }
              if (estado === 'PENDING') return;
              const miViaje = !!viaje?.driverId && viaje.driverId === useAppStore.getState().userFirebaseUid;
              if (miViaje && ['ACCEPTED', 'ARRIVED', 'IN_PROGRESS'].includes(estado)) return;
              soltarOferta();
            })
            .catch((e: any) => {
              // La regla de /rides solo deja leer al chofer un viaje PENDING o
              // uno con su driverId. Que se deniegue ES la respuesta: ya no está
              // PENDING y no es mío (lo tomó otro o expiró). Otros errores se
              // ignoran y se reintenta con el próximo snapshot.
              const pista = `${String(e?.code ?? '')} ${String(e?.message ?? '')}`.toLowerCase();
              const denegado =
                pista.includes('permission-denied') ||
                pista.includes('permission_denied') ||
                pista.includes('insufficient permissions');
              if (denegado) { soltarOferta(); return; }
              console.warn('[map] no se pudo comprobar el viaje de mi oferta:', ofertaId, e?.message);
            })
            .finally(() => {
              if (comprobandoOfertaPendienteRef.current === ofertaId) comprobandoOfertaPendienteRef.current = null;
            });
        }

        // ── FIX PANTALLA PEGADA ──
        // El chofer YA está viendo una "Nueva Solicitud". Hay que revisar si
        // esa solicitud sigue disponible: si el pasajero la canceló o la tomó
        // otro chofer, el ride sale de PENDING y ya NO está en `visible`.
        // Antes esto no se chequeaba → la tarjeta quedaba pegada para siempre.
        if (incomingPassengerRequestRef.current) {
          const shownId = shownRequestRideIdRef.current;
          const sigueDisponible = !!shownId && visible.some((r: any) => r.id === shownId);
          if (sigueDisponible) return; // la que ve sigue vigente: no tocar nada
          // GUARD OFERTA ACEPTADA: si sobre esta solicitud YA enviamos una
          // oferta, que salga de PENDING probablemente significa que el pasajero
          // la ACEPTÓ (no que la canceló). NO limpiamos aquí — el listener de
          // "mi viaje aceptado" toma el control y monta el viaje activo. Limpiar
          // aquí causaría un parpadeo a "esperando" antes de entrar al viaje.
          if (pendingOfferRideIdRef.current && pendingOfferRideIdRef.current === shownId) return;
          if (visible.length > 0) {
            // La que veía desapareció, pero hay otra → mostrar la siguiente.
            mostrarSolicitud(visible[0]);
            return;
          }
          // No queda ninguna solicitud → volver a "Esperando solicitudes".
          shownRequestRideIdRef.current = null;
          setIncomingPassengerRequest(false);
          // Solo se borra si NO hay viaje activo. Borrarlo con un viaje en
          // curso dejaba a `currentRideId` en null y, con él, todos los
          // manejadores (`if (!currentRideId) return`) mudos: el chofer
          // pulsaba "He llegado" o "Iniciar viaje" y no pasaba absolutamente
          // nada, sin ningún mensaje.
          if (!isActiveTripRef.current) setCurrentRideId(null);
          return;
        }

        // No está mostrando ninguna aún: si llega una, mostrarla + avisar.
        if (visible.length > 0) {
          setIncomingPassengerRequest(true);
          mostrarSolicitud(visible[0]);
          // FIX CRÍTICO LAUNCH — Sonido + vibración cuando llega una
          // oferta. Antes el listener detectaba el ride y actualizaba la
          // UI silenciosamente; si el chofer estaba mirando para otro
          // lado o con el celular en el bolsillo, perdía el viaje sin
          // enterarse. Ahora: sonido fuerte + vibración patrón largo.
          //
          // ── Y SUENA SOLO POR LO QUE ESTÁ EN SU ZONA ──────────────────
          //
          // Lo pidió el fundador: «que no le suene» por recogidas lejanas. La
          // tarjeta SIGUE APARECIENDO —no se esconde ni un viaje— pero el
          // teléfono no interrumpe por algo a 20 km si el chofer dijo que
          // trabaja en 3.
          //
          // LA EXCEPCIÓN NO ES OPCIONAL: si el servidor le mandó ESTE viaje a
          // ÉL (`dispatchedDriverId`), suena pase lo que pase. `dispatchRide`
          // avisa a UN solo chofer y no reintenta jamás; callar ese aviso
          // sería perder el viaje y dejar al pasajero sin carro a los 10
          // minutos. Además el push ya le llegó al teléfono, y un push sin
          // ruido ni tarjeta detrás parece una app rota.
          const meLoMandaronAMi =
            !!userFirebaseUid &&
            String(visible[0]?.dispatchedDriverId ?? '') === String(userFirebaseUid);
          if (esDeMiZona(visible[0]) || meLoMandaronAMi) {
            playNotificationSound().catch(() => {});
            try {
              Vibration.vibrate([0, 500, 250, 500, 250, 500], false);
              vibrarExito();
            } catch {}
          }
        }
      }, origen);
      return () => {
        unsubscribeRides();
        if (temporizadorVentanaRef.current) {
          clearTimeout(temporizadorVentanaRef.current);
          temporizadorVentanaRef.current = null;
        }
      };
    }

    if (role === 'DELIVERY_DRIVER') {
      const unsubscribeFood = firestoreService.listenToAvailableDeliveries((orders) => {
        // El conteo anterior sale del ref, no de la clausura: así el efecto ya
        // no necesita `driverDeliveries.length` en sus dependencias (era él
        // mismo quien lo cambiaba) y deja de rehacer la suscripción sola.
        const prevCount = conteoEntregasRef.current;
        conteoEntregasRef.current = orders.length;
        setDriverDeliveries(orders);
        // Mismo patrón: sonido + vibración cuando aparece un pedido nuevo.
        // Solo disparamos si la cantidad SUBIÓ (no en updates de pedidos
        // existentes que no agregan nuevos).
        if (orders.length > prevCount) {
          playNotificationSound().catch(() => {});
          try {
            Vibration.vibrate([0, 500, 250, 500, 250, 500], false);
            vibrarExito();
          } catch {}
        }
      });
      return () => unsubscribeFood();
    }
    // `zonaChofer` en las dependencias (y NO currentLocation) es a propósito:
    // así la suscripción se rehace SOLO cuando el chofer cambia de celda —
    // llega a otra zona y le aparecen sus viajes al instante — en vez de
    // reconstruirse en cada latido del GPS, que gastaría batería y datos.
    //
    // FUERA de las dependencias, a propósito: `incomingPassengerRequest`,
    // `isActiveTrip` y `driverDeliveries.length`. Los tres los ESCRIBE este
    // mismo efecto, así que estando aquí se cerraba y reabría el `onSnapshot`
    // cada vez que entraba una solicitud. Los tres se leen ahora por ref.
  }, [role, isOnline, zonaChofer]);

  // ────────────────────────────────────────────────────────────────────────
  // FLUJO UBER-STYLE — LISTENER DEL CHOFER PARA "MI RIDE ACEPTADO"
  // ----------------------------------------------------------------------
  // El chofer envía una oferta y NO cambia su estado local todavía. Solo
  // cuando el pasajero acepta esa oferta, Firestore actualiza ride.status
  // a 'ACCEPTED' y ride.driverId=me. Este listener detecta ese cambio y
  // activa la UI de Viaje en Curso del chofer, sincroniza el ride actual,
  // y avanza automáticamente los estados ARRIVED / IN_PROGRESS / COMPLETED
  // cuando el chofer mismo los cambia desde los botones de la card.
  // C2 FIX — SIN `isActiveTrip` en deps. Antes, cada set del estado dentro
  // del callback re-disparaba el effect → unsubscribe/resubscribe loop y
  // flickering entre estados. Ahora el listener solo se re-arma cuando
  // el chofer entra/sale del rol o de online. Leemos isActiveTrip via ref
  // para tener el valor fresco sin causar re-mounts.
  // ── LA ZONA DEL CHOFER ──────────────────────────────────────────────
  //
  // El oyente de solicitudes solo se vuelve a suscribir con
  // [role, isOnline, zonaChofer] (ver su comentario más abajo), así que leer
  // `radioZonaKm` o `currentLocation` dentro de su callback daría el valor de
  // cuando se montó: el chofer movería el control y no pasaría nada hasta
  // cruzar una celda de 22 km. Este archivo ya resuelve eso con refs y aquí se
  // sigue el mismo patrón.
  const radioZonaKmRef = useRef(radioZonaKm);
  useEffect(() => { radioZonaKmRef.current = radioZonaKm; }, [radioZonaKm]);
  const currentLocationRef = useRef(currentLocation);
  useEffect(() => { currentLocationRef.current = currentLocation; }, [currentLocation]);

  /**
   * ¿Este viaje cae dentro del radio que eligió el chofer?
   *
   * FALLA ABIERTO: cuando no se puede medir —sin GPS, o un viaje sin
   * coordenadas— devuelve `true`. No es pereza: el reparto del servidor avisa
   * a UN solo chofer y no reintenta nunca, así que un aviso que no suena es un
   * viaje que se muere a los 10 minutos y un pasajero sin carro. Es el mismo
   * criterio que el filtro de vehículo de este archivo.
   *
   * `haversineKm` (arriba, en este mismo archivo) ya saca las coordenadas del
   * viaje y ya devuelve null si no las tiene.
   */
  const esDeMiZona = (viaje: any): boolean => {
    const km = haversineKm(currentLocationRef.current, viaje);
    if (km === null) return true;
    return km <= (radioZonaKmRef.current || 6);
  };

  const isActiveTripRef = useRef(isActiveTrip);
  useEffect(() => { isActiveTripRef.current = isActiveTrip; }, [isActiveTrip]);
  useEffect(() => {
    if (role !== 'DRIVER' || !isOnline) {
      driverAcceptedRideUnsubRef.current?.();
      driverAcceptedRideUnsubRef.current = null;
      return;
    }
    const driverId = useAppStore.getState().userFirebaseUid;
    if (!driverId) return;

    // ── H22 (auditoría 16-sep-2026): LO QUE EL SERVIDOR YA DIJO DE CADA VIAJE ──
    // Ver `montarViajeDelChofer` y el portón anti-fantasma de abajo. Viven dentro
    // del efecto a propósito: al rehacer la suscripción (desconectarse, cambiar
    // de modo) se empieza de cero y nada de lo comprobado antes se arrastra.
    const comprobacionOferta = new Map<string, 'COMPROBANDO' | 'MIO' | 'AJENO'>();
    let suscripcionViva = true;
    // El último viaje que entregó el listener. Cuando la comprobación contesta,
    // solo se monta si el listener SIGUE entregando ese viaje: pudo haberse
    // cerrado o cambiado mientras se esperaba la lectura.
    let ultimoViajeEntregado: any = null;
    // Si un viaje montado subió el GPS a alta precisión y luego se SUELTA sin que
    // el listener entregue null (soltarViajeMontado), nadie lo bajaba. Se recuerda
    // aquí y se baja cuando ya no queda viaje (rama null o fantasma).
    let gpsSubidoPorViaje = false;

    // Ride LEGÍTIMO — mi oferta fue aceptada o reconecté a mi viaje.
    // (Es el mismo bloque de siempre, sacado a una función solo para poder
    // llamarlo también cuando el servidor confirma la oferta. No cambia nada.)
    const montarViajeDelChofer = (ride: any) => {
      // Se escribe con guarda de igualdad: este listener dispara en cada
      // snapshot del documento y sin la guarda cada latido del viaje pintaba
      // el mapa entero de nuevo.
      aplicarViajeDelChofer(ride);
      setCurrentRideId(ride.id);
      activeRideIdRef.current = ride.id;
      setIsActiveTrip(true);
      setPendingOfferRideId(null);
      setIncomingPassengerRequest(false);
      // Persistir en sesión local: este es AHORA mi viaje activo.
      rideSessionStore.setActiveRide(ride.id, 'DRIVER');
      rideSessionStore.setPendingOffer(null);
      const s: 'ACCEPTED' | 'ARRIVED' | 'IN_PROGRESS' =
        ride.status === 'IN_PROGRESS'
          ? 'IN_PROGRESS'
          : ride.status === 'ARRIVED'
            ? 'ARRIVED'
            : 'ACCEPTED';
      setRideStatus(s);
      // FIX TRACKING (#3) — Subir GPS a alta precisión desde que el chofer va
      // EN CAMINO al pasajero (ACCEPTED), no solo en IN_PROGRESS. Así el
      // pasajero ve al chofer moverse fluido y nota si se detiene.
      if (s === 'ACCEPTED' || s === 'ARRIVED' || s === 'IN_PROGRESS') {
        gpsSubidoPorViaje = true;
        locationService.setTrackingMode('ACTIVE_RIDE').catch(() => {});
      }
    };

    // El viaje montado ya no es mío (ver la guarda del listener): la misma
    // limpieza que cuando el listener no entrega nada, salvo bajar el GPS, porque
    // puede montarse otro viaje enseguida.
    const soltarViajeMontado = () => {
      console.warn('[map] el viaje montado ya no está vivo a mi nombre, se suelta:', activeRideIdRef.current);
      setIsActiveTrip(false);
      isActiveTripRef.current = false;
      setDriverCurrentRide(null);
      setCurrentRideId(null);
      activeRideIdRef.current = null;
      setRideStatus('ACCEPTED');
      rideSessionStore.clearActiveRide();
    };

    const bajarGpsSiQuedoSubido = () => {
      if (gpsSubidoPorViaje && !isActiveTripRef.current) {
        gpsSubidoPorViaje = false;
        locationService.setTrackingMode('IDLE').catch(() => {});
      }
    };

    // Fantasma — no es mi oferta ni mi viaje. Ignorar en la UI.
    const tratarComoFantasma = (ride: any) => {
      bajarGpsSiQuedoSubido();
      console.warn('[map] ride fantasma ignorado:', ride.id, 'status', ride.status);
      // LIMPIEZA — Si el fantasma tiene >10 min, es basura de una prueba
      // anterior (un viaje real recién aceptado en otro device sería más
      // nuevo). Lo cancelamos en Firestore para que no reaparezca.
      // H22 — ahora solo llega aquí un viaje SIN oferta mía comprobada en el
      // servidor. Antes también caía el viaje legítimo del chofer que había
      // ofertado a dos, y pasados 10 minutos se le cancelaba al pasajero que lo
      // estaba esperando.
      const ageMs = Date.now() - (
        ride.acceptedAt?.toMillis?.()
        ?? ride.updatedAt?.toMillis?.()
        ?? ride.createdAt?.toMillis?.()
        ?? 0
      );
      if (ageMs > 10 * 60 * 1000) {
        firestoreService.forceExpireOrphanRide(ride.id).catch(() => {});
      }
    };

    driverAcceptedRideUnsubRef.current?.();
    driverAcceptedRideUnsubRef.current = firestoreService.listenToMyAcceptedRideAsDriver(
      driverId,
      (ride) => {
        ultimoViajeEntregado = ride;
        if (!ride) {
          // No hay ride activo → limpiar UI del chofer si estaba en modo
          // viaje O si tenía una oferta pendiente (el pasajero eligió a
          // otro chofer, o canceló). H5 FIX.
          if (isActiveTripRef.current) {
            setIsActiveTrip(false);
            setDriverCurrentRide(null);
            setCurrentRideId(null);
            setRideStatus('ACCEPTED');
            rideSessionStore.clearActiveRide();
            // H28 (auditoría 16-sep-2026) — EL GPS NO BAJABA NUNCA. El único
            // `setTrackingMode('IDLE')` vivía en el listener del PASAJERO, donde
            // "soy el chofer de este viaje" no se cumple jamás. En el teléfono
            // del chofer el GPS seguía en alta precisión cada 5 s tras cada
            // viaje, con la notificación diciendo "Viaje en curso" con el chofer
            // ya libre. Esta rama es por donde pasa TODO cierre (completado,
            // cancelado por cualquiera de los dos, expirado).
            locationService.setTrackingMode('IDLE').catch(() => {});
            gpsSubidoPorViaje = false;
          }
          // Viaje soltado antes (soltarViajeMontado) con el GPS todavía arriba.
          bajarGpsSiQuedoSubido();
          setPendingOfferRideId(null);
          rideSessionStore.setPendingOffer(null);
          return;
        }

        // ANTI-FANTASMA — Gate crítico. El listener global trae CUALQUIER
        // ride con driverId=me + status activo, incluidos fantasmas de
        // pruebas anteriores. Solo entramos al viaje si su ID coincide con:
        //   (a) mi oferta pendiente (pendingOfferRideIdRef) — el pasajero
        //       acaba de aceptar mi oferta, O
        //   (b) mi viaje activo ya guardado (activeRideIdRef) — reconexión
        //       durante un viaje real.
        // Cualquier otro ride es un fantasma → lo ignoramos por completo
        // (el service ya lo auto-expira si es viejo). H22: salvo que el
        // servidor confirme que hay una oferta mía en él (ver abajo).
        //
        // Guard de hidratación: si la sesión aún no cargó de AsyncStorage,
        // NO tomamos decisiones (evita perder una aceptación legítima en el
        // primer snapshot antes de que los refs estén listos).
        if (!sessionHydratedRef.current) return;
        const rideId = String(ride.id);
        const known =
          ride.id === pendingOfferRideIdRef.current ||
          ride.id === activeRideIdRef.current;
        if (known) {
          montarViajeDelChofer(ride);
          return;
        }

        // El listener entrega el viaje que ya llevo montado MIENTRAS siga vivo a
        // mi nombre (`preferirRideId`, V13): una aceptación tardía de otra oferta
        // no lo tapa. Si aun así entrega OTRO, es que el montado ya no es mío
        // (terminó, o el servidor lo devolvió porque se aceptó después que este).
        // Seguir enseñándolo dejaría al chofer con la tarjeta de un viaje ajeno y
        // al pasajero de verdad sin chofer: se suelta y este viaje pasa por la
        // comprobación de siempre.
        if (isActiveTripRef.current && activeRideIdRef.current && String(activeRideIdRef.current) !== rideId) {
          soltarViajeMontado();
        }

        // ── H22 (auditoría 16-sep-2026): ANTES DE LLAMARLO FANTASMA, SE PREGUNTA ──
        // Las dos casillas de arriba son LOCALES y una sola cada una: el chofer
        // que ofertaba a A y luego a B tenía `pendingOfferRideId = B`, y cuando el
        // pasajero de A lo aceptaba, A caía aquí como fantasma y se ignoraba para
        // siempre. Lo mismo en el segundo teléfono de un chofer. El pasajero se
        // quedaba con "tu chofer va en camino" y nadie en camino.
        //
        // Ahora se le pregunta al servidor si en ESE viaje hay una oferta mía
        // (`tengoOfertaEnViaje`: la oferta aceptada y, si no es mía, una consulta
        // de respaldo; una sola vez por viaje). Si la hay, el viaje es
        // mío y se monta. Si no la hay, es un fantasma de verdad y se trata como
        // siempre. El flujo de ofertas no se toca: esto solo cambia a quién se le
        // cree cuando la casilla local no reconoce el viaje.
        const veredicto = comprobacionOferta.get(rideId);
        if (veredicto === 'MIO') {
          montarViajeDelChofer(ride);
          return;
        }
        if (veredicto === 'COMPROBANDO') return;
        if (veredicto === undefined) {
          comprobacionOferta.set(rideId, 'COMPROBANDO');
          firestoreService
            .tengoOfertaEnViaje(rideId, driverId, ride.acceptedOfferId ?? null)
            .then((esMio) => {
              if (!suscripcionViva) return;
              comprobacionOferta.set(rideId, esMio ? 'MIO' : 'AJENO');
              if (!esMio) {
                // Misma limpieza que antes se hacía en el primer snapshot: si se
                // dejara para el siguiente, un fantasma que no vuelve a cambiar
                // no se limpiaría nunca.
                tratarComoFantasma(ride);
                return;
              }
              // Solo si el listener sigue entregando ESTE viaje: si mientras se
              // leía se cerró o llegó otro, montarlo sería resucitarlo.
              // Que el listener siga entregando ESTE viaje ya prueba que no
              // encontró vivo el que había montado (ver arriba).
              const vigente = ultimoViajeEntregado;
              if (vigente && String(vigente.id) === rideId && sessionHydratedRef.current) {
                if (isActiveTripRef.current && activeRideIdRef.current && String(activeRideIdRef.current) !== rideId) {
                  soltarViajeMontado();
                }
                console.warn('[map] viaje aceptado que la sesión local no conocía — hay oferta mía, se monta:', rideId);
                montarViajeDelChofer(vigente);
              }
            })
            .catch((e: any) => {
              // "No lo pude comprobar" NO es "fantasma": no se cancela nada. Se
              // olvida el intento para volver a preguntar en el próximo snapshot.
              if (suscripcionViva) comprobacionOferta.delete(rideId);
              console.warn('[map] no se pudo comprobar la oferta del viaje', rideId, e?.code, e?.message);
            });
          return;
        }

        // Fantasma CONFIRMADO — el servidor ya dijo que no hay oferta mía.
        tratarComoFantasma(ride);
      },
      // V13 — con dos viajes vivos a mi nombre (un pasajero aceptó tarde una
      // oferta vieja), el listener entrega el que ya llevo en curso y no el más
      // reciente: así no se pierden los cambios del viaje real.
      () => activeRideIdRef.current
    );
    return () => {
      suscripcionViva = false;
      driverAcceptedRideUnsubRef.current?.();
      driverAcceptedRideUnsubRef.current = null;
    };
  }, [role, isOnline, sessionHydrated]);

  // Handlers de avance de estado desde los botones de la ActiveRideCard.
  // Cada uno solo llama al método de Firestore correspondiente — el
  // listener de arriba propaga el nuevo estado a la UI automáticamente,
  // así el chofer y el pasajero quedan sincronizados sin doble escritura.
  /**
   * AUTORIZA AL PASAJERO A VER MI UBICACIÓN EN VIVO (lado del CHOFER).
   *
   * ── POR QUÉ ESTABA ROTO ────────────────────────────────────────────────
   * La regla de `/driverLocations/{driverId}` es anti-acoso: solo deja leer al
   * propio chofer y al pasajero de SU viaje activo, y esa relación se demuestra
   * con un campo (`activePassengerId`) que **solo el chofer puede escribir**.
   *
   * La única llamada que lo escribía vivía dentro del listener que arranca
   * `handleRequestRide`, o sea, EN EL TELÉFONO DEL PASAJERO. Allí la condición
   * "si yo soy el chofer de este viaje" no se cumple nunca, así que la llave no
   * se escribía jamás: el pasajero pedía la posición del chofer, las reglas se
   * la denegaban, y **veía el carro congelado durante todo el viaje**.
   *
   * Aquí se escribe desde el lado correcto. Se limpia al terminar o cancelar,
   * porque un chofer sin viaje NO debe ser rastreable por nadie: ese es todo el
   * sentido de la regla.
   */
  /**
   * Última llave que ya se escribió en `driverLocations`.
   *
   * POR QUÉ: este efecto depende de `driverCurrentRide`, que cambia con cada
   * snapshot del viaje, así que disparaba UNA ESCRITURA A FIRESTORE por
   * snapshot — el mismo valor, una y otra vez, durante todo el viaje. Se
   * recuerda lo último escrito y no se repite. El valor sale del propio viaje,
   * así que cualquier cambio real sí vuelve a escribir.
   */
  const ultimoPasajeroAutorizadoRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const esChofer = role === 'DRIVER' || role === 'DELIVERY_DRIVER';
    if (!esChofer) return;
    const uid = useAppStore.getState().userFirebaseUid;
    if (!uid) return;

    const estado = String(driverCurrentRide?.status ?? '').toUpperCase();
    const soyElChofer = String(driverCurrentRide?.driverId ?? '') === uid;
    const pasajero = String(driverCurrentRide?.passengerId ?? '');
    const viajeVivo = ['ACCEPTED', 'ARRIVED', 'IN_PROGRESS'].includes(estado);

    const escribirLlave = (valor: string | null) => {
      if (ultimoPasajeroAutorizadoRef.current === valor) return;
      ultimoPasajeroAutorizadoRef.current = valor;
      firestoreService.setDriverActivePassenger(uid, valor);
    };

    if (isActiveTrip && soyElChofer && viajeVivo && pasajero) {
      escribirLlave(pasajero);
    } else if (!isActiveTrip) {
      // Sin viaje activo se retira el permiso. Si la app muriera antes de
      // llegar aquí, la segunda via de la regla (comprobar el viaje) tampoco
      // autorizaria, porque exige que el viaje siga vivo.
      escribirLlave(null);
    }
  }, [role, isActiveTrip, driverCurrentRide]);

  /**
   * Marca que el chofer llegó al punto de recogida.
   *
   * ── POR QUÉ RECIBE EL id Y COMPARA ANTES DE ESCRIBIR ──────────────────────
   * Antes validaba una cosa y escribía otra: el geofence comprobaba
   * `driverCurrentRide` (el viaje real del chofer) y esta función escribía
   * sobre `currentRideId`, que podía estar apuntando a una solicitud PENDIENTE
   * de otro pasajero. Escribir ARRIVED sobre un viaje ajeno lo deniegan las
   * reglas, y el chofer veía "No pudimos avisar tu llegada" sin haber tocado
   * nada — el fallo que el fundador reportó dos veces.
   *
   * El arreglo es determinista, no un retraso: si el id que se va a escribir no
   * es EXACTAMENTE el del viaje que el servidor dice que llevo, no se escribe.
   * Da igual en qué orden lleguen las actualizaciones.
   *
   * `automatico` distingue quién lo pidió: si lo disparó el geofence solo, un
   * fallo no debe sacarle una alerta a alguien que no tocó nada; queda en el
   * registro. Si lo pulsó él, sí se le avisa.
   */
  const handleDriverArrived = useCallback(async (rideIdParam?: string, automatico = false) => {
    // H42 (auditoría 16-sep-2026) — un solo aviso de llegada en vuelo, venga
    // del botón o del geofence. El segundo devuelve `undefined` sin escribir: el
    // geofence lo lee como "no quedó hecho" y vuelve a mirar en el siguiente
    // latido del GPS, que para entonces ya ve el ARRIVED y no hace nada.
    if (marcandoLlegadaRef.current) return;
    const id = rideIdParam ?? currentRideId;
    if (!id) return;
    const idDelViajeReal = String(driverCurrentRide?.id ?? '');
    if (idDelViajeReal !== String(id)) {
      console.warn('[driverArrived] id desalineado — no se escribe:', { intentado: id, real: idDelViajeReal });
      return;
    }
    marcandoLlegadaRef.current = true;
    setMarcandoLlegada(true);
    try {
      await firestoreService.driverArrivedAtPickup(id);
      vibrarExito();
      return true;
    } catch (e: any) {
      console.warn('[driverArrived] error:', e?.code, e?.message, 'viaje:', id);
      if (!automatico) {
        Alert.alert('Error', 'No pudimos avisar tu llegada. Intenta de nuevo.');
      }
      return false;
    } finally {
      marcandoLlegadaRef.current = false;
      setMarcandoLlegada(false);
    }
  }, [currentRideId, driverCurrentRide]);

  /**
   * Copia fresca de `handleDriverArrived` para el geofence.
   *
   * `handleDriverArrived` es un `useCallback` que depende de `driverCurrentRide`,
   * así que cambia de identidad en CADA latido del viaje. Tenerlo en las
   * dependencias del geofence hacía que su suscripción a `driverLocations` se
   * diera de baja y se volviera a montar constantemente.
   */
  const handleDriverArrivedRef = useRef(handleDriverArrived);
  useEffect(() => { handleDriverArrivedRef.current = handleDriverArrived; }, [handleDriverArrived]);

  // FIX TRACKING (#3) — El PASAJERO escucha la ubicación EN VIVO del chofer
  // asignado (driverLocations/{driverId}) durante el viaje activo, para verlo
  // moverse en el mapa. Antes nadie llamaba a listenToDriverLocation del lado
  // pasajero, así que si el chofer se detenía el pasajero no se enteraba.
  const [driverLiveCoord, setDriverLiveCoord] = useState<{ latitude: number; longitude: number } | null>(null);

  /**
   * LA OTRA MITAD DE LA POSICIÓN DEL CHOFER: SI TE LA NIEGAN, SE VUELVE A PEDIR.
   *
   * ── POR QUÉ LA PRIMERA SUSCRIPCIÓN SE CAE ────────────────────────────────
   * `driverLocations/{chofer}` es anti-acoso: solo lo lee el propio chofer y el
   * pasajero de SU viaje activo. Ese permiso no existe en el instante en que
   * esta pantalla se suscribe — se concede al aceptarse el viaje, en el
   * servidor. Aquí el efecto se dispara en cuanto el viaje pasa a activo, o sea
   * una carrera que a veces se pierde por décimas.
   *
   * Y perderla era definitivo: un `onSnapshot` rechazado por reglas NO vuelve a
   * intentarlo solo, está muerto. Como este efecto solo dependía del rol, del
   * viaje y del id del chofer — y ninguno de los tres vuelve a cambiar — nadie
   * se suscribía de nuevo: el pasajero se quedaba viendo el carro congelado EL
   * VIAJE ENTERO.
   *
   * ── CÓMO SE REINTENTA ────────────────────────────────────────────────────
   * No se puede distinguir "denegado" de "el documento todavía no existe":
   * `listenToDriverLocation` traduce las dos cosas a un `null` en el callback.
   * Da igual, porque la consecuencia es la misma (no hay posición) y el remedio
   * también. Así que la señal que se usa es la única que de verdad importa:
   * **pasó el plazo y no ha llegado ninguna coordenada válida.** Entonces se
   * cierra esta suscripción y se abre otra limpia.
   *
   * TODA ESPERA CON RELOJ Y SALIDA:
   *  • reloj → `PLAZOS_POSICION_CHOFER`, con el hueco creciendo;
   *  • salida → se para al cuarto intento, y el último listener se queda
   *    abierto: si el permiso llega tarde, la posición entra igual sin
   *    reintentos;
   *  • en cuanto llega UNA coordenada buena se apaga el reloj para siempre;
   *  • si el viaje acaba o cambia de chofer, el contador vuelve a cero y la
   *    limpieza mata el reloj — nada sigue latiendo después del viaje.
   *
   * Mientras no haya posición, el mapa NO inventa una: no pinta el marcador del
   * chofer y se queda con la recta de respaldo. Un punto adivinado en un viaje
   * sería peor que no tener punto.
   */
  const drvIdEnVivo = selectedDriver?.driverId || driverCurrentRide?.driverId;
  const [intentoPosicionChofer, setIntentoPosicionChofer] = useState(0);
  useEffect(() => {
    // Viaje nuevo o chofer nuevo: los intentos del anterior no cuentan.
    setIntentoPosicionChofer(0);
  }, [drvIdEnVivo, isActiveTrip, role]);

  useEffect(() => {
    const drvId = drvIdEnVivo;
    if (role !== 'PASSENGER' || !isActiveTrip || !drvId) {
      setDriverLiveCoord(null);
      return;
    }
    let vivo = true;
    let yaLlego = false;
    let reloj: ReturnType<typeof setTimeout> | null = null;

    const unsub = firestoreService.listenToDriverLocation(drvId, (loc: any) => {
      if (loc && typeof loc.lat === 'number' && typeof loc.lng === 'number') {
        yaLlego = true;
        if (reloj) { clearTimeout(reloj); reloj = null; }
        setDriverLiveCoord({ latitude: loc.lat, longitude: loc.lng });
      }
    });

    const plazo = PLAZOS_POSICION_CHOFER[intentoPosicionChofer];
    if (typeof plazo === 'number') {
      reloj = setTimeout(() => {
        if (!vivo || yaLlego) return;
        console.warn(
          `[posicion del chofer] sin coordenadas tras ${plazo} ms — reintento ${intentoPosicionChofer + 1} de ${PLAZOS_POSICION_CHOFER.length}`
        );
        setIntentoPosicionChofer((n) => n + 1);
      }, plazo);
    } else if (!yaLlego) {
      console.warn('[posicion del chofer] agotados los reintentos; la suscripcion queda abierta por si el permiso llega tarde');
    }

    return () => {
      vivo = false;
      if (reloj) clearTimeout(reloj);
      if (typeof unsub === 'function') unsub();
    };
  }, [role, isActiveTrip, drvIdEnVivo, intentoPosicionChofer]);

  /**
   * LA POSICIÓN VIVA DEL PROPIO CHOFER — EL ORIGEN DE SU LÍNEA.
   *
   * `currentLocation` solo se escribe en tres momentos: el fix inicial al abrir
   * el mapa, un vigilante que se apaga solo a los 30 s (y que ni siquiera
   * arranca si el primer fix fue preciso), y el botón "mi ubicación". Mientras
   * el chofer CONDUCE ese estado no cambia: su línea salía de donde encendió la
   * app y su punto azul se alejaba kilómetros. Es lo que el fundador iba a ver
   * en su prueba de calle.
   *
   * No se abre ningún GPS nuevo: `locationService` ya escribe la posición del
   * chofer en `driverLocations/{uid}` durante el viaje. Aquí solo se ESCUCHA lo
   * que ya se está escribiendo, que además es exactamente lo que ve el
   * pasajero, así que los dos miran el mismo punto.
   *
   * El guardado de 500 m del efecto de la ruta sigue en pie: sin él, cada tick
   * del GPS sería una llamada facturada a Directions.
   */
  const [miPosicionViva, setMiPosicionViva] = useState<{ lat: number; lng: number } | null>(null);
  useEffect(() => {
    // SCHOOL_DRIVER estaba en esta lista y en NINGUNA de las que dibujan: ni la
    // línea (`extremosDeLaLinea`), ni el pin de recogida, ni el encuadre. O sea
    // que el chofer escolar pagaba un listener a Firestore latiendo todo el
    // viaje para que nadie mirara el resultado — su ruta vive en su propia
    // pantalla (`school-route.tsx`). Los tres sitios dicen ahora lo mismo.
    const esChofer = role === 'DRIVER' || role === 'DELIVERY_DRIVER';
    const uid = useAppStore.getState().userFirebaseUid;
    if (!esChofer || !isActiveTrip || !uid) {
      setMiPosicionViva(null);
      return;
    }
    const unsub = firestoreService.listenToDriverLocation(uid, (loc: any) => {
      if (loc && typeof loc.lat === 'number' && typeof loc.lng === 'number') {
        setMiPosicionViva({ lat: loc.lat, lng: loc.lng });
      }
    });
    return () => { if (typeof unsub === 'function') unsub(); };
  }, [role, isActiveTrip, userFirebaseUid]);

  // FIX LLEGADA AUTO (#5) — Geofence del CHOFER: cuando su posición viva (la que
  // locationService mantiene fresca en driverLocations) queda a <60 m del punto
  // de recogida y el viaje sigue ACCEPTED, marcamos ARRIVED automáticamente (una
  // sola vez por viaje). Antes el chofer TENÍA que tocar "He llegado" a mano.
  const autoArrivedRideRef = useRef<string | null>(null);
  /** Lecturas de GPS seguidas dentro del radio de llegada. Ver mas abajo. */
  const lecturasCercaRef = useRef(0);
  useEffect(() => {
    if (role !== 'DRIVER' || rideStatus !== 'ACCEPTED' || !currentRideId) return;
    const uid = useAppStore.getState().userFirebaseUid;
    if (!uid || autoArrivedRideRef.current === currentRideId) return;
    const unsub = firestoreService.listenToDriverLocation(uid, (loc: any) => {
      if (!loc || typeof loc.lat !== 'number' || typeof loc.lng !== 'number') return;
      if (autoArrivedRideRef.current === currentRideId) return;
      // ── GUARDA CONTRA EL DISPARO EN FALSO ────────────────────────────────
      //
      // `rideStatus` (la condición del efecto) es estado LOCAL que arranca en
      // 'ACCEPTED' por defecto. En la prueba real, con los dos teléfonos a 6
      // metros, el geofence se armó con la solicitud todavía PENDIENTE y
      // disparó handleDriverArrived contra un viaje que aún no tenía chofer
      // asignado: la regla de Firestore lo denegó y el chofer vio "No pudimos
      // avisar tu llegada" sin haber tocado nada.
      //
      // Aquí se comprueba el documento del SERVIDOR: solo se marca la llegada
      // si el viaje realmente está ACCEPTED y el chofer asignado soy YO.
      // ── SE LEE EL REF, NO LA VARIABLE ───────────────────────────────────
      // Mismo valor (el ref se sincroniza en cuanto cambia el estado), pero
      // sin arrastrar `driverCurrentRide` a las dependencias del efecto. Con
      // él ahí, esta suscripción a `driverLocations` se daba de baja y se
      // volvía a montar EN CADA LATIDO del viaje: montar y desmontar un
      // listener nativo varias veces por segundo, durante todo el viaje.
      const viajeReal = driverCurrentRideRef.current;
      const estadoServidor = String(viajeReal?.status ?? '').toUpperCase();
      const choferAsignado = String(viajeReal?.driverId ?? '');
      if (estadoServidor !== 'ACCEPTED' || choferAsignado !== uid) return;
      // Tercera comprobación, la que faltaba: que el viaje que estoy validando
      // sea el MISMO al que se va a escribir. Sin esto, la guarda daba luz
      // verde mirando un viaje y la escritura iba a otro.
      if (String(viajeReal?.id ?? '') !== String(currentRideId)) return;
      const km = haversineKm({ lat: loc.lat, lng: loc.lng }, viajeReal);

      // ── DOS LECTURAS SEGUIDAS, NO UNA ───────────────────────────────────
      // Con una sola lectura bastaba con PASAR a 60 m por una calle paralela o
      // parar en un semaforo cerca para que se marcara la llegada. Y como al
      // llegar se abre solo el teclado del PIN, al chofer le saltaba un teclado
      // a pantalla completa TAPANDO EL MAPA MIENTRAS CONDUCIA. Eso no es una
      // molestia: es un peligro.
      //
      // Exigiendo dos posiciones seguidas dentro del radio, quien pasa de largo
      // ya no dispara nada, y quien de verdad se detiene alli marca igual de
      // rapido (las posiciones llegan cada pocos segundos).
      if (km == null || km > 0.06) {
        lecturasCercaRef.current = 0;
        return;
      }
      lecturasCercaRef.current += 1;
      if (lecturasCercaRef.current >= 2) {
        autoArrivedRideRef.current = currentRideId;
        // Si falla (un bache de red, por ejemplo), se libera la marca para
        // poder reintentar en el siguiente latido del GPS. Antes la marca se
        // ponía y no se quitaba nunca: un solo fallo apagaba la llegada
        // automática de ese viaje PARA SIEMPRE y obligaba al toque manual,
        // que es justo lo que el fundador quiere eliminar.
        handleDriverArrivedRef.current(String(viajeReal.id), true).then((ok) => {
          if (!ok) autoArrivedRideRef.current = null;   // libera para reintentar
        });
      }
    });
    return () => { if (typeof unsub === 'function') unsub(); };
    // Solo PRIMITIVOS en las dependencias. `driverCurrentRide` y
    // `handleDriverArrived` se leen por ref (arriba): cambian en cada latido
    // del viaje y aquí solo servían para remontar la suscripción sin motivo.
  }, [role, rideStatus, currentRideId]);

  // PIN DE VIAJE — El chofer abre el modal para ingresar el PIN de 4 dígitos
  // que el pasajero le dio. Sin PIN correcto el viaje no arranca (garantiza
  // que recogió al pasajero correcto y no cobra antes de que aborde).
  const handleStartRide = useCallback(() => {
    if (!currentRideId) return;
    setPinEntry('');
    setPinModalVisible(true);
  }, [currentRideId]);

  /**
   * El teclado del PIN se abre SOLO al llegar al punto de recogida.
   *
   * El fundador contó tres toques desde que llega hasta que arranca el viaje, y
   * uno de ellos —"Iniciar viaje (pasajero abordó)"— no hacía absolutamente
   * nada salvo abrir este teclado. Al entrar en ARRIVED ya no hay ninguna
   * decisión pendiente: lo único que falta es que el pasajero dicte su código.
   *
   * El ref por viaje evita que se reabra en bucle si el chofer lo cierra a
   * propósito (por ejemplo, si el pasajero aún no ha llegado al carro). El
   * botón manual de la tarjeta sigue existiendo para volver a abrirlo.
   *
   * IMPORTANTE: esto NO relaja la seguridad. El PIN se sigue tecleando y se
   * sigue verificando en el servidor; solo desaparece el toque de abrir la
   * ventana.
   */
  const pinAutoAbiertoRef = useRef<string | null>(null);
  useEffect(() => {
    if (role !== 'DRIVER' || rideStatus !== 'ARRIVED' || !currentRideId) return;
    if (pinAutoAbiertoRef.current === currentRideId) return;
    pinAutoAbiertoRef.current = currentRideId;
    setPinEntry('');
    setPinModalVisible(true);
  }, [role, rideStatus, currentRideId]);

  /**
   * ATRÁS CIERRA EL TECLADO DEL PIN — H39 (auditoría 16-sep-2026).
   *
   * El teclado del PIN es un `View` encima del mapa, no una pantalla ni un
   * `Modal`, así que el botón Atrás de Android caía directo al manejador global
   * de `_layout.tsx`: primer toque "Toca Atrás otra vez para salir", segundo
   * toque la app se cerraba. Justo con el chofer parado en la recogida y el
   * pasajero dictándole el código; con navegación por gestos bastaba rozar el
   * borde dos veces.
   *
   * Mientras el teclado está abierto, Atrás hace exactamente lo mismo que su
   * botón "Cancelar" y se queda con el evento. Android llama primero al último
   * manejador registrado, por eso este gana al global sin tocarlo. No toca la
   * validación del PIN: el botón "Iniciar viaje" de la tarjeta lo vuelve a abrir.
   */
  /**
   * ATRAS NO PUEDE TIRAR LA PANTALLA DEL COBRO — mismo motivo que el H39 del
   * teclado del PIN, justo aqui debajo.
   *
   * Los dos cierres son un `View` encima del mapa, no un `Modal`, asi que el
   * boton Atras de Android caia al manejador global y cerraba la app. Con
   * navegacion por gestos basta rozar el borde. Y al cerrarse se pierden las
   * cuentas del chofer: ninguna otra pantalla las lee, asi que el pasajero se
   * quedaria otra vez sin saber a donde pagar. Se traga el evento: de estas dos
   * pantallas solo se sale por sus botones.
   */
  useEffect(() => {
    if (Platform.OS !== 'android' || (!cierreDePago && !cobroPendiente)) return;
    const sub = BackHandler.addEventListener('hardwareBackPress', () => true);
    return () => sub.remove();
  }, [cierreDePago, cobroPendiente]);

  useEffect(() => {
    if (Platform.OS !== 'android' || !pinModalVisible) return;
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      setPinModalVisible(false);
      setPinEntry('');
      return true;
    });
    return () => sub.remove();
  }, [pinModalVisible]);

  /**
   * Arranca el viaje con el PIN que el pasajero le dictó al chofer.
   *
   * Acepta el PIN por parámetro porque se llama desde el propio campo de texto
   * en cuanto se teclea el cuarto dígito: en ese instante `pinEntry` todavía no
   * refleja la última tecla (el estado de React se aplica después), así que
   * leerlo daría solo 3 dígitos y saldría "PIN incompleto" sin motivo.
   */
  const submitStartWithPin = useCallback(async (pinParam?: string) => {
    // H44 (auditoría 16-sep-2026) — un solo envío del PIN a la vez. Es un ref y
    // no `pinSubmitting`: ese estado no está en las dependencias de este
    // callback y aquí dentro valdría el de hace un render. La validación del
    // PIN no cambia: sigue siendo entera del servidor.
    if (enviandoPinRef.current) return;
    if (!currentRideId) return;
    const pin = String(pinParam ?? pinEntry).replace(/[^0-9]/g, '');
    if (pin.length !== 4) {
      Alert.alert('PIN incompleto', 'Pídele al pasajero su código de 4 dígitos.');
      return;
    }
    enviandoPinRef.current = true;
    setPinSubmitting(true);
    try {
      // El PIN se verifica en el SERVIDOR (Cloud Function startRideWithPin).
      // Antes se comparaba en el teléfono y el chofer podía saltárselo por
      // completo — además el PIN estaba en el doc del viaje, que él lee.
      await callStartRideWithPin({ rideId: currentRideId, pin });
      setPinModalVisible(false);
      setPinEntry('');
      vibrarExito();
    } catch (e: any) {
      const mensaje = String(e?.message ?? '');
      const codigo = String(e?.code ?? '');
      if (mensaje.includes('PIN_MISMATCH')) {
        // Se vacía la caja para que pueda volver a teclear de inmediato. Con el
        // envío automático al cuarto dígito, dejarla llena obligaría a borrar a
        // mano y, peor, un dígito repetido gastaría intentos del tope de 8.
        setPinEntry('');
        Alert.alert('PIN incorrecto', 'El código no coincide. Verifica con el pasajero e intenta de nuevo.');
      } else if (mensaje.includes('RIDE_WITHOUT_PIN')) {
        // ── REINTENTAR NO SIRVE: SE CIERRA EL TECLADO ─────────────────────
        // El servidor rechaza arrancar un viaje que no tiene código de recogida.
        // El mensaje genérico decía "Intenta de nuevo" con el pasajero ya
        // sentado en el carro: mandaba al chofer a repetir algo que no puede
        // funcionar por más veces que lo teclee. Se le dice lo único que
        // resuelve —que el pasajero cancele y vuelva a pedir el viaje, que así
        // se genera un código nuevo— y se cierra la ventana, porque dejarla
        // abierta es invitarlo a insistir.
        console.warn('[startRide] viaje sin código de recogida:', mensaje);
        setPinModalVisible(false);
        setPinEntry('');
        Alert.alert(
          'Este viaje no tiene código',
          'No se puede iniciar. Pídele al pasajero que lo cancele y lo solicite de nuevo: así se genera un código nuevo y arrancan sin problema.'
        );
      } else if (codigo.includes('resource-exhausted') || mensaje.includes('Demasiados intentos')) {
        // Intentos agotados: teclear otra vez tampoco sirve. La ventana se deja
        // abierta —el chofer puede cerrarla él— pero el texto ya no promete que
        // reintentar vaya a servir de algo.
        console.warn('[startRide] intentos de PIN agotados:', mensaje);
        setPinEntry('');
        Alert.alert(
          'Se agotaron los intentos',
          'Este viaje bloqueó el código por demasiados intentos fallidos. Escribe a soporte para desbloquearlo; volver a teclearlo no va a funcionar.'
        );
      } else {
        console.warn('[startRide] error:', mensaje, codigo);
        Alert.alert('Error', 'No pudimos iniciar el viaje. Intenta de nuevo.');
      }
    } finally {
      enviandoPinRef.current = false;
      setPinSubmitting(false);
    }
  }, [currentRideId, pinEntry]);

  /**
   * H63 — lo que pasa DESPUÉS de cerrar un viaje o un envío, en un solo sitio:
   * vibración, GPS a reposo y la pantalla de calificar. Lo comparten el botón
   * de «Completar viaje» y el cierre del envío con código, para que los dos
   * caminos terminen exactamente igual.
   */
  /**
   * EL CIERRE DE UN VIAJE, EN UN SOLO SITIO.
   *
   * Esto vivia escrito a mano dentro de UNO de los tres `listenToRideStatus`.
   * Los otros dos —los del camino de reanudar, que es el que corre cuando
   * Android mata la app y el usuario vuelve a abrirla, o sea el mas comun en un
   * telefono de gama baja— seguian yendo directo a las estrellas: el pasajero
   * sin ver a donde pagar y al chofer sin preguntarle si cobro. El fallo
   * reportado el 29-sep habria seguido vivo justo por ahi.
   *
   * Devuelve true si abrio el cierre de pago; false si no habia nada que cobrar
   * y quien llama debe seguir con las estrellas.
   */
  const abrirCierreDePagoSiHaceFalta = useCallback((data: any): boolean => {
    const metodoFinal = String(data?.paymentMethod ?? 'CASH').toUpperCase();
    const soyElPasajero =
      !!data?.passengerId && data.passengerId === useAppStore.getState().userFirebaseUid;
    // Si la app cubre el viaje (primer viaje gratis, cupon de lealtad) NO se le
    // pide un peso a nadie. Ver el comentario largo de la rama COMPLETED.
    const loCubreLaApp = data?.cubiertoPorLaApp === true;
    if (!soyElPasajero || metodoFinal === 'CARD' || loCubreLaApp) return false;
    setCierreDePago({
      metodo: metodoFinal,
      monto: Number(data?.final_price ?? data?.finalPrice ?? data?.offerPrice ?? 0) || 0,
      cuentas: driverBankAccountsRef.current || [],
      chofer: String(data?.driverName ?? 'tu chofer'),
    });
    return true;
  }, []);

  /**
   * La pregunta del cobro al chofer, tambien en un solo sitio (ver
   * `abrirCierreDePagoSiHaceFalta`). Devuelve true si la abrio.
   */
  const abrirCobroDelChoferSiHaceFalta = useCallback((data: any, rideId: string): boolean => {
    const soyElChofer =
      !!data?.driverId && data.driverId === useAppStore.getState().userFirebaseUid;
    const metodo = String(data?.paymentMethod ?? 'CASH').toUpperCase();
    // Con tarjeta cobra Stripe, no el chofer. Y si la app cubre el viaje, el
    // chofer cobra de la app, no del pasajero: preguntarle si le pagaron ahi
    // solo lo confunde.
    if (!soyElChofer || !rideId || metodo === 'CARD' || data?.cubiertoPorLaApp === true) return false;
    setCobroPendiente({
      rideId,
      monto: Number(data?.final_price ?? data?.finalPrice ?? data?.offerPrice ?? 0) || 0,
      metodo,
      pasajero: data,
    });
    return true;
  }, []);

  const rematarCierre = useCallback((viajeCerrado: string | null, pasajero: any) => {
    vibrarExito();
    locationService.setTrackingMode('IDLE').catch(() => {});
    if (!viajeCerrado) return;

    // EL COBRO PRIMERO, LAS ESTRELLAS DESPUES.
    setCalifPasajeroViaje({
      rideId: viajeCerrado,
      nombre: String(pasajero?.passengerName ?? '') || 'Pasajero',
      foto: String(pasajero?.passengerPhoto ?? ''),
    });

    if (!abrirCobroDelChoferSiHaceFalta(pasajero, viajeCerrado)) {
      setCalifPasajeroVisible(true);
    }
  }, [abrirCobroDelChoferSiHaceFalta]);

  /** Responde la pregunta del cobro y pasa a calificar. */
  const responderCobro = useCallback(async (cobrado: boolean) => {
    const pend = cobroPendiente;
    if (!pend || confirmandoCobro) return;
    setConfirmandoCobro(true);
    try {
      await callConfirmarCobroDelViaje({ rideId: pend.rideId, cobrado });
    } catch (e: any) {
      // Que no se quede atrapado por un fallo de red: lo que no puede pasar es
      // que el chofer no pueda salir de esta pantalla.
      console.warn('[cobro] no se pudo registrar:', e?.message);
    } finally {
      setConfirmandoCobro(false);
      setCobroPendiente(null);
      setCalifPasajeroVisible(true);
    }
  }, [cobroPendiente, confirmandoCobro]);

  /**
   * H63 — EL CHOFER CIERRA EL ENVÍO CON EL CÓDIGO QUE LE DICTA QUIEN RECIBE.
   * No escribe el estado: lo pide a `completarEnvioConPin`, que compara el
   * código con Admin SDK. Las reglas no le dejan escribir COMPLETED en un
   * envío, así que este es su único camino (el otro es que el remitente lo
   * confirme desde su app).
   */
  const cerrarEnvioConPin = useCallback(async (pinTecleado?: string) => {
    if (!currentRideId || cerrandoEnvio) return;
    const pin = String(pinTecleado ?? entradaPinEntrega).replace(/[^0-9]/g, '');
    if (pin.length !== 4) return;
    setCerrandoEnvio(true);
    try {
      const viajeCerrado = currentRideId;
      const pasajero = driverCurrentRide;
      await callCompletarEnvioConPin({ rideId: viajeCerrado, pin });
      setModalPinEntrega(false);
      setEntradaPinEntrega('');
      rematarCierre(viajeCerrado, pasajero);
    } catch (e: any) {
      const mensaje = mensajeDeError(e, 'No pudimos cerrar el envío.');
      const codigo = String(e?.message ?? '');
      setEntradaPinEntrega('');
      if (codigo.includes('PIN_MISMATCH')) {
        Alert.alert(
          'Código incorrecto',
          'Ese no es el código de entrega. Pídeselo otra vez a quien recibe el paquete.'
        );
      } else if (codigo.includes('SIN_PIN_DE_ENTREGA')) {
        setModalPinEntrega(false);
        Alert.alert(
          'Este envío no trae código',
          'Pídele a quien envió el paquete que confirme la entrega desde su app. Así se cierra el envío.'
        );
      } else {
        Alert.alert('No se pudo cerrar', mensaje);
      }
    } finally {
      setCerrandoEnvio(false);
    }
  }, [currentRideId, entradaPinEntrega, cerrandoEnvio, driverCurrentRide, rematarCierre]);

  const handleCompleteRide = useCallback(async () => {
    if (!currentRideId) return;
    // H63 — en un envío no hay pasajero que llegue: hay un paquete que se
    // entrega. Preguntarlo como viaje invitaba a confirmar sin pensar.
    const esEnvio = String(driverCurrentRide?.serviceType ?? '') === 'DELIVERY';
    // H63 — un envío ya no se cierra con un «sí» en el teléfono del chofer:
    // lo cierra el código que dicta quien recibe el paquete (o el remitente
    // desde su app). Este botón abre el teclado de ese código.
    if (esEnvio) {
      setEntradaPinEntrega('');
      setModalPinEntrega(true);
      return;
    }
    Alert.alert(
      esEnvio ? 'Completar envío' : 'Completar viaje',
      esEnvio
        ? '¿Ya entregaste el paquete en su destino? Confirma solo si lo recibió la persona indicada. Al confirmar se cerrará el envío.'
        : '¿El pasajero llegó a su destino? Al confirmar se cerrará el viaje y se calificará al pasajero.',
      [
        { text: 'Aún no', style: 'cancel' },
        {
          text: 'Sí, completar',
          onPress: async () => {
            try {
              // El viaje que se acaba de cerrar, guardado ANTES de que
              // `currentRideId` se limpie: sin esto la pantalla de calificar se
              // abriría sin saber a quién califica.
              const viajeCerrado = currentRideId;
              const pasajero = driverCurrentRide;
              await firestoreService.completeRide(currentRideId);
              // H28 (auditoría 16-sep-2026) — viaje cerrado, GPS a modo espera,
              // y la calificación del pasajero, que es lo que el diálogo promete
              // dos líneas más arriba (es opcional: se puede omitir y seguir).
              rematarCierre(viajeCerrado, pasajero);
            } catch (e: any) {
              console.warn('[completeRide] error:', e?.message);
              Alert.alert('Error', 'No pudimos completar el viaje.');
            }
          },
        },
      ]
    );
  }, [currentRideId, driverCurrentRide, rematarCierre]);

  // PIN DE VIAJE (Uber-style) — Ahora el viaje NO lo arranca el pasajero: lo
  // arranca el CHOFER ingresando el PIN de 4 dígitos que el pasajero le da.
  // Antes el pasajero confirmaba identidad y eso auto-iniciaba el viaje, lo
  // que se saltaba el PIN. Ahora `onPassengerConfirm` solo confirma la
  // identidad visual y le recuerda al pasajero que le diga su PIN al chofer.
  const handlePassengerConfirmStart = useCallback(() => {
    if (!currentRideId || rideStatus !== 'ARRIVED') return;
    vibrarExito();
    if (activeRidePin) {
      Alert.alert(
        'Dale tu PIN al chofer',
        `Para iniciar el viaje, dile a tu chofer este código: ${activeRidePin}`,
        [{ text: 'Entendido' }]
      );
    }
  }, [currentRideId, rideStatus, activeRidePin]);

  // FIX COMPARTIR UBICACIÓN (#4) — El pasajero comparte su ubicación en vivo con
  // un familiar/amigo (link de Google Maps con sus coordenadas actuales) por
  // WhatsApp/SMS/lo que elija. Toma el GPS fresco al momento de tocar.
  /**
   * Le manda el PIN por WhatsApp a quien se va a montar.
   *
   * Las reglas de Firestore solo dejan leer el PIN a quien PIDIÓ el viaje, así
   * que cuando el viaje es para otra persona ella no lo tiene y el chofer no
   * puede arrancar. Se intenta abrir WhatsApp directamente; si no está
   * instalado, se cae a la hoja de compartir del sistema en vez de dejar el
   * botón mudo.
   */
  const compartirPinPorWhatsApp = useCallback(async (pin: string, direccion: string) => {
    const texto = direccion
      ? `Te pedí un viaje en Go Safely. Recogida en ${direccion}. Dile al chofer este PIN para que pueda arrancar: ${pin}`
      : `Te pedí un viaje en Go Safely. Dile al chofer este PIN para que pueda arrancar: ${pin}`;
    try {
      const url = `whatsapp://send?text=${encodeURIComponent(texto)}`;
      const puede = await Linking.canOpenURL(url);
      if (puede) {
        await Linking.openURL(url);
        return;
      }
    } catch {
      // Sin WhatsApp instalado o bloqueado por el SO: seguimos al plan B.
    }
    try {
      await Share.share({ message: texto, title: 'PIN del viaje' });
    } catch (e: any) {
      console.warn('[compartirPin] no se pudo compartir:', e?.message);
    }
  }, []);

  /** H63 — pasarle el código de entrega a quien va a recibir el paquete. */
  const compartirCodigoDeEntregaPorWhatsApp = useCallback(async (codigo: string) => {
    const texto = `Te mandé un paquete por Go Safely. Cuando llegue el chofer, dile este código para que pueda cerrar la entrega: ${codigo}`;
    try {
      const url = `whatsapp://send?text=${encodeURIComponent(texto)}`;
      if (await Linking.canOpenURL(url)) {
        await Linking.openURL(url);
        return;
      }
    } catch {
      // Sin WhatsApp instalado o bloqueado por el SO: seguimos al plan B.
    }
    try {
      await Share.share({ message: texto, title: 'Código de entrega' });
    } catch (e: any) {
      console.warn('[compartirCodigoEntrega] no se pudo compartir:', e?.message);
    }
  }, []);

  /**
   * H63 — la salida del remitente. Existe porque quien recibe puede no tener el
   * código a mano (o no usar la app): sin ella, un paquete entregado de verdad
   * se quedaría abierto para siempre. La decide quien envió, nunca el chofer.
   */
  const confirmarEntregaDelPaquete = useCallback(() => {
    if (!currentRideId || confirmandoEntrega) return;
    Alert.alert(
      'Cerrar el envío',
      '¿Confirmas que el paquete ya lo recibió la persona indicada? Al cerrarlo se le paga el envío al chofer.',
      [
        { text: 'Todavía no', style: 'cancel' },
        {
          text: 'Sí, ya lo recibieron',
          onPress: async () => {
            setConfirmandoEntrega(true);
            try {
              await callConfirmarEntregaDelEnvio({ rideId: currentRideId });
              vibrarExito();
            } catch (e: any) {
              Alert.alert('No se pudo cerrar', mensajeDeError(e, 'No pudimos cerrar el envío. Intenta de nuevo.'));
            } finally {
              setConfirmandoEntrega(false);
            }
          },
        },
      ]
    );
  }, [currentRideId, confirmandoEntrega]);

  const handleShareLiveLocation = useCallback(async () => {
    try {
      let lat = (currentLocation as any)?.lat;
      let lng = (currentLocation as any)?.lng;
      // Intentar un fix fresco; si falla, usamos el último conocido.
      try {
        const fresh = await locationService.getCurrentLocation?.(true);
        if (fresh?.coords && typeof fresh.coords.latitude === 'number') {
          lat = fresh.coords.latitude; lng = fresh.coords.longitude;
        }
      } catch {}
      if (typeof lat !== 'number' || typeof lng !== 'number') {
        Alert.alert('Ubicación no disponible', 'No pudimos obtener tu ubicación. Activa el GPS e intenta de nuevo.');
        return;
      }
      const mapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
      await Share.share({
        message: `Estoy aquí en tiempo real (Go Safely). Sigue mi ubicación: ${mapsUrl}`,
        url: mapsUrl,
        title: 'Mi ubicación en vivo',
      });
    } catch (e: any) {
      console.warn('[shareLiveLocation] error:', e?.message);
    }
  }, [currentLocation]);

  // FIX PERF (#1) — Restaurar el React.memo de <LiveMapContainer>.
  // Antes se le pasaba `onVehiclePress={(v)=>{...}}` (arrow inline) y
  // `pickupCoord={... ? {latitude,longitude} : null}` (objeto literal): ambos
  // creaban una referencia NUEVA en cada render del padre, anulando el memo y
  // forzando el re-render del mapa (y su parpadeo) en cada tick de estado.
  // Con useCallback/useMemo las referencias quedan estables y el mapa solo
  // se re-renderiza cuando sus datos realmente cambian.
  const handleMapVehiclePress = useCallback((v: any) => {
    // ── CON UNA SOLICITUD VIVA NO SE FIJA NINGÚN CHOFER ──────────────────
    //
    // Las cuatro hojas del pasajero se excluyen entre sí, y había un estado
    // que NINGUNA cubría: `selectedDriver` puesto con la solicitud viva. El
    // formulario exige `!selectedDriver`, la ficha «Chofer Cercano» exige
    // `!isSearching && !currentRideId`, la hoja de ofertas exige
    // `incomingDriverOffers || isActiveTrip`. Con las cuatro apagadas el
    // pasajero se quedaba con el mapa pelado: sin radar y sin botón de
    // cancelar, con su solicitud viva en el servidor.
    //
    // Y era alcanzable de un toque: el marcador de los carritos dice «Toca
    // para seleccionar», y estando el radar girando esto lo fijaba.
    //
    // Se cierra AQUÍ y no en el render porque este es el ÚNICO escritor de
    // `selectedDriver` que se alcanza con la solicitud viva: cerrarlo hace
    // imposible ese estado, en vez de taparlo con otra condición más.
    if (role === 'PASSENGER' && !incomingDriverOffers && !isActiveTrip && !isSearching && !currentRideId) {
      setSelectedDriver(v);
    }
  }, [role, incomingDriverOffers, isActiveTrip, isSearching, currentRideId]);

  const cLat = (currentLocation as any)?.lat;
  const cLng = (currentLocation as any)?.lng;
  /**
   * Punto de RECOGIDA.
   *
   * Antes era siempre el GPS del propio teléfono, sin mirar el rol: en el
   * móvil del chofer, el pin "Punto de recogida" le salía encima de sí mismo,
   * así que el mapa no tenía un segundo punto real que encuadrar. Para el
   * chofer, la recogida es el ORIGEN del viaje que aceptó, no dónde está él.
   */
  /**
   * LAS SOLICITUDES VIVAS, PARA PINTARLAS EN EL MAPA.
   *
   * Lo pidió el fundador: «ver en el mapa dónde hay más solicitudes de viaje».
   * Son las que su teléfono YA tiene cargadas en el feed y que ya puede
   * aceptar una por una, así que pintarlas no le enseña nada que no viera: lo
   * que cambia es que las ve TODAS a la vez y dónde están, en vez de de una en
   * una en la tarjeta.
   *
   * No es un mapa de calor del histórico: con los viajes que hay hoy, un mapa
   * histórico no señalaría una zona sino la casa de alguien. Y para trabajar
   * le sirve más saber dónde hay gente esperando AHORA.
   *
   * Solo con el viaje libre: durante un viaje el mapa es la ruta, y llenarlo
   * de chinchetas de trabajo que no puede tomar sería ruido encima de lo único
   * que importa en ese momento.
   */
  const solicitudesEnMapa = useMemo(() => {
    if (role !== 'DRIVER' || isActiveTrip) return [];
    return driverPendingRides
      .map((r: any) => {
        const lat = Number(r?.origin_lat ?? r?.originLat);
        const lng = Number(r?.origin_lng ?? r?.originLng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        const km = haversineKm(currentLocation, r);
        return {
          id: String(r.id),
          latitude: lat,
          longitude: lng,
          enZona: esDeMiZona(r),
          precio: Number(r?.offerPrice ?? 0) || 0,
          km,
        };
      })
      .filter(Boolean) as {
        id: string; latitude: number; longitude: number;
        enZona: boolean; precio: number; km: number | null;
      }[];
    // `radioZonaKm` entra aunque `esDeMiZona` lo lea por ref: sin él, mover el
    // control no repintaría los colores de las chinchetas hasta el próximo
    // cambio del feed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, isActiveTrip, driverPendingRides, radioZonaKm, currentLocation]);

  const pickupCoordMemo = useMemo(() => {
    if (!isActiveTrip) return null;
    const esChofer = role === 'DRIVER' || role === 'DELIVERY_DRIVER';
    if (esChofer) {
      const oLat = driverCurrentRide?.origin_lat ?? driverCurrentRide?.originLat;
      const oLng = driverCurrentRide?.origin_lng ?? driverCurrentRide?.originLng;
      return typeof oLat === 'number' && typeof oLng === 'number'
        ? { latitude: oLat, longitude: oLng }
        : null;
    }
    // ── EL PIN VA DONDE SE RECOGE, NO DONDE ESTÁ EL TELÉFONO ───────────────
    // Si la persona eligió otra recogida (el viaje es para su mamá), el pin
    // tiene que caer sobre ESA dirección. Con el GPS, pediría el carro para su
    // mamá y vería el pin encima de su propia casa: el mapa mintiendo.
    //
    // PRIMERO EL DOCUMENTO DEL VIAJE. `origenElegido` es estado de pantalla y
    // nadie lo rehidrata: al reabrir la app valía null y el pin volvía a caer
    // sobre el GPS, o sea que el fallo reaparecía entero en cuanto el teléfono
    // se bloqueaba un rato. `recogidaDelViaje` sale de `origin_lat`/`origin_lng`,
    // que son inmutables por regla y sobreviven al reinicio. `origenElegido`
    // queda para el rato entre elegir la dirección y que el viaje exista, y el
    // GPS solo si el viaje no trae coordenadas.
    if (recogidaDelViaje) {
      return { latitude: recogidaDelViaje.lat, longitude: recogidaDelViaje.lng };
    }
    if (origenElegido) {
      return { latitude: origenElegido.lat, longitude: origenElegido.lng };
    }
    return typeof cLat === 'number' ? { latitude: cLat, longitude: cLng } : null;
  }, [isActiveTrip, role, driverCurrentRide, cLat, cLng, origenElegido, recogidaDelViaje]);
  /**
   * La recogida que la pantalla puede NOMBRAR (con su dirección escrita).
   * Manda el documento; `origenElegido` cubre el rato en que el viaje aún no
   * existe. Null cuando no hay ninguna de las dos: sin dato no se dice nada.
   */
  const recogidaMostrada = useMemo(() => {
    if (recogidaDelViaje) return recogidaDelViaje;
    if (origenElegido) {
      return { lat: origenElegido.lat, lng: origenElegido.lng, texto: origenElegido.texto };
    }
    return null;
  }, [recogidaDelViaje, origenElegido]);
  /**
   * ¿LA RECOGIDA DE ESTE VIAJE ESTÁ LEJOS DE DONDE ESTOY?
   *
   * De aquí cuelgan el PIN grande y el botón de mandarlo por WhatsApp: quien se
   * va a montar no puede leer el PIN en las reglas de Firestore (solo lo lee
   * quien PIDIÓ el viaje), así que hay que pasárselo a mano.
   *
   * El documento del viaje NO guarda la intención de "esto es para otra
   * persona", y no se inventa: lo que sí se puede MEDIR es que el punto de
   * recogida no es donde está el teléfono. Cuando eso pasa, las dos cosas que
   * dice la caja son ciertas —la recogida es en otra dirección y quien se monta
   * necesita el código—, sea porque el viaje es para un familiar o porque quien
   * lo pidió se movió. Sin posición no se afirma nada.
   */
  const recogidaLejosDeMi = useMemo(() => {
    if (!recogidaMostrada) return false;
    // Elegida a mano en esta sesión: certeza, no hace falta medir.
    if (origenElegido) return true;
    if (typeof cLat !== 'number' || typeof cLng !== 'number') return false;
    const km = haversineKm({ lat: cLat, lng: cLng }, { lat: recogidaMostrada.lat, lng: recogidaMostrada.lng });
    // 250 m: por encima del error del GPS urbano y de una caminata hasta la
    // esquina, por debajo de cualquier "se lo pedí a mi mamá".
    return km != null && km > 0.25;
  }, [recogidaMostrada, origenElegido, cLat, cLng]);
  const driverCoordMemo = useMemo(
    () => (isActiveTrip ? driverLiveCoord : null),
    [isActiveTrip, driverLiveCoord]
  );

  // ── RUTA REAL POR LAS CALLES (la línea tipo InDriver) ──────────────────
  //
  // El backend ya existía y está DESPLEGADO: `getRoute`
  // (functions/src/directions.ts) con caché de 1 h, rate-limit y decodificador
  // de polyline oficial. `order-tracking.tsx` ya lo usa con éxito. Lo único
  // que faltaba era pedirla desde aquí: el `<MapPolyline>` lleva cableado el
  // prop `routeCoordinates` desde siempre, y el único sitio que monta el mapa
  // nunca se lo pasaba. Enchufe puesto, cable ausente.
  //
  // Va DESPUÉS de `driverCoordMemo`/`pickupCoordMemo` a propósito: son `const`,
  // y usarlas antes de su declaración revienta en runtime.
  //
  // Se re-pide SOLO cuando el chofer se ha movido más de 500 m (mismo umbral
  // que order-tracking): cada llamada a Directions se factura, y pedirla en
  // cada tick del GPS quemaría la cuota en minutos.
  const [rutaPorCalles, setRutaPorCalles] = useState<any[] | undefined>(undefined);
  /**
   * LO QUE SE ESTABA TIRANDO A LA BASURA.
   *
   * La misma respuesta de `getRoute` que trae la línea trae `durationText` y
   * `distanceText` calculados por Google con el tráfico real. Se leía solo
   * `coordinates` y se descartaba el resto, así que el ETA que veía la gente
   * salía de una LÍNEA RECTA a 30 km/h fijos. En Santo Domingo eso subestima
   * siempre: "Llega en 4 min" son doce. Es la misma llamada y el mismo dinero.
   */
  const [rutaInfo, setRutaInfo] = useState<{ durationText: string; distanceText: string } | null>(null);
  /**
   * Firma de la ruta QUE YA ESTÁ PINTADA: origen **y destino**.
   *
   * Dos cosas que arregla:
   *
   * 1) El destino va en la firma. Antes solo guardaba el origen, así que al
   *    pasar a IN_PROGRESS —cuando la línea deja de apuntar a la recogida y
   *    pasa a apuntar al destino— el chofer acababa de detenerse y no se había
   *    movido 500 m: el efecto salía por `return` y la línea seguía apuntando
   *    HACIA ATRÁS, con el pasajero ya a bordo.
   *
   * 2) **Se escribe SOLO cuando la respuesta llega bien.** Antes se marcaba
   *    ANTES de llamar. Si el efecto se re-evaluaba mientras la llamada iba en
   *    vuelo (un tick del GPS bastaba), la respuesta se descartaba y el ciclo
   *    siguiente salía por el guardado de 500 m sin pedir nada: la línea no
   *    volvía JAMÁS. Con la marca puesta al final, un fallo o una cancelación
   *    dejan el camino libre para volver a pedirla.
   */
  const ultimoOrigenRuta = useRef<{ lat: number; lng: number; destino: string } | null>(null);
  /**
   * Firma de la petición EN VUELO.
   *
   * Es el contrapeso de lo anterior: si la marca solo se pone al recibir, hay
   * una ventana en la que el efecto se re-evalúa (el GPS del chofer emite cada
   * pocos segundos) y volvería a pedir la MISMA ruta una y otra vez. Con esto
   * se pide una sola vez y las re-evaluaciones se quedan calladas.
   */
  const rutaEnVuelo = useRef<{ lat: number; lng: number; destino: string } | null>(null);
  /**
   * El destino que la pantalla quiere AHORA MISMO. Es el testigo de
   * cancelación: una respuesta cuyo destino ya no es este llega tarde y se tira.
   *
   * POR QUÉ NO SE USA UN `cancelado = true` EN LA LIMPIEZA DEL EFECTO: con el
   * chofer en marcha el efecto se re-evalúa en cada tick del GPS, así que ese
   * testigo daría por cancelada TODA petición y la línea nunca se pintaría —
   * o, peor, se re-pediría en bucle. Solo se cancela de verdad si cambió el
   * sitio al que vamos.
   */
  const destinoDeseadoRuta = useRef<string | null>(null);
  /**
   * Instante (ms) a partir del cual se puede reintentar tras un fallo. Sin
   * este enfriamiento, un fallo permanente —la API de Directions deshabilitada
   * en la clave, por ejemplo— se convertiría en una llamada por cada tick del
   * GPS durante todo el viaje.
   */
  const proximoIntentoRuta = useRef(0);

  // Contador que reabre la puerta del efecto de la ruta tras un fallo. Sube
  // solo desde `programarReintentoDeRuta`; su unico trabajo es ser una
  // dependencia que cambie de valor, para que el efecto vuelva a correr sin
  // esperar a que el chofer se mueva 500 m. Ver el comentario de esa funcion.
  const [reintentoRuta, setReintentoRuta] = useState(0);
  const temporizadorRuta = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * La franja discreta que dice qué pasó con la línea.
   *
   * Hasta ahora el fallo era MUDO (`console.warn` y nada más): desde el
   * teléfono era imposible saber por qué no había línea, así que cada prueba
   * de calle era a ciegas. Guardamos el motivo real y lo enseñamos.
   */
  const [avisoRuta, setAvisoRuta] = useState<{ tono: 'info' | 'espera' | 'error'; texto: string } | null>(null);

  /**
   * Último estado del viaje que ya se anunció. El listener del ride dispara en
   * CADA snapshot del documento, no solo cuando cambia el estado; sin este ref
   * el aviso sonoro se repetiría una y otra vez.
   */
  const estadoAnteriorDelViaje = useRef<'ACCEPTED' | 'ARRIVED' | 'IN_PROGRESS' | null>(null);
  /**
   * LOS DOS EXTREMOS DE LA LÍNEA, SEGÚN QUIÉN MIRA.
   *
   * Vive fuera del efecto a propósito: la RECTA DE RESPALDO del mapa necesita
   * exactamente los mismos dos puntos. Antes esa recta exigía `driverCoord`,
   * un estado que solo se llena cuando el rol es PASSENGER, así que en el
   * teléfono del chofer era null SIEMPRE y él nunca tenía línea de reserva: si
   * la ruta por calles fallaba, veía el mapa pelado. El pasajero siempre veía
   * algo; el chofer, nada. Con los extremos aquí, los dos perfiles comparten
   * la misma recta de respaldo.
   *
   * PASAJERO ESPERANDO (ACCEPTED/ARRIVED): del chofer que viene hasta el punto
   * de recogida.
   * PASAJERO A BORDO (IN_PROGRESS): hasta SU DESTINO. Ver abajo.
   * PASAJERO ELIGIENDO: de su origen (el elegido, si pide para otra persona)
   * hasta el destino que acaba de tocar.
   * CHOFER: de SU POSICIÓN VIVA hasta la recogida (antes de recoger) o hasta
   * el destino (con el pasajero a bordo).
   *
   * DEVUELVE SIEMPRE UN OBJETO, nunca null: cuando falta un extremo hace falta
   * poder decir CUÁL falta. Antes devolvía null y el efecto de abajo borraba la
   * ruta, el ETA y el aviso de una sola vez — silencio absoluto, que es lo peor
   * que puede hacer un mapa.
   */
  const extremosDeLaLinea = useMemo<{
    desde: { latitude: number; longitude: number } | null;
    hasta: { latitude: number; longitude: number } | null;
    /** Por qué no hay línea, para decirlo. `null` = no hacía falta ninguna. */
    motivo: { codigo: 'SIN_POSICION_CHOFER' | 'SIN_COORDENADAS'; texto: string } | null;
  }>(() => {
    const esChofer = role === 'DRIVER' || role === 'DELIVERY_DRIVER';
    let desde: { latitude: number; longitude: number } | null = driverCoordMemo;
    let hasta: { latitude: number; longitude: number } | null = pickupCoordMemo;
    let motivo: { codigo: 'SIN_POSICION_CHOFER' | 'SIN_COORDENADAS'; texto: string } | null = null;
    if (esChofer && isActiveTrip && driverCurrentRide) {
      // ── EL ORIGEN DE SU LÍNEA YA NO ESTÁ CONGELADO ─────────────────────
      // `currentLocation` no se vuelve a escribir mientras se conduce: la
      // línea salía de donde encendió la app. `miPosicionViva` es la posición
      // que `locationService` mantiene fresca en `driverLocations` durante el
      // viaje — la misma que ve el pasajero. `currentLocation` queda de
      // respaldo para el primer instante, antes del primer latido.
      const miLat = miPosicionViva?.lat ?? currentLocation?.lat;
      const miLng = miPosicionViva?.lng ?? currentLocation?.lng;
      desde = typeof miLat === 'number' && typeof miLng === 'number'
        ? { latitude: miLat, longitude: miLng }
        : null;
      // ── A DONDE APUNTA LA LINEA ────────────────────────────────────────
      // Antes apuntaba SIEMPRE al punto de recogida. Con el pasajero ya a
      // bordo, la linea verde y la camara seguian senalando hacia atras, a la
      // esquina donde lo recogio, en vez de a donde va. Con el viaje en marcha
      // el destino es lo unico que importa.
      //
      // Si el destino no tiene coordenada (mas de la mitad de los viajes
      // reales: el pasajero escribio la direccion a mano), se deja sin linea
      // en vez de dibujar una falsa hacia atras.
      if (rideStatus === 'IN_PROGRESS') {
        const dLat = driverCurrentRide.destinationLat;
        const dLng = driverCurrentRide.destinationLng;
        hasta = typeof dLat === 'number' && typeof dLng === 'number'
          ? { latitude: dLat, longitude: dLng }
          : null;
      } else {
        const oLat = driverCurrentRide.origin_lat ?? driverCurrentRide.originLat;
        const oLng = driverCurrentRide.origin_lng ?? driverCurrentRide.originLng;
        hasta = typeof oLat === 'number' && typeof oLng === 'number'
          ? { latitude: oLat, longitude: oLng }
          : null;
      }
      // Sin coordenada del destino no se dibuja nada — y se dice, que hasta
      // ahora esa ausencia era muda y parecía la app rota.
      if (!desde) {
        motivo = { codigo: 'SIN_POSICION_CHOFER', texto: 'Todavía no tenemos tu posición: revisa que el GPS esté encendido.' };
      } else if (!hasta) {
        motivo = {
          codigo: 'SIN_COORDENADAS',
          texto: rideStatus === 'IN_PROGRESS'
            ? 'Este viaje no trae el destino en coordenadas: usa la dirección escrita.'
            : 'Este viaje no trae la recogida en coordenadas: usa la dirección escrita.',
        };
      }
    } else if (!esChofer && isActiveTrip) {
      // ══ EL PASAJERO EN VIAJE ═══════════════════════════════════════════
      //
      // DOS FALLOS VIVÍAN AQUÍ, y los dos los reportó el fundador:
      //
      // 1) LA LÍNEA NUNCA APUNTABA AL DESTINO. `pickupCoordMemo` devuelve
      //    SIEMPRE el punto de recogida y el "hasta" solo cambiaba al destino
      //    dentro de la rama del chofer. Encadenado con la guarda de los 150 m:
      //    con los dos teléfonos juntos la distancia chofer→recogida es casi
      //    cero, entraba la guarda, y NO aparecía línea en toda la prueba. Ya a
      //    bordo, lo que el pasajero necesita ver es hacia dónde VA.
      //
      // 2) UN DATO QUE FALTA BORRABA TODA LA LÍNEA EN SILENCIO. Los dos
      //    extremos colgaban de la posición viva del chofer, que llega del OTRO
      //    teléfono: basta con que ese documento no exista todavía, o que la
      //    regla lo deniegue, para quedarse sin nada. Origen y destino SÍ se
      //    conocen (están en el viaje), así que con el pasajero a bordo se
      //    dibuja igual desde la recogida, y si de verdad no se puede trazar
      //    nada, se dice.
      const destino = destinoDelViaje
        ? { latitude: destinoDelViaje.lat, longitude: destinoDelViaje.lng }
        : (typeof destinationLat === 'number' && typeof destinationLng === 'number'
            ? { latitude: destinationLat, longitude: destinationLng }
            : null);
      if (rideStatus === 'IN_PROGRESS') {
        hasta = destino;
        desde = driverCoordMemo ?? pickupCoordMemo;
        if (!hasta) {
          motivo = { codigo: 'SIN_COORDENADAS', texto: 'Este viaje no trae el destino en coordenadas: no hay ruta que trazar.' };
        } else if (!desde) {
          motivo = { codigo: 'SIN_POSICION_CHOFER', texto: 'Todavía no recibimos la posición del chofer.' };
        }
      } else {
        desde = driverCoordMemo;
        hasta = pickupCoordMemo;
        if (!desde) {
          motivo = { codigo: 'SIN_POSICION_CHOFER', texto: 'Todavía no recibimos la posición del chofer — ya viene en camino.' };
        } else if (!hasta) {
          motivo = { codigo: 'SIN_COORDENADAS', texto: 'No tenemos el punto de recogida en coordenadas.' };
        }
      }
    } else if (!esChofer && !isActiveTrip) {
      // ── LA TERCERA LÍNEA, LA QUE FALTABA ────────────────────────────────
      // El pasajero elegía destino y no veía NADA: ni por dónde iría el viaje
      // ni cuánto tarda. `driverCoordMemo` y `pickupCoordMemo` valen null
      // fuera de un viaje activo, así que este efecto salía por la puerta de
      // atrás antes de pedir nada.
      //
      // La línea sale del ORIGEN ELEGIDO, no del GPS: si pide el viaje para su
      // mamá, la previsualización tiene que salir de casa de su mamá o el mapa
      // le estaría mintiendo mientras decide el precio.
      const miLat = origenElegido?.lat ?? currentLocation?.lat;
      const miLng = origenElegido?.lng ?? currentLocation?.lng;
      desde = typeof miLat === 'number' && typeof miLng === 'number'
        ? { latitude: miLat, longitude: miLng }
        : null;
      hasta = typeof destinationLat === 'number' && typeof destinationLng === 'number'
        ? { latitude: destinationLat, longitude: destinationLng }
        : null;
    }
    if (!desde || !hasta) return { desde: null, hasta: null, motivo };
    return { desde, hasta, motivo: null };
  }, [
    driverCoordMemo, pickupCoordMemo, role, isActiveTrip, driverCurrentRide, rideStatus,
    miPosicionViva?.lat, miPosicionViva?.lng,
    currentLocation?.lat, currentLocation?.lng,
    destinationLat, destinationLng, origenElegido, destinoDelViaje,
  ]);

  /**
   * La recta de respaldo del mapa: los mismos dos extremos, en el formato que
   * espera `<MapPolyline>`. Memoizada porque viaja como prop a un componente
   * con `React.memo`: un array nuevo en cada render volvería a rasterizar el
   * mapa entero (es el congelamiento que ya se corrigió una vez).
   */
  const rectaRespaldo = useMemo(
    // Solo durante un viaje activo, que es donde el pasajero YA tenía recta de
    // respaldo y el chofer no la tenía nunca. En la fase de elegir destino se
    // deja la pantalla como está: esta ronda no añade elementos al mapa donde
    // hoy no hay ninguno.
    () => (isActiveTrip && extremosDeLaLinea.desde && extremosDeLaLinea.hasta
      ? [extremosDeLaLinea.desde, extremosDeLaLinea.hasta]
      : null),
    [isActiveTrip, extremosDeLaLinea]
  );

  /**
   * EL RÓTULO DEL CHOFER ASIGNADO — donde Uber pone "Blanco Toyota Corolla · A123456".
   *
   * El marcador tenía `🚗`, "Tu chofer" y "Viene en camino" escritos a mano,
   * así que quien pedía un motor veía un carro y la burbuja no decía nada,
   * teniendo la marca, el modelo, el color y la placa en este mismo archivo.
   *
   * REGLA: cuando no hay dato, NO se inventa. Sin vehículo conocido esto
   * devuelve null y el mapa pinta el genérico de siempre.
   */
  const rotuloChofer = useMemo<RotuloChofer | null>(() => {
    if (!isActiveTrip || !selectedDriver) return null;
    const tipo = String(selectedDriver.vehicleType ?? selectedDriver.type ?? '').trim();
    const linea = [selectedDriver.vehicleColor, selectedDriver.vehicleBrand, selectedDriver.vehicleModel]
      .map((v: any) => String(v ?? '').trim())
      .filter((v: string) => v.length > 0)
      .join(' ');
    const placa = String(selectedDriver.licensePlate ?? '').trim();
    // Ni vehículo, ni marca, ni placa: nada que decir que sea verdad.
    if (!tipo && !linea && !placa) return null;
    return {
      emoji: emojiDeVehiculo(tipo),
      titulo: linea || 'Tu chofer',
      descripcion: placa ? `Placa ${placa}` : 'Viene en camino',
    };
  }, [isActiveTrip, selectedDriver]);

  useEffect(() => {
    const { desde, hasta, motivo } = extremosDeLaLinea;
    if (!desde || !hasta) {
      // ── FALTA UN EXTREMO: NO ES LO MISMO QUE "NO HAY VIAJE" ──────────────
      // Antes este camino borraba de golpe la ruta, el ETA y el aviso, sin
      // decir nada. Y se recorre en cuanto falta la posición viva del chofer,
      // que llega del OTRO teléfono: un documento que aún no existe, o una
      // regla que lo deniega, dejaban el mapa mudo con el viaje en marcha.
      //
      // Ahora: lo que YA está pintado se conserva mientras el hueco sea la
      // posición del chofer (la ruta sigue siendo verdad, solo falta el punto
      // que se mueve); si el hueco es una coordenada que no existe, la línea sí
      // se borra —apuntaría a un sitio equivocado— y en los dos casos se DICE.
      const esperandoAlChofer = motivo?.codigo === 'SIN_POSICION_CHOFER';
      if (!esperandoAlChofer) {
        setRutaPorCalles(undefined);
        setRutaInfo(null);
        ultimoOrigenRuta.current = null;
      }
      setAvisoRuta(motivo ? { tono: esperandoAlChofer ? 'espera' : 'error', texto: motivo.texto } : null);
      rutaEnVuelo.current = null;
      destinoDeseadoRuta.current = null;
      return;
    }

    // ── TRAYECTO CORTÍSIMO: DECIRLO, NO CALLARSE ──────────────────────────
    // Con origen y destino a metros, Google devuelve una polilínea vacía o de
    // un solo punto. Antes eso dejaba el mapa mudo y sin ningún aviso: es
    // exactamente lo que le pasó al fundador probando con los dos teléfonos
    // juntos, y lo llevó a pensar que la función estaba rota.
    const km = haversineKm(
      { lat: desde.latitude, lng: desde.longitude },
      { lat: hasta.latitude, lng: hasta.longitude }
    );
    if (km != null && km < 0.15) {
      setRutaPorCalles(undefined);
      setRutaInfo(null);
      setAvisoRuta({ tono: 'info', texto: 'Ya estás en el punto — quedan menos de 150 metros.' });
      ultimoOrigenRuta.current = null;
      rutaEnVuelo.current = null;
      destinoDeseadoRuta.current = null;
      return;
    }

    /**
     * PROGRAMA EL REINTENTO — la pieza que faltaba.
     *
     * Antes esto era solo `proximoIntentoRuta.current = Date.now() + 30_000`:
     * arma un enfriamiento y NADIE lo despierta. El efecto tiene una sola
     * dependencia (`extremosDeLaLinea`), así que la recuperación quedaba
     * colgada de que el chofer se moviera 500 m. Parado en un semáforo, la
     * línea recta se quedaba puesta todo el trayecto — que es exactamente lo
     * que se ve en la captura del fundador yendo a recoger.
     *
     * Ahora el temporizador existe: a los 30 s sube el contador, el efecto
     * vuelve a correr y se pide la ruta otra vez. Se guarda el temporizador en
     * un ref para no dejar varios corriendo a la vez.
     */
    const programarReintentoDeRuta = () => {
      proximoIntentoRuta.current = Date.now() + 30_000;
      if (temporizadorRuta.current) clearTimeout(temporizadorRuta.current);
      temporizadorRuta.current = setTimeout(() => {
        temporizadorRuta.current = null;
        // Solo si seguimos queriendo ir al mismo sitio. Si el viaje terminó o
        // cambió el destino, este reintento ya no le sirve a nadie.
        if (destinoDeseadoRuta.current) setReintentoRuta((n) => n + 1);
      }, 30_000);
    };

    // Firma del destino con 5 decimales (~1 m): suficiente para distinguir dos
    // destinos y estable frente al ruido del GPS.
    const firmaDestino = `${hasta.latitude.toFixed(5)},${hasta.longitude.toFixed(5)}`;
    destinoDeseadoRuta.current = firmaDestino;

    /** ¿Esta firma va al mismo sitio y desde menos de 500 m de distancia? */
    const mismaRuta = (ref: { lat: number; lng: number; destino: string } | null) => {
      if (!ref || ref.destino !== firmaDestino) return false;
      // ~111 km por grado de latitud: suficiente para un umbral de 500 m.
      return Math.hypot(desde.latitude - ref.lat, desde.longitude - ref.lng) * 111_000 < 500;
    };

    // EL GUARDADO DE 500 M SIGUE EN PIE. Sin él, la posición viva del chofer
    // dispararía una llamada facturada a Directions por cada tick del GPS.
    if (mismaRuta(ultimoOrigenRuta.current)) return;   // ya está pintada
    if (mismaRuta(rutaEnVuelo.current)) return;        // ya se está pidiendo
    // Enfriamiento tras un fallo: un error permanente (Directions no habilitada
    // en la clave) no puede convertirse en una llamada por segundo.
    if (Date.now() < proximoIntentoRuta.current) return;

    rutaEnVuelo.current = { lat: desde.latitude, lng: desde.longitude, destino: firmaDestino };
    // ⚠️ CON PLAZO. Sin él, una llamada que no resuelve —sin cobertura, que es
    //    justo cuando más falta hace la ruta— deja `rutaEnVuelo` puesto para
    //    siempre y la línea no se vuelve a pedir en toda la sesión. Al vencer
    //    cae en el `catch` de abajo, que ya programa el reintento.
    conPlazo(
      callGetRoute({
        origin: { lat: desde.latitude, lng: desde.longitude },
        destination: { lat: hasta.latitude, lng: hasta.longitude },
        mode: 'driving',
      }),
      15000,
      'trazar la ruta'
    )
      .then((r) => {
        // CANCELACIÓN: solo si mientras venía la respuesta cambió el sitio al
        // que vamos. Un simple tick del GPS ya NO la descarta.
        if (destinoDeseadoRuta.current !== firmaDestino) return;
        rutaEnVuelo.current = null;
        const coords = r.data?.coordinates;
        // DOS PUNTOS COMO MÍNIMO: con uno solo se pintaba una línea invisible
        // y parecía que la app no había hecho nada.
        if (Array.isArray(coords) && coords.length >= 2) {
          // getRoute devuelve {lat,lng}; react-native-maps quiere
          // {latitude,longitude}. Mismo mapeo que order-tracking.tsx:169.
          setRutaPorCalles(coords.map((c: any) => ({ latitude: c.lat, longitude: c.lng })));
          setAvisoRuta(null);
          // LA MARCA, AL FINAL Y SOLO SI SALIÓ BIEN.
          ultimoOrigenRuta.current = { lat: desde.latitude, lng: desde.longitude, destino: firmaDestino };
          // La duración y la distancia REALES venían en esta misma respuesta y
          // se tiraban. Ahora alimentan el ETA de los dos perfiles.
          const duracion = String(r.data?.durationText ?? '').trim();
          const distancia = String(r.data?.distanceText ?? '').trim();
          setRutaInfo(duracion ? { durationText: duracion, distanceText: distancia } : null);
        } else {
          setRutaPorCalles(undefined);
          setRutaInfo(null);
          setAvisoRuta({ tono: 'error', texto: 'No pudimos trazar la ruta — sigue el mapa.' });
          ultimoOrigenRuta.current = null;
          programarReintentoDeRuta();
        }
      })
      .catch((e: any) => {
        // EL MOTIVO SE GUARDA Y SE ENSEÑA. Antes esto era un `console.warn` y
        // nada más: desde el teléfono era imposible saber por qué no había
        // línea, así que cada prueba de calle salía a ciegas. `REQUEST_DENIED`
        // o `OVER_QUERY_LIMIT` se arreglan en Google Cloud, no aquí, y hay que
        // poder leerlos.
        const motivo = `${e?.code ?? ''} ${e?.message ?? ''}`.trim() || 'sin dato';
        console.warn('[map] no se pudo trazar la ruta:', motivo);
        if (destinoDeseadoRuta.current !== firmaDestino) return;
        // SE LIMPIA EN EL CATCH: si no, la marca quedaría puesta sin línea
        // pintada y el guardado de 500 m impediría volver a pedirla nunca.
        rutaEnVuelo.current = null;
        ultimoOrigenRuta.current = null;
        programarReintentoDeRuta();
        // EL MOTIVO SE GUARDA EN EL LOG, NO EN LA CARA DE NADIE. Se estaba
        // enseñando el `code` y el `message` crudos de Google (`REQUEST_DENIED`,
        // `OVER_QUERY_LIMIT`…) a alguien parado en la calle: no le dice nada,
        // no puede hacer nada con eso y parece que la app se rompió. Para
        // diagnosticar ya está el `console.warn` de arriba, que sigue llevando
        // el motivo entero.
        setAvisoRuta({
          tono: 'error',
          texto: 'No pudimos trazar la ruta — sigue el mapa.',
        });
      });
    // Una sola dependencia: el memo de arriba ya reúne rol, estado del viaje,
    // posición viva, origen elegido y destino. Menos superficie para carreras.
    // `reintentoRuta` está aquí a propósito: es lo que hace que un fallo se
    // pueda recuperar solo. Sin él, este efecto solo despierta cuando el chofer
    // se mueve 500 m.
  }, [extremosDeLaLinea, reintentoRuta]);


  // --- DESVÍO DE RUTA (chofer propone → pasajero aprueba/rechaza) ---
  const [deviationModal, setDeviationModal] = useState<{
    visible: boolean;
    role: 'DRIVER' | 'PASSENGER' | null;
    reason?: string;
  }>({ visible: false, role: null });

  /**
   * Devuelve `altoHoja` a 0 cuando NO hay ninguna hoja montada.
   *
   * `onLayout` solo dispara al montar y al cambiar de tamano: al DESMONTARSE no
   * avisa. Sin esto, en cuanto el chofer rechaza una solicitud o termina un
   * viaje, la tarjeta desaparece pero el mapa sigue reservandole el hueco, y
   * encuadra contra una tarjeta que ya no existe.
   */
  useEffect(() => {
    const hayHoja = incomingDriverOffers || isActiveTrip || incomingPassengerRequest || driverDeliveries.length > 0;
    if (!hayHoja) setAltoHoja(0);
  }, [incomingDriverOffers, isActiveTrip, incomingPassengerRequest, driverDeliveries.length]);

  /**
   * LOS PUNTOS QUE EL MAPA DEBE ABARCAR EN CADA FASE.
   *
   * Este es el corazón del arreglo del encuadre. Antes la cámara solo sabía
   * centrarse en un punto con zoom fijo, así que con el chofer a más de ~600 m
   * el otro extremo de la línea caía fuera de la pantalla y había que mover el
   * mapa a mano para verla.
   *
   * Devuelve `null` cuando no hay nada que encuadrar: en ese caso manda el
   * comportamiento de siempre y no se toca la cámara.
   */
  const puntosParaEncuadrar = useMemo(() => {
    const num = (v: any) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
    const punto = (la: any, lo: any) => {
      const a = num(la), o = num(lo);
      return a !== null && o !== null ? { latitude: a, longitude: o } : null;
    };
    // ── DESDE DONDE ESTÁ, NO DESDE DONDE ESTABA ──────────────────────────
    // La ronda anterior movió el ORIGEN DE LA LÍNEA a `miPosicionViva` pero
    // dejó aquí `currentLocation`, que es justo el estado que no se vuelve a
    // escribir mientras el chofer conduce: la línea salía de donde está y la
    // cámara encuadraba donde estaba, así que su propia línea se le salía del
    // encuadre. Misma fuente para las dos cosas; `currentLocation` queda de
    // respaldo para el primer instante, antes del primer latido.
    const yo = punto(miPosicionViva?.lat ?? cLat, miPosicionViva?.lng ?? cLng);
    const esChofer = role === 'DRIVER' || role === 'DELIVERY_DRIVER';

    // 1. Si hay ruta trazada, se encuadra la RUTA ENTERA y no solo sus puntas:
    //    una ruta que rodea el Malecón se sale del rectángulo de sus extremos.
    if (Array.isArray(rutaPorCalles) && rutaPorCalles.length >= 2) return rutaPorCalles;

    // 2. Mientras gira el radar buscando chofer, la cámara se queda quieta:
    //    que el mapa baile mientras esperas es de las cosas que más molestan.
    if (isSearching) return null;

    if (esChofer) {
      const r = driverCurrentRide;
      // 3. Solicitud entrante: ve dónde está el pasajero mientras decide.
      if (!isActiveTrip && incomingPassengerRequest && driverPendingRides?.[0]) {
        const s = driverPendingRides[0];
        const p = punto(s.origin_lat ?? s.originLat, s.origin_lng ?? s.originLng);
        return yo && p ? [yo, p] : null;
      }
      // 4. Yendo a recoger: él y el punto de recogida.
      if (isActiveTrip && (rideStatus === 'ACCEPTED' || rideStatus === 'ARRIVED')) {
        const p = punto(r?.origin_lat ?? r?.originLat, r?.origin_lng ?? r?.originLng);
        return yo && p ? [yo, p] : null;
      }
      // 5. Con el pasajero a bordo: él y el destino (cuando el destino tiene
      //    coordenada — en más de la mitad de los viajes reales NO la tiene
      //    porque se escribió a mano, y entonces no hay nada que encuadrar).
      if (isActiveTrip && rideStatus === 'IN_PROGRESS') {
        const d = punto(r?.destinationLat, r?.destinationLng);
        return yo && d ? [yo, d] : null;
      }
      return null;
    }

    // 6. Pasajero en viaje: EXACTAMENTE los dos extremos de su línea.
    //    Estaba escrito aparte como "chofer + recogida", así que con el
    //    pasajero ya a bordo la cámara encuadraba hacia atrás, a la esquina
    //    donde se montó, mientras la línea (con el arreglo de esta ronda) va
    //    hacia el destino. La cámara y la línea tienen que mirar lo mismo.
    if (isActiveTrip) {
      const { desde, hasta } = extremosDeLaLinea;
      // Sin los dos extremos NO se toca la cámara: mover el mapa a un punto
      // suelto mientras falta el otro es el "temblor" de siempre.
      return desde && hasta ? [desde, hasta] : null;
    }
    // 7. Pasajero eligiendo destino: él y el destino elegido.
    const d = punto(destinationLat, destinationLng);
    return yo && d ? [yo, d] : null;
  }, [
    rutaPorCalles, role, isActiveTrip, isSearching, rideStatus,
    incomingPassengerRequest, driverPendingRides, driverCurrentRide,
    driverCoordMemo, pickupCoordMemo, cLat, cLng, destinationLat, destinationLng,
    miPosicionViva?.lat, miPosicionViva?.lng, extremosDeLaLinea,
  ]);

  // Chofer: abre el modal para proponer un desvío con su motivo.
  const openDeviationProposal = useCallback(() => {
    setDeviationModal({ visible: true, role: 'DRIVER' });
  }, []);

  // Pasajero: la detección de propuestas de desvío del chofer vive ahora DENTRO
  // del callback del listener principal de rideStatus (ver secciones L~871 y
  // L~1144). Antes había un onSnapshot duplicado al mismo doc — consolidado.

  const handleDeviationApprove = useCallback(
    async (reason: string) => {
      const role = deviationModal.role;
      setDeviationModal({ visible: false, role: null });
      if (!currentRideId) return;
      try {
        if (role === 'DRIVER') {
          await firestoreService.proposeRouteDeviation(currentRideId, reason);
          showAlert('Desvío enviado', 'Le avisamos al pasajero para que confirme el cambio de ruta.', 'navigate', colors.primary);
        } else {
          await firestoreService.respondRouteDeviation(currentRideId, true);
        }
      } catch (e: any) {
        console.warn('[routeDeviation] approve error:', e?.message);
      }
    },
    [deviationModal.role, currentRideId, colors.primary]
  );

  // ══════════════════════════════════════════════════════════════════════
  // CANCELAR COMO CHOFER — UN SOLO CAMINO, CON O SIN MOTIVO DE ESPERA
  // ══════════════════════════════════════════════════════════════════════
  //
  // Este cuerpo estaba escrito ENTERO dentro del prop `onCancel` de la tarjeta.
  // Sale aquí por una razón concreta: la cancelación «me voy porque esperé 10
  // minutos» tiene que pasar por EXACTAMENTE las mismas protecciones que la
  // normal —el cerrojo contra el doble toque, soltar el viaje solo cuando el
  // servidor confirma, y el aviso honesto si falla— y copiarlas habría sido
  // tener dos versiones que se separan a la primera corrección.
  //
  // Lo único que cambia con motivo: no se le pregunta «esto te va a costar»,
  // porque no le va a costar. Ya confirmó eligiendo el motivo.
  const cancelarComoChofer = async (motivoDeEspera?: string) => {
                // ── H07 (auditoría 16-sep-2026): EL CHOFER TAMBIÉN SE ENTERA ──
                // Antes, si la cancelación fallaba, el `catch` la tragaba y la
                // pantalla del chofer se reseteaba igual: él creía haber
                // cancelado y el pasajero se quedaba viendo "tu chofer va en
                // camino" sin que nadie viniera. Ahora el viaje solo se suelta
                // cuando el servidor lo confirma.
                const soltarViajeDelChofer = () => {
                  setIsActiveTrip(false);
                  setDriverCurrentRide(null);
                  setCurrentRideId(null);
                  setRideStatus('ACCEPTED');
                  rideSessionStore.clearActiveRide();
                  // H28 — viaje cerrado, GPS a modo espera.
                  locationService.setTrackingMode('IDLE').catch(() => {});
                };
                const viaje = currentRideId;
                if (!viaje) {
                  soltarViajeDelChofer();
                  return;
                }
                if (cancelandoViajeRef.current) return;
                // H24 — el chofer también sabe lo que cuesta antes de tocar.
                // Con motivo de espera NO se pregunta «esto te va a costar»:
                // justamente no cuesta. El chofer ya confirmó eligiendo el
                // motivo en su propia pantalla.
                if (!motivoDeEspera) {
                  const aviso = avisoAntesDeCancelar(true, viaje, rideStatus);
                  if (!(await confirmarCancelacion(aviso.titulo, aviso.mensaje))) return;
                }
                if (cancelandoViajeRef.current) return;
                cancelandoViajeRef.current = true;
                setCancelandoViaje(true);
                try {
                  await firestoreService.cancelRide(viaje, 'DRIVER_CANCELLED', 'DRIVER', motivoDeEspera);
                  soltarViajeDelChofer();
                } catch (e: any) {
                  if (e?.code === 'ride/ya-terminado') {
                    // Ya estaba cerrado por otro lado: soltarlo es la verdad.
                    soltarViajeDelChofer();
                    const completado = e?.estado === 'COMPLETED';
                    const cancelado = e?.estado === 'CANCELLED';
                    showAlert(
                      completado ? 'El viaje ya terminó' : cancelado ? 'Viaje Cancelado' : 'Viaje cerrado',
                      completado
                        ? 'Este viaje ya estaba completado.'
                        : cancelado
                          ? 'Este viaje ya estaba cancelado.'
                          : 'Este viaje ya no está activo.',
                      completado ? 'checkmark-circle' : 'close-circle',
                      completado ? colors.primary : colors.error
                    );
                  } else {
                    console.warn('[driver cancel active]', e?.code, e?.message);
                    showAlert(
                      'No pudimos cancelar el viaje',
                      'El viaje sigue activo y el pasajero lo sigue viendo así. Revisa tu conexión y vuelve a tocar «Cancelar Viaje».',
                      'alert-circle',
                      colors.error
                    );
                  }
                } finally {
                  cancelandoViajeRef.current = false;
                  setCancelandoViaje(false);
                }
  };

  const [motivoEsperaVisible, setMotivoEsperaVisible] = useState(false);

  const handleDeviationReject = useCallback(async () => {
    const role = deviationModal.role;
    setDeviationModal({ visible: false, role: null });
    if (!currentRideId) return;
    try {
      // Solo el pasajero que rechaza escribe el veredicto. Si el chofer toca
      // "No" es cancelar su propia propuesta — no persiste nada.
      if (role === 'PASSENGER') {
        await firestoreService.respondRouteDeviation(currentRideId, false);
      }
    } catch (e: any) {
      console.warn('[routeDeviation] reject error:', e?.message);
    }
  }, [deviationModal.role, currentRideId]);

  /**
   * EL BOTÓN DE CONECTARSE, CON CERROJO Y CON RATO DE ESPERA VISIBLE.
   *
   * Todo el trabajo real vive en `ejecutarCambioDeConexion`. Esta envoltura
   * solo se ocupa de dos cosas que antes no existían:
   *
   *  · CERROJO. El botón sigue vivo mientras se habla con el servidor. Dos
   *    toques seguidos lanzaban dos cambios que se pisaban entre sí, y de ahí
   *    salía el estado invertido: la pantalla apagada con el GPS emitiendo, o
   *    al revés. El segundo toque ahora no hace nada.
   *  · DECIRLO. Mientras se resuelve, el botón dice "Conectando…" en vez de
   *    quedarse mudo con la etiqueta de antes.
   *
   * El `finally` suelta el cerrojo en TODOS los caminos, incluido el del aviso
   * de ubicación en segundo plano, que sale del handler y lo vuelve a llamar
   * cuando el chofer acepta.
   */
  /**
   * CERRAR EL VIAJE DEL PASAJERO — DE VERDAD.
   *
   * ═══ EL FALLO QUE ESTO CIERRA ═══
   *
   * El fundador, tras el primer viaje real completo:
   *
   *   "al terminar el viaje el perfil del pasajero no se cierra correctamente,
   *    sino que vuelve al inicio donde mismo escribió su dirección de partida,
   *    como si estuviera pidiéndolo nuevamente"
   *
   * Y tenía toda la razón. Al calificar se limpiaba el viaje (`selectedDriver`,
   * `ratingRideId`, `currentRideId`) pero NO el formulario: el destino, el
   * sector y el precio seguían escritos en el estado. Como la pantalla de pedir
   * se pinta cuando no hay viaje activo, reaparecía con todo puesto — y desde
   * fuera eso no se distingue de "la app me está pidiendo otro viaje". Con
   * dinero de por medio, ese susto es motivo suficiente para desinstalar.
   *
   * Un viaje que termina tiene que dejar la pantalla como estaba antes de
   * pedirlo. Todo lo que se llenó para ESE viaje se borra aquí.
   */
  /**
   * QUÉ DECIRLE AL PASAJERO CUANDO SU VIAJE APARECE CANCELADO.
   *
   * No todas las cancelaciones son lo mismo, y "El viaje fue cancelado" a secas
   * deja a la persona sin saber qué hacer. El servidor ahora corta por su cuenta
   * dos casos —pedir viaje teniendo uno en curso, y pedir muchos seguidos— y
   * cada uno necesita su frase. `null` significa que no hay nada que avisar:
   * `auto_new_request` es la limpieza de la solicitud anterior cuando el propio
   * pasajero acaba de pedir otra, así que un cartel ahí solo asusta.
   */
  const avisoDeCancelacion = (data: any): { titulo: string; cuerpo: string } | null => {
    const motivo = String(data?.cancelReason ?? '');
    if (motivo === 'auto_new_request' || motivo === 'auto_expired_stale') return null;
    if (motivo === 'duplicado_viaje_en_curso') {
      return {
        titulo: 'Ya tienes un viaje en curso',
        cuerpo: 'Esta solicitud no se envió: tu chofer anterior sigue en camino. Cancela ese viaje si quieres pedir otro.',
      };
    }
    if (motivo === 'exceso_de_solicitudes') {
      return {
        titulo: 'Demasiadas solicitudes seguidas',
        cuerpo: 'Espera un momento antes de volver a pedir viaje.',
      };
    }
    return { titulo: 'Viaje Cancelado', cuerpo: 'El viaje fue cancelado.' };
  };

  const cerrarViajeDelPasajero = () => {
    // Si la oferta de demostración venía en camino, aquí se corta: si no,
    // aparecería sobre una pantalla ya cerrada. Ver la nota del reloj.
    pararLaOfertaDeDemostracion();
    setSelectedDriver(null);
    setRatingRideId(null);
    setCurrentRideId(null);
    setIsActiveTrip(false);
    // De vuelta al valor inicial, no a null: el tipo no admite null y el
    // proximo viaje tiene que empezar en ACCEPTED como el primero.
    setRideStatus('ACCEPTED');
    setActiveRidePin(null);
    // La sesion en disco tambien: si no, al reabrir la app se restaura el
    // viaje que ya termino. Mismo cierre que usa manejarViajeExpirado.
    rideSessionStore.clearActiveRide();
    cleanupRideListeners();
    // ── Y EL FORMULARIO, QUE ERA LO QUE FALTABA ────────────────────
    setDestination('');
    setSectorDestino('');
    setOfferPrice('');
    setDestinationLat(null);
    setDestinationLng(null);
    setPassengers('1');
    // H34 / H64 (auditoría 16-sep-2026) — la descripción del paquete no tenía
    // UN solo reset en todo el archivo: el siguiente envío salía con la del
    // anterior, y el repartidor nuevo leía lo que llevaba OTRA persona ("sobre
    // con RD$15.000"). `serviceType` se queda: es preferencia, no dato del viaje.
    setPackageDescription('');
    // H35 — LA RECOGIDA CAMBIADA A MANO TAMBIÉN ERA DE ESE VIAJE. Quedaba la
    // dirección de la mamá en el campo y, con `origenManual` en true, el
    // autorelleno por GPS apagado para siempre: el pasajero apurado que solo
    // escribía el destino mandaba al chofer a la recogida del viaje anterior.
    // Se vuelve a "mi ubicación": el efecto del autorelleno retoma solo.
    setOrigenManual(false);
    setOrigenLat(null);
    setOrigenLng(null);
    setOrigenTexto(direccionActualRef.current || '');
    // Los buscadores se vuelven a montar con los valores de arriba. Ver
    // `versionFormulario`: sin esto, si el formulario estaba a la vista, las
    // cajas seguían enseñando lo que el estado ya había borrado.
    setVersionFormulario((v) => v + 1);
    // Las ofertas del viaje que acaba de terminar no pueden sobrevivirlo.
    setReceivedOffers([]);
    setIncomingDriverOffers(false);
    setIsSearching(false);
  };

  /**
   * CANCELAR LA BÚSQUEDA DE CHOFER.
   *
   * Vive aquí, en una sola función, porque ahora se llama desde DOS sitios: el
   * formulario mientras el radar gira y la hoja de ofertas. Ese segundo sitio es
   * el que faltaba: en cuanto llegaba la primera oferta, el formulario se
   * escondía detrás de la hoja y con él el único botón de cancelar que existía.
   * La pasajera se quedaba sin salida y la solicitud viva en el servidor.
   *
   * Cancela de verdad en Firestore (dejarla PENDING permite que un chofer la
   * acepte minutos después y salga a buscar a alguien que ya se fue) y después
   * cierra la pantalla COMPLETA con `cerrarViajeDelPasajero`: eso incluye borrar
   * el destino y el precio del formulario. Antes se dejaban puestos a propósito
   * "para volver a pedir sin escribirlo todo otra vez", y el fundador lo reportó
   * como fallo el 19-sep: al volver, la pantalla con la dirección puesta parece
   * que el viaje sigue pedido.
   */
  const cancelarBusqueda = async () => {
    pararLaOfertaDeDemostracion();
    const idCancelado = currentRideId;
    setIsSearching(false);
    setReceivedOffers([]);
    setIncomingDriverOffers(false);
    choferesRechazadosRef.current.clear();
    ofertasRechazadasRef.current.clear();
    // ⚠️ AL SERVIDOR NO, SI EL VIAJE ES DE MENTIRA.
    // El de demostración no existe en Firestore, así que esta llamada falla
    // siempre y cae en el `catch` de abajo — que le suelta a la persona
    // «Cancelamos la búsqueda en tu teléfono, pero el servidor no lo confirmó.
    // Si un chofer te ofrece un viaje, ignora la oferta». Un aviso que asusta,
    // sobre un chofer que no existe, por una cancelación que no hacía falta.
    if (idCancelado && !esViajeFalso(idCancelado)) {
      try {
        // AUDIT FIX — sin `?.`: si el método faltara en runtime el fallo tiene
        // que llegar al catch, no quedarse en silencio.
        await firestoreService.cancelRideAsPassenger(idCancelado);
      } catch (e: any) {
        console.warn('[cancel ride] firestore falló:', e?.message);
        showAlert(
          'Cancelación local',
          'Cancelamos la búsqueda en tu teléfono, pero el servidor no lo confirmó. Si un chofer te ofrece un viaje, ignora la oferta.',
          'warning',
          '#FF9500'
        );
      }
    }
    cerrarViajeDelPasajero();
  };

  /**
   * RECHAZAR LA OFERTA DE UN CHOFER.
   *
   * Antes esto solo borraba la tarjeta de la lista en memoria, y de ahí salían
   * los dos fallos que reportó el fundador el 19-sep (viaje LvrCVZjnl…):
   *   · el chofer no se enteraba NUNCA: su pantalla se quedaba en "Oferta
   *     enviada — esperando respuesta del pasajero" hasta que el cron expiraba
   *     la solicitud diez minutos después;
   *   · la pasajera caía en el formulario con la dirección puesta, sin radar y
   *     sin botón de cancelar, con la solicitud todavía viva en el servidor.
   *
   * Ahora el rechazo se escribe en el viaje (`rechazados`), que es el documento
   * que el chofer ya está leyendo en su feed, y la pantalla vuelve al radar: la
   * solicitud sigue viva para los demás choferes —rechazar a uno no es cancelar
   * el viaje— y con el radar vuelven el botón de cancelar y el tope de 6 min.
   */
  const rechazarOferta = (offer: any) => {
    const chofer = String(offer?.driverId ?? '').trim();
    if (chofer) choferesRechazadosRef.current.add(chofer);
    // Por su id también: una oferta sin `driverId` no se puede apuntar por
    // chofer y el siguiente snapshot la devolvía a la pantalla.
    const idOferta = String(offer?.id ?? '').trim();
    if (idOferta) ofertasRechazadasRef.current.add(idOferta);
    const quedan = receivedOffers.filter(
      (o: any) =>
        o.id !== offer.id &&
        (!o?.driverId || !choferesRechazadosRef.current.has(String(o.driverId)))
    );
    setReceivedOffers(quedan);
    if (quedan.length === 0) {
      setIncomingDriverOffers(false);
      // Al radar solo si la solicitud sigue siendo una solicitud. Con un viaje
      // ya aceptado, `isSearching` armaría el tope de 6 minutos que cancela.
      if (currentRideId && !isActiveTripRef.current) setIsSearching(true);
    }
    if (currentRideId && chofer) {
      firestoreService.rejectDriverOffer(currentRideId, chofer).catch((e: any) =>
        // No se le grita a la pasajera por esto: su pantalla ya hizo lo correcto
        // y el chofer, en el peor caso, se limpia cuando la solicitud expira.
        console.warn('[rechazo] no se pudo avisar al chofer:', e?.message)
      );
    }
  };

  // El temporizador del reintento de ruta no puede sobrevivir a la pantalla:
  // si lo hace, dispara un `setState` sobre un componente desmontado.
  useEffect(() => {
    return () => {
      if (temporizadorRuta.current) {
        clearTimeout(temporizadorRuta.current);
        temporizadorRuta.current = null;
      }
    };
  }, []);

  const handleToggleOnline = async () => {
    if (cambioConexionRef.current) return;
    cambioConexionRef.current = true;
    setCambiandoConexion(true);
    try {
      await ejecutarCambioDeConexion();
    } catch (e: any) {
      // Nada debería llegar aquí: cada tramo tiene su propio catch. Si algo se
      // escapa, el chofer se entera igual en vez de quedarse con un botón
      // congelado.
      console.warn('[map] cambio de conexión falló:', e?.message);
      showAlert(
        'No pudimos completar el cambio',
        'Vuelve a tocar el botón en un momento.',
        'alert-circle',
        '#FF3B30'
      );
    } finally {
      cambioConexionRef.current = false;
      setCambiandoConexion(false);
    }
  };

  const ejecutarCambioDeConexion = async () => {
    // Ponerse en línea significa entrar a la cola real de viajes. Sin cuenta
    // no hay a quién asignarle nada, así que se corta aquí CON aviso — antes
    // el flujo seguía y moría en un `if (!driverId)` mudo más abajo: el
    // chofer tocaba el botón y no pasaba absolutamente nada.
    if (bloquearSiInvitado('conectarte y recibir viajes')) return;

    const nextState = !isOnline;

    const driverId = useAppStore.getState().userFirebaseUid;
    const userName = useAppStore.getState().userName || 'Chofer Go Safely';

    // GUARD: si el chofer no completó verificación (licencia, matrícula,
    // seguro, fotos del vehículo), no puede ponerse online. El papel de buena
    // conducta salió de esta lista el 27-sep-2026: solo se le pide a quien
    // transporta estudiantes (school-driver-setup.tsx).
    // Esto es el mismo guard que ahora aplica la regla Firestore — lo
    // chequeamos client-side primero para mostrar UX clara en vez de
    // un permission-denied genérico.
    if (nextState && driverId) {
      try {
        // CON RELOJ: sin cobertura esta lectura puede no contestar nunca, y
        // el chofer se quedaba con el botón en "Conectando…" para siempre. Al
        // vencer cae en el catch de abajo, que ya dice qué pasó.
        const me = await conPlazo(
          firestoreService.getUserProfile(driverId),
          10000,
          'validar tu cuenta'
        );
        if (!me?.isDriverVerified) {
          // ── DISTINGUIR "EN REVISIÓN" DE "TE FALTAN PAPELES" ──────────────
          // Antes había un solo mensaje: le pedía licencia, matrícula, seguro y
          // fotos del vehículo. Al chofer que YA los había subido y estaba
          // esperando revisión —que es el primero que va a probar la app, y
          // también el revisor de Google— le decía que le faltaban documentos
          // que acababa de mandar, y el botón "Completar ahora" lo devolvía a
          // subirlos de nuevo: el servidor rechaza el reenvío mientras hay una
          // revisión abierta, así que terminaba en un error. Un círculo cerrado.
          const enRevision = String(me?.kycStatus ?? '').toUpperCase() === 'PENDING';
          if (enRevision) {
            Alert.alert(
              'Tu verificación está en revisión',
              'Ya recibimos tus documentos y una persona de nuestro equipo los está revisando. ' +
              'Te avisamos por notificación en cuanto quede aprobada, y ahí podrás conectarte.',
              [{ text: 'Entendido' }]
            );
            return;
          }
          Alert.alert(
            'Falta tu verificación de chofer',
            'Antes de aceptar viajes, completa la verificación: licencia, matrícula, seguro y fotos del vehículo.',
            [
              { text: 'Más tarde', style: 'cancel' },
              { text: 'Completar ahora', onPress: () => router.replace('/kyc-welcome' as any) },
            ]
          );
          return;
        }
        // GUARD adicional: chofer terminó su prueba sin método de pago.
        if (me?.driverBlockedNoPayment) {
          Alert.alert(
            'Cuenta bloqueada',
            // 19-sep-2026: la cuota ya se puede pagar con TARJETA desde
            // Billetera (pago único; CUOTA_TARJETA_ENABLED). Esta es la puerta
            // por la que más choferes pausados llegan: se le dice y se le
            // lleva directo a pagar.
            Number((me as any)?.pendingCharge?.amount) > 0
              ? 'Tu membresía del mes no está pagada. Págala con tarjeta en Billetera y vuelves en cuanto Stripe confirme el pago, o recarga con tu comprobante de transferencia o depósito y se reactiva en cuanto lo aprobemos.'
              : 'Tu prueba terminó sin pago registrado. Paga tu cuota con tarjeta en Billetera o recarga con tu comprobante de transferencia o depósito para volver a aceptar viajes.',
            [
              { text: 'Más tarde', style: 'cancel' },
              { text: 'Pagar ahora', onPress: () => router.push('/wallet') },
            ]
          );
          return;
        }
        // ══ EL SEGURO VENCIDO ═══════════════════════════════════════════
        //
        // Lo apaga el repaso diario (`revisarSegurosVencidos`, seguro.ts) y las
        // reglas de Firestore le cierran los viajes (`isVerifiedDriver`). Sin
        // esta puerta el chofer tocaba "ponerse en línea", el servidor le
        // negaba cada escritura y no había NADA que le dijera que era el
        // seguro: `seguroVencidoBloqueo` no se leía en una sola línea de la app.
        //
        // Renovar el seguro es lo único de sus papeles que puede arreglar solo
        // (decisión del fundador), así que el botón lleva derecho a hacerlo.
        if ((me as any)?.seguroVencidoBloqueo === true) {
          Alert.alert(
            'Tu seguro está vencido',
            'No podemos dejarte aceptar viajes con el seguro vencido — si pasa algo, el pasajero y tú ' +
            'quedan sin cobertura. Renuévalo y mándanos la foto de la póliza: la revisamos y te ' +
            'reactivamos el perfil.',
            [
              { text: 'Más tarde', style: 'cancel' },
              { text: 'Enviar mi póliza', onPress: () => router.push('/seguro' as any) },
            ]
          );
          return;
        }

        // ── EL PAPEL DE BUENA CONDUCTA YA NO SE LE PIDE AL CHOFER NI AL
        //    REPARTIDOR (decisión del fundador, 27-sep-2026) ────────────────
        // Aquí vivían dos candados: uno bloqueaba al chofer cuyo papel hubiera
        // rechazado el admin, y otro —el que de verdad mordía— le cerraba la
        // calle al terminar la prueba de 15 días si no lo había subido.
        //
        // Ese segundo candado paró al propio fundador. Y medido en producción
        // ese mismo día: de los 5 choferes que existen, NINGUNO había subido
        // nunca ese papel y ninguno tenía estado de revisión — o sea, el
        // requisito no filtraba a nadie, solo apagaba la flota entera el día
        // 15. Con cédula, licencia, matrícula, seguro y fotos del vehículo ya
        // revisados a mano, se considera suficiente.
        //
        // ⚠️ SIGUE EXIGIÉNDOSE EN LA RUTA ESCOLAR, y ese candado NO está aquí:
        // vive en school-driver-setup.tsx (mira `conductPaperStatus` antes de
        // dejar publicar una ruta). Es otro flujo y otra pantalla, y se queda
        // como está: ahí el papel es lo que protege a un niño.

        // ══ SIN PLACA VALIDA NO SE SALE A LA CALLE ══════════════════════
        //
        // Esto no es una suposición. El 10-sep-2026, leyendo Firestore: de los
        // 5 choferes que existen, CUATRO no tienen placa ninguna, y uno de esos
        // cuatro está `isDriverVerified: true` con `driverVerifiedAt` del 24 de
        // junio — verificado, con permiso para aceptar viajes, y sin marca, ni
        // modelo, ni color, ni placa. Un pasajero que pidiera ese viaje no
        // tendría NADA que mirar en la acera.
        //
        // La verificación de identidad y el vehiculo son dos cosas distintas y
        // se habían quedado sueltas: `kyc-documents` avisa de lo que falta pero
        // deja seguir, y `isDriverVerified` no mira el vehiculo.
        //
        // ⚠️ SE BLOQUEA AQUI Y NO EN LA VERIFICACION, Y ES DELIBERADO. Quitarle
        //    `isDriverVerified` a un chofer ya aprobado por no tener la placa
        //    sería deshacer una revisión humana que sí se hizo (su cédula y su
        //    cara están comprobadas). Lo que falta es el carro, así que lo que
        //    se cierra es salir a la calle, con un camino de 30 segundos para
        //    arreglarlo. No se le quita nada a nadie: se le pide lo que falta.
        const placaGuardada = String(me?.licensePlate ?? '').trim();
        if (!placaValida(placaGuardada)) {
          Alert.alert(
            placaGuardada ? 'Revisa la placa de tu vehículo' : 'Falta la placa de tu vehículo',
            placaGuardada
              ? 'La placa guardada no tiene la forma de una placa dominicana. El pasajero compara ' +
                'esa placa con el metal de tu carro antes de montarse, así que tiene que estar exacta.'
              : 'El pasajero necesita saber qué vehículo esperar y poder comprobar la placa al ' +
                'montarse. Escríbela una vez y no te la vuelve a pedir.',
            [
              { text: 'Más tarde', style: 'cancel' },
              { text: 'Escribirla ahora', onPress: () => router.push('/kyc-documents' as any) },
            ]
          );
          return;
        }
      } catch (e) {
        console.warn('No se pudo verificar bloqueo de chofer:', e);
        // Solo abortamos si íbamos a activar online. Desconectarse siempre se permite.
        if (nextState) {
          // showAlert (no Alert.alert): Alert.alert es no-op en RN-web, así que
          // en el build web el chofer no vería nada. showAlert usa el modal
          // interno que sí renderiza en ambas plataformas.
          showAlert(
            'No pudimos validar tu cuenta',
            'Revisa tu conexión e inténtalo de nuevo en un momento.',
            'alert-circle',
            '#FF3B30'
          );
          return;
        }
      }
    }

    // TELÉFONO OBLIGATORIO — El chofer necesita número para que el pasajero lo
    // contacte. Bloquea ponerse online sin teléfono (cubre login con Google).
    if (nextState) {
      const phoneDigits = String(useAppStore.getState().userPhone ?? '').replace(/[^0-9]/g, '');
      if (phoneDigits.length < 8) {
        Alert.alert(
          'Agrega tu teléfono',
          'Para recibir viajes necesitas un número de contacto para que el pasajero pueda comunicarse contigo.',
          [
            { text: 'Ahora no', style: 'cancel' },
            { text: 'Agregar teléfono', onPress: () => router.push('/add-phone') },
          ]
        );
        return;
      }
    }

    // AVISO DESTACADO — Ubicación en segundo plano (POLÍTICA GOOGLE PLAY).
    // Causa del rechazo 2026-07: la app pedía el permiso de background location
    // SIN mostrar antes una divulgación destacada. Debe aparecer ANTES del
    // diálogo del sistema y requerir acción afirmativa del usuario.
    if (nextState && UBICACION_SEGUNDO_PLANO && !bgDisclosureAcceptedRef.current) {
      // Con la ubicación de segundo plano APAGADA (el modo del envío a Play)
      // el aviso destacado no aplica: no se va a pedir ese permiso nunca.
      // SIN cortocircuito por `hasBackgroundPermission()`. Antes, si el permiso
      // ya estaba concedido, el aviso no se mostraba — y con el fallo que
      // acabamos de cerrar (el permiso se pedía al abrir el mapa) eso
      // significaba que en la práctica NO se mostraba casi nunca. Para el
      // formulario de ubicación de Play hay que poder grabar el vídeo con el
      // aviso saliendo ANTES del diálogo del sistema, así que sale siempre la
      // primera vez de cada sesión.
      setBgDisclosureVisible(true);
      return; // esperamos que acepte; el modal re-ejecuta este handler
    }

    // ══════════════════════════════════════════════════════════════════════
    // EL INTERRUPTOR REFLEJA LO QUE PASÓ, NO LO QUE SE INTENTÓ
    // ══════════════════════════════════════════════════════════════════════
    // ANTES: `setIsOnline(nextState)` se ponía en `true` de entrada y las dos
    // operaciones que hacen que "estar en línea" signifique algo vivían dentro
    // del MISMO `try`, una detrás de otra. Si la primera —la presencia—
    // fallaba, la segunda —el GPS— no se ejecutaba NUNCA, y el `catch` solo
    // escribía en la consola. Resultado: el chofer veía "En línea" el turno
    // entero mientras su posición no se actualizaba para nadie. No daba error,
    // y quien lo sufría (el pasajero) no era quien lo veía.
    //
    // AHORA son dos intentos INDEPENDIENTES, cada uno con su reloj, y el
    // interruptor se mueve al final, cuando ya se sabe qué pasó.
    if (!driverId) {
      // Sin uid no hay presencia que escribir ni posición que publicar. Antes
      // se llegaba hasta aquí, se pintaba "En línea" y no se hacía nada.
      showAlert(
        'No pudimos conectarte',
        'Tu sesión todavía no está lista. Cierra y vuelve a abrir la app, y toca "Conectarse" otra vez.',
        'alert-circle',
        '#FF3B30'
      );
      return;
    }

    if (nextState) {
      // ── CONECTARSE ──────────────────────────────────────────────────────
      // (1) PRESENCIA: /drivers dice que este chofer existe y está libre. Sin
      //     esto el pasajero NO lo encuentra: su consulta es
      //     `where isOnline == true`, y las posiciones que publica el GPS no
      //     escriben ese campo.
      // (2) SEGUIMIENTO: el GPS que refresca dónde está. Sin esto se queda
      //     clavado en el punto donde encendió.
      // Hacen falta las dos. Se intentan las dos aunque la primera falle, y
      // solo si las dos salen bien se enciende el interruptor.
      const here = currentLocation ?? undefined;

      let presenciaOk = false;
      try {
        await conPlazo(
          firestoreService.setDriverOnline(driverId, {
            isOnline: true,
            vehicleType: vehiculoParaPublicar as any,
            userName,
            lat: here?.lat,
            lng: here?.lng,
          }),
          12000,
          'registrar tu presencia'
        );
        presenciaOk = true;
      } catch (e: any) {
        console.warn('[map] presencia al conectar falló:', e?.message);
      }

      let rastreoOk = false;
      try {
        // 30 s, no menos: por aquí puede salir el diálogo de permiso del
        // sistema, y un reloj corto vencería mientras el chofer lo está
        // leyendo. Es la única espera de este handler que puede depender de
        // una persona.
        await conPlazo(locationService.startTracking(driverId, 'IDLE'), 30000, 'encender tu GPS');
        // `startTracking` no devuelve nada y SE SALE EN SILENCIO si el chofer
        // negó el permiso de ubicación: que no haya excepción no prueba que
        // haya GPS vivo. La única prueba es el vigilante, y a él se le
        // pregunta.
        //
        // En web nunca hubo seguimiento (el servicio se sale antes de
        // instalarlo), así que exigirlo dejaría al chofer sin poder conectarse
        // en el navegador. Ahí manda la presencia, como hasta hoy.
        rastreoOk = Platform.OS === 'web' ? true : locationService.modoDeRastreoActual() !== null;
      } catch (e: any) {
        console.warn('[map] arranque del GPS falló:', e?.message);
      }

      if (presenciaOk && rastreoOk) {
        setIsOnline(true);
        // RESUME — Persistir la INTENCIÓN de estar online en AsyncStorage. Se
        // guarda AQUÍ, ya con las dos mitades vivas: guardarla antes era
        // persistir una conexión que no llegó a existir.
        rideSessionStore.setDriverOnline(true).catch(() => {});
        return;
      }

      // ── NO QUEDÓ CONECTADO ──────────────────────────────────────────────
      // El interruptor se queda apagado Y se deshace la mitad que sí arrancó:
      // media conexión es peor que ninguna. Un chofer visible sin GPS es un
      // carro congelado en el mapa del pasajero; un GPS sin presencia gasta
      // batería y datos para nadie.
      setIsOnline(false);
      rideSessionStore.setDriverOnline(false).catch(() => {});
      if (Platform.OS !== 'web') {
        // SIN CONDICIÓN, y esto importa: si el reloj venció, `startTracking`
        // sigue su camino por dentro y puede instalar el vigilante DESPUÉS de
        // que dijimos que no se pudo. `stopTracking` sube el contador de
        // generación, así que ese vigilante que llega tarde se apaga solo en
        // vez de quedarse emitiendo con la pantalla en "Conectarse". Llamarlo
        // cuando no hay nada encendido no cuesta nada.
        await locationService.stopTracking().catch((e: any) =>
          console.warn('[map] no se pudo apagar el GPS tras una conexión fallida:', e?.message)
        );
      }
      if (presenciaOk) {
        // Va sin esperar respuesta: si el apagado tampoco llega ahora, queda
        // encolado, y Firestore entrega las escrituras EN ORDEN, así que el
        // servidor termina apagado igual.
        firestoreService
          .setDriverOnline(driverId, { isOnline: false })
          .catch((e: any) => console.warn('[map] no se pudo revertir la presencia:', e?.message));
      }
      showAlert(
        'No pudimos conectarte',
        !presenciaOk
          ? 'No llegamos a avisar que estás disponible, así que ningún pasajero te vería. Revisa tu conexión y toca "Conectarse" otra vez.'
          : 'No pudimos encender tu ubicación, y sin ella tu carro se quedaría congelado en el mapa del pasajero. Revisa que la app tenga permiso de ubicación y que el GPS esté encendido, y toca "Conectarse" otra vez.',
        'alert-circle',
        '#FF3B30'
      );
      return;
    }

    // ── DESCONECTARSE ─────────────────────────────────────────────────────
    // El orden aquí es el contrario, y no es un detalle: PRIMERO se apaga lo
    // que emite. Antes, apagar la presencia y parar el GPS estaban en el mismo
    // `try` y en ese orden: si la escritura fallaba, `stopTracking()` no se
    // ejecutaba, y el teléfono seguía publicando posición con la pantalla
    // diciendo "Conectarse". El mismo fallo visto por el otro lado.
    //
    // Y desconectarse NUNCA se bloquea: si algo falla, se falla apagado.
    setIsOnline(false);
    rideSessionStore.setDriverOnline(false).catch(() => {});
    setIncomingPassengerRequest(false);
    setDriverDeliveries([]);

    try {
      await conPlazo(locationService.stopTracking(), 8000, 'apagar tu GPS');
    } catch (e: any) {
      console.warn('[map] no se pudo detener el GPS:', e?.message);
    }

    // Dos intentos con relojes cortos (8 s y 5 s): el chofer ya está apagado
    // en pantalla y con el GPS parado, así que esto solo persigue la
    // confirmación del servidor. No puede tener el botón tomado medio minuto.
    let apagadoOk = false;
    const plazosApagado = [8000, 5000];
    for (const plazo of plazosApagado) {
      try {
        await conPlazo(
          firestoreService.setDriverOnline(driverId, { isOnline: false }),
          plazo,
          'apagar tu presencia'
        );
        apagadoOk = true;
        break;
      } catch (e: any) {
        console.warn('[map] apagar la presencia falló:', e?.message);
      }
    }
    if (!apagadoOk) {
      showAlert(
        'Te desconectamos en este teléfono',
        'Apagamos tu ubicación en este teléfono, pero el servidor no confirmó que quedaste fuera de servicio. Si te sigue llegando algún viaje, revisa tu conexión y toca "Desconectarse" de nuevo.',
        'warning',
        '#FF9500'
      );
    }
  };

  if (typeof window === 'undefined') {
    return (
      <View style={[styles.container, { alignItems: 'center', justifyContent: 'center', backgroundColor: colors.background }]}>
        {/* El texto llevaba el color por defecto (negro) sobre el fondo negro
            del contenedor: el mensaje existia y no se podia leer. El escudo de
            carga de mas abajo ya se habia corregido; este se quedo atras. */}
        <Text style={{ color: colors.text }}>Cargando mapa en tiempo real...</Text>
      </View>
    );
  }

  // ESCUDO DE CARGA — bloquea el MapView y los listeners de Firestore hasta
  // que (1) Firebase Auth haya confirmado la sesión y (2) tengamos coords
  // iniciales del GPS.
  //
  // ⚠️ CADA CANDADO TIENE AHORA SU RELOJ (ver `vencioEsperaSesion` /
  // `vencioEsperaUbicacion`, arriba). Antes no lo tenían: los dos esperaban un
  // aviso que puede no llegar, y esta pantalla se quedaba en "Buscando tu
  // ubicación…" PARA SIEMPRE en un teléfono recién instalado, donde el diálogo
  // del permiso queda encolado detrás del de notificaciones.
  //
  // Vencido el plazo se pasa igual: el mapa se abre centrado donde se pueda y
  // una franja dice lo que falta (ver `avisoArranque` en el render).
  const esperandoSesion = !isFirebaseReady && !vencioEsperaSesion;
  const esperandoUbicacion = !currentLocation && !vencioEsperaUbicacion;
  if (esperandoSesion || esperandoUbicacion) {
    return (
      <View style={[styles.container, { alignItems: 'center', justifyContent: 'center', backgroundColor: colors.background }]}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={{ color: colors.text, marginTop: 12, fontWeight: '600' }}>
          {esperandoSesion ? 'Verificando sesión…' : 'Buscando tu ubicación…'}
        </Text>
        {/* Que se sepa que esto NO es para siempre: la espera tiene fin y la
            pantalla va a avanzar aunque el GPS o la sesión no contesten. */}
        <Text style={{ color: colors.textSecondary, marginTop: 6, fontSize: 12, textAlign: 'center', paddingHorizontal: 32 }}>
          {esperandoSesion
            ? 'Si tarda, el mapa se abre igual en unos segundos.'
            : 'Si no aparece el permiso de ubicación, el mapa se abre igual en unos segundos.'}
        </Text>
      </View>
    );
  }

  // ── LO QUE FALTA, DICHO ─────────────────────────────────────────────────
  // Si se llegó hasta aquí sin sesión o sin ubicación es porque venció el plazo
  // del escudo, no porque el dato ya no importe. Se nombra el hueco real; no se
  // rellena con nada (sin ubicación NO se afirma "estás en Santo Domingo": se
  // dice que no pudimos ubicarte y que el mapa se abrió en el centro).
  // Plano: no son hooks, así que pueden vivir después del escudo.
  const avisoArranque: { texto: string; pulsable: boolean } | null =
    !isFirebaseReady
      ? {
          texto: 'No pudimos confirmar tu sesión. Puedes ver el mapa; para pedir un viaje, cierra y vuelve a abrir la app.',
          pulsable: false,
        }
      : !currentLocation
        ? {
            texto: 'No pudimos ubicarte — el mapa se abrió en el centro de la ciudad. Toca aquí para reintentar.',
            pulsable: true,
          }
        : null;
  /** Alto donde empieza la primera franja: baja si está puesto el cintillo. */
  const topFranjaBase = (!isActiveTrip && (role === 'PASSENGER' || role === 'DRIVER'))
    ? (Platform.OS === 'ios' ? 186 : 166)
    : (Platform.OS === 'ios' ? 120 : 100);

  return (
    <View style={styles.container}>
      {/* MULTA POR CANCELACIONES — Overlay bloqueante si el usuario tiene multa
          pendiente. No renderiza nada si no hay multa. */}
      <PenaltyGate />
      {/* Contenedor del mapa con display:'none' cuando la tab pierde foco.
          Esto libera el SurfaceView nativo SIN destruir el MapView, evitando
          el bug del mapa "sangrando" sobre otras pantallas y la lentitud del
          re-mount cuando se vuelve a la tab Viaje. */}
      {/* FIX — React Native 0.85 ELIMINÓ StyleSheet.absoluteFillObject (solo
          queda absoluteFill). Esparcir undefined no lanza error, así que el
          fallo era MUDO: este contenedor se quedaba sin posición absoluta y el
          mapa perdía su encuadre a pantalla completa. Se usan los valores
          explícitos para no depender de qué exporte la versión de turno. */}
      <View
        style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          display: isTabFocused ? 'flex' : 'none',
        }}
        pointerEvents={isTabFocused ? 'auto' : 'none'}
      >
        <LiveMapContainer
          // El listener de drivers online solo se activa para PASSENGER autenticado.
          // Sin userFirebaseUid las rules de /drivers rechazan la query → evitamos
          // el permission-denied disparando el listener únicamente cuando hay sesión.
          // `isTabFocused` también: sin él, el listener de choferes en línea
          // seguía cobrando lecturas mientras el usuario estaba en Comida, en
          // Actividad o en Perfil, con el mapa oculto y sin mirar nada.
          // `isFirebaseReady` va EXPLÍCITO desde que el escudo tiene reloj: antes
          // lo garantizaba el escudo (no se llegaba aquí sin sesión confirmada)
          // y ahora sí se puede llegar, con el uid rehidratado del disco pero
          // sin sesión viva. Sin esta condición, esa entrada abriría la query a
          // /drivers que las reglas rechazan, que es la cascada de errores que
          // el escudo existía para evitar.
          enabled={isTabFocused && isFirebaseReady && role === 'PASSENGER' && !!userFirebaseUid}
          // El foco va SUELTO además de dentro de `enabled`: el mapa necesita
          // saber que se está desprendiendo para no quitar 50 marcadores justo
          // en ese instante (ver el efecto de vehículos).
          enFoco={isTabFocused}
          mapRegion={mapRegion}
          onVehiclePress={handleMapVehiclePress}
          // FIX TRACKING (#3) — Ubicación EN VIVO del chofer + punto de recogida
          // reales (antes eran coords fijas de demo). El mapa dibuja el marcador
          // del chofer y lo sigue mientras se mueve hacia el pasajero.
          // Refs memoizadas (ver FIX PERF #1) para no romper el React.memo.
          driverCoord={driverCoordMemo}
          pickupCoord={pickupCoordMemo}
          // Las solicitudes vivas del chofer, para que las vea TODAS en el
          // mapa y no de una en una en la tarjeta. Ver `solicitudesEnMapa`.
          solicitudes={solicitudesEnMapa}
          // LA LÍNEA DE RUTA POR LAS CALLES. El `<MapPolyline>` ya estaba
          // cableado a este prop desde siempre, pero este —el único sitio que
          // monta el mapa— nunca lo pasaba: enchufe puesto, cable ausente. Por
          // eso se veía una recta atravesando manzanas en vez de la ruta.
          routeCoordinates={rutaPorCalles}
          // La recta de reserva, para los DOS perfiles. La del chofer no
          // existía: su `driverCoord` es null por rol y la condición del mapa
          // la exigía, así que cuando Directions fallaba veía el mapa pelado.
          rectaRespaldo={rectaRespaldo}
          // Marca, modelo, color, placa y emoji REALES del chofer asignado.
          // Null cuando no hay datos: el marcador se queda con el genérico.
          rotuloChofer={rotuloChofer}
          puntosParaEncuadrar={puntosParaEncuadrar}
          altoHoja={altoHoja}
          reencuadrarAhora={reencuadrarAhora}
        />
      </View>

      {/* SOS BUTTON
          Mientras busca el GPS (hasta 20 s) el escudo se cambia por el giro y
          el botón se atenua y se bloquea. La palabra "SOS" NO se toca: el
          aspecto del botón es identidad, y en una emergencia no es momento de
          que cambie de nombre. Quien use lector de pantalla lo oye dicho. */}
      <TouchableOpacity
        style={[styles.sosButton, sosBuscandoUbicacion && styles.sosButtonTrabajando]}
        onPress={handleSOS}
        disabled={sosBuscandoUbicacion}
        accessibilityRole="button"
        accessibilityState={{ disabled: sosBuscandoUbicacion, busy: sosBuscandoUbicacion }}
        accessibilityLabel={sosBuscandoUbicacion ? 'SOS: buscando tu ubicación, espera' : 'SOS: llamar a emergencias'}
      >
        {sosBuscandoUbicacion
          ? <ActivityIndicator size="small" color="#FFF" />
          : <Ionicons name="shield" size={22} color="#FFF" />}
        <Text style={styles.sosText}>SOS</Text>
      </TouchableOpacity>

      {/* MI UBICACIÓN — botón flotante Uber-style que recentra al usuario */}
      <TouchableOpacity
        style={styles.myLocationButton}
        onPress={centerOnMyLocation}
        activeOpacity={0.85}
        accessibilityLabel="Centrar en mi ubicación"
      >
        <Ionicons name="locate" size={24} color={colors.primary} />
      </TouchableOpacity>

      {/* NAVEGAR — BURBUJA FLOTANTE DEL CHOFER (pedido del fundador, 16-sep-2026).
          El botón "Navegar" vive al final de la hoja del viaje, debajo de
          "Cancelar", y el chofer no lo encuentra justo cuando tiene que salir.
          Esta burbuja flota sobre el mapa, siempre encima de la hoja (su alto se
          mide), en los dos momentos en que hace falta: al aceptar (a recoger) y
          con el pasajero a bordo (al destino). Al llegar se oculta: ese paso es
          el del PIN y no se le pone nada encima. Es el mismo NavigationLauncher
          de la hoja, así que abre la misma app con la misma lógica. */}
      {(() => {
        // En web no hay app de navegación que abrir: ahí queda el botón de la hoja.
        if (Platform.OS === 'web') return null;
        if (role !== 'DRIVER' || !isActiveTrip || incomingPassengerRequest || !driverCurrentRide) return null;
        const aRecoger = rideStatus === 'ACCEPTED';
        const alDestino = rideStatus === 'IN_PROGRESS';
        if (!aRecoger && !alDestino) return null;
        const lat = Number(aRecoger
          ? (driverCurrentRide.originLat ?? driverCurrentRide.origin_lat)
          : (driverCurrentRide.destinationLat ?? driverCurrentRide.destination_lat));
        const lng = Number(aRecoger
          ? (driverCurrentRide.originLng ?? driverCurrentRide.origin_lng)
          : (driverCurrentRide.destinationLng ?? driverCurrentRide.destination_lng));
        const hayPunto = Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
        // El servidor le puso punto a un destino escrito a mano: casi siempre
        // acierta, pero la burbuja es lo que el chofer toca, y el aviso largo
        // vive al final de la hoja. El rótulo lo dice en dos palabras.
        const aproximado = alDestino && hayPunto && driverCurrentRide.destinoAproximado === true;
        // ── EL FALLO QUE REPORTÓ EL FUNDADOR (17-sep-2026) ──────────────────
        // Tras el PIN la burbuja desaparecía: el destino se había escrito a
        // mano, llegó sin coordenadas y aquí se devolvía null. El chofer se
        // quedaba sin NINGUNA forma de navegar al destino. Ahora, sin punto, la
        // burbuja navega por la dirección escrita (con su sector). El servidor
        // además intenta ponerle punto al crear el viaje (functions/src/destino.ts).
        const textoDestino = alDestino
          ? [String(driverCurrentRide.destination ?? '').trim(), String(driverCurrentRide.destinationSector ?? '').trim()]
              .filter(Boolean).join(', ')
          : '';
        if (!hayPunto && !textoDestino) return null;
        return (
          <NavigationLauncher
            // Remontar al cambiar de tramo: el destino y el rótulo son otros.
            key={`burbuja-${String(driverCurrentRide.id ?? '')}-${rideStatus}-${hayPunto ? 'p' : 't'}`}
            variante="burbuja"
            etiqueta={aRecoger ? 'Ir a recoger' : (aproximado ? 'Destino aprox.' : 'Ir al destino')}
            rideId={String(driverCurrentRide?.id ?? '') || null}
            destinationLat={hayPunto ? lat : null}
            destinationLng={hayPunto ? lng : null}
            destinoTexto={textoDestino || undefined}
            destinationLabel={aRecoger
              ? (driverCurrentRide.originAddress || 'Punto de recogida')
              : (driverCurrentRide.destination || 'Destino')}
            estiloContenedor={{ position: 'absolute', left: 16, bottom: altoHoja + 16, zIndex: 20 }}
          />
        );
      })()}

      {/* HEADER OVERLAY */}
      <View style={[styles.headerOverlay, { backgroundColor: colors.primary }]}>
        <View style={styles.roleBadge}>
          {/* El invitado veía "Modo Conductor" por descarte — confuso para
              cualquiera, y muy malo para el revisor de Google Play. */}
          {/* Cada rol se nombra explícitamente. El `else` decía "Modo
              Conductor" para TODO lo que no reconocía, así que un dueño de
              restaurante o de rent car abría el mapa y leía que era chofer.
              Verificado en el navegador con el rol RESTAURANT_OWNER. */}
          <Text style={styles.roleText}>
            {role === 'PASSENGER' ? 'Modo Pasajero'
             : role === 'DELIVERY_DRIVER' ? 'Modo Repartidor'
             : role === 'SCHOOL_DRIVER' ? 'Modo Transporte Escolar'
             : role === 'DRIVER' ? 'Modo Chofer'
             : role === 'FOOD_DELIVERY' ? 'Modo Comida'
             : role === 'RESTAURANT_OWNER' ? 'Modo Restaurante'
             : role === 'RENT_CAR_OWNER' ? 'Modo Rent Car'
             : role === 'CAR_RENTER' ? 'Modo Busco Vehículo'
             : role === 'SCHOOL_DIRECTOR' ? 'Modo Escuela'
             : 'Go Safely'}
            {invitado ? ' · Vista previa' : ''}
          </Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
           <Text style={[styles.logoText, { color: colors.white }]}>Go <Text style={styles.logoHighlight}>Safely</Text></Text>
           <TouchableOpacity onPress={() => router.push('/notifications')} style={styles.bellButton}>
             <Ionicons name="notifications-outline" size={24} color={colors.white} />
             {/* FIX BUG #5 — El badge solo aparece si hay no-leídas reales
                 (antes estaba siempre encendido). */}
             {unreadNotifs > 0 && <View style={[styles.notifBadge, { backgroundColor: colors.error }]} />}
           </TouchableOpacity>
        </View>
      </View>

      {/* PROMO BANNERS — ocultos durante un viaje activo. En pleno viaje el
          usuario necesita ver SU viaje, no un cupón: es como lo hacen Uber e
          InDriver y lo pidió el fundador expresamente. */}
      {!isActiveTrip && role === 'PASSENGER' && (
        <View style={styles.promoBannerPassenger}>
          <Ionicons name={PASSENGER_PROMOS[activePromoIndex].icon as any} size={20} color={PASSENGER_PROMOS[activePromoIndex].color} />
          <Text style={styles.promoTextPassenger}>
            <Text style={{fontWeight: 'bold'}}>{PASSENGER_PROMOS[activePromoIndex].title}</Text> {PASSENGER_PROMOS[activePromoIndex].text}
          </Text>
        </View>
      )}

      {!isActiveTrip && role === 'DRIVER' && (
        <View style={styles.promoBannerDriver}>
          <Ionicons name={DRIVER_PROMOS[activePromoIndex].icon as any} size={20} color={DRIVER_PROMOS[activePromoIndex].color} />
          <Text style={styles.promoTextDriver}>
            <Text style={{fontWeight: 'bold'}}>{DRIVER_PROMOS[activePromoIndex].title}</Text> {DRIVER_PROMOS[activePromoIndex].text}
          </Text>
        </View>
      )}

      {/* ── EL MAPA SE ABRIÓ SIN TODO LO QUE QUERÍA ─────────────────────────
          La pantalla ya no se queda esperando para siempre (ver el reloj del
          escudo), pero avanzar callado sería peor: la persona vería el mapa
          centrado en el centro de la ciudad sin su punto azul y sin saber por
          qué. Esto lo dice, y es PULSABLE: reintentar la ubicación es la salida.

          Cuando falta la sesión no hay nada que reintentar desde aquí, así que
          el toque no hace nada y el texto dice qué hacer. */}
      {avisoArranque && (
        <TouchableOpacity
          activeOpacity={avisoArranque.pulsable ? 0.8 : 1}
          onPress={avisoArranque.pulsable ? centerOnMyLocation : undefined}
          style={[
            styles.franjaRuta,
            {
              top: topFranjaBase,
              backgroundColor: colors.card,
              borderColor: colors.error,
            },
          ]}
        >
          <Ionicons name="warning-outline" size={16} color={colors.error} />
          <Text style={[styles.franjaRutaTexto, { color: colors.text }]} numberOfLines={3}>{avisoArranque.texto}</Text>
        </TouchableOpacity>
      )}

      {/* ── POR QUÉ NO HAY LÍNEA ────────────────────────────────────────────
          Hasta ahora el fallo de la ruta era MUDO: solo un `console.warn`.
          Desde el teléfono, en la calle, era imposible distinguir "la app está
          rota" de "Google no contestó" o de "estás encima del punto". Franja
          discreta, arriba, sin tapar el mapa ni robarle el toque.

          Se corre hacia abajo cuando el cintillo de promociones está puesto,
          para no montarse encima de él — y otro tanto cuando arriba está la
          franja de arranque, que si no se taparían entre ellas. */}
      {avisoRuta && (
        <View
          pointerEvents="none"
          style={[
            styles.franjaRuta,
            {
              top: topFranjaBase + (avisoArranque ? 52 : 0),
              // ── COLORES DEL TEMA, NO FIJOS ────────────────────────────
              // Eran un crema y un verde pálido escritos a mano, con el texto en
              // gris oscuro: en modo oscuro la franja se quedaba blanquísima
              // encima del mapa y no combinaba con nada. El tono lo llevan ahora
              // el borde y el icono, que sí existen en las dos paletas.
              backgroundColor: colors.card,
              // `espera` es su propio tono: un "todavía no llegó el dato" no es
              // un fallo (rojo) ni un logro (verde), y pintarlo de cualquiera de
              // los dos hacía que la franja dijera algo que no era.
              borderColor: avisoRuta.tono === 'error'
                ? colors.error
                : avisoRuta.tono === 'espera'
                  ? colors.textSecondary
                  : colors.success,
            },
          ]}
        >
          <Ionicons
            name={avisoRuta.tono === 'error'
              ? 'alert-circle-outline'
              : avisoRuta.tono === 'espera'
                ? 'time-outline'
                : 'checkmark-circle-outline'}
            size={16}
            color={avisoRuta.tono === 'error'
              ? colors.error
              : avisoRuta.tono === 'espera'
                ? colors.textSecondary
                : colors.success}
          />
          <Text style={[styles.franjaRutaTexto, { color: colors.text }]} numberOfLines={2}>{avisoRuta.texto}</Text>
        </View>
      )}

      {/* DRIVER SELECTION UI (PASSENGER) — preview al tocar un chofer en el
          mapa. GATE DURO: solo cuando NO hay ningún flujo de viaje activo
          (ni buscando, ni ofertas, ni viaje en curso, ni ride creado). Antes
          este card se colaba encima del viaje activo y confundía al pasajero
          ("Conductor Disponible" en vez del viaje en curso). */}
      {role === 'PASSENGER' && selectedDriver && !incomingDriverOffers && !isActiveTrip && !isSearching && !currentRideId && (
        <View onLayout={medirHoja} style={[styles.bottomSheet, { backgroundColor: colors.card, maxHeight: alto * 0.55, paddingBottom: 95 + margenAbajo }]}>
           <View style={{flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 15}}>
             <View>
               <Text style={[styles.sheetTitle, { color: colors.text, marginBottom: 5, textAlign: 'left' }]}>Chofer Cercano</Text>
               {/* ── EL SELLO SOLO CUANDO ES VERDAD ─────────────────────────
                   Era texto FIJO: se lo ponía a cualquier chofer que apareciera
                   en el mapa, verificado o no. Es el mismo sello que la ronda
                   pasada vino a apagar en las otras dos pantallas y aquí se
                   quedó encendido. El dato real —`isDriverVerified`, el que
                   escribe el KYC— ya viene en el chofer normalizado del mapa; si
                   no está verificado no se pinta NADA, que es lo único honesto
                   que se puede decir de alguien de quien no consta nada. */}
               {selectedDriver.isDriverVerified === true && (
                 <Text style={{color: '#00C853', fontWeight: 'bold', fontSize: 12}}>🛡️ Validado por Go Safely</Text>
               )}
             </View>
             <TouchableOpacity onPress={() => setSelectedDriver(null)} style={{padding: 5}}>
                <Ionicons name="close-circle" size={28} color={colors.textSecondary} />
             </TouchableOpacity>
           </View>

           <View style={{flexDirection: 'row', alignItems: 'center', marginBottom: 20, backgroundColor: 'rgba(0,0,0,0.03)', padding: 15, borderRadius: 15}}>
              {/* LA FOTO REAL, CUANDO LA HAY.
                  Antes esto era un `Ionicons name="person"` escrito a mano:
                  nunca una foto, aunque el servidor sí manda `driverPhoto` al
                  aprobar el KYC. El icono queda SOLO como respaldo cuando el
                  chofer todavía no tiene selfie aprobada — no se inventa nada. */}
              {selectedDriver.driverPhoto ? (
                <Image
                  source={{ uri: String(selectedDriver.driverPhoto) }}
                  style={{ width: 50, height: 50, borderRadius: 25, marginRight: 15 }}
                  resizeMode="cover"
                />
              ) : (
                <View style={{width: 50, height: 50, borderRadius: 25, backgroundColor: colors.primary, justifyContent: 'center', alignItems: 'center', marginRight: 15}}>
                  <Ionicons name="person" size={24} color="#FFF" />
                </View>
              )}
              <View style={{flex: 1}}>
                {/* Sin rating falso ni ETA inventada. Mostramos "Nuevo" si el
                    chofer no tiene calificaciones reales, y la distancia real
                    si la tenemos. */}
                <Text style={{fontSize: 18, fontWeight: 'bold', color: colors.text}}>
                  {selectedDriver.userName || selectedDriver.driverName || 'Chofer'}{'  '}
                  {(() => {
                    const r = ratingDisplay(selectedDriver.rating, selectedDriver.ratingCount);
                    return r.isNew
                      ? <Text style={{fontSize: 12, color: '#FF9500', fontWeight: '700'}}>Nuevo</Text>
                      : <Text style={{fontSize: 14, color: '#FFD700'}}>⭐ {r.label}</Text>;
                  })()}
                </Text>
                <Text style={{fontSize: 14, color: colors.textSecondary}}>
                  {selectedDriver.type === 'CAR' ? '🚗 Carro' : selectedDriver.type === 'MOTORCYCLE' ? '🏍️ Motor' : '🚐 Minibus'}
                  {typeof selectedDriver.lat === 'number' ? ` • ${distanceLabel(currentLocation, { origin_lat: selectedDriver.lat, origin_lng: selectedDriver.lng })}` : ''}
                </Text>
              </View>
           </View>

           {/* ── EL BOTÓN DECÍA LO QUE NO HACE ──────────────────────────────
               Decía "Solicitar Viaje" pegado a la foto de un chofer concreto, y
               lo que hace es abrir la subasta a TODOS los choferes cerca: este
               puede no ofertar nunca. Quien lo pulsaba creía estar pidiéndole el
               viaje a él y después no entendía por qué le llegaban ofertas de
               otros —o ninguna—. El botón y la línea de encima dicen ahora lo
               que pasa de verdad. */}
           <Text style={{ fontSize: 11.5, color: colors.textSecondary, marginBottom: 8, textAlign: 'center' }}>
             Tu solicitud se envía a todos los choferes cerca. Este puede ofertar o no.
           </Text>
           <Button3D
             title="Pedir viaje a los choferes cerca"
             onPress={() => {
               setSelectedDriver(null);
               handleRequestRide();
             }}
             color={colors.accent}
           />
        </View>
      )}

      {/* ANTI-FLICKER SHIELD — mientras decidimos si hay viaje que restaurar,
          mostramos un spinner discreto en vez del formulario, para no
          parpadear "Solicitar Viaje" antes de aparecer el viaje en curso. */}
      {role === 'PASSENGER' && resumeChecking && !isActiveTrip && !incomingDriverOffers && (
        <View onLayout={medirHoja} style={[styles.bottomSheet, { backgroundColor: colors.card, alignItems: 'center', paddingVertical: 30, maxHeight: alto * 0.55, paddingBottom: 95 + margenAbajo }]}>
          <ActivityIndicator size="small" color={colors.primary} />
          <Text style={{ color: colors.textSecondary, marginTop: 10, fontWeight: '600' }}>Restaurando tu sesión…</Text>
        </View>
      )}

      {/* PASSENGER UI — formulario de solicitud. Oculto durante viaje activo
          y mientras resolvemos el resume.
          El INVITADO también lo ve: antes entraba y entontraba un mapa gris sin
          un solo control, así que ni el revisor de Google ni un curioso podían
          probar la app. Al pulsar "Solicitar viaje" ya existe un guard que le
          pide crear cuenta (más abajo en handleRequestRide), que hasta ahora era
          código muerto porque el botón nunca llegaba a dibujarse. */}
      {/* El alto de la hoja sube de 0,5 a 0,58 porque el formulario tiene un
          campo MÁS (la recogida). Con el tope viejo, el botón "Solicitar Viaje"
          quedaba enterrado bajo dos pantallas de desplazamiento. Sigue por
          debajo del 0,6 que ya usa la hoja de ofertas. */}
      {role === 'PASSENGER' && !resumeChecking && !selectedDriver && !incomingDriverOffers && !isActiveTrip && (
        <View onLayout={medirHoja} style={[styles.bottomSheet, { backgroundColor: colors.card, maxHeight: alto * 0.58, paddingBottom: 95 + margenAbajo }]}>
          <View style={styles.sheetHandle} />
          <ScrollView showsVerticalScrollIndicator contentContainerStyle={{ paddingBottom: 20 }} keyboardShouldPersistTaps="handled" nestedScrollEnabled>
            {/* EL AVISO VA EN LAS DOS HOJAS DEL PASAJERO, Y ES A PROPÓSITO.
                En esta se PIDE el viaje y en la otra se VE: si el cintillo
                solo estuviera en una, habría media app donde el modo
                demostración es invisible. Él mismo se esconde cuando está
                apagado, así que en uso normal no ocupa ni un píxel.
                Ver components/CintilloDemostracion.tsx. */}
            <CintilloDemostracion />
            {/* SERVICE TYPE TABS */}
          <View style={[styles.serviceTypeContainer, { backgroundColor: '#F5F7FA', padding: 6, borderWidth: 1, borderColor: '#E4E7EB' }]}>
            <TouchableOpacity 
              style={[styles.serviceTab, serviceType === 'RIDE' && styles.serviceTabActive]} 
              onPress={() => {
                setServiceType('RIDE');
                // Volver a Viaje apaga logística: si no, el botón se queda
                // diciendo «Pedir cotización» en la pantalla de pedir un taxi.
                setEnvioPorLogistica(false);
              }}
            >
              <Text style={[styles.serviceTabText, serviceType === 'RIDE' && styles.serviceTabTextActive, { color: serviceType === 'RIDE' ? '#FFF' : '#627D98', fontSize: 15 }]}>
                🚕 Viaje
              </Text>
            </TouchableOpacity>

            <TouchableOpacity 
              style={[styles.serviceTab, serviceType === 'DELIVERY' && { backgroundColor: '#FF9500' }]} 
              onPress={() => {
                setServiceType('DELIVERY');
                // ══ EL FALLO QUE HABÍA AQUÍ ════════════════════════════
                //
                // El selector de vehículo se escondía en modo paquete —estaba
                // envuelto en `serviceType === 'RIDE'`— pero el campo
                // `vehicleType` seguía viajando en la solicitud con el valor
                // por defecto de este estado: 'CAR'. Y `dispatch.ts` filtra los
                // choferes por ese campo sin mirar `serviceType`.
                //
                // Resultado medido: TODO paquete salía pidiendo CARRO, así que
                // un motorista no veía ni un solo envío — justo quien debería
                // hacerlo, que es el más rápido y el más barato. Y si antes
                // habías elegido minibús para un viaje, tus llaves salían
                // pidiendo un minibús.
                //
                // Ahora el selector se ve en los dos modos, y al entrar en
                // paquete se arranca en MOTOR, que es lo que pide un paquete
                // normal. Si no cabe en un motor, el usuario lo cambia — pero
                // eligiendo, no por un valor que arrastró de otra pantalla.
                // Arranca en MOTOR, no en lo que arrastrara de la pestaña de
                // viajes. Un paquete normal va en motor: es el más rápido, el
                // más barato y el que más choferes tiene. Si no cabe, el
                // usuario lo cambia — pero ahora VIENDO el selector, que es lo
                // que faltaba.
                setSelectedVehicleType('MOTORCYCLE');
                setEnvioPorLogistica(false);
                // Entrar en la pestaña no es elegir: la marca se pone solo
                // cuando la persona TOCA una tarjeta.
                setVehiculoElegidoPorMi(false);
                // Y el conteo de pasajeros se ajusta al motor. Sin esto, quien
                // venía de elegir Minibús (que pone 6) se llevaba un viaje con
                // `vehicleType: 'MOTORCYCLE'` y `passengers: '6'` — un motor
                // para seis personas, y nada lo valía ni aquí ni en el servidor.
                setPassengers('1');
              }}
            >
              <Text style={[styles.serviceTabText, serviceType === 'DELIVERY' && styles.serviceTabTextActive, { color: serviceType === 'DELIVERY' ? '#FFF' : '#627D98', fontSize: 15 }]}>
                📦 Enviar Paquete
              </Text>
            </TouchableOpacity>
          </View>

          {/* El título decía "¿A dónde vamos?" justo encima de un campo que
              ahora pregunta DÓNDE TE RECOGEMOS: la misma confusión que este
              bloque vino a quitar. Nombra las dos direcciones, no una. */}
          <Text style={[styles.sheetTitle, { color: colors.text, marginTop: 12 }]}>
            {serviceType === 'RIDE' ? 'Recogida y destino' : 'Recogida y entrega'}
          </Text>
          
          {/* ══ CAMPO 1 — RECOGIDA ══════════════════════════════════════════
              Antes esta dirección NO existía como dato editable: solo aparecía
              dentro del texto de relleno del campo de destino ("Desde Calle
              18W, Cancino a…"), y en React Native ese texto se borra al
              escribir la primera letra. La caja anunciaba el origen mientras
              estaba vacía y después se quedaba solo con el destino: por eso
              confundía.

              Va en su propio contenedor con `zIndex` MÁS ALTO que el del
              destino: su desplegable de sugerencias se dibuja por encima del
              campo de abajo, no debajo de él. */}
          <View style={{ position: 'relative', zIndex: 120, marginBottom: 10 }}>
            <Text style={[styles.etiquetaCampo, { color: colors.textSecondary }]}>RECOGIDA</Text>
            <PlacesAutocomplete
              // Remontar limpio al cerrar un viaje (H35): ver `versionFormulario`.
              key={`recogida-${versionFormulario}`}
              placeholder="¿Dónde te recogemos?"
              initialValue={origenTexto}
              // ── EL VIAJE PARA LA MAMÁ ──────────────────────────────────
              // Sin coordenadas el despacho hace early-return y el viaje queda
              // invisible para TODOS los choferes. Aquí no vale el texto libre:
              // o se elige una sugerencia de Google, o no hay recogida.
              permitirTextoLibre={false}
              onPlaceSelected={(p) => {
                const label = p.formattedAddress || p.displayName || '';
                setOrigenTexto(label);
                const hayCoord = typeof p.lat === 'number' && typeof p.lng === 'number';
                setOrigenLat(hayCoord ? p.lat : null);
                setOrigenLng(hayCoord ? p.lng : null);
                // Deja de ser automático en cuanto el texto se aparta de la
                // dirección donde está el teléfono. Si vuelve a coincidir,
                // vuelve a rellenarse solo.
                //
                // VACIAR EL CAMPO ES VOLVER A "MI UBICACIÓN". Sin esta rama,
                // borrar la caja con la (X) dejaba un origen manual vacío y sin
                // coordenadas: un callejón sin salida del que solo se sale
                // eligiendo una sugerencia, aunque quien la vació solo quisiera
                // volver a su propia dirección.
                const vacio = label.trim().length === 0;
                setOrigenManual(!vacio && label.trim() !== String(currentAddressLabel ?? '').trim());
                Haptics.selectionAsync().catch(() => {});
              }}
              autoFocus={false}
            />
            {/* ── CUANDO EL GPS TODAVIA NO SABE DONDE ESTAS ─────────────
                Se dice en el sitio donde se mira —debajo de la direccion que
                esta mal— y no en un aviso suelto. Mientras el GPS afina, la
                direccion que se ensena es la ciudad y el codigo postal, sin
                calle: exactamente la senal de que el punto no es fino. */}
            {recogidaImprecisa && (
              <View style={[styles.avisoOtraPersona, { backgroundColor: 'rgba(255,149,0,0.10)', borderColor: '#FF9500' }]}>
                <Ionicons name="locate-outline" size={14} color="#B36200" style={{ marginRight: 6 }} />
                <Text style={[styles.avisoOtraPersonaTexto, { color: '#B36200' }]}>
                  Afinando tu ubicación
                  {typeof precisionMetros === 'number' ? ` (±${Math.round(precisionMetros)} m)` : ''}
                  . Espera unos segundos o escribe tu dirección para que el chofer llegue
                  exactamente donde estás.
                </Text>
              </View>
            )}
            {origenElegido && (
              <View style={[styles.avisoOtraPersona, { backgroundColor: colors.backgroundElement, borderColor: colors.border }]}>
                <Ionicons name="people-outline" size={14} color={colors.text} style={{ marginRight: 6 }} />
                <Text style={[styles.avisoOtraPersonaTexto, { color: colors.text }]}>
                  El chofer irá a esta dirección, no a donde estás tú. Si el viaje es para otra
                  persona, pásale el PIN cuando el chofer acepte.
                </Text>
              </View>
            )}
          </View>

          {/* ══ CAMPO 2 — DESTINO ═══════════════════════════════════════════ */}
          <View style={{ position: 'relative', zIndex: 99, marginBottom: 12 }}>
            <Text style={[styles.etiquetaCampo, { color: colors.textSecondary }]}>
              {serviceType === 'RIDE' ? 'DESTINO' : 'ENTREGA'}
            </Text>
            {/* PlacesAutocomplete REAL conectado a Google Places API vía Cloud Function.
                Reemplaza el array hardcoded SUGGESTIONS que solo conocía 5 lugares
                fijos. Ahora encuentra cualquier dirección, calle o POI en R.D. */}
            <PlacesAutocomplete
              // Remontar limpio al cerrar un viaje (H06): ver `versionFormulario`.
              key={`destino-${versionFormulario}`}
              placeholder={serviceType === 'RIDE' ? 'Calle, número y sector' : 'Dirección de entrega'}
              initialValue={destination}
              onPlaceSelected={(p) => {
                const label = p.formattedAddress || p.displayName || '';
                setDestination(label);
                // H4 — Guardar las coords del destino para propagar al ride.
                // Son la ÚNICA señal de "el destino tiene punto en el mapa":
                // el booleano paralelo que había aquí podía quedar apagado con
                // las coordenadas puestas y por ahí salían viajes sin sector.
                setDestinationLat(typeof p.lat === 'number' ? p.lat : null);
                setDestinationLng(typeof p.lng === 'number' ? p.lng : null);
                Haptics.selectionAsync().catch(() => {});
              }}
              autoFocus={false}
            />

            {/* ── ESTE SELLO SOLO SABE UNA COSA ──────────────────────────
                Decía "Coordenadas GPS validadas y seguras", y su única
                condición es `hayCoordenadaDestino`: que el destino traiga un
                punto. No se valida nada contra nada, y "seguras" no describe
                ninguna comprobación que exista en el código. Ahora dice
                exactamente lo que la condición sabe. */}
            {hayCoordenadaDestino && (
              <View style={styles.gpsValidatedBadge}>
                <Ionicons name="shield-checkmark" size={12} color="#00C853" style={{ marginRight: 6 }} />
                <Text style={styles.gpsValidatedText}>Destino ubicado en el mapa ✓</Text>
              </View>
            )}

            {/* ══ SECTOR OBLIGATORIO ══════════════════════════════════════
                Aparece en cuanto hay DESTINO ESCRITO, que es cuando el pasajero
                ya sabe a dónde va y el campo no estorba.

                Cuelga del destino, no de un booleano paralelo ni de las
                coordenadas: el booleano se apagaba al cambiar de pestaña sin
                borrar las coordenadas, y las coordenadas faltan siempre que la
                dirección se escribe a mano. Por las dos puertas salía lo mismo:
                un viaje con destino y `destinationSector: null`.

                POR QUÉ ES OBLIGATORIO (lo pidió el fundador, que fue chofer):
                mucha gente escribe solo el nombre de la calle. En Santo Domingo
                la misma calle existe en varios sectores y a distancias muy
                distintas, así que el chofer acepta a ciegas y descubre tarde
                que el viaje es el doble de largo. De ahí salen las discusiones
                y las cancelaciones.

                Con el sector delante, el chofer decide con la información
                completa ANTES de aceptar. Es la queja número uno contra Uber e
                InDriver aquí.

                CUELGA DEL DESTINO ESCRITO, NO DE SUS COORDENADAS: la caja del
                destino admite texto libre, y con la calle escrita a mano este
                campo no se dibujaba y la validación no exigía nada — el viaje
                salía con destino y sin sector. Sin punto en el mapa el sector es
                lo ÚNICO que sitúa el viaje. */}
            {hayDestinoEscrito && (
              <View style={{ marginTop: 10 }}>
                <Text style={[styles.etiquetaSector, { color: colors.text }]}>
                  ¿En qué sector, barrio o residencial? <Text style={{ color: colors.error }}>*</Text>
                </Text>
                <TextInput
                  style={[
                    styles.campoSector,
                    // Verde cuando está puesto, rojo mientras falta: es un campo
                    // OBLIGATORIO y el ámbar de antes no existe en la paleta —
                    // sobre el fondo oscuro se perdía y el campo parecía opcional.
                    { color: colors.text, borderColor: sectorDestino.trim() ? colors.success : colors.error },
                  ]}
                  placeholder="Ej. Los Prados, Villa Mella, Residencial El Edén"
                  placeholderTextColor={colors.textSecondary}
                  value={sectorDestino}
                  onChangeText={setSectorDestino}
                  maxLength={60}
                />
                <Text style={[styles.ayudaSector, { color: colors.textSecondary }]}>
                  El chofer necesita saber la zona. Una calle sola puede estar en varios sectores.
                </Text>
              </View>
            )}
          </View>

          {/* ══ SELECTOR DE TIPO DE TRANSPORTE ════════════════════════════
              Antes esto estaba envuelto en `serviceType === 'RIDE'`, o sea que
              en modo paquete DESAPARECÍA — pero el campo seguía viajando con
              'CAR' por defecto. Ver el comentario de la pestaña de arriba.

              El minibús solo sale en modo VIAJE: es el de 5-6 pasajeros y no
              tiene nada que hacer en un envío. Y en modo PAQUETE sale además
              Logística, que es otro camino — no crea un viaje. */}
          <View style={{ marginBottom: 16 }}>
              <Text style={[styles.vehicleSelectorLabel, { color: colors.textSecondary }]}>
                {serviceType === 'RIDE' ? 'TIPO DE TRANSPORTE SOLICITADO' : '¿EN QUÉ LO ENVÍAS?'}
              </Text>
              <View style={styles.vehicleSelectorRow}>
                <TouchableOpacity 
                  style={[
                    styles.vehicleTypeCard, 
                    selectedVehicleType === 'MOTORCYCLE' && !envioPorLogistica && { backgroundColor: 'rgba(0, 200, 83, 0.1)', borderColor: '#00C853', borderWidth: 2 },
                    { borderColor: colors.border }
                  ]}
                  onPress={() => {
                    setSelectedVehicleType('MOTORCYCLE');
                    setEnvioPorLogistica(false);
                    setVehiculoElegidoPorMi(true);
                    setPassengers('1');
                  }}
                >
                  <Image source={require('../../../assets/images/motor.png')} style={styles.vehicleIconImage} resizeMode="contain" />
                  <Text style={[styles.vehicleTypeName, { color: colors.text }]}>Motor</Text>
                  <Text style={styles.vehicleTypeDesc}>1 pas.</Text>
                </TouchableOpacity>

                <TouchableOpacity 
                  style={[
                    styles.vehicleTypeCard, 
                    selectedVehicleType === 'CAR' && !envioPorLogistica && { backgroundColor: 'rgba(0, 200, 83, 0.1)', borderColor: '#00C853', borderWidth: 2 },
                    { borderColor: colors.border }
                  ]}
                  onPress={() => {
                    setSelectedVehicleType('CAR');
                    setEnvioPorLogistica(false);
                    setVehiculoElegidoPorMi(true);
                    if (parseInt(passengers, 10) > 4 || isNaN(parseInt(passengers, 10)) || parseInt(passengers, 10) < 1) {
                      setPassengers('1');
                    }
                  }}
                >
                  <Image source={require('../../../assets/images/carro.png')} style={styles.vehicleIconImage} resizeMode="contain" />
                  <Text style={[styles.vehicleTypeName, { color: colors.text }]}>Carro</Text>
                  <Text style={styles.vehicleTypeDesc}>1-4 pas.</Text>
                </TouchableOpacity>

                {/* El minibús es el de 5-6 PASAJEROS. No sale en modo paquete:
                    no tiene nada que hacer en un envío, y es justo el que el
                    fundador declaró intocable. */}
                {serviceType === 'RIDE' && (
                  <TouchableOpacity
                    style={[
                      styles.vehicleTypeCard,
                      selectedVehicleType === 'MINIBUS' && { backgroundColor: 'rgba(0, 200, 83, 0.1)', borderColor: '#00C853', borderWidth: 2 },
                      { borderColor: colors.border }
                    ]}
                    onPress={() => {
                      setSelectedVehicleType('MINIBUS');
                      setEnvioPorLogistica(false);
                      setVehiculoElegidoPorMi(true);
                      if (parseInt(passengers, 10) <= 4 || isNaN(parseInt(passengers, 10))) {
                        setPassengers('6');
                      }
                    }}
                  >
                    <Image source={require('../../../assets/images/mini bus.png')} style={styles.vehicleIconImage} resizeMode="contain" />
                    <Text style={[styles.vehicleTypeName, { color: colors.text }]}>Minibus</Text>
                    <Text style={styles.vehicleTypeDesc}>5+ pas.</Text>
                  </TouchableOpacity>
                )}

                {/* ══ LOGÍSTICA ══════════════════════════════════════════════
                    Aquí conecta el renglón nuevo, y es el sitio que señaló el
                    fundador: la persona que quiere mover algo ya está en esta
                    pantalla.

                    Pero NO es un vehículo más de la misma lista: es otro
                    camino. Un motor o un carro se despachan al instante por un
                    precio que el pasajero ofrece; una mudanza no se puede
                    despachar así — hay que verla y cotizarla. Por eso esta
                    tarjeta no crea un viaje: lleva a pedir cotizaciones. */}
                {serviceType === 'DELIVERY' && VERTICAL_LOGISTICA_VISIBLE && (
                  <TouchableOpacity
                    style={[
                      styles.vehicleTypeCard,
                      envioPorLogistica && { backgroundColor: 'rgba(239, 108, 0, 0.1)', borderColor: '#EF6C00', borderWidth: 2 },
                      { borderColor: colors.border }
                    ]}
                    onPress={() => setEnvioPorLogistica(true)}
                  >
                    <Ionicons name="cube" size={28} color={envioPorLogistica ? '#EF6C00' : colors.textSecondary} />
                    <Text style={[styles.vehicleTypeName, { color: colors.text }]}>Logística</Text>
                    <Text style={styles.vehicleTypeDesc}>Camioneta, camión</Text>
                  </TouchableOpacity>
                )}
              </View>

              {envioPorLogistica && (
                <Text style={{ color: colors.textSecondary, fontSize: 13, lineHeight: 19, marginTop: 8 }}>
                  Para lo que no cabe en un carro: mudanzas, carga, materiales o un autobús
                  para gira. Pones lo que necesitas, los transportistas te cotizan y tú eliges.
                </Text>
              )}
            </View>

          <View style={[styles.row, { marginBottom: 15 }]}>
            {serviceType === 'RIDE' ? (
              <TextInput 
                style={[styles.input, { flex: 1, marginBottom: 0, backgroundColor: colors.background, borderColor: colors.border, color: colors.text }]}
                placeholder="Pasajeros (ej. 1)"
                placeholderTextColor={colors.textSecondary}
                keyboardType="number-pad"
                value={passengers}
                onChangeText={handlePassengersChange}
              />
            ) : (
              <TextInput 
                style={[styles.input, { flex: 1, marginBottom: 0, backgroundColor: colors.background, borderColor: colors.border, color: colors.text }]}
                placeholder="📦 ¿Qué envías? (ej. Llaves)"
                placeholderTextColor={colors.textSecondary}
                value={packageDescription}
                onChangeText={setPackageDescription}
              />
            )}
          </View>

          {/* GRAN INPUT DE TARIFA */}
          <View style={{ marginBottom: 20 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8, paddingHorizontal: 4 }}>
              <Ionicons name="pricetag" size={16} color={isFreeTrip ? "#FF9500" : "#00C853"} style={{ marginRight: 6 }} />
              <Text style={{ fontSize: 13, fontWeight: '900', color: isFreeTrip ? "#FF9500" : "#00C853", textTransform: 'uppercase', letterSpacing: 1 }}>
                {isFreeTrip
                  ? (freeTripReason === 'FIRST' ? '¡Tu PRIMER viaje es GRATIS!' : '¡Tienes un viaje GRATIS!')
                  : tarifaSugerida !== null
                    ? 'Tu Tarifa (RD$) — la app ya la calculó'
                    : 'Indica tu Tarifa (RD$)'}
              </Text>
            </View>
            {isFreeTrip ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255, 149, 0, 0.1)', borderWidth: 2, borderColor: '#FF9500', borderRadius: 16, paddingHorizontal: 20, height: 70, justifyContent: 'center' }}>
                <Text style={{ fontSize: 20, fontWeight: '900', color: '#FF9500', textAlign: 'center' }}>🎁 Tarifa Cubierta por la App</Text>
              </View>
            ) : (
              <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(0, 200, 83, 0.1)', borderWidth: 2, borderColor: '#00C853', borderRadius: 16, paddingHorizontal: 20, height: 70 }}>
                <Text style={{ fontSize: 32, fontWeight: '900', color: '#00C853', marginRight: 10 }}>$</Text>
                <TextInput
                  style={{ flex: 1, height: '100%', fontSize: 32, fontWeight: 'bold', color: '#00C853', outlineStyle: 'none' } as any}
                  placeholder="0"
                  placeholderTextColor="rgba(0, 200, 83, 0.4)"
                  keyboardType="numeric"
                  value={offerPrice}
                  // SOLO DIGITOS, Y SE LIMPIA AL ESCRIBIR, NO AL VALIDAR.
                  // `keyboardType="numeric"` deja teclear el separador, y en RD
                  // la gente escribe 1,500 o 1.500 para decir mil quinientos.
                  // `parseInt('1,500')` es 1: un numero positivo, finito y por
                  // debajo del techo, asi que se colaba por los TRES guardarrailes
                  // de handleRequestRide (NaN, <=0 y >100000) sin un solo aviso.
                  // El pasajero creia ofrecer RD$1,500 y al chofer le llegaba una
                  // oferta de RD$1. Encontrado el 15-oct-2026, con la app en Play.
                  // Se corta AQUI y no en cada parseInt porque hay cuatro (2965,
                  // 3055, 3189 y el de esta pantalla): limpiando la entrada quedan
                  // los cuatro a salvo sin tocar ninguno.
                  onChangeText={(v) => setOfferPrice(v.replace(/[^0-9]/g, ''))}
                />
                <Ionicons name="cash" size={32} color="#00C853" style={{ opacity: 0.8 }} />
              </View>
            )}
            {/* AQUÍ ESTABA LA FUNCIÓN MÁS DIFERENTE DE LA APP, Y ERA INVISIBLE.
                El pasajero veía una casilla con «$0» y nada que le dijera que
                ese número lo pone ÉL y que el chofer puede aceptarlo o
                contraofertar. Quien no lo sabe, no lo usa. Una línea. */}
            {/* En el viaje GRATIS no hay casilla de precio (arriba sale «Tarifa
                cubierta por la app»), así que aquí no se puede decir «el que
                acuerden es el que se paga»: sería mentira. Se aprovecha para
                enseñarle la función de cara al siguiente viaje. */}
            <Text style={{ fontSize: 12, color: colors.textSecondary, marginTop: 6, lineHeight: 17 }}>
              {isFreeTrip
                ? 'Este no lo pagas tú. Desde el próximo, el precio lo ofreces tú y el chofer lo acepta o te propone otro.'
                : 'Tú ofreces el precio. El chofer lo acepta o te propone otro, y el que acuerden es el que se paga.'}
            </Text>
            {/* UNA NOTA PARA EL CHOFER, ANTES DE QUE ACEPTE.
                Para «prefiero un carro comodo», «llevo dos maletas», «estoy
                en el porton azul». No cambia a quien se le manda la
                solicitud: el chofer la lee y decide. En los envios no sale,
                porque ahi ya esta «¿Que envias?». */}
            {serviceType !== 'DELIVERY' && (
              <TextInput
                style={[styles.input, { marginTop: 10, marginBottom: 0, backgroundColor: colors.background, borderColor: colors.border, color: colors.text }]}
                placeholder="Nota para el chofer (opcional)"
                placeholderTextColor={colors.textSecondary}
                value={notaDelPasajero}
                onChangeText={setNotaDelPasajero}
                maxLength={200}
              />
            )}
            {/* ── EL PRECIO QUE CALCULA LA APP (27-sep-2026) ──────────────────
                Pedido del fundador: «la app también debe calcular el precio de
                la tarifa según el destino del pasajero, como mismo lo tiene
                Uber, ya que algunos no escogen la sección de poner el monto».
                Se pinta SOLO cuando hay destino con coordenadas — sin eso no
                hay cálculo honesto que enseñar. No pisa lo que la persona
                escriba: es un botón, y ella decide. */}
            {!isFreeTrip && tarifaSugerida !== null && (
              <TouchableOpacity
                onPress={() => setOfferPrice(String(tarifaSugerida))}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityLabel={`Usar la tarifa calculada por la app, ${tarifaSugerida} pesos`}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginTop: 8,
                  paddingVertical: 10,
                  paddingHorizontal: 14,
                  borderRadius: 12,
                  borderWidth: 1,
                  borderColor: String(offerPrice) === String(tarifaSugerida) ? '#00C853' : colors.border,
                  backgroundColor: String(offerPrice) === String(tarifaSugerida)
                    ? 'rgba(0,200,83,0.10)'
                    : colors.background,
                }}
              >
                <View style={{ flex: 1, paddingRight: 10 }}>
                  <Text style={{ color: colors.text, fontSize: 14, fontWeight: '700' }}>
                    {String(offerPrice) === String(tarifaSugerida)
                      ? `Tarifa de la app: ${pesos(tarifaSugerida)}`
                      : `La app calcula ${pesos(tarifaSugerida)} para este viaje`}
                  </Text>
                  <Text style={{ color: colors.textSecondary, fontSize: 12, marginTop: 2 }}>
                    {kmEstimadosDelViaje !== null
                      ? `Unos ${kmEstimadosDelViaje.toFixed(1)} km. Puedes ofrecer más o menos: el chofer decide si acepta.`
                      : 'Puedes ofrecer más o menos: el chofer decide si acepta.'}
                  </Text>
                </View>
                {String(offerPrice) === String(tarifaSugerida) ? (
                  <Ionicons name="checkmark-circle" size={22} color="#00C853" />
                ) : (
                  <Text style={{ color: '#00C853', fontWeight: '800', fontSize: 13 }}>USAR</Text>
                )}
              </TouchableOpacity>
            )}

            {/* En el viaje cubierto NO hay campo de precio, pero el chofer sí
                cobra: se dice cuánto le paga la app, que es lo que antes eran
                250 fijos fuera donde fuera. */}
            {isFreeTrip && tarifaSugerida !== null && (
              <Text style={{ fontSize: 12, color: colors.textSecondary, marginTop: 6, paddingHorizontal: 4 }}>
                {`La app le pagará ${pesos(Math.min(tarifaSugerida, TOPE_VIAJE_CUBIERTO_DOP))} al chofer por este viaje (unos ${(kmEstimadosDelViaje ?? 0).toFixed(1)} km).`}
              </Text>
            )}

            {/* ══════════════════════════════════════════════════════════════
                SE PASA DEL VIAJE GRATIS: SE DICE ANTES, Y CUÁNTO
                ══════════════════════════════════════════════════════════════
                Decisión del fundador (1-oct-2026): «si pasa del tope, que la app
                lo detecte y le diga claramente que está pasando del rango, y si
                está dispuesto a pagar la diferencia».

                Y cierra algo que ya estaba pasando: el tope de RD$800 existe
                desde antes (`MAX_FREE_RIDE_CREDIT_DOP`, functions/src/
                loyalty.ts) y recorta el crédito del chofer. Pero NADIE lo decía:
                en un viaje de RD$1.200 el chofer hacía la carrera entera y se
                comía RD$400 en silencio. El abuso que había que evitar ya
                existía, y lo pagaba el chofer.

                La cifra se calcula con la misma tabla que usa el servidor
                (`suggestFare` ↔ `TARIFAS_DEL_SERVIDOR`), y el servidor la vuelve
                a calcular y la escribe en el viaje — aquí solo se avisa. */}
            {isFreeTrip && tarifaSugerida !== null && tarifaSugerida > TOPE_VIAJE_CUBIERTO_DOP && (
              <View style={styles.avisoDiferencia}>
                <Ionicons name="alert-circle" size={17} color="#B36200" />
                <Text style={styles.avisoDiferenciaTexto}>
                  {`Este viaje pasa del rango que cubre el viaje gratis. La app pone ${pesos(TOPE_VIAJE_CUBIERTO_DOP)} y tú le pagas al chofer la diferencia: ${pesos(tarifaSugerida - TOPE_VIAJE_CUBIERTO_DOP)}. Si no quieres pagarla, elige un destino más cerca.`}
                </Text>
              </View>
            )}

            {/* V24 — por qué no sale el primer viaje gratis (H04). */}
            {!isFreeTrip && primerGratisEnEspera && !firstRideUsed && (
              <Text style={{ fontSize: 12, color: colors.textSecondary, marginTop: 6, paddingHorizontal: 4 }}>
                Tu primer viaje gratis se activa cuando tu cuenta tenga teléfono verificado, correo verificado o cédula.
              </Text>
            )}
          </View>

          {/* PAYMENT & PREFERENCES ROW */}
          <View style={styles.preferencesRow}>
            <TouchableOpacity 
              style={[styles.prefPill, { backgroundColor: 'rgba(0, 200, 83, 0.1)', borderColor: '#00C853' }]}
              onPress={() => setPaymentModalVisible(true)}
            >
              {/* Eran DOS casos para TRES métodos: cualquier cosa que no fuera
                  efectivo se rotulaba «Tarjeta». El pasajero elegía
                  Transferencia y su propia pastilla le decía Tarjeta — encima
                  de una opción que ni siquiera está habilitada para viajes
                  (CUSTOMER_CARD_ENABLED). El resto del proyecto ya lo hace
                  bien; este sitio se quedó atrás. */}
              <Ionicons
                name={paymentMethod === 'CASH' ? 'cash' : paymentMethod === 'TRANSFER' ? 'business' : 'card'}
                size={16}
                color="#00C853"
              />
              <Text style={[styles.prefText, { color: '#00C853', fontWeight: 'bold' }]}>
                {paymentMethod === 'CASH' ? 'Efectivo' : paymentMethod === 'TRANSFER' ? 'Transferencia' : 'Tarjeta'}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity 
              style={[styles.prefPill, { backgroundColor: preferences.quiet ? colors.accent : colors.background }]}
              onPress={() => setPreferences(p => ({...p, quiet: !p.quiet}))}
            >
              <Ionicons name="volume-mute" size={16} color={preferences.quiet ? '#FFF' : colors.text} />
              <Text style={[styles.prefText, { color: preferences.quiet ? '#FFF' : colors.text }]}>Silencio</Text>
            </TouchableOpacity>

            <TouchableOpacity 
              style={[styles.prefPill, { backgroundColor: preferences.ac ? colors.accent : colors.background }]}
              onPress={() => setPreferences(p => ({...p, ac: !p.ac}))}
            >
              <Ionicons name="snow" size={16} color={preferences.ac ? '#FFF' : colors.text} />
              <Text style={[styles.prefText, { color: preferences.ac ? '#FFF' : colors.text }]}>A/C</Text>
            </TouchableOpacity>
          </View>

          </ScrollView>
          {/* ── EL RADAR VA FUERA DEL SCROLL, Y NO ES UN DETALLE ────────

              Este bloque vivia DENTRO del ScrollView, al final del todo.
              Con el formulario entero por encima —pestanas, «Donde te
              recogemos», «A donde vas», preferencias— quedaba unos 400-600 dp
              mas abajo del pliegue: habia que desplazar dos o tres veces para
              ver que la app seguia buscando.

              Y eso es justo lo que rompia el rechazo. Al rechazar una oferta
              la hoja de ofertas se cierra y esta se MONTA DE CERO, con el
              scroll arriba: el pasajero veia su direccion puesta y el
              formulario tal cual, identico a antes de pedir el viaje. La
              busqueda seguia viva —`isSearching` en true y la solicitud
              PENDING en el servidor— pero en la pantalla no habia ni rastro.
              Lo reporto el fundador como «se cancela la busqueda» el 19-sep y
              otra vez el 17-oct: la primera vez se arreglo el ESTADO y nadie
              movio el radar de sitio.

              Peor todavia: lo que si se veia era el boton «Solicitar Viaje».
              Tocarlo CANCELA la solicitud anterior (`requestRide` limpia los
              PENDING previos del mismo pasajero, firestoreService.ts:757), asi
              que la pantalla invitaba al unico gesto que de verdad cancelaba.

              Fuera del scroll el pie siempre esta a la vista, y mientras se
              busca lo que se ve es «Buscando choferes cercanos...» y
              «Cancelar busqueda» — no un boton para volver a pedir. Es el
              mismo patron que la hoja de ofertas, cuyo «Cancelar busqueda»
              tambien vive detras de su `</ScrollView>` y por eso no se pierde.

              RECHAZAR A UN CHOFER NO ES CANCELAR: la solicitud sigue viva
              para los demas y el radar tiene que decirlo. */}
          {isSearching ? (
            // FIX UX — Antes solo había botón "Buscando..." y el user no
            // tenía cómo cancelar si ningún chofer aceptaba. Ahora mostramos
            // el estado + un botón rojo de cancelación claro.
            <View style={{ gap: 10 }}>
              {/* FIX RECHAZO vc13 de Google Play — "Unresponsive UI elements,
                  such as buttons or icons". Aquí había un <Button3D> con
                  onPress vacío: se hundía al tocarlo y no pasaba nada. Un
                  revisor lo prueba, no reacciona, y lo reporta como roto.
                  Ahora es un INDICADOR de estado (no táctil, sin apariencia
                  de botón) con spinner. La acción real — cancelar — está en
                  el botón de abajo, que sí responde. */}
              <View
                accessible
                accessibilityRole="progressbar"
                accessibilityLabel="Buscando choferes cercanos"
                style={{
                  height: 52,
                  borderRadius: 12,
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  backgroundColor: colors.card,
                  borderWidth: 1,
                  borderColor: colors.border,
                }}
              >
                <ActivityIndicator size="small" color={serviceType === 'RIDE' ? colors.accent : '#FF9500'} />
                {/* EL MENSAJE CAMBIA, PORQUE LA ESPERA CAMBIA.
                    Decía «Buscando choferes cercanos…» del segundo 1 al 360:
                    seis minutos con el mismo texto y la misma ruedita. A los
                    veinte segundos la persona ya no está leyendo un estado,
                    está preguntándose si la app se colgó — y el revisor de
                    Apple, que prueba desde un sitio sin un solo chofer
                    conectado, concluye exactamente eso.
                    El tope de 6 minutos NO se toca: un pasajero de Santo
                    Domingo a las 3 de la mañana puede esperar dos o tres
                    minutos legítimamente, y recortárselo le quitaría un viaje
                    de verdad. Lo que cambia es lo que se le DICE. */}
                <Text style={{ color: colors.text, fontSize: 15, fontWeight: '600', textAlign: 'center', flexShrink: 1 }}>
                  {buscandoHaceRato
                    ? 'Todavía no hay choferes cerca. Seguimos buscando…'
                    : 'Buscando choferes cercanos…'}
                </Text>
              </View>
              <TouchableOpacity
                style={{
                  height: 48,
                  borderRadius: 12,
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'row',
                  borderWidth: 1.5,
                  borderColor: '#FF3B30',
                  backgroundColor: 'rgba(255,59,48,0.08)',
                }}
                // Una sola función para los dos botones de cancelar (este y el
                // de la hoja de ofertas). El aviso de fallo va por `showAlert`:
                // `Alert.alert` no pinta NADA en web, y esta pantalla también
                // corre ahí. Ver `cancelarBusqueda`.
                onPress={cancelarBusqueda}
                activeOpacity={0.7}
              >
                <Ionicons name="close-circle" size={18} color="#FF3B30" style={{ marginRight: 6 }} />
                <Text style={{ color: '#FF3B30', fontSize: 15, fontWeight: '700' }}>Cancelar búsqueda</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <Button3D
              // H12 — mientras la solicitud viaja (GPS + creación del viaje) el
              // botón lo dice; el segundo toque ya no crea otro viaje.
              title={
                solicitandoViaje
                  ? 'Enviando solicitud…'
                  : envioPorLogistica
                    ? 'Pedir cotizaciones'
                    : serviceType === 'RIDE' ? 'Solicitar Viaje' : 'Solicitar Repartidor'
              }
              // Logística NO pasa por `handleRequestRide`: no crea un viaje ni
              // entra en el reparto de choferes. Lleva a su propia pantalla, y
              // de ahí sale un trabajo que los transportistas cotizan.
              onPress={
                envioPorLogistica
                  ? () => router.push('/logistica-pedir' as any)
                  : handleRequestRide
              }
              color={
                envioPorLogistica ? '#EF6C00' : serviceType === 'RIDE' ? colors.accent : '#FF9500'
              }
            />
          )}
        </View>
      )}

      {/* PASSENGER OFFERS / VIAJE EN CURSO — DESACOPLADO.
          Antes este bloque solo renderizaba con `incomingDriverOffers`, así
          que en un RESUME (isActiveTrip=true pero incomingDriverOffers=false)
          el pasajero NO veía su viaje en curso y aterrizaba en el formulario.
          Ahora renderiza con `incomingDriverOffers || isActiveTrip`. */}
      {role === 'PASSENGER' && (incomingDriverOffers || isActiveTrip) && (
        <View onLayout={medirHoja} style={[styles.bottomSheet, { maxHeight: alto * 0.6, paddingBottom: 95 + margenAbajo }]}>
          <Text style={styles.sheetTitle}>{isActiveTrip ? 'Viaje en Curso' : 'Choferes Ofreciendo Viaje'}</Text>
          <ScrollView showsVerticalScrollIndicator contentContainerStyle={{ paddingBottom: 20 }} keyboardShouldPersistTaps="handled" nestedScrollEnabled>
            {/* Aquí es donde más falta hace: esta es la pantalla que dice
                «Carlos R. viene en camino». Sin el cintillo justo encima, es
                exactamente la pantalla que haría a alguien bajar a la acera. */}
            <CintilloDemostracion />
            {isActiveTrip ? (
              <View>
                <ActiveRideCard
                  /* ESTA RAMA ES LA DEL PASAJERO (se pinta con `selectedDriver`,
                     el chofer que le acepto el viaje). Decirlo aqui es lo que
                     impide que la tarjeta se voltee cuando el telefono quedo en
                     Modo Conductor. Ver `soyElChoferDeEsteViaje`. */
                  soyElChoferDeEsteViaje={false}
                  userName={selectedDriver?.driverName || selectedDriver?.userName || "Chofer"}
                  // El respaldo "4.9" escrito a mano ponía estrellas a un chofer
                  // que quizá no tiene ninguna calificación. Un hueco honesto
                  // pesa menos que una estrella falsa.
                  rating={selectedDriver?.rating ?? null}
                  // ── EL CONTADOR SE PERDÍA UNA LÍNEA ANTES DE PINTARSE ──────
                  // La nota del chofer llegaba, pero `ratingCount` no se pasaba
                  // y el componente lo declara 0 por defecto: `ratingDisplay`
                  // exige count > 0 para mostrar estrellas, así que TODO chofer
                  // salía "Nuevo" aquí. Un chofer con 200 calificaciones veía
                  // sus estrellas en la tarjeta de la oferta y "Nuevo" en la
                  // pantalla siguiente: la app se contradecía sola en dos
                  // pantallas seguidas.
                  // Sin dato, `null` (no 0): es la diferencia entre "no tiene
                  // calificaciones" y "no sabemos cuántas tiene".
                  ratingCount={
                    typeof selectedDriver?.ratingCount === 'number' ? selectedDriver.ratingCount : null
                  }
                  // La verificación REAL del chofer, tal como la publicó él al
                  // ofertar. Sin dato la tarjeta no pinta escudo: nunca un `true`
                  // fijo que afirme algo que nadie comprobó.
                  otherPartyVerified={
                    typeof selectedDriver?.isFaceIdVerified === 'boolean'
                      ? selectedDriver.isFaceIdVerified
                      : null
                  }
                  vehicleInfo={selectedDriver?.vehicleInfo || undefined}
                  otherPartyPhoto={selectedDriver?.driverPhoto || null}
                  otherPartyPhone={selectedDriver?.driverPhone || null}
                  rideId={currentRideId}
                  vehicleBrand={selectedDriver?.vehicleBrand || null}
                  vehicleModel={selectedDriver?.vehicleModel || null}
                  vehicleColor={selectedDriver?.vehicleColor || null}
                  licensePlate={selectedDriver?.licensePlate || null}
                  // ── EL RANGO Y EL CARRO SE PERDIAN AQUI ──────────────────
                  // Los tres datos ya viajan en la oferta y ya los sabe pintar
                  // `FichaPersona`; esta pantalla simplemente no se los pasaba.
                  // El pasajero los veia al elegir chofer y los perdia en la
                  // pantalla siguiente.
                  // `null` cuando no hay dato, nunca un relleno: la ficha
                  // prefiere no pintar nada a pintar un rango falso.
                  driverLevel={
                    typeof selectedDriver?.nivel === 'string' ? selectedDriver.nivel : null
                  }
                  driverCompletedTrips={
                    typeof selectedDriver?.viajesCompletados === 'number'
                      ? selectedDriver.viajesCompletados
                      : null
                  }
                  vehiclePhotoUrl={selectedDriver?.vehiclePhotoUrl || null}
                  rideStatus={rideStatus}
                  arrivedAtMs={llegadaDelChoferMs}
                  // Decía "Llegando en 3 min" SIEMPRE, pasara lo que pasara.
                  // A los 10 minutos de espera ese cartel es una burla y el
                  // pasajero deja de creerle a la app para siempre. Ahora se
                  // calcula con la posición real del chofer; si todavía no la
                  // tenemos, se dice lo que se sabe y nada más.
                  eta={
                    rideStatus === 'ARRIVED'
                      ? 'Tu chofer ya llegó'
                      // Primero el tiempo REAL de la ruta (viene en la misma
                      // respuesta que la línea verde y ya cuenta el tráfico).
                      // La línea recta a 30 km/h queda solo de respaldo, para
                      // cuando Directions no contestó.
                      : rutaInfo?.durationText
                        ? `Llega en ${rutaInfo.durationText}`
                        : driverLiveCoord && currentLocation
                          ? `Llega en ${etaLabel(
                              { lat: driverLiveCoord.latitude, lng: driverLiveCoord.longitude },
                              { origin_lat: currentLocation.lat, origin_lng: currentLocation.lng }
                            )}`
                          : 'Tu chofer va en camino'
                  }
                  ridePin={activeRidePin}
                  onPassengerConfirm={handlePassengerConfirmStart}
                  cancelando={cancelandoViaje}
                  onCancel={async () => {
                    // ── H05 / H23 (auditoría 16-sep-2026): EL ÉXITO SOLO SI ES VERDAD ──
                    // Antes el `catch` se tragaba el rechazo y el flujo seguía
                    // igual: borraba la sesión y anunciaba "cancelado con éxito"
                    // con el viaje VIVO en el servidor. El chofer seguía
                    // manejando hacia alguien que se había ido, y el pasajero,
                    // sin su viaje en pantalla, chocaba con "Ya tienes un viaje
                    // en curso" al pedir otro, hasta 4 horas.
                    //
                    // Ahora se limpia y se felicita SOLO tras la confirmación del
                    // servidor (`cancelRide` es una transacción: ver por qué en
                    // firestoreService.ts). Si falla, el viaje se queda montado
                    // con su botón, que es la forma de reintentar.
                    const viaje = currentRideId;
                    if (!viaje) {
                      // Tarjeta sin viaje detrás: no hay nada que cancelar en el
                      // servidor. Se cierra sin anunciar una cancelación que no
                      // ocurrió.
                      cerrarViajeDelPasajero();
                      return;
                    }
                    // ⚠️ EL DE DEMOSTRACIÓN SE CIERRA AQUÍ MISMO.
                    // Si siguiera, pasarían dos cosas feas: el aviso de
                    // `avisoAntesDeCancelar` le advertiría de una MULTA por
                    // cancelar un viaje que no existe, y después
                    // `cancelRide` fallaría contra un id inventado y le diría
                    // «No pudimos cancelar el viaje. Tu viaje sigue activo».
                    //
                    // Y se apaga el modo: quien cancela está diciendo «ya
                    // vi suficiente», y es la misma promesa que hace el final
                    // normal de la demostración.
                    if (esViajeFalso(viaje)) {
                      cerrarViajeDelPasajero();
                      useAppStore.getState().setQaMockRide(false);
                      showAlert(
                        'Demostración terminada',
                        'Saliste de la demostración. No había ningún chofer en camino y no se cobró nada. Ya volviste a los viajes reales.',
                        'checkmark-circle',
                        '#00C853'
                      );
                      return;
                    }
                    if (cancelandoViajeRef.current) return;
                    // H24 — antes de cancelar, decir lo que cuesta (o que no
                    // cuesta) según el momento. Ver `avisoAntesDeCancelar`.
                    {
                      const aviso = avisoAntesDeCancelar(false, viaje, rideStatus);
                      if (!(await confirmarCancelacion(aviso.titulo, aviso.mensaje))) return;
                    }
                    // Mientras el diálogo estaba abierto pudo arrancar otra
                    // cancelación: se vuelve a mirar el candado.
                    if (cancelandoViajeRef.current) return;
                    cancelandoViajeRef.current = true;
                    setCancelandoViaje(true);
                    try {
                      // La regla de Firestore solo lo permite al passengerId del ride.
                      await firestoreService.cancelRide(viaje, 'PASSENGER_CANCELLED', 'PASSENGER');
                      cerrarViajeDelPasajero();
                      showAlert('Viaje Cancelado', 'El viaje ha sido cancelado con éxito.', 'close-circle', colors.error);
                    } catch (e: any) {
                      if (e?.code === 'ride/ya-terminado') {
                        if (e?.estado === 'COMPLETED') {
                          // El listener del viaje abre la calificación: aquí
                          // no se cierra nada, solo se explica.
                          showAlert(
                            'El viaje ya terminó',
                            'Tu chofer ya había completado este viaje, así que no se canceló.',
                            'checkmark-circle',
                            colors.primary
                          );
                        } else {
                          // CANCELLED, EXPIRED o ya no existe: el viaje está
                          // muerto de verdad, cerrarlo aquí es decir la verdad.
                          cerrarViajeDelPasajero();
                          showAlert(
                            e?.estado === 'CANCELLED' ? 'Viaje Cancelado' : 'Viaje cerrado',
                            e?.estado === 'CANCELLED' ? 'Este viaje ya estaba cancelado.' : 'Este viaje ya no está activo.',
                            'close-circle',
                            colors.error
                          );
                        }
                      } else {
                        console.warn('No se pudo persistir la cancelación:', e?.code, e?.message);
                        showAlert(
                          'No pudimos cancelar el viaje',
                          'Tu viaje sigue activo. Revisa tu conexión y vuelve a tocar «Cancelar Viaje».',
                          'alert-circle',
                          colors.error
                        );
                      }
                    } finally {
                      cancelandoViajeRef.current = false;
                      setCancelandoViaje(false);
                    }
                  }}
                />
                {/* ══ EL PIN, PARA QUIEN SE VA A MONTAR ═══════════════════════
                    Las reglas solo dejan leer el PIN a quien PIDIÓ el viaje
                    (firestore.rules: /rides/{id}/private/security). Si el viaje
                    es para otra persona, ella no lo tiene y el chofer no puede
                    arrancar: hay que pasárselo a mano.

                    Solo aparece cuando la recogida se cambió a mano — en un
                    viaje normal el PIN ya se enseña en la tarjeta y repetirlo
                    aquí sería ruido. */}
                {/* Colgaba de `origenElegido`, estado de pantalla que nadie
                    rehidrata: al reabrir la app con el viaje en curso esta caja
                    —y con ella el PIN y el botón de WhatsApp— DESAPARECÍA, justo
                    cuando quien se va a montar todavía no tiene el código.
                    `recogidaLejosDeMi` se calcula contra el documento del viaje,
                    así que sobrevive a cerrar la app. */}
                {recogidaLejosDeMi && activeRidePin && (
                  <View style={[styles.cajaPinCompartir, { backgroundColor: colors.backgroundElement, borderColor: colors.accent }]}>
                    <Text style={[styles.cajaPinTitulo, { color: colors.textSecondary }]}>PIN para quien se monta</Text>
                    <Text style={[styles.cajaPinNumero, { color: colors.text }]}>{activeRidePin}</Text>
                    <Text style={[styles.cajaPinAyuda, { color: colors.textSecondary }]}>
                      Recogida en {recogidaMostrada?.texto || 'la dirección de recogida'}. El chofer
                      necesita este código para arrancar el viaje.
                    </Text>
                    <TouchableOpacity
                      onPress={() => compartirPinPorWhatsApp(activeRidePin, recogidaMostrada?.texto ?? '')}
                      activeOpacity={0.85}
                      accessibilityRole="button"
                      accessibilityLabel="Enviar el PIN del viaje por WhatsApp"
                      style={{ marginTop: 10, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 12, borderRadius: 12, backgroundColor: '#25D366' }}
                    >
                      <Ionicons name="logo-whatsapp" size={18} color="#FFF" />
                      <Text style={{ color: '#FFF', fontWeight: 'bold', fontSize: 14 }}>Enviar el PIN por WhatsApp</Text>
                    </TouchableOpacity>
                  </View>
                )}
                {/* ══ H63 — EL CÓDIGO CON EL QUE SE CIERRA EL ENVÍO ═══════════
                    Solo aparece en envíos de paquete: `pinDeEntrega` únicamente
                    existe cuando el viaje se pidió como DELIVERY, y solo lo
                    puede leer quien pidió el viaje (firestore.rules).

                    Por qué hace falta: en un envío no se monta nadie, así que
                    el PIN de recogida no prueba que el paquete llegó. Este
                    código lo dicta QUIEN RECIBE, y sin él el chofer no puede
                    cerrar el envío. Si quien recibe no lo tiene a mano, el
                    botón de abajo cierra el envío desde aquí — pero lo pulsa el
                    remitente, no el chofer. */}
                {envioDePaqueteVivo && (rideStatus === 'ACCEPTED' || rideStatus === 'ARRIVED' || rideStatus === 'IN_PROGRESS') && (
                  <View style={[styles.cajaPinCompartir, { backgroundColor: colors.backgroundElement, borderColor: '#FF9500', marginTop: 12 }]}>
                    <Text style={[styles.cajaPinTitulo, { color: colors.textSecondary }]}>Código de entrega del paquete</Text>
                    <Text style={[styles.cajaPinNumero, { color: colors.text }]}>{pinDeEntrega ?? '—'}</Text>
                    <Text style={[styles.cajaPinAyuda, { color: colors.textSecondary }]}>
                      {pinDeEntrega
                        ? 'Dáselo a quien va a recibir el paquete. El chofer necesita que se lo dicten para poder cerrar el envío.'
                        : 'Este envío se pidió antes de que existiera el código. Ciérralo tú desde aquí cuando el paquete haya llegado.'}
                    </Text>
                    {!!pinDeEntrega && (
                    <TouchableOpacity
                      onPress={() => compartirCodigoDeEntregaPorWhatsApp(pinDeEntrega)}
                      activeOpacity={0.85}
                      accessibilityRole="button"
                      accessibilityLabel="Enviar el código de entrega por WhatsApp"
                      style={{ marginTop: 10, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 12, borderRadius: 12, backgroundColor: '#25D366' }}
                    >
                      <Ionicons name="logo-whatsapp" size={18} color="#FFF" />
                      <Text style={{ color: '#FFF', fontWeight: 'bold', fontSize: 14 }}>Enviar el código por WhatsApp</Text>
                    </TouchableOpacity>
                    )}
                    {rideStatus === 'IN_PROGRESS' && (
                      <TouchableOpacity
                        onPress={confirmarEntregaDelPaquete}
                        disabled={confirmandoEntrega}
                        activeOpacity={0.85}
                        accessibilityRole="button"
                        accessibilityLabel="Confirmar que el paquete ya fue entregado"
                        style={{ marginTop: 8, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 12, borderRadius: 12, backgroundColor: confirmandoEntrega ? colors.textSecondary : '#FF9500' }}
                      >
                        <Ionicons name="checkmark-done" size={18} color="#FFF" />
                        <Text style={{ color: '#FFF', fontWeight: 'bold', fontSize: 14 }}>
                          {confirmandoEntrega ? 'Cerrando...' : 'Ya lo recibieron: cerrar envío'}
                        </Text>
                      </TouchableOpacity>
                    )}
                  </View>
                )}
                {/* FIX (#4) — Compartir mi ubicación en vivo con un familiar */}
                <TouchableOpacity
                  onPress={handleShareLiveLocation}
                  activeOpacity={0.85}
                  style={{ marginTop: 12, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 13, borderRadius: 12, backgroundColor: '#208AEF' }}
                >
                  <Ionicons name="share-social" size={18} color="#FFF" />
                  <Text style={{ color: '#FFF', fontWeight: 'bold', fontSize: 14 }}>Compartir mi ubicación en vivo</Text>
                </TouchableOpacity>
                {paymentMethod === 'TRANSFER' && (
                  <Button3D
                    title="Ver Cuentas del Chofer"
                    onPress={() => setPaymentModalVisible(true)}
                    color="#FF9800"
                    style={{ marginTop: 15 }}
                  />
                )}
                {/* ══ EL ESPACIO PATROCINADO ═══════════════════════════════════
                    Va AQUI, al final del panel: debajo de la tarjeta del chofer,
                    del PIN y de los botones de pago. Nada de lo que importa se
                    mueve de sitio por un anuncio.
                    Es el unico hueco de la app con atencion de sobra — de tres a
                    ocho minutos mirando el mapa esperando el carro — y hasta hoy
                    estaba vacio. Al CHOFER no se le ensena ninguno: el componente
                    devuelve null sin rol de pasajero. */}
                <EspacioPatrocinado hueco="espera" />
              </View>
            ) : (
              receivedOffers.map((offer) => (
                <View key={offer.id} style={{ marginBottom: 15 }}>
                  <RideOfferCard
                    type="PASSENGER_VIEW"
                    userName={offer.driverName || offer.userName || 'Chofer'}
                    // La cara solo cuando el SERVIDOR ya selló la oferta con la
                    // selfie aprobada (sellarReputacionDeLaOferta). Antes de eso
                    // la que trae es la que mandó el teléfono del chofer.
                    userPhoto={offer.reputacionSource === 'SERVER_TRIGGER' ? offer.driverPhoto || undefined : undefined}
                    rating={offer.rating}
                    ratingCount={typeof offer.ratingCount === 'number' ? offer.ratingCount : null}
                    distance={offer.distance}
                    timeEst={offer.timeEst}
                    price={offer.price}
                    vehicleInfo={offer.vehicleInfo}
                    vehicleBrand={offer.vehicleBrand || null}
                    vehicleModel={offer.vehicleModel || null}
                    vehicleColor={offer.vehicleColor || null}
                    licensePlate={offer.licensePlate || null}
                    /* La foto del carro. La estampa el servidor en la oferta
                       (sellarReputacionDeLaOferta), copiada a vehicle_images/ y
                       NUNCA la del KYC. Ver functions/src/fotoVehiculo.ts. */
                    vehiclePhotoUrl={offer.vehiclePhotoUrl || null}
                    nivel={typeof offer.nivel === 'string' ? offer.nivel : null}
                    viajesCompletados={
                      typeof offer.viajesCompletados === 'number' ? offer.viajesCompletados : null
                    }
                    isFaceIdVerified={offer.isFaceIdVerified}
                    onAccept={() => handleAcceptOffer(offer)}
                    // SIN onCounterOffer A PROPÓSITO (lado pasajero).
                    // El modal de contraoferta escribe en /rides/{id}/offers, y
                    // esa regla exige `isVerifiedDriver()` (firestore.rules:572).
                    // Un pasajero JAMÁS la pasa: la escritura se deniega, la
                    // promesa muere sin catch y el botón se queda mudo — el
                    // rechazo vc13 de Google, literal.
                    // El pasajero ya fija su precio al pedir el viaje, y aquí
                    // tiene Aceptar y Rechazar, que sí funcionan. Para darle
                    // una contraoferta real hace falta un campo propio en el
                    // ride + UI del chofer que la responda; hasta entonces, no
                    // se pinta un botón que no puede funcionar.
                    // El rechazo AVISA AL CHOFER (antes solo borraba la tarjeta
                    // de esta pantalla y él se quedaba esperando para siempre) y
                    // devuelve el radar si no queda ninguna oferta. Ver
                    // `rechazarOferta`.
                    onReject={() => rechazarOferta(offer)}
                  />
                </View>
              ))
            )}
          </ScrollView>
          {/* ── LA SALIDA QUE NO EXISTÍA ────────────────────────────────────
              Con la primera oferta esta hoja tapa el formulario, y el único
              botón de cancelar que había vivía en el formulario. La pasajera
              se quedaba con dos opciones —aceptar o rechazar— y su solicitud
              seguía viva en el servidor, con el chofer esperando respuesta.
              Aquí está la tercera: parar. Solo mientras se elige chofer: con
              el viaje en curso la cancelación es otra (la de ActiveRideCard,
              que avisa al chofer y cuenta para la multa). */}
          {!isActiveTrip && (
            <TouchableOpacity
              onPress={cancelarBusqueda}
              activeOpacity={0.7}
              accessibilityRole="button"
              accessibilityLabel="Cancelar la búsqueda de chofer"
              style={{
                height: 48,
                borderRadius: 12,
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'row',
                borderWidth: 1.5,
                borderColor: '#FF3B30',
                backgroundColor: 'rgba(255,59,48,0.08)',
                marginTop: 4,
              }}
            >
              <Ionicons name="close-circle" size={18} color="#FF3B30" style={{ marginRight: 6 }} />
              <Text style={{ color: '#FF3B30', fontSize: 15, fontWeight: '700' }}>Cancelar búsqueda</Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* MODAL DE CONTRAOFERTA (NEGOCIACIÓN) */}
      {/* PIN DE VIAJE (chofer) — Modal para ingresar el código del pasajero.
          Overlay absoluto (mismo patrón que el modal de contraoferta) porque
          RN Modal no está importado y en Android es más predecible. */}
      {pinModalVisible && (
        <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
          <View style={{ backgroundColor: colors.card, padding: 24, borderRadius: 20, width: '85%', maxWidth: 400 }}>
            <Ionicons name="keypad" size={40} color="#00C853" style={{ alignSelf: 'center', marginBottom: 10 }} />
            <Text style={{ fontSize: 20, fontWeight: 'bold', color: colors.text, textAlign: 'center', marginBottom: 8 }}>Ingresa el PIN del pasajero</Text>
            <Text style={{ fontSize: 14, color: colors.textSecondary, textAlign: 'center', marginBottom: 18 }}>Pídele al pasajero su código de 4 dígitos para iniciar el viaje.</Text>

            <TextInput
              style={[styles.input, { backgroundColor: colors.background, color: colors.text, borderColor: colors.border, fontSize: 28, textAlign: 'center', letterSpacing: 8, fontWeight: '900' }]}
              placeholder="____"
              placeholderTextColor={colors.textSecondary}
              keyboardType="number-pad"
              maxLength={4}
              value={pinEntry}
              onChangeText={(t) => {
                const limpio = t.replace(/[^0-9]/g, '');
                setPinEntry(limpio);
                // Al cuarto dígito arranca solo. Es literalmente lo que pidió el
                // fundador: "desde que el pasajero entregue el PIN el viaje
                // inicia". Se pasa el valor por parámetro porque `pinEntry`
                // todavía no se ha actualizado en este instante.
                if (limpio.length === 4 && !pinSubmitting) submitStartWithPin(limpio);
              }}
              autoFocus
            />

            {/* ── CONTACTO, SIN TENER QUE CERRAR EL PIN ────────────────────
                Este modal es una capa a pantalla completa con zIndex 9999, y se
                abre SOLO al llegar a la recogida. Tapaba la tarjeta del viaje,
                que es donde viven «Llamar» y «Escribir». Reportado desde la
                calle: el chofer llega, el pasajero no aparece, y no tiene cómo
                avisarle — justo el momento en que más falta hace.
                Las dos acciones son las MISMAS de ActiveRideCard: `tel:` con el
                número limpio, y la pantalla de chat con el rideId, que es lo que
                ata la conversación a ESTE viaje. */}
            <View style={{ flexDirection: 'row', gap: 10, marginTop: 16 }}>
              <TouchableOpacity
                onPress={() => {
                  const crudo = String(driverCurrentRide?.passengerPhone ?? '').trim();
                  if (!crudo) {
                    showAlert(
                      'Sin teléfono',
                      'Este pasajero no dejó un número. Escríbele por el chat del viaje.',
                      'call',
                      '#FF9500'
                    );
                    return;
                  }
                  Linking.openURL(`tel:${crudo.replace(/[\s\-()]/g, '')}`).catch(() => {
                    showAlert('No pudimos abrir la llamada', `Marca directamente: ${crudo}`, 'call', '#FF3B30');
                  });
                }}
                style={{
                  flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
                  gap: 8, paddingVertical: 12, borderRadius: 12,
                  backgroundColor: colors.background, borderWidth: 1, borderColor: colors.border,
                }}
                accessibilityRole="button"
                accessibilityLabel="Llamar al pasajero"
              >
                <Ionicons name="call" size={18} color="#00C853" />
                <Text style={{ color: colors.text, fontWeight: '700', fontSize: 14 }}>Llamar</Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => {
                  router.push({
                    pathname: '/chat',
                    params: {
                      rideId: currentRideId ?? '',
                      partnerName: driverCurrentRide?.passengerName ?? 'Pasajero',
                      partnerPhone: driverCurrentRide?.passengerPhone ?? '',
                    },
                  } as any);
                }}
                style={{
                  flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
                  gap: 8, paddingVertical: 12, borderRadius: 12,
                  backgroundColor: colors.background, borderWidth: 1, borderColor: colors.border,
                }}
                accessibilityRole="button"
                accessibilityLabel="Escribirle al pasajero"
              >
                <Ionicons name="chatbubble-ellipses" size={18} color={colors.primary} />
                <Text style={{ color: colors.text, fontWeight: '700', fontSize: 14 }}>Escribir</Text>
              </TouchableOpacity>
            </View>

            <Text style={{ fontSize: 12, color: colors.textSecondary, textAlign: 'center', marginTop: 10 }}>
              ¿No lo ves? Llámalo o escríbele sin cerrar esta pantalla.
            </Text>

            <View style={{ flexDirection: 'row', gap: 10, marginTop: 14 }}>
              <View style={{ flex: 1 }}>
                <Button3D title="Cancelar" color={colors.textSecondary} onPress={() => { setPinModalVisible(false); setPinEntry(''); }} />
              </View>
              <View style={{ flex: 1 }}>
                <Button3D title={pinSubmitting ? 'Verificando...' : 'Iniciar viaje'} color="#00C853" onPress={() => submitStartWithPin()} />
              </View>
            </View>
          </View>
        </View>
      )}

      {/* ── ¿TE PAGÓ EL CLIENTE? (chofer) ────────────────────────
          El chofer cerraba el viaje y la app nunca le preguntaba si había
          cobrado. Ahora se pregunta, y la respuesta QUEDA ESCRITA en el viaje
          (paymentStatus), que es lo único que permite resolver después un
          desacuerdo sobre si el dinero llegó. */}
      {cobroPendiente && (
        <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.65)', justifyContent: 'center', alignItems: 'center', zIndex: 9998, padding: 20 }}>
          <View style={{ backgroundColor: colors.card, borderRadius: 20, width: '100%', maxWidth: 400, padding: 24 }}>
            <Ionicons
              name={cobroPendiente.metodo === 'TRANSFER' ? 'business' : 'cash'}
              size={38}
              color="#00C853"
              style={{ alignSelf: 'center', marginBottom: 10 }}
            />
            <Text style={{ fontSize: 19, fontWeight: 'bold', color: colors.text, textAlign: 'center' }}>
              ¿Te pagó el cliente?
            </Text>
            <Text style={{ fontSize: 30, fontWeight: '900', color: '#00C853', textAlign: 'center', marginTop: 8 }}>
              {pesos(cobroPendiente.monto)}
            </Text>
            <Text style={{ fontSize: 13.5, color: colors.textSecondary, textAlign: 'center', marginTop: 6, marginBottom: 20 }}>
              {cobroPendiente.metodo === 'TRANSFER'
                ? 'Por transferencia — revisa que el dinero haya entrado antes de confirmar'
                : 'En efectivo'}
            </Text>

            <Button3D
              title={confirmandoCobro ? 'Guardando…' : 'Sí, ya me pagó'}
              color="#00C853"
              onPress={() => responderCobro(true)}
            />
            <TouchableOpacity
              onPress={() => responderCobro(false)}
              disabled={confirmandoCobro}
              style={{ marginTop: 12, paddingVertical: 12, alignItems: 'center' }}
              accessibilityRole="button"
            >
              <Text style={{ color: '#FF9500', fontWeight: '700', fontSize: 14.5 }}>
                Todavía no me ha pagado
              </Text>
            </TouchableOpacity>
            <Text style={{ fontSize: 11.5, color: colors.textSecondary, textAlign: 'center', marginTop: 4, lineHeight: 17 }}>
              Queda anotado en el viaje con la hora. Si después te paga, avísanos por soporte.
            </Text>
          </View>
        </View>
      )}

      {/* ── CIERRE DE PAGO DEL PASAJERO ──────────────────────────
          Lo que faltaba. Antes, al terminar el viaje, el pasajero pasaba del
          trayecto directo a las estrellas y NUNCA veía a dónde pagar: el botón
          de las cuentas vivía dentro del panel del viaje activo y se desmontaba
          al cerrar, y el propio código borraba las cuentas en ese mismo
          instante. Esta pantalla sale ANTES de calificar, que es el momento en
          que de verdad se paga. */}
      {cierreDePago && (
        <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.65)', justifyContent: 'center', alignItems: 'center', zIndex: 9998, padding: 20 }}>
          <View style={{ backgroundColor: colors.card, borderRadius: 20, width: '100%', maxWidth: 420, maxHeight: '86%', overflow: 'hidden' }}>
            <ScrollView contentContainerStyle={{ padding: 22 }}>
              <Ionicons
                name={cierreDePago.metodo === 'TRANSFER' ? 'business' : 'cash'}
                size={38}
                color="#00C853"
                style={{ alignSelf: 'center', marginBottom: 8 }}
              />
              <Text style={{ fontSize: 19, fontWeight: 'bold', color: colors.text, textAlign: 'center' }}>
                Viaje terminado
              </Text>
              <Text style={{ fontSize: 30, fontWeight: '900', color: '#00C853', textAlign: 'center', marginTop: 6 }}>
                {pesos(cierreDePago.monto)}
              </Text>
              <Text style={{ fontSize: 13.5, color: colors.textSecondary, textAlign: 'center', marginTop: 6, marginBottom: 16 }}>
                {cierreDePago.metodo === 'TRANSFER'
                  ? 'Transfiérele a ' + cierreDePago.chofer
                  : 'Págale a ' + cierreDePago.chofer + ' en efectivo'}
              </Text>

              {cierreDePago.metodo === 'TRANSFER' && (
                cierreDePago.cuentas.length > 0 ? (
                  cierreDePago.cuentas.map((c: any, i: number) => {
                    const numero = String(c?.accountNumber ?? c?.numero ?? '');
                    return (
                      <View
                        key={String(c?.id ?? i)}
                        style={{ backgroundColor: colors.background, borderRadius: 12, padding: 14, marginBottom: 10, borderWidth: 1, borderColor: colors.border }}
                      >
                        <Text style={{ color: colors.text, fontWeight: '800', fontSize: 15 }}>
                          {String(c?.bankName ?? c?.banco ?? 'Cuenta')}
                        </Text>
                        {!!(c?.accountHolder ?? c?.titular) && (
                          <Text style={{ color: colors.textSecondary, fontSize: 12.5, marginTop: 2 }}>
                            A nombre de {String(c?.accountHolder ?? c?.titular)}
                          </Text>
                        )}
                        <TouchableOpacity
                          onPress={async () => {
                            if (!numero) return;
                            try {
                              await Clipboard.setStringAsync(numero);
                              vibrarExito();
                              showAlert('Copiado', 'Número de cuenta copiado: ' + numero, 'copy', '#00C853');
                            } catch {
                              showAlert('No se pudo copiar', 'Anótalo: ' + numero, 'copy', '#FF9500');
                            }
                          }}
                          style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 8, gap: 10 }}
                          accessibilityRole="button"
                          accessibilityLabel="Copiar el número de cuenta"
                        >
                          <Text style={{ color: colors.text, fontWeight: '700', fontSize: 16, letterSpacing: 0.5, flexShrink: 1 }}>
                            {numero || '—'}
                          </Text>
                          <Ionicons name="copy-outline" size={19} color={colors.primary} />
                        </TouchableOpacity>
                      </View>
                    );
                  })
                ) : (
                  /* LA VERDAD, NO UN CALLEJÓN SIN SALIDA. El mensaje que había
                     decía «este chofer no ha registrado cuentas» y ahí se acababa:
                     ni qué hacer, ni cómo hablarle. */
                  <View style={{ backgroundColor: 'rgba(255,149,0,0.12)', borderRadius: 12, padding: 14, marginBottom: 12 }}>
                    <Text style={{ color: '#B36200', fontWeight: '800', fontSize: 14.5, marginBottom: 4 }}>
                      Este chofer todavía no registró su cuenta en la app
                    </Text>
                    <Text style={{ color: '#B36200', fontSize: 13, lineHeight: 19 }}>
                      Pídele el número por el chat o págale en efectivo. No le transfieras a ninguna
                      cuenta que no te haya dado él mismo.
                    </Text>
                    <TouchableOpacity
                      onPress={() => {
                        // NO se abren las estrellas ni se cierra este cierre.
                        // `DriverRatingModal` es un <Modal> de React Native: se
                        // pinta en su propia ventana, POR ENCIMA de cualquier
                        // zIndex. Abrirlo aqui dejaba al pasajero en /chat con
                        // las estrellas tapandolo, y sin el numero de cuenta
                        // porque el cierre ya se habia borrado. Se va al chat y
                        // al volver esta pantalla sigue donde estaba.
                        const idDelViaje = ratingRideId || '';
                        const nombre = cierreDePago.chofer;
                        router.push({
                          pathname: '/chat',
                          params: { rideId: idDelViaje, partnerName: nombre },
                        } as any);
                      }}
                      style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 12, paddingVertical: 11, borderRadius: 10, backgroundColor: '#FF9500' }}
                      accessibilityRole="button"
                    >
                      <Ionicons name="chatbubble-ellipses" size={17} color="#FFF" />
                      <Text style={{ color: '#FFF', fontWeight: '800', fontSize: 14 }}>Pedirle la cuenta por el chat</Text>
                    </TouchableOpacity>
                  </View>
                )
              )}

              <Text style={{ fontSize: 11.5, color: colors.textSecondary, textAlign: 'center', marginTop: 4, marginBottom: 14, lineHeight: 17 }}>
                Go Safely no cobra comisión por el viaje: este dinero va completo para tu chofer.
              </Text>

              <Button3D
                title="Listo, ya le pagué"
                color="#00C853"
                onPress={() => { setCierreDePago(null); setRatingModalVisible(true); }}
              />
            </ScrollView>
          </View>
        </View>
      )}

      {/* H63 — el chofer cierra el envío tecleando el código que le dicta quien
          recibe el paquete. No escribe el estado: lo valida el servidor. */}
      {modalPinEntrega && (
        <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
          <View style={{ backgroundColor: colors.card, padding: 24, borderRadius: 20, width: '85%', maxWidth: 400 }}>
            <Ionicons name="cube" size={40} color="#FF9500" style={{ alignSelf: 'center', marginBottom: 10 }} />
            <Text style={{ fontSize: 20, fontWeight: 'bold', color: colors.text, textAlign: 'center', marginBottom: 8 }}>Código de entrega</Text>
            <Text style={{ fontSize: 14, color: colors.textSecondary, textAlign: 'center', marginBottom: 18 }}>
              Pídele a quien recibe el paquete su código de 4 dígitos. Sin él no se puede cerrar el envío.
            </Text>

            <TextInput
              style={[styles.input, { backgroundColor: colors.background, color: colors.text, borderColor: colors.border, fontSize: 28, textAlign: 'center', letterSpacing: 8, fontWeight: '900' }]}
              placeholder="____"
              placeholderTextColor={colors.textSecondary}
              keyboardType="number-pad"
              maxLength={4}
              value={entradaPinEntrega}
              onChangeText={(t) => {
                const limpio = t.replace(/[^0-9]/g, '');
                setEntradaPinEntrega(limpio);
                // Igual que el PIN de recogida: al cuarto dígito se envía solo.
                if (limpio.length === 4 && !cerrandoEnvio) cerrarEnvioConPin(limpio);
              }}
              autoFocus
            />

            <View style={{ flexDirection: 'row', gap: 10, marginTop: 12 }}>
              <View style={{ flex: 1 }}>
                <Button3D title="Cancelar" color={colors.textSecondary} onPress={() => { setModalPinEntrega(false); setEntradaPinEntrega(''); }} />
              </View>
              <View style={{ flex: 1 }}>
                <Button3D title={cerrandoEnvio ? 'Verificando...' : 'Cerrar envío'} color="#FF9500" onPress={() => cerrarEnvioConPin()} />
              </View>
            </View>
          </View>
        </View>
      )}

      {showCounterOfferModal && (
        <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
          <View style={{ backgroundColor: colors.card, padding: 24, borderRadius: 20, width: '85%', maxWidth: 400 }}>
            <Ionicons name="swap-horizontal" size={40} color="#FF9500" style={{ alignSelf: 'center', marginBottom: 10 }} />
            <Text style={{ fontSize: 20, fontWeight: 'bold', color: colors.text, textAlign: 'center', marginBottom: 10 }}>Negociar Tarifa</Text>
            <Text style={{ fontSize: 14, color: colors.textSecondary, textAlign: 'center', marginBottom: 20 }}>Ingresa la nueva tarifa que deseas proponer al pasajero.</Text>
            
            {/* ══ MONTOS SUGERIDOS ═══════════════════════════════════════
                Antes solo había la caja de texto vacía: negociar exigía pensar
                una cifra y teclearla, y mucha gente simplemente no contraofertaba.
                Los montos salen de la oferta que hay sobre la mesa, no de una
                lista fija, así que siempre tienen sentido para ESE viaje.
                Tocar uno lo escribe en la caja — se puede seguir editando a
                mano, que es lo que pediste. */}
            {(() => {
              const base = Number(String(offerPrice).replace(',', '.')) || 0;
              if (base <= 0) return null;
              // Redondeados a 10 pesos: nadie negocia con céntimos.
              const alDiez = (n: number) => Math.max(10, Math.round(n / 10) * 10);
              // EL PRECIO QUE CUBRE LOS DOS TRAMOS. Es el único de los cuatro
              // que mira los kilómetros en vez del bolsillo del pasajero.
              const eco = contraofertaEconomiaRef.current;
              const justo =
                eco && eco.kmTotal > 0
                  ? alDiez(
                      suggestFare(
                        eco.kmTotal,
                        (eco.vehiculo === 'MOTORCYCLE' || eco.vehiculo === 'MINIBUS'
                          ? eco.vehiculo
                          : 'CAR') as any
                      )
                    )
                  : null;
              // ══════════════════════════════════════════════════════════
              // UNA CONTRAOFERTA ES PARA PEDIR MÁS, NUNCA MENOS
              // ══════════════════════════════════════════════════════════
              // Esto sugería `base * 0.85` y `base * 0.95`: con el pasajero
              // ofreciendo RD$120, al chofer le salían RD$100 y RD$110 como las
              // DOS PRIMERAS opciones, y tocando una se ofrecía a trabajar por
              // menos de lo que ya le estaban dando. El fundador lo vio en la
              // calle: «si el cliente ofrece 120, la barra comienza por 110, y
              // de ahí no va subiendo; si es 120 debe aparecer 130, o 150».
              //
              // El chofer ya tiene el botón de ACEPTAR para quedarse con el
              // precio del pasajero. Este modal existe solo para el otro caso:
              // que ese precio no le dé. Así que todo lo que salga aquí tiene
              // que estar POR ENCIMA.
              //
              // `justo` —el precio que cubre los dos tramos según los km— se
              // queda SOLO si también está por encima. Cuando el pasajero ya
              // ofreció de más, ese número sigue saliendo como TEXTO arriba
              // (es información útil), pero no como botón: un botón que baja el
              // precio en la pantalla de pedir más es una trampa para el chofer.
              const sugerencias = Array.from(
                new Set(
                  [alDiez(base * 1.1), alDiez(base * 1.25), alDiez(base * 1.5), justo].filter(
                    (x): x is number => typeof x === 'number' && x > base
                  )
                )
              ).sort((a, b) => a - b);
              return (
                <View style={{ marginBottom: 14 }}>
                  <Text style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 8, textAlign: 'center' }}>
                    Toca un monto o escribe el tuyo
                  </Text>
                  {justo != null && (
                    <Text style={{ fontSize: 11.5, color: colors.textSecondary, marginBottom: 8, textAlign: 'center' }}>
                      {pesos(justo)} cubre los {eco!.kmTotal.toFixed(1)} km — ir a buscarlo y llevarlo
                    </Text>
                  )}
                  <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 8 }}>
                    {sugerencias.map((m) => {
                      const elegido = String(m) === String(newCounterPrice).trim();
                      return (
                        <TouchableOpacity
                          key={m}
                          onPress={() => { setNewCounterPrice(String(m)); vibrarExito(); }}
                          style={[
                            styles.montoSugerido,
                            {
                              backgroundColor: elegido ? '#00C853' : colors.background,
                              borderColor: elegido ? '#00C853' : colors.border,
                            },
                          ]}
                          accessibilityRole="button"
                        >
                          <Text style={[styles.montoSugeridoTexto, { color: elegido ? '#FFF' : colors.text }]}>
                            {pesos(m)}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </View>
              );
            })()}

            <TextInput
              style={[styles.input, { backgroundColor: colors.background, color: colors.text, borderColor: colors.border, fontSize: 18, textAlign: 'center' }]}
              placeholder="Ej. 300"
              placeholderTextColor={colors.textSecondary}
              keyboardType="number-pad"
              value={newCounterPrice}
              onChangeText={setNewCounterPrice}
            />

            <View style={{ flexDirection: 'row', gap: 10, marginTop: 10 }}>
              <View style={{ flex: 1 }}>
                <Button3D title="Cancelar" color={colors.textSecondary} onPress={() => setShowCounterOfferModal(false)} />
              </View>
              <View style={{ flex: 1 }}>
                {/* H40 — mismo candado que "Aceptar Viaje": un toque, una oferta. */}
                <Button3D title={enviandoOferta ? 'Enviando…' : 'Enviar'} color="#FF9500" onPress={() => conCandadoDeOferta(async () => {
                  if(!newCounterPrice) return Alert.alert('Error', 'Ingresa un monto.');
                  // FIX AUDIT — Validar numérico/positivo igual que en la
                  // solicitud del pasajero. Sin esto, un texto crudo ("abc",
                  // negativo) llegaba a `offer.price` y generaba NaN/basura
                  // downstream. El cobro real es server-side, pero blindamos
                  // el dato de display para no confundir al pasajero.
                  // V46/V57 — y con la MISMA forma que exige la regla
                  // (`precioCuerdo`: dígitos con hasta 2 decimales, tope RD$
                  // 100,000). "1,500" o "1.500" pasaban aquí como 1.5 y la regla
                  // los denegaba; "1000000" también. Los separadores de miles se
                  // quitan y se envía el monto ya normalizado.
                  let precioContraoferta = String(newCounterPrice).replace(/\s+/g, '');
                  if (/^\d{1,3}([.,]\d{3})+$/.test(precioContraoferta)) {
                    precioContraoferta = precioContraoferta.replace(/[.,]/g, '');
                  }
                  {
                    const formaValida = /^\d{1,6}([.,]\d{1,2})?$/.test(precioContraoferta);
                    const counter = Number(precioContraoferta.replace(',', '.'));
                    if (!formaValida || !Number.isFinite(counter) || counter < 1) {
                      showAlert('Monto inválido', 'Escribe solo el número, sin letras ni signos (ej. 300), mayor que cero y con hasta 2 decimales.', 'alert-circle', '#FF3B30');
                      return;
                    }
                    if (counter > 100000) {
                      showAlert('Monto inválido', `La tarifa no puede pasar de ${pesos(100000)}.`, 'alert-circle', '#FF3B30');
                      return;
                    }
                    precioContraoferta = String(counter);
                  }
                  // AUDIT FIX RIDE — Antes cerrábamos modal + setOfferPrice
                  // ANTES de esperar Firestore. Si el server rechazaba, la UI
                  // ya mostraba el precio nuevo pero la contraoferta nunca
                  // llegaba. Ahora esperamos el resultado y solo actualizamos
                  // UI si la escritura fue exitosa.
                  // ── ESTA CONTRAOFERTA ES DE UN VIAJE CONCRETO ─────────────
                  // Antes esto era `if (currentRideId) { …escribir… }` y el
                  // aviso de «Contraoferta Enviada» estaba FUERA del if: si
                  // mientras el chofer escribía el monto su solicitud se
                  // cancelaba (o el feed apuntaba `currentRideId` a otra), la
                  // app le decía que la había enviado sin haber escrito nada —
                  // o se la escribía a OTRO pasajero. Ahora manda el id que se
                  // congeló al abrir el modal, y si ya no hay, se dice y se
                  // cierra. Ver `contraofertaRideIdRef`.
                  const idDeLaContraoferta = contraofertaRideIdRef.current || currentRideId;
                  if (!idDeLaContraoferta) {
                    setShowCounterOfferModal(false);
                    contraofertaRideIdRef.current = null;
                    showAlert(
                      'Esa solicitud ya no está',
                      'Se canceló o la tomó otro chofer mientras escribías. No se envió nada.',
                      'alert-circle',
                      '#FF9500'
                    );
                    return;
                  }
                  {
                    // FIX AUDIT #3: sin UID real → no enviar oferta huérfana
                    const driverId = useAppStore.getState().userFirebaseUid;
                    if (!driverId) {
                      Alert.alert('Sesión requerida', 'Inicia sesión para enviar contraofertas.');
                      return;
                    }
                    // El `await` de abajo puede fallar por regla (chofer sin
                    // verificar, multa pendiente) o por red. Sin este try el
                    // rechazo era una promesa muerta: el modal se quedaba
                    // abierto, sin mensaje, y el botón parecía roto.
                    try {
                    // FIX PROFESIONALISMO — Antes "Tu Conductor", "Toyota
                    // Corolla 2023", "1.0 km", "2 min" estaban hardcoded.
                    // El pasajero veía datos genéricos sin importar quién
                    // ofertara. Ahora todos los campos son reales del store
                    // del chofer + computados al vuelo, INCLUYENDO datos
                    // del vehículo (Uber-style) para que sepa qué carro esperar.
                    const meD = useAppStore.getState();
                    const brand = meD.vehicleBrand ?? '';
                    const model = meD.vehicleModel ?? '';
                    const color = meD.vehicleColor ?? '';
                    // `selectedVehicleType` es el SELECTOR DEL PASAJERO: usarlo aquí
                    // como respaldo hacía que a un motorista sin datos el pasajero le
                    // leyera "CAR", un dato falso. `vehiculoParaPublicar` es el
                    // vehículo real del chofer.
                    const vehicleLabel = [color, brand, model].filter(Boolean).join(' ') || (vehiculoParaPublicar || 'Vehículo');
                    await firestoreService.sendRideOffer(idDeLaContraoferta, driverId, {
                      driverId,
                      userName: String(meD.userName ?? '').trim() || 'Chofer',
                      driverName: String(meD.userName ?? '').trim() || 'Chofer',
                      driverPhoto: String(meD.userPhoto ?? '').trim() || null,
                      driverPhone: String(meD.userPhone ?? '').trim() || null,
                      // ── CONTRATO (d) — AQUÍ NO VA LA REPUTACIÓN DEL CHOFER ──
                      // `rating`, `ratingCount` e `isFaceIdVerified` los mandaba
                      // ESTE teléfono, o sea el propio interesado: quien firma su
                      // mérito puede firmarse otro, y es justo la tarjeta que el
                      // pasajero mira antes de montarse con un desconocido.
                      // Ahora los estampa el servidor (`sellarReputacionDeLaOferta`
                      // en functions/src/rides.ts) leyendo /users/{driverId} con
                      // Admin SDK, y firestore.rules DENIEGA la oferta que traiga
                      // cualquiera de esos nombres (`camposDeReputacionDeLaOferta`).
                      // NO LOS VUELVAS A PONER: la oferta entera sería rechazada.
                      distance: distanceLabel(currentLocation, driverCurrentRide),
                      timeEst: etaLabel(currentLocation, driverCurrentRide),
                      price: precioContraoferta,
                      vehicleInfo: vehicleLabel,
                      vehicleType: vehiculoParaPublicar as any,
                      vehicleBrand: brand || null,
                      vehicleModel: model || null,
                      vehicleColor: color || null,
                      licensePlate: meD.licensePlate ?? null,
                      // El escudo de verificado tampoco viaja desde aquí: ver la
                      // nota del CONTRATO (d) unas líneas más arriba.
                    });
                    // ANTI-FANTASMA — Si es el CHOFER quien contraoferta,
                    // registrar ESTE ride como su oferta pendiente para que
                    // el listener global lo reconozca al ser aceptado.
                      if (meD.userRole === 'DRIVER') {
                        setPendingOfferRideId(idDeLaContraoferta);
                        pendingOfferRideIdRef.current = idDeLaContraoferta;
                        rideSessionStore.setPendingOffer(idDeLaContraoferta);
                      }
                    } catch (e: any) {
                      console.warn('[contraoferta]', e?.code, e?.message);
                      const denegado = String(e?.code || '').includes('permission-denied');
                      showAlert(
                        'No pudimos enviar tu contraoferta',
                        denegado
                          // V46/V57 — el rechazo de la regla no dice por qué: puede
                          // ser la cuenta o el monto. No se culpa a la cuenta sola.
                          ? 'El servidor rechazó tu contraoferta. Revisa que el monto sea correcto (sin puntos ni comas de miles, hasta RD$ 100,000). Si lo es, revisa tu verificación de chofer y si tienes alguna multa pendiente.'
                          : 'Revisa tu conexión e intenta de nuevo.',
                        'alert-circle',
                        '#FF3B30'
                      );
                      return;
                    }
                  }

                  // Solo cerramos modal + actualizamos precio local si la
                  // escritura a Firestore tuvo éxito arriba (no lanzó).
                  contraofertaRideIdRef.current = null;
                  setShowCounterOfferModal(false);
                  setOfferPrice(precioContraoferta);
                  showAlert('Contraoferta Enviada', `Hemos enviado tu propuesta de ${pesos(precioContraoferta)} al pasajero. Esperando su confirmación.`, 'time', '#FF9500');
                })} />
              </View>
            </View>
          </View>
        </View>
      )}

      {/* DRIVER UI */}
      {/* El REPARTIDOR también entra aquí. Antes la condición era solo
          `role === 'DRIVER'`, así que quien elegía Repartidor aterrizaba en el
          mapa SIN UN SOLO CONTROL: ni "Conectarse", ni estado, nada. Una
          pantalla con un mapa y punto — exactamente lo que Google describe
          como "la app no carga". El repartidor ya tiene su listener de pedidos
          (map.tsx:1449); lo que le faltaba era el botón para encenderlo. */}
      {(role === 'DRIVER' || role === 'DELIVERY_DRIVER') && !incomingPassengerRequest && !isActiveTrip && (
        <View style={[styles.driverControls, { bottom: (Platform.OS === 'ios' ? 110 : 95) + margenAbajo }]}>
           {/* La etiqueta dice en qué punto está de verdad. Mientras se habla
               con el servidor no se queda muda con el texto de antes: el
               chofer sabía que había pulsado, no que estuviera pasando algo, y
               volvía a pulsar. El segundo toque ya no hace nada (cerrojo en
               `handleToggleOnline`), pero el botón tiene que decirlo. */}
           <Button3D
            title={
              cambiandoConexion
                ? (isOnline ? 'Desconectando…' : 'Conectando…')
                : (isOnline ? 'Desconectarse' : 'Conectarse')
            }
            onPress={handleToggleOnline}
            color={isOnline ? colors.primary : colors.accent}
          />
          {isOnline && !pendingOfferRideId && (
            <View style={styles.onlineStatus}>
              <Text style={{color: colors.white, fontWeight: 'bold'}}>
                {role === 'DELIVERY_DRIVER'
                  ? 'Buscando entregas cercanas...'
                  : 'Buscando viajes cercanos...'}
              </Text>
            </View>
          )}
          {/* ESTADO INTERMEDIO: Envié oferta, esperando al pasajero.
              Uber-style: el chofer sabe que su oferta fue enviada y que el
              pasajero tiene la palabra. Si otro chofer gana o el pasajero
              rechaza, este estado se limpia solo (no dispara el listener
              de ride ACEPTADO). Botón manual para cancelar la oferta. */}
          {isOnline && pendingOfferRideId && (
            <View style={[styles.onlineStatus, { backgroundColor: '#FF9500' }]}>
              <Ionicons name="time" size={16} color="#FFF" style={{ marginRight: 6 }} />
              <Text style={{color: '#FFF', fontWeight: 'bold'}}>Oferta enviada — esperando respuesta del pasajero</Text>
            </View>
          )}
        </View>
      )}

      {/* DRIVER — VIAJE EN CURSO (después de que el pasajero aceptó al chofer).
          Se renderiza cuando el listener detectó ride.status IN ['ACCEPTED',
          'ARRIVED', 'IN_PROGRESS'] con driverId=me. Muestra la ActiveRideCard
          con los botones de progreso (He llegado / Iniciar / Completar). */}
      {role === 'DRIVER' && isActiveTrip && !incomingPassengerRequest && (
        <View onLayout={medirHoja} style={[styles.bottomSheet, { backgroundColor: colors.card, maxHeight: alto * 0.6, paddingBottom: 95 + margenAbajo }]}>
          <ScrollView showsVerticalScrollIndicator contentContainerStyle={{ paddingBottom: 20 }}>
            <Text style={[styles.sheetTitle, { color: colors.text }]}>
              {String(driverCurrentRide?.serviceType ?? '').toUpperCase() === 'DELIVERY'
                ? 'Envío en Curso'
                : 'Viaje en Curso'}
            </Text>

            {/* El paquete sigue siendo lo primero que tiene que ver: aceptar
                el envío no es el final: tiene que saber qué recoge cuando
                llegue, y la tarjeta de la solicitud ya desapareció. */}
            {String(driverCurrentRide?.serviceType ?? '').toUpperCase() === 'DELIVERY' && (
              <View style={[styles.destinoAviso, { backgroundColor: 'rgba(0,122,255,0.10)', borderColor: '#208AEF', marginBottom: 12 }]}>
                <Ionicons name="cube" size={16} color="#208AEF" />
                <View style={{ flex: 1, marginLeft: 8 }}>
                  <Text style={[styles.destinoEtiqueta, { color: '#208AEF' }]}>📦 ENVÍO DE PAQUETE</Text>
                  <Text style={[styles.destinoTexto, { color: colors.text }]} numberOfLines={3}>
                    {driverCurrentRide?.packageDescription
                      || 'El pasajero no describió el paquete — pregúntale al recogerlo.'}
                  </Text>
                </View>
              </View>
            )}

            {/* Ruta recogida / destino */}
            <View style={[styles.routeCard, { backgroundColor: colors.background, borderColor: colors.border, marginBottom: 12 }]}>
              <View style={styles.routeNode}>
                <View style={[styles.dot, { backgroundColor: colors.primary }]} />
                <View style={styles.routeTextContainer}>
                  <Text style={[styles.routeLabel, { color: colors.textSecondary }]}>Recogida</Text>
                  <Text style={[styles.routeAddress, { color: colors.text }]}>
                    {driverCurrentRide?.originAddress || 'GPS del pasajero'}
                  </Text>
                </View>
              </View>
              <View style={[styles.routeLine, { borderLeftColor: colors.border }]} />
              <View style={styles.routeNode}>
                <View style={[styles.square, { backgroundColor: colors.accent }]} />
                <View style={styles.routeTextContainer}>
                  <Text style={[styles.routeLabel, { color: colors.textSecondary }]}>Destino</Text>
                  <Text style={[styles.routeAddress, { color: colors.text }]}>
                    {driverCurrentRide?.destination || '—'}
                  </Text>
                </View>
              </View>
            </View>

            <ActiveRideCard
              /* ESTA RAMA ES LA DEL CHOFER (se pinta con `driverCurrentRide`, el
                 viaje que el lleva). Ver `soyElChoferDeEsteViaje`. */
              soyElChoferDeEsteViaje={true}
              userName={driverCurrentRide?.passengerName || 'Pasajero'}
              // Sin nota real, null: la tarjeta lo traduce a "Nuevo".
              rating={driverCurrentRide?.passengerRating?.toString() ?? null}
              // ── AQUÍ EL CONTADOR NO EXISTE, Y SE DICE ──────────────────────
              // Al chofer le falta el mismo dato que al pasajero, pero por otra
              // razón: buscado en todo el proyecto, NADIE escribe un contador de
              // calificaciones del pasajero. El viaje guarda `passengerRating`
              // (una nota suelta, la del último viaje) y nada más; el contador
              // solo existe del lado del chofer (`driverRatingCount` en /users,
              // que calcula ratings.ts).
              // Así que se pasa `null` a propósito y la tarjeta dice "Nuevo":
              // sin dato es sin dato. El 0 por defecto decía lo mismo por
              // casualidad, y una casualidad no se puede leer al revisar esto.
              ratingCount={null}
              // La verificación REAL del pasajero, del documento del viaje. Los
              // viajes creados ANTES de este cambio no traen el campo: llega
              // `null` y la tarjeta dice "Pasajero" a secas, que es la verdad.
              otherPartyVerified={
                typeof driverCurrentRide?.passengerVerified === 'boolean'
                  ? driverCurrentRide.passengerVerified
                  : null
              }
              otherPartyPhoto={driverCurrentRide?.passengerPhoto || null}
              otherPartyPhone={driverCurrentRide?.passengerPhone || null}
              rideId={currentRideId}
              rideStatus={rideStatus}
              arrivedAtMs={
                driverCurrentRide?.arrivedAt?.toMillis?.()
                ?? (typeof driverCurrentRide?.arrivedAt === 'number' ? driverCurrentRide.arrivedAt : null)
              }
              onDriverArrived={() => handleDriverArrived()}
              // H42 — "He llegado" no admite otro toque mientras se avisa.
              marcandoLlegada={marcandoLlegada}
              onStartRide={handleStartRide}
              onCompleteRide={handleCompleteRide}
              // El chofer veía "En camino" / "Viaje activo": dos textos que no
              // le dicen nada. La ruta que ya tiene dibujada sabe cuánto falta
              // —a la recogida antes de recoger, al destino después—, así que
              // se lo decimos. Si Directions no contestó, vuelven los textos
              // de siempre y nunca queda un hueco.
              eta={
                rutaInfo?.durationText
                  ? (rideStatus === 'IN_PROGRESS'
                      ? `Destino a ${rutaInfo.durationText}`
                      : `Recogida a ${rutaInfo.durationText}`)
                  : (rideStatus === 'IN_PROGRESS' ? 'Viaje activo' : 'En camino')
              }
              cancelando={cancelandoViaje}
              onCancel={() => cancelarComoChofer()}
              onCancelarPorEspera={() => setMotivoEsperaVisible(true)}
            />

            {/* Reportar desvío de ruta (chofer, durante el viaje en curso) */}
            {rideStatus === 'IN_PROGRESS' && (
              <TouchableOpacity
                onPress={openDeviationProposal}
                style={{ marginTop: 12, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 12, borderRadius: 12, backgroundColor: '#FF9800' }}
              >
                <Ionicons name="warning" size={18} color="#FFF" />
                <Text style={{ color: '#FFF', fontWeight: 'bold', fontSize: 14 }}>Reportar desvío de ruta</Text>
              </TouchableOpacity>
            )}

            {/* Navegación al destino (solo cuando ya recogió).
                ANTES: sin coordenadas se quitaba el botón y quedaba solo un aviso
                al fondo de la hoja — el chofer no tenía cómo navegar (el fallo
                del 17-sep). AHORA: con punto navega al punto; sin punto, por la
                dirección escrita. El componente valida las coordenadas igual que
                la burbuja (antes aquí bastaba con `!= null`, y un 0 habría
                mandado al chofer a `ll=0,0`). */}
            {rideStatus === 'IN_PROGRESS' && (() => {
              const lat = Number(driverCurrentRide?.destinationLat ?? driverCurrentRide?.destination_lat);
              const lng = Number(driverCurrentRide?.destinationLng ?? driverCurrentRide?.destination_lng);
              const hayPunto = Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
              const esEnvio = String(driverCurrentRide?.serviceType ?? '') === 'DELIVERY';
              const conQuien = esEnvio ? 'con quien envía el paquete' : 'con el pasajero';
              const destinoEscrito = String(driverCurrentRide?.destination ?? '').trim();
              const sector = String(driverCurrentRide?.destinationSector ?? '').trim();
              const textoDestino = [destinoEscrito, sector].filter(Boolean).join(', ');
              const aproximado = hayPunto && driverCurrentRide?.destinoAproximado === true;
              return (
                <>
                  {(hayPunto || !!textoDestino) && (
                    <View style={{ marginTop: 12, gap: 8 }}>
                      <NavigationLauncher
                        rideId={String(driverCurrentRide?.id ?? '') || null}
            destinationLat={hayPunto ? lat : null}
                        destinationLng={hayPunto ? lng : null}
                        destinoTexto={textoDestino || undefined}
                        destinationLabel={driverCurrentRide?.destination}
                      />
                    </View>
                  )}
                  {/* El servidor le buscó el punto a una dirección escrita a mano:
                      casi siempre acierta, pero no es el punto que eligió la persona. */}
                  {aproximado && (
                    <View style={{ marginTop: 10, padding: 12, backgroundColor: '#FFF3E0', borderRadius: 12 }}>
                      <Text style={{ color: '#E65100', fontSize: 13, fontWeight: '600' }}>
                        📍 Punto aproximado, sacado de la dirección escrita
                        {driverCurrentRide?.destinoResueltoDireccion ? `: ${driverCurrentRide.destinoResueltoDireccion}` : ''}. Confírmalo {conQuien}.
                      </Text>
                    </View>
                  )}
                  {!hayPunto && (
                    <View style={{ marginTop: 10, padding: 12, backgroundColor: '#FFF3E0', borderRadius: 12 }}>
                      <Text style={{ color: '#E65100', fontSize: 13, fontWeight: '600' }}>
                        {textoDestino
                          ? `⚠️ Este destino no tiene punto exacto en el mapa: la app de navegación lo buscará por la dirección escrita. Confírmalo ${conQuien}: ${textoDestino}`
                          : `⚠️ Este viaje no trae dirección de destino. Pregúntala ${conQuien}.`}
                      </Text>
                    </View>
                  )}
                </>
              );
            })()}
            {/* Navegación a recogida (chofer en camino a levantar al pasajero) */}
            {rideStatus === 'ACCEPTED' && (driverCurrentRide?.originLat || driverCurrentRide?.origin_lat) && (
              <View style={{ marginTop: 12, gap: 8 }}>
                <NavigationLauncher
                  rideId={String(driverCurrentRide?.id ?? '') || null}
                  destinationLat={driverCurrentRide?.originLat ?? driverCurrentRide?.origin_lat}
                  destinationLng={driverCurrentRide?.originLng ?? driverCurrentRide?.origin_lng}
                  destinationLabel={driverCurrentRide?.originAddress || 'Punto de recogida'}
                />
              </View>
            )}
          </ScrollView>
        </View>
      )}

      {/* DRIVER INCOMING REQUEST — Feed de solicitudes NUEVAS del pasajero.
          Este bloque es SOLO para el estado "aún no acepté / esperando
          respuesta del pasajero a mi oferta". El estado "viaje en curso"
          (isActiveTrip=true) tiene su propio bloque arriba con la
          ActiveRideCard + botones de progreso. */}
      {/* El REPARTIDOR también abre este panel. Las tarjetas de pedido viven
          aquí dentro (`driverDeliveries.map`, más abajo), pero la condición era
          `role === 'DRIVER' && incomingPassengerRequest`, y ese
          `incomingPassengerRequest` solo se enciende dentro de la rama de
          DRIVER: para un repartidor NUNCA era cierto. O sea que sus pedidos se
          cargaban del servidor y no se pintaban en ninguna parte — se quedaba
          en "Buscando entregas cercanas..." para siempre. */}
      {/* EL REPARTIDOR SIEMPRE TIENE PUERTA.
          La condición exigía `driverDeliveries.length > 0`, o sea que el panel
          solo existía mientras hubiera pedidos READY. En cuanto tomaba uno, el
          pedido dejaba de estar READY, el panel desaparecía — y como la única
          entrada a la pantalla de entrega era el salto de justo después de
          reclamarlo, cerrar la app lo dejaba con la comida encima y sin camino
          de vuelta. Ahora el panel está siempre para su rol y el texto dice lo
          que hay: pedidos, ninguno, o su entrega en curso. */}
      {((role === 'DRIVER' && incomingPassengerRequest) ||
        role === 'DELIVERY_DRIVER') && !isActiveTrip && (
        <View onLayout={medirHoja} style={[styles.bottomSheet, { backgroundColor: colors.card, maxHeight: alto * 0.5, paddingBottom: 95 + margenAbajo }]}>

          {/* TOPE DE ALTURA (maxHeight arriba) — Esta era la ÚNICA de las seis
              hojas sin tope y sin desplazamiento: crecía con el contenido hasta
              tapar casi toda la pantalla, y el chofer no veía el mapa mientras
              decidía. Sus cinco hermanas ya usaban este patrón (0.5-0.6 del
              alto); aquí se usa 0.45 porque en esta fase EL MAPA ES LA
              INFORMACIÓN: es donde ve dónde está el pasajero.

              El título y la cuenta atrás quedan FUERA del área desplazable:
              son lo que le dice cuánto tiempo le queda para decidir. */}
          {/* ══ LA CUENTA ATRÁS NO CORRE PARA EL REPARTIDOR ══════════════
              Esta misma hoja la abren DOS perfiles. Al chofer de viajes sí le
              corren los segundos: la solicitud se le retira sola. Al repartidor
              no le caduca nada —un pedido se queda en READY hasta que alguien lo
              toma—, así que "¡15 seg restantes!" le metía prisa por algo que no
              iba a pasar, y "Nueva Solicitud" nombraba un viaje que no existe.
              El rol ya decide qué se pinta debajo; aquí decide también el rótulo.
              La rama del chofer de viajes queda EXACTAMENTE como estaba. */}
          <View style={{flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12}}>
            <Text style={[styles.sheetTitle, { color: colors.text, marginBottom: 0 }]}>
              {role === 'DELIVERY_DRIVER' ? 'Pedidos listos para recoger' : 'Nueva Solicitud'}
            </Text>
            <View style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
              {driverPendingRides.length > 1 && (
                <Text style={{ color: colors.textSecondary, fontSize: 12 }}>
                  {/* La pantalla solo pinta UNA tarjeta, así que sin este
                      desglose el chofer no tiene cómo ver qué hace su radio. */}
                  +{driverPendingRides.length - 1} en espera
                  {` · ${driverPendingRides.filter(esDeMiZona).length} en tu zona`}
                </Text>
              )}
              {role !== 'DELIVERY_DRIVER' && (
                <View style={{backgroundColor: 'rgba(255, 149, 0, 0.15)', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12}}>
                  <Text style={{color: '#FF9500', fontWeight: 'bold', fontSize: 12}}>¡15 seg restantes!</Text>
                </View>
              )}
            </View>
          </View>

          <ScrollView
            style={{ flexShrink: 1 }}
            showsVerticalScrollIndicator
            contentContainerStyle={{ paddingBottom: 8 }}
            nestedScrollEnabled
          >

          {/* Aqui habia una segunda ficha de "Recogida / Destino" que repetia lo
              que cada tarjeta de solicitud ya muestra abajo, y ademas leia
              SIEMPRE de driverPendingRides[0]: con varias solicitudes ensenaba
              los datos de otra. Ocupaba ~150 dp, que es justo lo que empujaba el
              boton "Aceptar Viaje" fuera de la pantalla en un telefono pequeno.
              Los estilos routeCard/routeNode/routeLine NO se borran: los usa el
              panel de viaje en curso. */}

          {/* ══ ESTA CABECERA LEÍA EL FORMULARIO DEL PASAJERO ═══════════════
              `serviceType`, `packageDescription`, `selectedVehicleType`,
              `passengers` y el rótulo "Pago en Efectivo" son estado LOCAL del
              formulario que el pasajero rellena en esta misma pantalla. En el
              teléfono de un chofer ese formulario no se dibuja nunca, así que
              `serviceType` se quedaba en 'RIDE' de por vida: la rama de envíos
              era código muerto que no se ejecutó jamás. Al chofer le decía
              "🚗 Carro • 1 Pasajero" cuando lo que le iban a dar era un
              paquete, y "Pago en Efectivo" a alguien que ya pagó con tarjeta
              —eso es pedirle el dinero dos veces a un pasajero, en la calle.
              Ahora todo sale del DOCUMENTO de la solicitud que está mirando. */}
          {driverPendingRides.length > 0 && (() => {
            const s: any = driverPendingRides[0];
            const esEnvio = String(s?.serviceType ?? 'RIDE').toUpperCase() === 'DELIVERY';
            const tipoVehiculo = String(s?.vehicleType ?? 'CAR').toUpperCase();
            const cuantos = parseInt(String(s?.passengers ?? '1'), 10) || 1;
            const metodo = String(s?.paymentMethod ?? 'CASH').toUpperCase();
            const textoPago =
              metodo === 'CARD' ? 'Pagado con Tarjeta'
                : metodo === 'TRANSFER' ? 'Pago por Transferencia'
                : 'Pago en Efectivo';
            return (
              <View style={{flexDirection: 'column', marginBottom: 20, marginHorizontal: 8, gap: 10}}>
                {/* `flexWrap` porque ahora el texto del pago es variable
                    ("Pago por Transferencia" es más largo que el fijo de
                    antes) y en un teléfono de 360 dp la fila se salía. */}
                <View style={{flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap'}}>
                  {esEnvio ? (
                    <>
                      <Ionicons name="cube" size={16} color={colors.primary} style={{marginRight: 6}} />
                      <Text style={{color: colors.text, fontWeight: '600', fontSize: 14}}>📦 Envío de paquete</Text>
                    </>
                  ) : (
                    <>
                      <Ionicons name={tipoVehiculo === 'MOTORCYCLE' ? "bicycle" : tipoVehiculo === 'MINIBUS' ? "bus" : "car"} size={16} color={colors.primary} style={{marginRight: 6}} />
                      <Text style={{color: colors.text, fontWeight: '600', fontSize: 14}}>
                        {tipoVehiculo === 'MOTORCYCLE' ? '🏍️ Motor' : tipoVehiculo === 'MINIBUS' ? '🚐 Minibus' : '🚗 Carro'} • {cuantos} {cuantos === 1 ? 'Pasajero' : 'Pasajeros'}
                      </Text>
                    </>
                  )}
                  <View style={{width: 4, height: 4, borderRadius: 2, backgroundColor: colors.border, marginHorizontal: 12}} />
                  <Ionicons name="card" size={16} color={colors.accent} style={{marginRight: 6}} />
                  <Text style={{color: colors.text, fontWeight: '600', fontSize: 14, flexShrink: 1}}>{textoPago}</Text>
                </View>

                {/* EL AVISO QUE FALTABA. Si el pasajero va a pagar por
                    transferencia y este chofer no tiene ninguna cuenta puesta,
                    nadie se enteraba: aceptaba, hacía el viaje, y al final el
                    pasajero no tenía a dónde transferirle. Se dice ANTES de
                    aceptar, que es cuando todavía se puede arreglar. */}
                {metodo === 'TRANSFER' && misCuentasDeCobro !== null && misCuentasDeCobro.length === 0 && (
                  <TouchableOpacity
                    onPress={() => router.push('/driver-bank-accounts' as any)}
                    style={{ flexDirection: 'row', alignItems: 'center', gap: 9, backgroundColor: 'rgba(255,149,0,0.12)', borderRadius: 10, paddingVertical: 10, paddingHorizontal: 12 }}
                    accessibilityRole="button"
                    accessibilityLabel="Registrar mi cuenta para recibir transferencias"
                  >
                    <Ionicons name="alert-circle" size={18} color="#FF9500" />
                    <Text style={{ color: '#B36200', fontSize: 12.5, fontWeight: '600', flex: 1, lineHeight: 17 }}>
                      Este pasajero va a pagar por transferencia y tú no tienes cuenta registrada.
                      Toca aquí para ponerla, o acuerda con él cobrar en efectivo.
                    </Text>
                    <Ionicons name="chevron-forward" size={16} color="#FF9500" />
                  </TouchableOpacity>
                )}

                {/* ── ESTE SELLO SE CONTRADECÍA CONSIGO MISMO ────────────────
                    Se pintaba SIN NINGUNA CONDICIÓN, y unas líneas más abajo, en
                    el mismo panel, la tarjeta avisaba de que faltaba el sector.
                    Dos afirmaciones contrarias sobre el mismo viaje, y el chofer
                    decidiendo con las dos delante.

                    "Exacta y validada" solo es cierto cuando el destino trae
                    coordenadas Y sector: con la calle sola, el chofer acepta a
                    ciegas —una misma calle está en varios sectores y a distancias
                    muy distintas—. Si falta cualquiera de las dos no se pinta
                    nada: el aviso "Sin sector indicado" de la tarjeta ya dice la
                    verdad y no hace falta contradecirlo aquí. */}
                {typeof s?.destinationLat === 'number' &&
                  typeof s?.destinationLng === 'number' &&
                  s?.destinoAproximado !== true &&
                  String(s?.destinationSector ?? '').trim().length > 0 && (
                  <View style={{flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(0, 200, 83, 0.1)', paddingVertical: 6, paddingHorizontal: 10, borderRadius: 8, alignSelf: 'flex-start'}}>
                    <Ionicons name="shield-checkmark" size={14} color="#00C853" style={{marginRight: 6}} />
                    {/* La CONDICIÓN ya se corrigió (coordenadas Y sector), pero
                        el texto seguía prometiendo una exactitud que nadie mide:
                        "exacta" y "validada" no corresponden a ninguna
                        comprobación del código. Dice lo que la condición
                        comprobó, que es justo lo que el chofer necesita saber. */}
                    <Text style={{color: '#00C853', fontWeight: 'bold', fontSize: 12}}>🛡️ Destino con punto en el mapa y sector ✓</Text>
                  </View>
                )}
              </View>
            );
          })()}

          {/* Feed de solicitudes NUEVAS. isActiveTrip nunca es true aquí
              (el guard del bloque padre lo impide) — el viaje activo se
              renderiza en el bloque anterior con ActiveRideCard. */}
          {(
            driverDeliveries.length > 0 ? (
              /* ══ AQUÍ HABÍA UN BOTÓN QUE NO PODÍA FUNCIONAR ════════════════
                 Se pintaba una tarjeta por pedido con un "Aceptar" que llamaba a
                 `firestoreService.assignDeliveryToDriver`: un write DIRECTO sobre
                 /food_orders poniendo `status:'ASSIGNED'` y `driverId`. Las reglas
                 lo deniegan por tres motivos A LA VEZ (firestore.rules, match
                 /food_orders): el repartidor todavía no es el `driverId` del
                 pedido, 'ASSIGNED' está entre los estados prohibidos, y `driverId`
                 está en la lista negra de campos. Y como el `await` no tenía
                 try/catch, la promesa reventaba sin que nadie la recogiera: el
                 `router.push` de la línea siguiente no llegaba a correr. El
                 repartidor tocaba "Aceptar" y no pasaba NADA —ni pedido, ni aviso,
                 ni error—. Es el mismo patrón por el que ya nos rechazaron.

                 Y la tarjeta además inventaba lo que él mira para decidir:
                 `deliveryFee` NO EXISTE en /food_orders (mira los campos que
                 escribe `createFoodOrder`, functions/src/orders.ts), así que el
                 `|| 100` le prometía RD$100 fijos por cualquier pedido, y "Cerca"
                 se pintaba sin medir ni una distancia.

                 El camino bueno ya existía: la callable `claimFoodOrder`, que hace
                 la asignación atómica con Admin SDK, y la pantalla /deliveries-feed
                 que la llama, ordena por distancia REAL y enseña total, propina y
                 dirección sacados del documento. Estaba huérfana: nada en la app
                 navegaba hasta ella. Este panel se queda con lo único que aportaba
                 de verdad —avisar, con sonido y vibración, de que hay pedidos— y
                 abre esa pantalla. Un solo feed del mismo dato: el que funciona. */
              <View style={{ marginBottom: 15, gap: 10 }}>
                <Text style={{ color: colors.text, fontSize: 15, fontWeight: '700' }}>
                  {driverDeliveries.length === 0
                    ? 'No hay pedidos listos ahora mismo'
                    : driverDeliveries.length === 1
                    ? 'Hay 1 pedido listo para recoger'
                    : `Hay ${driverDeliveries.length} pedidos listos para recoger`}
                </Text>
                <Text style={{ color: colors.textSecondary, fontSize: 13 }}>
                  {driverDeliveries.length === 0
                    ? 'Aquí aparecerán en cuanto un restaurante termine uno. Si ya tomaste un pedido, entra para continuar la entrega.'
                    : 'Ábrelos para ver a qué distancia está cada restaurante, el total y la propina de cada pedido, y tomar el que quieras.'}
                </Text>
                <TouchableOpacity
                  onPress={() => router.push('/deliveries-feed')}
                  accessibilityRole="button"
                  accessibilityLabel="Ver pedidos disponibles"
                  style={{
                    backgroundColor: colors.primary,
                    paddingVertical: 14,
                    borderRadius: 12,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  <Ionicons name="bicycle" size={18} color="#FFF" />
                  <Text style={{ color: '#FFF', fontWeight: 'bold', fontSize: 15 }}>
                    {driverDeliveries.length === 0 ? 'Mis entregas' : 'Ver pedidos disponibles'}
                  </Text>
                </TouchableOpacity>
              </View>
            ) : driverPendingRides.length > 0 ? (
              // UNA SOLICITUD A LA VEZ. Antes se pintaba una tarjeta por cada
              // viaje pendiente (hasta 10 permite el servicio), y eso era lo que
              // de verdad hacia crecer la hoja hasta tapar la pantalla. Al
              // rechazar, `onReject` saca esta del array y aparece la siguiente
              // sola: es como funcionan Uber e InDriver. El contador "+N en
              // espera" del titulo le dice que hay mas.
              [driverPendingRides[0]].filter(Boolean).map((ride: any) => (
                <View key={ride.id} style={{ marginBottom: 15 }}>
                  {/* ══ ESTO ES UN PAQUETE, NO UNA PERSONA ═════════════════
                      El chofer decidía a ciegas: nada en esta tarjeta decía
                      que era una mensajería, así que salía esperando recoger a
                      alguien. Va lo PRIMERO, antes incluso del destino, porque
                      es lo que cambia la decisión. Usa el mismo bloque de
                      aviso (`destinoAviso`) que "LO PAGA LA APP". */}
                  {String(ride.serviceType ?? '').toUpperCase() === 'DELIVERY' && (
                    <View style={[styles.destinoAviso, { backgroundColor: 'rgba(0,122,255,0.10)', borderColor: '#208AEF' }]}>
                      <Ionicons name="cube" size={16} color="#208AEF" />
                      <View style={{ flex: 1, marginLeft: 8 }}>
                        <Text style={[styles.destinoEtiqueta, { color: '#208AEF' }]}>📦 ENVÍO DE PAQUETE</Text>
                        <Text style={[styles.destinoTexto, { color: colors.text }]} numberOfLines={3}>
                          {ride.packageDescription || 'El pasajero no describió el paquete — pregúntale antes de salir.'}
                        </Text>
                      </View>
                    </View>
                  )}
                  {/* LA NOTA DEL PASAJERO. Va junto al envio y antes del
                      destino: es de las pocas cosas que cambian la decision
                      de aceptar o no. */}
                  {!!ride.notaDelPasajero && (
                    <View style={[styles.destinoAviso, { backgroundColor: 'rgba(255,149,0,0.10)', borderColor: '#FF9500' }]}>
                      <Ionicons name="chatbubble-ellipses" size={16} color="#FF9500" />
                      <View style={{ flex: 1, marginLeft: 8 }}>
                        <Text style={[styles.destinoEtiqueta, { color: '#FF9500' }]}>NOTA DEL PASAJERO</Text>
                        <Text style={[styles.destinoTexto, { color: colors.text }]} numberOfLines={4}>
                          {ride.notaDelPasajero}
                        </Text>
                        {/* La nota la escribe un desconocido y la lee el chofer
                            ANTES de que exista chat, asi que el bloquear/reportar
                            del chat no le sirve: todavia no hay chat. Esta es la
                            unica salida que tiene en ese momento. */}
                        <TouchableOpacity
                          onPress={() => reportarNota(ride)}
                          accessibilityRole="button"
                          accessibilityLabel="Reportar esta nota como ofensiva"
                          style={{ alignSelf: 'flex-start', marginTop: 6, paddingVertical: 6, paddingHorizontal: 10, minHeight: 32, justifyContent: 'center' }}
                        >
                          <Text style={{ fontSize: 12, fontWeight: '700', color: '#FF3B30' }}>
                            Reportar esta nota
                          </Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                  )}
                  {/* ══ A DÓNDE VA, ANTES DE ACEPTAR ═══════════════════════
                      El chofer veía la distancia hasta el PASAJERO pero no a
                      dónde iba el viaje. Aceptaba a ciegas y descubría después
                      que era el doble de largo. Ahora la calle Y el sector van
                      arriba del todo, que es lo que pidieron los choferes. */}
                  <View style={[styles.destinoAviso, { backgroundColor: colors.card, borderColor: colors.border }]}>
                    <Ionicons name="flag" size={16} color="#FF9500" />
                    <View style={{ flex: 1, marginLeft: 8 }}>
                      <Text style={[styles.destinoEtiqueta, { color: colors.textSecondary }]}>DESTINO</Text>
                      <Text style={[styles.destinoTexto, { color: colors.text }]} numberOfLines={2}>
                        {ride.destination || 'Sin dirección'}
                      </Text>
                      {ride.destinationSector ? (
                        <Text style={styles.destinoSector}>📍 {ride.destinationSector}</Text>
                      ) : (
                        <Text style={[styles.destinoSinSector, { color: colors.textSecondary }]}>
                          Sin sector indicado — confírmalo antes de aceptar
                        </Text>
                      )}
                    </View>
                  </View>
                  {/* Si la app cubre la tarifa, el chofer tiene que verlo ANTES
                      de aceptar. La marca la verifica el servidor contra el
                      estado de lealtad real del pasajero (dispatch.ts): si no
                      está respaldada, se retira y aquí no se pinta. */}
                  {ride.cubiertoPorLaApp === true && (
                    <View style={[styles.destinoAviso, { backgroundColor: 'rgba(255,149,0,0.12)', borderColor: '#FF9500' }]}>
                      <Ionicons name="gift" size={16} color="#FF9500" />
                      <View style={{ flex: 1, marginLeft: 8 }}>
                        <Text style={[styles.destinoEtiqueta, { color: '#FF9500' }]}>LO PAGA LA APP</Text>
                        <Text style={[styles.destinoTexto, { color: colors.text }]}>
                          No le cobres al pasajero. Go Safely te abona esta tarifa.
                        </Text>
                      </View>
                    </View>
                  )}
                  <RideOfferCard
                    type="DRIVER_VIEW"
                    userName={ride.passengerName || 'Pasajero'}
                    userPhoto={ride.passengerPhoto || undefined}
                    rating={ride.passengerRating?.toString() ?? null}
                    distance={distanceLabel(currentLocation, ride)}
                    timeEst={etaLabel(currentLocation, ride)}
                    largoViaje={largoDelViajeLabel(ride) ?? undefined}
                    economia={economiaLabel(currentLocation, ride) ?? undefined}
                    // `ride.lejos` lo calcula el servicio con 6 km fijos para
                    // todo el país y medidos desde el origen CONGELADO al
                    // suscribirse. Aquí se mide fresco, con el GPS de ahora y
                    // el radio que eligió el chofer: por eso el control se
                    // nota al instante, sin rehacer la suscripción.
                    lejos={!esDeMiZona(ride)}
                    price={(ride.offerPrice || 0).toString()}
                    vehicleInfo={
                      String(ride.serviceType ?? '').toUpperCase() === 'DELIVERY'
                        ? '📦 Paquete'
                        : `${nombreDeVehiculo(ride.vehicleType)} • ${ride.passengers || 1} pas.`
                    }
                    // ── EL SELLO QUE SIEMPRE DECÍA QUE SÍ ─────────────────────
                    // Era el literal `true`: con el muro de KYC del pasajero
                    // relajado a propósito, un pasajero SIN verificar le salía al
                    // chofer con escudo verde, justo en la decisión de montar a un
                    // desconocido en su carro. Ahora es el dato REAL del viaje.
                    isFaceIdVerified={ride.passengerVerified === true}
                    // H40 — mientras la oferta viaja, "Aceptar Viaje" gira y no
                    // admite otro toque. Ver `conCandadoDeOferta`.
                    enviando={enviandoOferta}
                    onAccept={() => conCandadoDeOferta(async () => {
                      const isAuthenticated = await biometricService.authenticateUser('Verifica tu identidad para aceptar este viaje (Seguridad Obligatoria)');
                      if (!isAuthenticated && Platform.OS !== 'web') {
                        Alert.alert('Acceso Denegado', 'No pudimos verificar tu identidad. El viaje ha sido bloqueado por seguridad.');
                        return;
                      }
                      const driverId = useAppStore.getState().userFirebaseUid;
                      if (!driverId) return;
                      const meAccept = useAppStore.getState();
                      const brandA = meAccept.vehicleBrand ?? '';
                      const modelA = meAccept.vehicleModel ?? '';
                      const colorA = meAccept.vehicleColor ?? '';
                      // Mismo motivo que en la contraoferta: el respaldo tiene que ser
                      // el vehículo REAL del chofer, no el que eligió el pasajero.
                      const vehicleLabelA = [colorA, brandA, modelA].filter(Boolean).join(' ') || (vehiculoParaPublicar || 'Vehículo');
                      // FIX CRÍTICO FLUJO UBER-STYLE — Antes:
                      //   setIsActiveTrip(true) inmediatamente → chofer entraba
                      //   a "Viaje en Curso" ANTES de que el pasajero aceptara
                      //   su oferta. Si el pasajero rechazaba u otro chofer
                      //   ganaba, el chofer se quedaba con UI de viaje activo
                      //   fantasma → confusión total.
                      //
                      // Ahora:
                      //   1) Enviamos la oferta al pasajero (sin cambiar estado).
                      //   2) Guardamos `pendingOfferRideId` para mostrar UI
                      //      "Oferta enviada — esperando respuesta del pasajero".
                      //   3) `listenToMyAcceptedRideAsDriver` detecta cuando
                      //      el pasajero acepta (ride.status='ACCEPTED' &
                      //      driverId=me) → recién ahí setIsActiveTrip(true).
                      //   4) Si el pasajero elige a otro chofer o cancela,
                      //      nuestro listener no dispara y limpiamos la UI.
                      try {
                        await firestoreService.sendRideOffer(ride.id, driverId, {
                          driverId,
                          userName: String(meAccept.userName ?? '').trim() || 'Chofer',
                          driverName: String(meAccept.userName ?? '').trim() || 'Chofer',
                          driverPhoto: String(meAccept.userPhoto ?? '').trim() || null,
                          driverPhone: String(meAccept.userPhone ?? '').trim() || null,
                          // CONTRATO (d) — la reputación (`rating`, `ratingCount`,
                          // `isFaceIdVerified`) la estampa el servidor, no este
                          // teléfono. Ver la nota larga en la contraoferta.
                          distance: distanceLabel(currentLocation, ride),
                          timeEst: etaLabel(currentLocation, ride),
                          price: (ride.offerPrice || 0).toString(),
                          vehicleInfo: vehicleLabelA,
                          vehicleType: vehiculoParaPublicar as any,
                          vehicleBrand: brandA || null,
                          vehicleModel: modelA || null,
                          vehicleColor: colorA || null,
                          licensePlate: meAccept.licensePlate ?? null,
                        });
                        // Marcar solo como "oferta pendiente" — NO entra a Viaje.
                        // ANTI-FANTASMA — Persistir en sesión local + ref para
                        // que el listener global reconozca ESTE ride como legítimo
                        // cuando el pasajero lo acepte (y no lo trate de fantasma).
                        setPendingOfferRideId(ride.id);
                        pendingOfferRideIdRef.current = ride.id;
                        rideSessionStore.setPendingOffer(ride.id);
                        setDriverCurrentRide(ride);
                        showAlert('Oferta enviada', 'Esperando que el pasajero acepte tu oferta. Si otro chofer gana, esta oferta expira automáticamente.', 'time', '#FF9500');
                      } catch (e: any) {
                        console.warn('[driver sendOffer]', e);
                        // Limpiar cualquier side-effect y avisar al chofer.
                        rideSessionStore.setPendingOffer(null);
                        // showAlert (no Alert.alert): el éxito usa showAlert y
                        // Alert.alert es no-op en RN-web — el fallo debe verse
                        // igual en ambas plataformas.
                        showAlert(
                          'No pudimos enviar tu oferta',
                          'Revisa tu conexión e intenta de nuevo. Otro chofer puede tomar este viaje si tardas.',
                          'alert-circle',
                          '#FF3B30'
                        );
                      }
                    })}
                    onCounterOffer={() => {
                      // El id se congela AQUÍ: ver `contraofertaRideIdRef`.
                      contraofertaRideIdRef.current = ride.id;
                      // Y con él, los kilómetros: dentro del modal ya no se
                      // tiene el viaje a mano.
                      {
                        const kmViaje = kmDelViaje(ride);
                        const kmRecogida = haversineKm(currentLocation, ride);
                        contraofertaEconomiaRef.current =
                          kmViaje == null
                            ? null
                            : {
                                kmTotal: kmViaje + (kmRecogida ?? 0),
                                vehiculo: String(ride.vehicleType || 'CAR').toUpperCase(),
                              };
                      }
                      setCurrentRideId(ride.id);
                      setShowCounterOfferModal(true);
                    }}
                    onReject={() => {
                      rejectedRideIdsRef.current.add(ride.id);
                      setDriverPendingRides((prev: any[]) => prev.filter((r: any) => r.id !== ride.id));
                      if (driverPendingRides.length <= 1) {
                        setIncomingPassengerRequest(false);
                      }
                      // ── Y SI YA LE HABÍA OFERTADO A ESE VIAJE ────────────
                      // La tarjeta sigue enseñando «Rechazar» después de
                      // ofertar (el viaje sigue PENDING, así que sigue en el
                      // feed). Si el chofer cambia de idea y la rechaza, la
                      // píldora naranja «Oferta enviada — esperando respuesta»
                      // se quedaba encendida contra una solicitud que él mismo
                      // acaba de descartar: el viaje sale de su feed, así que
                      // ni el bloque V56 la podía apagar.
                      if (pendingOfferRideIdRef.current === ride.id) {
                        pendingOfferRideIdRef.current = null;
                        setPendingOfferRideId(null);
                        rideSessionStore.setPendingOffer(null);
                      }
                      if (shownRequestRideIdRef.current === ride.id) {
                        shownRequestRideIdRef.current = null;
                      }
                    }}
                  />
                </View>
              ))
            ) : (
              <View style={{ alignItems: 'center', paddingVertical: 24 }}>
                <Ionicons name="car-outline" size={44} color={colors.textSecondary} />
                <Text style={{ color: colors.textSecondary, marginTop: 10, fontSize: 14, textAlign: 'center' }}>
                  Esperando solicitudes de pasajeros...
                </Text>
                <TouchableOpacity style={{ marginTop: 14 }} onPress={() => setIncomingPassengerRequest(false)}>
                  <Text style={{ color: colors.primary, fontSize: 13, fontWeight: '600' }}>Cancelar y volver</Text>
                </TouchableOpacity>
              </View>
            )
          )}
          </ScrollView>
        </View>
      )}

      {/* MODALS */}


      <PaymentModal 
        visible={paymentModalVisible}
        selectedMethod={paymentMethod}
        onSelect={(method) => {
          setPaymentMethod(method);
          setPaymentModalVisible(false);
        }}
        onClose={() => setPaymentModalVisible(false)}
        driverBankAccounts={driverBankAccounts}
      />

      <CustomModal 
        visible={customModalConfig.visible}
        title={customModalConfig.title}
        message={customModalConfig.message}
        iconName={customModalConfig.icon}
        iconColor={customModalConfig.color}
        onClose={() => setCustomModalConfig({ ...customModalConfig, visible: false })}
      />

      {/* RATING MODAL (Cuando el viaje termina) */}
      {/* AVISO DESTACADO de ubicación en segundo plano (requisito Google Play).
          Se muestra ANTES del permiso del sistema; al aceptar re-ejecutamos
          handleToggleOnline, que ya dispara el diálogo nativo. */}
      <BackgroundLocationDisclosure
        visible={bgDisclosureVisible}
        onAccept={() => {
          bgDisclosureAcceptedRef.current = true;
          setBgDisclosureVisible(false);
          handleToggleOnline();
        }}
        onDecline={() => setBgDisclosureVisible(false)}
      />

      <DriverRatingModal
        visible={ratingModalVisible}
        driverName={selectedDriver?.driverName || selectedDriver?.userName || "Chofer"}
        driverPhoto={selectedDriver?.driverPhoto || ''}
        onClose={() => {
          // Omitir la calificacion tambien CIERRA el viaje. Antes solo se
          // escondia el modal y el pasajero se quedaba con el viaje viejo
          // colgando en pantalla.
          setRatingModalVisible(false);
          cerrarViajeDelPasajero();
        }}
        onSubmit={async (estrellas, comentario) => {
          // La callable del servidor (`submitRideRating`, ratings.ts) deriva el
          // driverId del viaje y actualiza el promedio real del chofer.
          //
          // ── AHORA VIAJA LA NOTA EXACTA Y EL COMENTARIO ────────────────
          // Antes el modal solo emitía 'EXCELENTE' | 'REGULAR' | 'MALO' y aquí se
          // traducían a 5, 3 y 1. Un chofer que merecía un 4 recibía un 5 o un 3,
          // y esa nota es la que ve el próximo pasajero. El comentario NUNCA se
          // mandaba, aunque `submitRideRating` lo acepta y lo guarda desde el
          // principio: en el primer viaje real de la app la calificación quedó
          // escrita con `comment: null` por eso, no porque el servidor fallara.
          if (ratingRideId) {
            try {
              const driverRating =
                Number.isFinite(estrellas) && estrellas >= 1 && estrellas <= 5 ? estrellas : 5;
              await callSubmitRideRating({
                rideId: ratingRideId,
                driverRating,
                comment: comentario || undefined,
              });
            } catch (e: any) {
              console.warn('[submitRideRating] error:', e?.message);
            }
          }
          setRatingModalVisible(false);
          cerrarViajeDelPasajero();
          showAlert('Gracias por tu opinión', 'Tu calificación ayuda a mejorar la comunidad de Go Safely.', 'star', '#FFD700');
        }}
      />

      {/* EL CHOFER CALIFICA AL PASAJERO — la misma pantalla de estrellas, con
          otro título. Ver `submitPassengerRating` (functions/src/ratings.ts). */}
      <DriverRatingModal
        visible={califPasajeroVisible}
        driverName={califPasajeroViaje?.nombre || 'Pasajero'}
        driverPhoto={califPasajeroViaje?.foto || ''}
        titulo="Viaje completado"
        pregunta="¿Qué tal el pasajero?"
        etiquetaPersona="Pasajero"
        onClose={() => {
          setCalifPasajeroVisible(false);
          setCalifPasajeroViaje(null);
        }}
        onSubmit={async (estrellas, comentario) => {
          const viaje = califPasajeroViaje?.rideId;
          if (viaje) {
            try {
              await callSubmitPassengerRating({
                rideId: viaje,
                passengerRating:
                  Number.isFinite(estrellas) && estrellas >= 1 && estrellas <= 5 ? estrellas : 5,
                comment: comentario || undefined,
              });
            } catch (e: any) {
              // Que falle la calificación NO puede dejar al chofer atrapado en
              // esta pantalla con el viaje ya cerrado: se avisa y se sigue.
              console.warn('[submitPassengerRating] error:', e?.message);
            }
          }
          setCalifPasajeroVisible(false);
          setCalifPasajeroViaje(null);
        }}
      />

      {/* El chofer esperó sus minutos y el pasajero no apareció: se va sin
          que le cuente, diciendo por qué. Ver MotivoDeEsperaModal.tsx. */}
      <MotivoDeEsperaModal
        visible={motivoEsperaVisible}
        minutosEsperando={(() => {
          // Solo para el texto del modal. El reloj que decide es el del
          // servidor (ver MotivoDeEsperaModal.tsx y penalties.ts).
          const ms =
            driverCurrentRide?.arrivedAt?.toMillis?.() ??
            (typeof driverCurrentRide?.arrivedAt === 'number' ? driverCurrentRide.arrivedAt : 0);
          return ms ? Math.max(0, Math.floor((Date.now() - ms) / 60000)) : 0;
        })()}
        onCerrar={() => setMotivoEsperaVisible(false)}
        onElegir={(codigo) => {
          setMotivoEsperaVisible(false);
          cancelarComoChofer(codigo);
        }}
      />

      {/* Desvío de ruta — chofer propone (con motivo), pasajero aprueba/rechaza */}
      <RouteDeviationModal
        visible={deviationModal.visible}
        role={deviationModal.role}
        /* El motivo ya se guardaba en el estado y NO se le pasaba al modal: al
           pasajero se le preguntaba si aprueba un desvío sin decirle cuál ni
           por qué. Ver el comentario del prop en RouteDeviationModal.tsx. */
        reason={deviationModal.reason}
        onApprove={handleDeviationApprove}
        onReject={handleDeviationReject}
      />

      {/* MURO DE SEGURIDAD — ahora controlado por KYC_WALL_ENFORCED.

          Historia: la bandera existía en featureFlags.ts:102 puesta en false,
          pero NADIE la leía (era código muerto), así que el muro se pintaba
          siempre. Combinado con el backend de KYC caído (Railway devuelve 404
          en todas sus rutas), eso dejaba a TODO pasajero nuevo — incluido el
          revisor de Google Play — atrapado en un bucle sin salida:
          muro → KYC → servidor inexistente → "Verificación Fallida" → muro.
          Ese es el rechazo vc16 "Your app does not open or load".

          Ahora la bandera SÍ manda. Con KYC_WALL_ENFORCED = false el pasajero
          entra al mapa y el KYC queda como paso opcional (sigue exigido para
          CONDUCIR, que es donde importa de verdad). Vuelve a ponerla en true
          cuando el backend de verificación esté vivo otra vez. */}
      {KYC_WALL_ENFORCED && !isVerified && role === 'PASSENGER' && (
        <View style={styles.safetyWallContainer}>
          <View style={styles.safetyWallCard}>
            <View style={styles.shieldIconContainer}>
              <Ionicons name="shield-checkmark" size={60} color="#FF9500" />
            </View>
            
            <Text style={styles.safetyWallTitle}>Muro de Seguridad</Text>
            <Text style={styles.safetyWallSubtitle}>Validación de Identidad Obligatoria</Text>
            
            {/* ── ESTE MURO PROMETÍA UNA BIOMETRÍA QUE NO EXISTE ──────────────
                Decía "validación biométrica en tiempo real" y "escudo de perfil
                biométrico aprobado". Los Términos y la Política de Privacidad de
                esta MISMA app dicen en negrita lo contrario: la selfie y la
                cédula las compara a mano una persona de Go Safely, sin
                reconocimiento facial, sin prueba de vida y sin plantillas
                faciales (terms.tsx, privacy.tsx, kyc-biometrics.tsx).
                Con un rechazo de Google Play encima por la declaración de datos
                biométricos, una pantalla que promete lo que la letra pequeña
                niega no es un matiz de redacción: es la contradicción que el
                revisor busca. Aquí se dice lo que de verdad se hace. */}
            <Text style={styles.safetyWallText}>
              Para entrar al mapa, ver tarifas y viajar, te pedimos una selfie y la foto de tu cédula. Las compara a mano una persona autorizada de Go Safely: no usamos reconocimiento facial, prueba de vida ni ningún sistema automático, y no generamos plantillas de tu rostro.
            </Text>
            
            <View style={styles.safetyInstructions}>
              <View style={styles.instructionItem}>
                <Ionicons name="camera" size={20} color={colors.primary} />
                <Text style={styles.instructionItemText}>Una selfie tomada con la cámara</Text>
              </View>
              <View style={styles.instructionItem}>
                <Ionicons name="card" size={20} color={colors.primary} />
                <Text style={styles.instructionItemText}>Documento de identidad oficial</Text>
              </View>
              <View style={styles.instructionItem}>
                <Ionicons name="shield-checkmark" size={20} color={colors.primary} />
                <Text style={styles.instructionItemText}>Revisión a mano de nuestro equipo</Text>
              </View>
            </View>
            
            <TouchableOpacity
              style={[styles.verifyButton, { backgroundColor: colors.accent }]}
              onPress={() => router.push('/kyc-welcome')}
            >
              <Text style={styles.verifyButtonText}>Comenzar Verificación</Text>
              <Ionicons name="arrow-forward" size={20} color="#FFF" />
            </TouchableOpacity>
            {/* FIX AUDIT #2: botón "Saltar por ahora (Modo Pruebas)" ELIMINADO.
                Hacía setVerified(true) bypaseando KYC para cualquier usuario.
                La única forma de pasar el muro es completar la verificación. */}
          </View>
        </View>
      )}
    </View>
  );
}

// MAPA PREMIUM STYLE (DARK MODE / CORPORATIVO)
const premiumMapStyle = [
  {
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#212121"
      }
    ]
  },
  {
    "elementType": "labels.icon",
    "stylers": [
      {
        "visibility": "off"
      }
    ]
  },
  {
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#757575"
      }
    ]
  },
  {
    "elementType": "labels.text.stroke",
    "stylers": [
      {
        "color": "#212121"
      }
    ]
  },
  {
    "featureType": "administrative",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#757575"
      }
    ]
  },
  {
    "featureType": "road",
    "elementType": "geometry.fill",
    "stylers": [
      {
        "color": "#2c2c2c"
      }
    ]
  },
  {
    "featureType": "road.highway",
    "elementType": "geometry.fill",
    "stylers": [
      {
        "color": "#3c3c3c"
      }
    ]
  },
  {
    "featureType": "water",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#000000"
      }
    ]
  }
];

// COMPONENTE DE MAPA AISLADO PARA OPTIMIZAR RENDERIZADO
// Previene que toda la pantalla y formularios se re-rendericen cada vez que llega un tick de driver.
/**
 * Rótulo del marcador del chofer asignado.
 *
 * Lo calcula el padre y se pasa entero para no romper el `React.memo`: un
 * objeto literal nuevo en cada render volvería a rasterizar el mapa.
 * Todos los campos pueden faltar — cuando faltan NO se inventa nada, se deja
 * el genérico de siempre.
 */
type RotuloChofer = {
  emoji: string;
  titulo: string;
  descripcion?: string;
};

/**
 * Tope de carritos pintados a la vez.
 *
 * No es un número de gusto: cada marcador con emoji cuesta una Bitmap medida y
 * rasterizada en el hilo principal (ver `src/components/Map/MapView.tsx`), y con
 * cincuenta a la vez Android levanta un ANR. Doce llenan la pantalla de sobra a
 * zoom de barrio, y los que no caben se cuentan en la etiqueta.
 */
const MAX_CARRITOS_PINTADOS = 12;

/**
 * CUÁNTO SE ESPERA ANTES DE VOLVER A PEDIR LA POSICIÓN DEL CHOFER, y cuántas
 * veces. Cuatro intentos repartidos en ~35 s, con el hueco creciendo.
 *
 * No es un número a ojo: cubre la ventana entre que el pasajero ve
 * "¡Viaje confirmado!" y que el servidor acaba de conceder el permiso de
 * lectura sobre `driverLocations/{chofer}`. Se para a los 35 s a propósito —
 * reintentar para siempre contra una regla que de verdad deniega es una
 * suscripción latiendo y cobrando todo el viaje para nada.
 */
const PLAZOS_POSICION_CHOFER = [4000, 6000, 10000, 15000];

type SolicitudEnMapa = {
  id: string;
  latitude: number;
  longitude: number;
  enZona: boolean;
  precio: number;
  km: number | null;
};

const LiveMapContainer = React.memo(({ onVehiclePress, routeCoordinates, mapRegion, enabled = true, enFoco = true, driverCoord = null, pickupCoord = null, rectaRespaldo = null, rotuloChofer = null, puntosParaEncuadrar = null, altoHoja = 0, reencuadrarAhora = 0, solicitudes = [] }: { onVehiclePress?: (vehicle: any) => void, routeCoordinates?: any[], mapRegion?: any, enabled?: boolean, enFoco?: boolean, driverCoord?: { latitude: number; longitude: number } | null, pickupCoord?: { latitude: number; longitude: number } | null, rectaRespaldo?: { latitude: number; longitude: number }[] | null, rotuloChofer?: RotuloChofer | null, puntosParaEncuadrar?: any[] | null, altoHoja?: number, reencuadrarAhora?: number, solicitudes?: SolicitudEnMapa[] }) => {
  // Ref para animateToRegion imperativo — se dispara cada vez que el padre
  // actualiza mapRegion (GPS inicial, botón centrar, etc.) para que el mapa
  // se mueva suavemente sin esperar al ciclo de re-render de region={}.
  const mapRef = useRef<any>(null);

  // ══ ¿YA EXISTE EL MAPA DE GOOGLE POR DEBAJO? ═════════════════════
  //
  // Tener el componente montado NO significa que el mapa nativo exista. Entre
  // que React pinta el <MapView> y Google Play Services entrega el objeto
  // `GoogleMap` pasan cientos de milisegundos — y en un teléfono lento, o con
  // otra versión de Play Services, más. Durante esa ventana el mapa es una
  // caja vacía que acepta props y no puede hacer nada con ellas.
  //
  // ESO CAUSÓ LOS DOS FALLOS QUE EL FUNDADOR REPORTÓ, y los dos son el mismo:
  //
  //  1. LA APP SE CERRABA. Medido en Crashlytics con datos reales: 19 cierres,
  //     4 usuarios, `com.rnmaps.maps.MapView.applyBaseMapPadding` →
  //     `NullPointerException` en `GoogleMap.setPadding` sobre un objeto null.
  //     Es un fallo de la librería: `MapManager.setMapPadding` protege la
  //     primera llamada (`applyBaseMapPadding` aplaza si la vista aún no está
  //     medida) y en la línea SIGUIENTE hace `view.map.setPadding(...)` sin
  //     comprobar nada. Con `map` en null, cierre en seco. Por eso apareció en
  //     vc36, que es cuando añadimos `mapPadding` para arreglar el encuadre.
  //
  //  2. LA LÍNEA DE RUTA NO APARECÍA. Los efectos de cámara se disparaban
  //     igual, `fitToCoordinates` no hacía nada sobre un mapa inexistente, y
  //     como sus dependencias ya no volvían a cambiar, **el encuadre se perdía
  //     para siempre**. La línea SÍ se dibujaba; la cámara nunca iba hacia ella.
  //
  // Con esta bandera, nada toca el mapa antes de tiempo y todo lo que se
  // aplazó se aplica en cuanto está listo (por eso va en las dependencias).
  //
  // ══ POR QUÉ ES UN CONTADOR Y NO UN BOOLEANO ══════════════════════════════
  //
  // Era `useState(false)` y `setMapaListo(true)`. En Android, `onMapReady`
  // vuelve a dispararse CADA VEZ que la vista se re-adjunta a la ventana (la
  // librería recrea el `GoogleMap` en `onAttachedToWindow`), que es justo lo
  // que pasa al volver de otra pestaña — aquí el mapa no se desmonta, se oculta
  // con `display:'none'`. Pero `setMapaListo(true)` sobre `true` no cambia el
  // estado, así que no re-renderiza y NINGUNO de los tres efectos de cámara
  // volvía a correr: se cambiaba de pestaña, se volvía, y el mapa se quedaba
  // sin padding, sin encuadre y sin seguir al chofer, para siempre.
  //
  // Un contador SÍ cambia de valor en cada aviso. `mapaListo` sigue existiendo
  // para las guardas (se lee igual de bien), pero las DEPENDENCIAS miran el
  // contador, que es lo único que distingue "ya estaba listo" de "acaba de
  // volver a estarlo".
  const [avisosDeMapaListo, setAvisosDeMapaListo] = useState(0);
  const mapaListo = avisosDeMapaListo > 0;

  useEffect(() => {
    // ── LA MISMA GUARDA QUE EL EFECTO DE AL LADO, Y POR LA MISMA RAZÓN ──────
    //
    // Este efecto lleva la cámara a UN solo punto con zoom fijo (0.01). Cuando
    // hay una ruta trazada, el encuadre de más abajo ya puso la cámara donde
    // cabe TODO — y entonces llegaba el siguiente latido del GPS, `mapRegion`
    // cambiaba unos metros, y esto deshacía el encuadre recentrando sobre un
    // punto único, con la ruta fuera de pantalla.
    //
    // Pasaba en los primeros 30 segundos (el vigilante del GPS se apaga solo a
    // los 30 s), que es exactamente la ventana en la que alguien mira el mapa
    // después de pedir el viaje: la ruta se encuadraba bien y se deshacía sola
    // un segundo después.
    //
    // Con dos o más puntos que abarcar, manda el encuadre. El efecto de al lado
    // (seguir al chofer) ya traía esta guarda; a este le faltaba.
    if (puntosParaEncuadrar && puntosParaEncuadrar.length >= 2) return;
    if (!mapaListo || !mapRef.current || !mapRegion) return;
    mapRef.current.animateToRegion(
      {
        latitude: mapRegion.latitude,
        longitude: mapRegion.longitude,
        latitudeDelta: mapRegion.latitudeDelta ?? 0.01,
        longitudeDelta: mapRegion.longitudeDelta ?? 0.01,
      },
      800
    );
    // Dependencias por VALOR y no por objeto: `mapRegion` se recrea en cada
    // latido del GPS aunque las coordenadas sean las mismas, y eso lanzaba una
    // animación de 800 ms que chocaba con la del encuadre. Ese choque era el
    // "temblor" del mapa.
  }, [avisosDeMapaListo, mapRegion?.latitude, mapRegion?.longitude, puntosParaEncuadrar]);

  // FIX TRACKING (#3) — Seguir al chofer en vivo: cada vez que llega una nueva
  // posición del chofer durante el viaje, el mapa se mueve suavemente para
  // mantenerlo a la vista (así el pasajero SIEMPRE ve por dónde viene y si se
  // detuvo). Solo se activa cuando hay driverCoord (viaje activo).
  const estadoEncuadre = useRef(nuevoEstadoEncuadre());

  // ── CADA AVISO ES UN MAPA NUEVO POR DEBAJO ──────────────────────────────
  // `encuadrarMapa` recuerda el último encuadre aplicado para no repetirlo por
  // moverse dos metros. Al re-adjuntarse la vista, la cámara nativa vuelve a
  // su región inicial y ese recuerdo deja de describir nada: sin borrarlo, el
  // umbral de 120 m bloquearía el re-encuadre y el mapa se quedaría mirando el
  // centro de la ciudad con la ruta fuera de pantalla.
  //
  // Va declarado ANTES que los efectos de cámara a propósito: React los corre
  // en orden de declaración, así que el borrado ocurre antes del re-encuadre.
  useEffect(() => {
    if (!avisosDeMapaListo) return;
    estadoEncuadre.current = nuevoEstadoEncuadre();
  }, [avisosDeMapaListo]);

  useEffect(() => {
    // Cuando hay dos o más puntos que abarcar manda el encuadre de abajo. Si
    // los dos efectos movieran la cámara a la vez, se pisarían y el mapa
    // temblaría — que es como se veía antes.
    if (puntosParaEncuadrar && puntosParaEncuadrar.length >= 2) return;
    if (!mapaListo || !mapRef.current || !driverCoord) return;
    mapRef.current.animateToRegion(
      { latitude: driverCoord.latitude, longitude: driverCoord.longitude, latitudeDelta: 0.012, longitudeDelta: 0.012 },
      700
    );
  }, [avisosDeMapaListo, driverCoord?.latitude, driverCoord?.longitude, puntosParaEncuadrar]);

  /**
   * EL ENCUADRE. Mantiene a la vista todo lo que importa en cada fase:
   * el chofer y el pasajero, o la ruta entera si ya está trazada.
   */
  useEffect(() => {
    // Sin el mapa nativo, `fitToCoordinates` no hace nada Y el efecto no vuelve
    // a correr: el encuadre se perdería entero. Esperar aquí es lo que hace que
    // la ruta se vea.
    if (!mapaListo) return;
    encuadrarMapa(mapRef, estadoEncuadre.current, {
      puntos: puntosParaEncuadrar ?? [],
      margenArribaDp: 120,
      // Un respiro pequeño, NO el alto de la tarjeta: ese ya va en `mapPadding`,
      // y en Android los dos márgenes se suman. Poniendo los dos, la ruta se
      // encuadraría con el doble de margen y saldría ridículamente lejos.
      margenAbajoDp: 40,
      margenLadosDp: 48,
    });
  }, [avisosDeMapaListo, puntosParaEncuadrar, altoHoja]);

  // El botón de "mi ubicación" es una petición explícita: recupera el mando
  // aunque el usuario acabe de arrastrar el mapa.
  useEffect(() => {
    if (!reencuadrarAhora || !mapaListo) return;
    limpiarGesto(estadoEncuadre.current);
    encuadrarMapa(mapRef, estadoEncuadre.current, {
      puntos: puntosParaEncuadrar ?? [],
      margenArribaDp: 120, margenAbajoDp: 40, margenLadosDp: 48,
      forzar: true,
    });
  }, [reencuadrarAhora]);

  // Suscripción a drivers online reales desde Firestore.
  // Solo activa cuando `enabled` (típicamente: role === 'PASSENGER' Y uid
  // real confirmado por el escudo de carga del padre). Sin esa garantía, las
  // rules de Firestore rechazan la query y la app dispara errores en cascada.
  const [vehicles, setVehicles] = useState<any[]>([]);

  React.useEffect(() => {
    // GUARDIA ESTRICTA — re-validamos el uid aquí aunque el padre ya lo haya
    // chequeado. Esto blinda contra cualquier re-render que pudiera disparar
    // el listener sin sesión confirmada.
    const uid = useAppStore.getState().userFirebaseUid;
    if (!enabled || !uid) {
      // ══ NO SE VACÍAN LOS MARCADORES AL PERDER EL FOCO ══════════════════
      //
      // Esto hacía `setVehicles([])` a secas, y `enabled` incluye el foco de la
      // pestaña: al salir de Viaje se quitaban de golpe hasta 50 marcadores EN
      // EL MISMO INSTANTE en que la pantalla se desprende. Y ahí la librería
      // tiene una bomba: al perder el foco vacía su lista interna de hijos y
      // solo la restaura si el mapa estaba listo; después, quitar un marcador
      // hace `features.remove(index)` sin comprobar límites →
      // IndexOutOfBoundsException fatal. Es la misma familia del cierre de
      // `setPadding` sobre null.
      //
      // No es nuestra librería, pero SÍ es nuestro momento de borrarlos: la
      // suscripción se corta igual (no se paga una lectura de más), y los
      // marcadores se quedan montados —invisibles, el contenedor está en
      // `display:'none'`— hasta que se pueda tocar el mapa sin peligro.
      //
      // Cuando la limpieza toca de verdad (otro rol, sesión cerrada) el foco ya
      // volvió: entonces sí se vacía, con el mapa adjunto y listo.
      if (enFoco) setVehicles([]);
      return;
    }
    const unsubscribe = firestoreService.listenToOnlineDrivers((drivers) => {
      // Normalizamos a la forma {id, lat, lng, type} que el resto del componente espera.
      //
      // ── LO QUE ESTE NORMALIZADOR TIRABA A LA BASURA ─────────────────────
      // El servidor escribe `driverPhoto` e `isDriverVerified` en /drivers/{uid}
      // al aprobar el KYC, y la regla deja al pasajero leerlos. El dato viajaba,
      // llegaba al teléfono… y se botaba AQUÍ, una línea antes de pintarlo: por
      // eso la hoja "Conductor Cercano" salía siempre con un icono vacío.
      // La marca, el modelo y el color van por la misma razón: hoy /drivers
      // todavía no los lleva, y cuando el espejo los escriba el marcador podrá
      // decirlos sin tocar nada aquí.
      //
      // ── LA PLACA NO, Y NO ES UN OLVIDO ──────────────────────────────────
      // Se leía `d.licensePlate` de /drivers. Esa lectura salía SIEMPRE vacía
      // —la ronda pasada quitó la placa de esa colección a propósito— y el
      // comentario de arriba decía lo contrario, que es la peor combinación:
      // código muerto que parece pendiente de un dato que nunca debe llegar.
      // /drivers lo lee CUALQUIERA con sesión y en bloque, así que una placa
      // ahí es un padrón de matrículas con posición en vivo al lado.
      // La placa le llega al pasajero por el documento del viaje (la congela
      // `acceptRideOffer`) o por `getDriverPublicInfo`, que el servidor acota
      // al pasajero de ese viaje. Ninguna de las dos pasa por aquí.
      const mapped = drivers
        .filter((d: any) => typeof d.lat === 'number' && typeof d.lng === 'number')
        .map((d: any) => ({
          id: d.id,
          lat: d.lat,
          lng: d.lng,
          type: d.vehicleType ?? 'CAR',
          userName: d.userName,
          rating: d.rating,
          ratingCount: d.ratingCount ?? null,
          driverPhoto: d.driverPhoto ?? null,
          isDriverVerified: d.isDriverVerified === true,
          vehicleBrand: d.vehicleBrand ?? null,
          vehicleModel: d.vehicleModel ?? null,
          vehicleColor: d.vehicleColor ?? null,
        }));
      setVehicles(mapped);
    });
    return () => unsubscribe();
    // `enFoco` va en las dependencias porque decide CUÁNDO es seguro vaciar:
    // sin él, el barrido de limpieza se quedaría pendiente para siempre.
  }, [enabled, enFoco]);

  const { default: MapView, MapMarker, MapPolyline } = require('../../components/Map/MapView');

  // FALLBACK ROBUSTO: si por cualquier razón mapRegion llega undefined desde el padre,
  // arrancamos centrados en Santo Domingo en vez del default global del SDK
  // (que muestra Asia/EUA aleatoriamente y crea la impresión de "app demo").
  const SANTO_DOMINGO_FALLBACK = {
    latitude: 18.4861,
    longitude: -69.9312,
    latitudeDelta: 0.08,
    longitudeDelta: 0.08,
  };
  const effectiveRegion = mapRegion ?? SANTO_DOMINGO_FALLBACK;

  // ══ CUÁNTOS CARRITOS SE PINTAN A LA VEZ ══════════════════════════════════
  //
  // Cada marcador con hijo React obliga a Android a MEDIR, MAQUETAR y
  // RASTERIZAR una Bitmap EN EL HILO PRINCIPAL durante el medio segundo que
  // `MapView.tsx` deja el seguimiento encendido. Con 50 a la vez, y encima
  // mientras el mapa inicializa, eso es un ANR de manual — el "se congela" que
  // el fundador reportó y que no deja ni un stack trace.
  //
  // LA PROP QUE HABÍA PREPARADA NO SE ACTIVA: `pinNativo` quita el hijo (coste
  // cero) pero cambia el emoji por el pin rojo de Google. El aspecto de los
  // carritos es identidad, no una decisión técnica, así que queda como está.
  //
  // Lo que sí se puede hacer sin tocar cómo se ven: pintar MENOS. Se quedan los
  // más cercanos al centro de lo que se está mirando —que son los únicos que
  // alguien va a tocar— y se DICE cuántos hay en total, para que nadie crea que
  // la app solo encontró doce.
  const ultimosCarritosRef = useRef<any[]>([]);
  const carritosVisibles = useMemo(() => {
    // SIN FOCO NO SE CAMBIA LA LISTA. Quitar un marcador mientras la pantalla se
    // desprende es la misma bomba que el efecto de arriba esquiva: la librería
    // ya vació su lista interna de hijos y `features.remove(index)` se sale de
    // rango. Congelada, no se quita ninguno hasta que se vuelva a mirar.
    if (!enFoco) return ultimosCarritosRef.current;
    let lista = vehicles;
    if (vehicles.length > MAX_CARRITOS_PINTADOS) {
      const cLat = effectiveRegion.latitude;
      const cLng = effectiveRegion.longitude;
      // Distancia al cuadrado en grados: no hace falta la raíz ni la corrección
      // por latitud para ORDENAR, y así son 50 restas en vez de 50 trigonometrías.
      lista = [...vehicles]
        .sort((a, b) => {
          const da = (a.lat - cLat) ** 2 + (a.lng - cLng) ** 2;
          const db = (b.lat - cLat) ** 2 + (b.lng - cLng) ** 2;
          return da - db;
        })
        .slice(0, MAX_CARRITOS_PINTADOS);
    }
    ultimosCarritosRef.current = lista;
    return lista;
    // El centro se redondea a ~100 m: con el valor crudo, cada latido del GPS
    // reordenaría la lista y montar/desmontar marcadores es justo el trabajo que
    // estamos evitando.
  }, [vehicles, enFoco, Math.round(effectiveRegion.latitude * 1000), Math.round(effectiveRegion.longitude * 1000)]);
  const carritosNoPintados = vehicles.length - carritosVisibles.length;

  // ══ SIN FOCO NO SE DESMONTA NINGÚN HIJO DEL MAPA ════════════════════
  //
  // Es la misma bomba que ya esquivaban los marcadores de choferes, y la ronda
  // pasada solo se protegió esa lista: los otros cuatro hijos se quedaron sin la
  // guarda, así que el cierre siguió ahí.
  //
  // EN ANDROID, cuando la vista del mapa pierde el foco, `react-native-maps`
  // hace `savedFeatures = new ArrayList<>(features); features.clear()`: su lista
  // nativa de hijos queda VACÍA. Si en ese instante React desmonta cualquier
  // hijo, la librería llama a `features.remove(index)` sin comprobar límites →
  // IndexOutOfBoundsException y la app se cierra en seco. Misma familia que el
  // `setPadding` sobre null.
  //
  // Y los cuatro desaparecen SOLOS, sin que nadie toque nada: basta con que
  // llegue el snapshot que termina o cancela el viaje (se van el marcador del
  // chofer, el de recogida y las dos líneas de golpe), o que Directions
  // responda y la ruta por calles sustituya a la recta de respaldo. Eso ocurre
  // igual mientras el usuario está en Comida, en Actividad o en Perfil — es
  // decir, exactamente con la pestaña sin foco.
  //
  // Por eso se congelan TODOS a la vez y en un solo sitio: sin foco se sigue
  // pintando lo último que ya estaba pintado (invisible de todos modos, el
  // contenedor está en `display:'none'`), y al volver la pestaña se aplica de
  // golpe lo que haya cambiado, con el mapa adjunto y en condiciones de
  // aceptarlo. Los efectos de cámara siguen leyendo los props VIVOS: mover la
  // cámara no toca la lista de hijos.
  const ultimosHijosRef = useRef({ routeCoordinates, rectaRespaldo, driverCoord, pickupCoord, rotuloChofer });
  const hijos = useMemo(() => {
    if (!enFoco) return ultimosHijosRef.current;
    const actuales = { routeCoordinates, rectaRespaldo, driverCoord, pickupCoord, rotuloChofer };
    ultimosHijosRef.current = actuales;
    return actuales;
  }, [enFoco, routeCoordinates, rectaRespaldo, driverCoord, pickupCoord, rotuloChofer]);

  return (
    <>
    <MapView
      ref={mapRef}
      style={styles.map}
      // FIX PERF (#2) — `initialRegion` (no `region`). Con `region` controlado,
      // cada render re-aplicaba la región y "peleaba" con los animateToRegion
      // imperativos de arriba → parpadeo/salto del mapa. Ahora la cámara se
      // inicializa una sola vez y todo movimiento posterior lo manejan los
      // efectos animateToRegion (GPS, centrar, seguir al chofer).
      initialRegion={effectiveRegion}
      showsUserLocation={true}
      showsMyLocationButton={false}
      /* EL MAPA YA EXISTE: avisa aquí. Hasta este momento no se le puede pedir
         nada al mapa nativo (ver el comentario largo de `mapaListo` arriba). */
      onMapReady={() => setAvisosDeMapaListo((n) => n + 1)}
      /* El mapa reserva abajo el alto REAL de la tarjeta. Con esto, el "centro"
         del mapa pasa a ser el centro de la franja que SE VE, no el del
         rectángulo completo: es lo que arregla el "no aparece centralizado" que
         reportó el fundador, y evita que el chofer quede detrás de la tarjeta.

         ⚠️ LA PROP NO SE PASA HASTA QUE EL MAPA EXISTE, y la ausencia tiene que
         ser REAL (por eso el reparto condicional y no `undefined`): mientras la
         clave `mapPadding` no esté en las props, la librería no ejecuta su
         setter, que es el que revienta. Esto es lo que cerró los 19 cierres de
         Crashlytics. NO lo conviertas en `mapPadding={mapaListo ? x : undefined}`
         — con Fabric eso puede seguir invocando el setter. */
      {...(mapaListo
        ? { mapPadding: { top: 0, right: 0, bottom: altoHoja, left: 0 } }
        : {})}
      /* Si el usuario arrastra el mapa con el dedo, deja de re-encuadrarse solo
         durante unos segundos. Se lee `isGesture` a propósito: nuestras propias
         animaciones también disparan este evento, pero con esa marca en falso.
         Sin esto, la app le devolvería la cámara cada vez que intentara mirar
         otra cosa. */
      onRegionChangeStart={(_r: any, detalles: any) => {
        if (detalles?.isGesture) marcarGestoDelUsuario(estadoEncuadre.current, 12000);
      }}
    >
      {/* DOS PUNTOS COMO MÍNIMO. Con uno solo se dibujaba una línea invisible:
          el mapa quedaba mudo y parecía que la app no había hecho nada. */}
      {hijos.routeCoordinates && hijos.routeCoordinates.length >= 2 && (
        <MapPolyline coordinates={hijos.routeCoordinates} strokeColor="#00C853" strokeWidth={5} />
      )}

      {/* RECTA DE RESPALDO — se dibuja solo cuando todavía no hay ruta por las
          calles (antes se pintaban las dos superpuestas, la verde por la calle
          y esta atravesando manzanas por encima).

          Antes exigía `driverCoord && pickupCoord`, y `driverCoord` solo se
          llena cuando el rol es PASSENGER: en el teléfono del CHOFER era null
          siempre, así que si Directions fallaba él se quedaba con el mapa
          pelado. Ahora los extremos los calcula el padre para el perfil que
          esté mirando, y los dos tienen respaldo. */}
      {hijos.rectaRespaldo && hijos.rectaRespaldo.length === 2 && !(hijos.routeCoordinates && hijos.routeCoordinates.length >= 2) && (
        <MapPolyline coordinates={hijos.rectaRespaldo} strokeColor="#208AEF" strokeWidth={5} />
      )}
      {/* Marcador EN VIVO del chofer asignado.
          El rótulo llega calculado desde el padre con el vehículo REAL. Si no
          hay datos del vehículo, `rotuloChofer` viene en null y se pinta el
          genérico de siempre: nunca se inventa una marca ni una placa. */}
      {hijos.driverCoord && (
        <MapMarker
          key="assigned-driver-live"
          coordinate={hijos.driverCoord}
          title={hijos.rotuloChofer?.titulo ?? 'Tu chofer'}
          description={hijos.rotuloChofer?.descripcion ?? 'Viene en camino'}
          iconEmoji={hijos.rotuloChofer?.emoji ?? '🚗'}
        />
      )}
      {/* Punto de recogida del pasajero */}
      {hijos.pickupCoord && (
        <MapMarker
          key="pickup-point"
          coordinate={hijos.pickupCoord}
          title="Punto de recogida"
          iconEmoji="📍"
        />
      )}

      {/* ── DÓNDE HAY GENTE ESPERANDO ────────────────────────────────────
          Una chincheta por solicitud viva. El emoji distingue las de su zona
          (🙋) de las de fuera (🟠), con el mismo criterio que el sello ámbar
          de la tarjeta: aquí no se esconde ninguna, igual que allí.
          `mapaListo` por el mismo motivo que los carritos: esperar a que el
          mapa exista antes de meterle marcadores. */}
      {mapaListo && solicitudes.map((s) => (
        <MapMarker
          key={`solicitud-${s.id}`}
          coordinate={{ latitude: s.latitude, longitude: s.longitude }}
          title={s.precio > 0 ? `Viaje por RD$ ${s.precio.toLocaleString('es-DO')}` : 'Solicitud de viaje'}
          description={
            (s.km !== null ? `A ${s.km < 1 ? 'menos de 1' : Math.round(s.km)} km de ti` : 'Esperando chofer') +
            (s.enZona ? ' · en tu zona' : ' · fuera de tu zona')
          }
          iconEmoji={s.enZona ? '🙋' : '🟠'}
        />
      ))}

      {/* Vehículos online (solo cuando NO hay viaje activo — al explorar).

          `mapaListo` en la condición NO es decorativo: sin él, las hasta 50
          Bitmaps se rasterizaban en el hilo principal justo mientras el mapa
          nativo se estaba inicializando, que es el peor momento posible. Ahora
          esperan a que el mapa exista; llegan unos milisegundos más tarde y
          nadie lo nota. */}
      {mapaListo && !hijos.driverCoord && carritosVisibles.map((v: any) => (
        <MapMarker
          key={v.id}
          coordinate={{ latitude: v.lat, longitude: v.lng }}
          title={v.type === 'CAR' ? 'Carro' : v.type === 'MOTORCYCLE' ? 'Motor' : 'Minibus'}
          description="Toca para seleccionar"
          iconEmoji={v.type === 'CAR' ? '🚗' : v.type === 'MOTORCYCLE' ? '🏍️' : '🚐'}
          onPress={() => onVehiclePress && onVehiclePress(v)}
        />
      ))}
    </MapView>
    {/* SE DICE CUÁNTOS HAY. Pintar menos sin avisar sería mentir por omisión:
        quien ve doce carritos en una ciudad llena creería que no hay más. */}
    {mapaListo && !hijos.driverCoord && carritosNoPintados > 0 && (
      <View pointerEvents="none" style={[styles.contadorCarritos, { bottom: 16 + altoHoja }]}>
        <Text style={styles.contadorCarritosTexto}>
          {`Mostrando ${carritosVisibles.length} de ${vehicles.length} choferes cercanos`}
        </Text>
      </View>
    )}
    </>
  );
});
LiveMapContainer.displayName = 'LiveMapContainer';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    // ── POR QUE ESTE FONDO YA NO ES NEGRO ─────────────────────────────────
    // Era '#000', y es la UNICA superficie negra a pantalla completa en todo
    // el camino del pasajero. El mapa de Google se dibuja ENCIMA de este
    // contenedor; si por lo que sea no llega a componerse —depende de la
    // version de Play Services y del driver de graficos del telefono, no de
    // nuestro codigo— lo que queda es negro absoluto.
    //
    // Un usuario del fundador lo vivio asi: instalacion limpia, inicia sesion,
    // y "se queda en negro". La app estaba VIVA todo el tiempo. No habia
    // ningun error que atrapar, porque no habia error: habia un color.
    //
    // Con el fondo claro, ese mismo fallo deja de parecer una app muerta: se
    // ven la cabecera, el boton SOS y la hoja de busqueda, y se diagnostica de
    // un vistazo. Un fallo visible se arregla; uno negro se abandona.
    backgroundColor: Colors.background,
    ...(Platform.OS === 'web' ? {
      maxWidth: 800,
      width: '100%',
      alignSelf: 'center',
    } : {}),
  },
  map: {
    width: '100%',
    height: '100%',
  },
  myLocationButton: {
    position: 'absolute',
    right: 16,
    bottom: height * 0.52 + 16,
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#FFF',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.18,
    shadowRadius: 6,
    elevation: 6,
    zIndex: 5,
  },
  headerOverlay: {
    position: 'absolute',
    top: Platform.OS === 'ios' ? 50 : 30,
    left: 20,
    right: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: Colors.primary,
    padding: 12,
    borderRadius: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 5,
    elevation: 5,
  },
  roleBadge: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  roleText: {
    color: '#FFF',
    fontSize: 12,
    fontWeight: 'bold',
  },
  logoText: {
    fontSize: 20,
    fontWeight: '900',
  },
  logoHighlight: {
    color: Colors.accent,
  },
  bellButton: {
    padding: 4,
    position: 'relative',
  },
  notifBadge: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  sheetHandle: {
    width: 40,
    height: 5,
    borderRadius: 3,
    backgroundColor: '#D0D0D0',
    alignSelf: 'center',
    marginTop: -8,
    marginBottom: 12,
  },
  bottomSheet: {
    position: 'absolute',
    bottom: 0,
    width: '100%',
    backgroundColor: Colors.white,
    borderTopLeftRadius: 30,
    borderTopRightRadius: 30,
    padding: SIZES.padding,
    paddingTop: 16,
    // El 110/95 estaba calibrado a ojo contra la tab bar flotante, sin contar
    // la barra del sistema: con edge-to-edge el contenido del panel quedaba
    // debajo de los tres botones de Android. Ahora se le suma el inset real.
    paddingBottom: (Platform.OS === 'ios' ? 110 : 95),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -5 },
    shadowOpacity: 0.1,
    shadowRadius: 10,
    elevation: 20,
  },
  sheetTitle: {
    fontSize: SIZES.large,
    fontWeight: 'bold',
    color: Colors.text,
    marginBottom: 16,
    textAlign: 'center',
  },
  input: {
    backgroundColor: Colors.background,
    borderWidth: 1,
    borderColor: Colors.border,
    borderRadius: SIZES.radius,
    height: 50,
    paddingHorizontal: 16,
    fontSize: SIZES.medium,
    color: Colors.text,
    marginBottom: 12,
  },
  row: {
    flexDirection: 'row',
  },
  driverControls: {
    position: 'absolute',
    bottom: Platform.OS === 'ios' ? 110 : 95,
    left: 20,
    right: 20,
  },
  onlineStatus: {
    backgroundColor: Colors.accent,
    padding: 12,
    borderRadius: 20,
    alignItems: 'center',
    marginTop: 12,
    shadowColor: Colors.accent,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
  },
  routeText: {
    fontSize: SIZES.medium,
    fontWeight: '600',
    color: Colors.textSecondary,
    marginBottom: 4,
    textAlign: 'center',
  },
  passengerCountText: {
    fontSize: SIZES.font,
    color: Colors.primary,
    fontWeight: 'bold',
    marginBottom: 16,
    textAlign: 'center',
  },
  routeCard: {
    borderWidth: 1,
    borderRadius: 16,
    padding: 16,
    marginBottom: 20,
  },
  routeNode: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 12,
  },
  square: {
    width: 12,
    height: 12,
    borderRadius: 4,
    marginRight: 12,
  },
  routeLine: {
    height: 24,
    borderLeftWidth: 2,
    marginLeft: 5,
    marginVertical: 4,
  },
  routeTextContainer: {
    flex: 1,
  },
  routeLabel: {
    fontSize: 11,
    fontWeight: '600',
    marginBottom: 2,
  },
  routeAddress: {
    fontSize: 14,
    fontWeight: '700',
  },
  sosButton: {
    position: 'absolute',
    right: 20,
    top: Platform.OS === 'ios' ? 120 : 100,
    backgroundColor: '#FF3B30',
    width: 50,
    height: 50,
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#FF3B30',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.4,
    shadowRadius: 8,
    elevation: 8,
    zIndex: 10,
  },
  // Atenuado + el giro dentro: las dos señales juntas, porque con la mano
  // encima del teléfono y el sol de frente una sola no se lee.
  sosButtonTrabajando: {
    opacity: 0.7,
  },
  sosText: {
    color: '#FFF',
    fontSize: 10,
    fontWeight: '900',
    marginTop: -2,
  },
  // El aviso de que el viaje gratis no llega: naranja de advertencia, no rojo
  // de error — no es un fallo, es una condición que el pasajero puede aceptar.
  avisoDiferencia: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginTop: 8,
    marginHorizontal: 4,
    padding: 10,
    borderRadius: 10,
    backgroundColor: 'rgba(255,149,0,0.12)',
  },
  avisoDiferenciaTexto: { flex: 1, fontSize: 12, lineHeight: 17, color: '#B36200', fontWeight: '600' },
  preferencesRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
    gap: 8,
  },
  prefPill: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.05)',
  },
  prefText: {
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 6,
  },
  promoBannerPassenger: {
    position: 'absolute',
    top: Platform.OS === 'ios' ? 120 : 100,
    left: 20,
    right: 80,
    backgroundColor: '#FFF3E0',
    borderRadius: 12,
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#FFE0B2',
    shadowColor: '#E65100',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  promoTextPassenger: {
    color: '#E65100',
    fontSize: 12,
    marginLeft: 8,
    flex: 1,
    lineHeight: 16,
  },
  promoBannerDriver: {
    position: 'absolute',
    top: Platform.OS === 'ios' ? 120 : 100,
    left: 20,
    right: 20,
    backgroundColor: '#E8F5E9',
    borderRadius: 12,
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#C8E6C9',
    shadowColor: '#2E7D32',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  promoTextDriver: {
    color: '#2E7D32',
    fontSize: 12,
    marginLeft: 8,
    flex: 1,
    lineHeight: 16,
  },
  safetyWallContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(10, 37, 64, 0.96)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    zIndex: 10000,
  },
  safetyWallCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 24,
    padding: 24,
    alignItems: 'center',
    width: '100%',
    maxWidth: 380,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.25,
    shadowRadius: 15,
    elevation: 10,
  },
  shieldIconContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: 'rgba(255, 149, 0, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  safetyWallTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#0A2540',
    textAlign: 'center',
    marginBottom: 4,
  },
  safetyWallSubtitle: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#FF9500',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 16,
    textAlign: 'center',
  },
  safetyWallText: {
    fontSize: 14,
    color: '#627D98',
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 20,
  },
  safetyInstructions: {
    width: '100%',
    backgroundColor: '#F5F7FA',
    borderRadius: 16,
    padding: 16,
    marginBottom: 24,
    gap: 12,
  },
  instructionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  instructionItemText: {
    fontSize: 13,
    color: '#0A2540',
    fontWeight: '600',
  },
  verifyButton: {
    width: '100%',
    height: 52,
    borderRadius: 16,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    shadowColor: '#00C853',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 4,
  },
  verifyButtonText: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: 'bold',
  },
  bypassButton: {
    marginTop: 16,
    paddingVertical: 8,
  },
  bypassButtonText: {
    color: '#627D98',
    fontSize: 12,
    fontWeight: 'bold',
    textDecorationLine: 'underline',
  },
  serviceTypeContainer: {
    flexDirection: 'row',
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    borderRadius: 14,
    padding: 4,
    marginBottom: 12,
  },
  serviceTab: {
    flex: 1,
    paddingVertical: 10,
    alignItems: 'center',
    borderRadius: 10,
  },
  serviceTabActive: {
    backgroundColor: Colors.accent,
  },
  serviceTabText: {
    fontSize: 14,
    fontWeight: '600',
  },
  serviceTabTextActive: {
    fontWeight: 'bold',
  },
  /** Etiqueta de cuántos choferes se están pintando. `bottom` se fija en línea,
   *  según el alto de la tarjeta, para que nunca quede debajo de ella. */
  contadorCarritos: {
    position: 'absolute',
    left: 16,
    // Negro translucido a propósito: es un rótulo SOBRE EL MAPA, y el mapa no
    // sigue el tema de la app. Con un color de la paleta se perdería encima de
    // las calles claras en un tema y de las oscuras en el otro.
    backgroundColor: 'rgba(0,0,0,0.65)',
    borderRadius: 10,
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  contadorCarritosTexto: {
    color: '#FFF',
    fontSize: 11,
    fontWeight: '600',
  },
  /** Franja de estado de la ruta. `top` se fija en línea, según haya cintillo. */
  franjaRuta: {
    position: 'absolute',
    left: 20,
    right: 80,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderRadius: 12,
    borderWidth: 1,
    paddingVertical: 9,
    paddingHorizontal: 12,
    elevation: 3,
  },
  /** El color entra en línea desde el tema (ver la franja en el render). */
  franjaRutaTexto: {
    flex: 1,
    fontSize: 11.5,
    lineHeight: 15,
    fontWeight: '600',
  },
  gpsValidatedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 200, 83, 0.1)',
    borderRadius: 8,
    paddingVertical: 6,
    paddingHorizontal: 10,
    marginTop: 4,
    alignSelf: 'flex-start',
  },
  montoSugerido: {
    paddingHorizontal: 14,
    paddingVertical: 9,
    borderRadius: 20,
    borderWidth: 1.5,
  },
  montoSugeridoTexto: {
    fontSize: 13.5,
    fontWeight: '700',
  },
  // ── Sector obligatorio del destino ──────────────────────────────────
  /** Caja del PIN. Colores en línea desde el tema: el marrón sobre ámbar que
   *  tenía era ilegible en oscuro, y ahí el PIN es lo único que hace arrancar
   *  el viaje. */
  cajaPinCompartir: {
    marginTop: 12,
    borderRadius: 14,
    borderWidth: 1,
    padding: 14,
  },
  cajaPinTitulo: {
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.6,
  },
  cajaPinNumero: {
    fontSize: 30,
    fontWeight: '900',
    letterSpacing: 6,
    marginTop: 4,
  },
  cajaPinAyuda: {
    fontSize: 11.5,
    lineHeight: 16,
    marginTop: 4,
  },
  /** Rótulo de cada uno de los dos campos de dirección (RECOGIDA / DESTINO).
   *  El color entra en línea desde el tema. */
  etiquetaCampo: {
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.6,
    marginBottom: 6,
    marginLeft: 2,
  },
  avisoOtraPersona: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    borderRadius: 10,
    borderWidth: 1,
    paddingVertical: 8,
    paddingHorizontal: 10,
    marginTop: 8,
  },
  avisoOtraPersonaTexto: {
    flex: 1,
    fontSize: 11.5,
    lineHeight: 16,
    fontWeight: '600',
  },
  etiquetaSector: {
    fontSize: 12.5,
    fontWeight: '700',
    marginBottom: 6,
  },
  campoSector: {
    borderWidth: 1.5,
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 11,
    fontSize: 15,
  },
  ayudaSector: {
    fontSize: 11.5,
    marginTop: 6,
    lineHeight: 16,
  },
  // ── Destino visible para el chofer ANTES de aceptar ─────────────────
  destinoAviso: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    borderWidth: 1,
    borderRadius: 12,
    padding: 12,
    marginBottom: 8,
  },
  destinoEtiqueta: {
    fontSize: 10,
    fontWeight: 'bold',
    letterSpacing: 1,
    marginBottom: 2,
  },
  destinoTexto: {
    fontSize: 14,
    fontWeight: '600',
    lineHeight: 19,
  },
  destinoSector: {
    fontSize: 13.5,
    fontWeight: '700',
    color: '#00875A',
    marginTop: 3,
  },
  destinoSinSector: {
    fontSize: 11.5,
    fontStyle: 'italic',
    marginTop: 3,
  },
  gpsValidatedText: {
    color: '#00C853',
    fontSize: 12,
    fontWeight: '600',
  },
  suggestionsDropdown: {
    position: 'absolute',
    top: 54,
    left: 0,
    right: 0,
    borderRadius: 12,
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 6,
    elevation: 5,
    zIndex: 999,
  },
  suggestionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderBottomWidth: 1,
  },
  suggestionTitle: {
    fontSize: 14,
    fontWeight: '600',
  },
  suggestionSubtitle: {
    fontSize: 11,
    color: '#829AB1',
    marginTop: 2,
  },
  vehicleSelectorLabel: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  vehicleSelectorRow: {
    flexDirection: 'row',
    gap: 10,
    width: '100%',
  },
  vehicleTypeCard: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  vehicleTypeEmoji: {
    fontSize: 22,
    marginBottom: 4,
  },
  vehicleTypeName: {
    fontSize: SIZES.font,
    fontWeight: 'bold',
  },
  vehicleIconImage: {
    width: 40,
    height: 40,
    marginBottom: 4,
  },
  vehicleTypeDesc: {
    fontSize: 10,
    color: '#829AB1',
    marginTop: 2,
  },
});
